/**
 * English locale dictionary.
 *
 * Acts as the schema: every other locale is typed against this object so
 * missing keys break the build. Add a new key here first, then translate.
 *
 * `as const` is intentionally NOT used: we want the inferred type to use
 * `string` at the leaves so other locales can supply their own values.
 * Missing keys still fail the build because the structural shape is enforced.
 */

import { marketingEn } from "./marketing";
import { projectSetupEn } from "./project-setup";

export const en: DictionaryShape = {
  /** Bell notifications for a booting preview (server/preview/preview-notice.ts). */
  previewNotice: {
    readyTitle: "Preview is ready",
    readyMessage: "{target} is running and ready to open.",
    failedTitle: "Preview did not start",
    failedMessage: "{target} did not start: {reason}.",
  },
  /** "Review pull request" step before a patch is opened as a PR on the git host. */
  prPreview: {
    title: "Review pull request",
    subtitle: "This is how the pull request and commit will appear on the git host. Adjust if needed, then confirm.",
    loading: "Preparing pull request\u2026",
    loadFailed: "Could not prepare the pull request.",
    target: "{repo} \u2190 into {base}",
    unverified: "Opened without passing verification: a notice with your reason is placed at the top of the description.",
    titleLabel: "Title",
    descriptionLabel: "Description",
    descriptionPlaceholder: "Why this change is needed, what reviewers should look at\u2026",
    tabPullRequest: "Pull request",
    tabCommit: "Commit message",
    branchLine: "wants to merge into {base}",
    cancel: "Cancel",
    confirm: "Open pull request",
    opening: "Opening\u2026",
  },
  /**
   * The sandbox-run list shared by the issue's Sandbox tab and the patch
   * page's verification card. One vocabulary, because the two surfaces show
   * the same rows and must not name them differently.
   */
  sandboxRunList: {
    loading: "Loading verifications\u2026",
    empty: "No verifications yet.",
    open: "Open run",
    showAll: "Show {n} older",
    // A run always verifies ONE patch version. Saying which one is the whole
    // point of the marker: a green run for an earlier draft is not a green
    // light for the version on screen.
    olderVersion: "earlier version",
    olderVersionHint: "This run verified an earlier version of this change, not the current one.",
  },
  agentSandbox: {
    title: "Agent sandbox",
    intro: "How much this project's coding agent may do inside its container. You can only be stricter than the platform allows, never wider — so a wrong value here can hobble your own project and nothing else.",
    disabledNotice: "The agent sandbox is off on this deployment. These values are stored but do nothing yet: the agent currently runs on the server itself. Your FixControl administrator can enable this.",
    shell: "Shell",
    shellHint: "May the agent run commands in its container?",
    shellLevel: {
      none: "No shell",
      readonly: "May run, may not edit",
      full: "Full",
    },
    network: "Network",
    networkHint: "How far the container may reach. Note: only 'none' and 'open' are enforced by Docker itself.",
    networkLevel: {
      none: "No network",
      anthropic: "Model API only",
      registries: "Also package registries",
      open: "Open",
    },
    writeScope: "Write scope",
    writeScopeHint: "Where the agent may write. Enforced by the mount, not by the tool list.",
    writeScopeLevel: {
      none: "Read only",
      worktree: "Its own checkout only",
      worktree_and_tmp: "Checkout and /tmp",
    },
    maxRuntimeMs: "Runtime (ms)",
    maxCommands: "Max commands",
    cpus: "CPUs",
    memoryMb: "Memory (MB)",
    pids: "Processes",
    inherit: "Inherit ({value})",
    inheritHint: "Leaving a box empty means inherit from the platform. The grey value under a box is what a run actually gets.",
    clamped: "clamped to",
    clampNotice: "The platform ceiling is lower than this project asked for. The stored wish is kept — if the ceiling is raised later, your value applies after all.",
    saved: "Saved",
    loadFailed: "Could not load the agent sandbox policy",
    saveFailed: "Save failed",
    notANumber: "{field} is not a number",
  },
  // Public marketing-page copy (use-cases, integrations, pricing). Kept in
  // per-page fragments under ./marketing so pages translate independently.
  ...marketingEn,
  // Project setup redesign copy, one fragment per track (./project-setup).
  ...projectSetupEn,
  // Members page: the last-admin refusal, now that organization admins and
  // managers are exempt from it (they keep managing the project without a
  // member row), names the way out instead of a dead end.
  projectMembersGuard: {
    lastAdmin: "This person is the last project administrator. Make someone else project administrator first, or ask an administrator of your organization to make this change.",
  },
  // Shared data-table column headers + creator-attribution labels. Lives in
  // its own namespace (covered by DictionaryShape's index signature) so every
  // queue table can reuse the same "Created" / "Modified" / "Created by"
  // strings without a per-page key. See <RelativeTime> / <CreatedBy>.
  datatable: {
    created: "Created",
    updated: "Modified",
    createdBy: "Created by",
    resizeColumn: "Adjust column width",
    resizeHint: "Drag to resize · double-click to restore",
    resetWidths: "Reset column widths",
    actor: {
      system: "System",
      device: "API",
      source: {
        slack: "Slack",
        email: "E-mail",
        api: "API",
        intake: "Intake form",
        split: "Issue split",
        seed: "Demo data",
      },
    },
  },
  common: {
    loading: "Loading…",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    create: "Create",
    close: "Close",
    open: "Open",
    yes: "Yes",
    no: "No",
    search: "Search",
    back: "Back",
    next: "Next",
    submit: "Submit",
    error: "Something went wrong.",
    failedToLoad: "Failed to load",
    tryAgain: "Retry",
    empty: "Nothing to show",
    filters: {
      project: "Project",
      allProjects: "All projects",
      clearProject: "Clear project filter",
      /** The name list failed to load, so the id is all the control has —
       *  say that instead of printing a bare `proj-mtwtr2ck`. */
      projectUnnamed: "Project {id} (name unavailable)",
    },
    /** Provider ids that are ordinary words, not product names — the brand
     *  names live in src/lib/display-labels.ts and are not translated. */
    providers: {
      email: "Email",
      webhook: "Webhook",
      local: "Local",
      ssh: "SSH",
    },
    /** Tenant-wide user roles (users.role). */
    roles: {
      admin: "Admin",
      manager: "Manager",
      operator: "Operator",
    },
  },

  language: {
    label: "Language",
    switchTo: "Switch to {name}",
  },

  nav: {
    /** Accessible name for the main left rail — the settings rail is also
     *  an <aside>, and two unnamed complementary landmarks are
     *  indistinguishable to anyone navigating by landmark. */
    sidebarLabel: "Main navigation",
    overview: "Overview",
    work: "Work",
    aiResults: "AI results",
    context: "Context",
    agents: "Agents",
    system: "System",
    fixcontrol: "FixControl",
    aiOperations: "AI Operations",
    aiOperationsMetrics: "Operational metrics",
    platform: "Platform",
    dashboard: "Dashboard",
    inbox: "Inbox",
    notifications: "Notifications",
    approvals: "Approvals",
    issues: "Issues",
    customerDirectory: "Contacts",
    missions: "AI Missions",
    projects: "Projects",
    workspaces: "Work sessions",
    patches: "Patches",
    sandboxRuns: "Sandbox runs",
    followUps: "Follow-ups",
    knowledge: "Knowledge",
    codebasePosture: "Codebase health",
    memory: "AI memory",
    tenants: "Organizations",
    aiTeam: "AI Team",
    skills: "Skills",
    governance: "Governance",
    governanceHierarchy: "Policy & scope",
    orchestrationConsole: "Orchestration",
    runs: "Agent runs",
    commandCenter: "Command Center",
    settings: "Settings",
    customers: "Customers",
    signups: "Signups",
    adminSettings: "Settings",
    patchDelivery: "Patch delivery",
    gitProvidersHealth: "Git providers health",
    office365Health: "Office 365 health",
    knowledgeEmbeddings: "Knowledge embeddings",
    brandExplorer: "Brand Explorer",
    internalDocs: "Documentation",
    aiCapacity: "AI capacity",
    newIssue: "New Issue",
    signOut: "Sign out",
    activeAgents: "{count} active",
    agentsRunning: "{count} agent running",
    agentsRunningPlural: "{count} agents running",
    taskLabels: {
      support: {
        dashboard: "Overview",
        inbox: "Customer questions",
        issues: "Tickets",
        knowledge: "Knowledge",
      },
      engineering: {
        issues: "Problems",
        missions: "AI investigations",
        patches: "Proposed changes",
        sandboxRuns: "Checks & reviews",
        workspaces: "Work sessions",
      },
    },
    profileHint: {
      text: "You're seeing every section. Pick a work profile for a tidier navigation that fits your task.",
      action: "Choose work profile",
      dismiss: "Hide",
    },
  },

  breadcrumb: {
    back: "Back",
    integrations: "Channels",
  },

  topbar: {
    dashboard: "Dashboard",
    live: "Live",
    searchPlaceholder: "Search or jump to…",
    commandCenter: "Command Center",
    openCommandCenter: "Open Command Center (AI assistant)",
    openNavigation: "Open navigation",
    openHelp: "Open help for this page",
    supportMode: "Support mode",
    supportModeDetail: "— viewing {name} as FixControl staff. Actions are recorded against this tenant.",
    backToCustomers: "Back to customers",
    stopSupport: "Stop support",
    stopSupportTitle: "Switch back to your home tenant ({name})",
  },

  help: {
    drawerLabel: "Help",
    title: "Help",
    browseTitle: "All topics",
    close: "Close help",
    backToTopic: "Back to topic",
    openExternal: "Open in new tab",
    openExternalTitle: "Open this article in the standalone documentation site",
    browseAll: "Browse all topics",
    sectionEmpty: "(no pages)",
    loading: "Loading…",
    empty: "No documentation found.",
    audienceUser: "User Guide",
    audienceAdmin: "Admin Guide",
    readMore: "Read more",
    /**
     * One sentence each, for the concepts this product cannot avoid putting
     * on screen. Written for someone who has never read the architecture and
     * never should have to: what it is, in terms of their own goal. The
     * drawer is where the long version lives.
     */
    explain: {
      validationStrategy: "Where FixControl proves a change works before anyone approves it — on FixControl's own machines, or inside your environment when the change needs systems only you can reach.",
      environmentSettings: "How your application starts. FixControl works this out from the repository; set it here when you know better, and every check from then on starts it your way.",
      /**
       * The environment settings FIELDS, one sentence each. Every one says
       * what the field does and what happens if it is changed — the second
       * half is the part a form normally leaves to the reader's imagination,
       * and the reader here has never heard of a container.
       */
      envStartCommand: "How your application is started once everything is installed. FixControl filled this in from the run that actually worked; change it only if it started the wrong part of your project.",
      envTargetName: "A name for this way of starting your application, so the rest of FixControl can refer to it — the preview link, for one. Only you see it, and renaming it changes nothing about how the application runs.",
      envCommand: "The one command that starts your application, exactly as you would type it yourself. Get it wrong and the check fails with the application never answering; leave it alone and the next check starts it the way the last successful one did.",
      envPort: "The number your application listens on inside its own machine. It is never opened to the internet — FixControl connects to it from the inside. Put the wrong number here and the check waits at a door with nothing behind it, and fails.",
      envReadinessPath: "The page FixControl opens to decide your application is up — often / or a status page. Pick one that answers only when the application is genuinely ready: a page that answers too early makes a broken start look successful.",
      envReadinessTimeout: "How many seconds your application may take before it answers that page. Too low and a slow but healthy start is reported as a failure; too high and a genuinely broken start keeps every check waiting for nothing.",
      envVars: "Settings your application reads from its surroundings, one NAME=value per line, set every time it starts. Never put passwords or keys here — they are stored exactly as typed. The values FixControl generates or reads from your own systems are listed above and need no entry.",
      envSystemPackages: "Extra software the machine needs to build your code — the PostgreSQL client library, say. What you add comes on top of what is already installed; leaving one out removes nothing. A name outside the approved list is refused with the reason, never installed quietly.",
      envInstallSteps: "Extra commands to run AFTER the preparation steps above, for a project that needs one more thing before it can be built. They run in order on every check and add to the wait, so leave this empty unless a check demonstrably failed for want of one.",
      envNotes: "Anything else FixControl should know when it works out how to run this project, in your own words. It is read the next time the environment is worked out, and never executed.",
      envSaveAndVerify: "Save keeps your settings for the next check. Save and verify also starts your application once, now, to prove the settings work — the only way to find that out before a real change depends on them.",
      preview: "A running copy of your application with the proposed change in it, on a private link that expires. Click through it yourself before you approve.",
      browserVerification: "FixControl opens the running copy in a browser and records what it saw — so \"it works\" is a screenshot and a page that answered, not a claim.",
      reproduction: "When a check fails, FixControl looks around inside the environment it failed in and writes down what it found, before it tries again.",
      fcAgent: "A small service you run in your own environment. It calls out to FixControl, never the other way round, so FixControl can check a change against systems that are not reachable from the internet.",
      approvalRouting: "Who gets asked before something happens, and for which kind of change. Riskier changes ask more senior people; nothing reaches a customer or production without the answer being recorded.",
      agentsVsSkills: "Agents are the workers — each has a job and a rulebook. Skills are the things a worker knows how to do. Give an agent a skill and it can do that; take it away and it cannot.",
      sandbox: "A throwaway copy of your project, cut off from the internet and from your systems. Every proposed change is tried there first, and nothing in it survives the check.",
      aiCapacity: "Whether the AI providers behind FixControl are refusing work — out of credit, rate-limited, or asked for more than a model can read at once — and who that is affecting.",
    },
  },

  tenantSwitcher: {
    heading: "Switch workspace",
    selectTenant: "Select workspace",
    noTenant: "No workspace",
    empty: "No workspaces yet.",
    manage: "Manage workspaces",
    switching: "Switching…",
    home: "Home",
    platform: "Platform",
    blocked: "Blocked",
    deleting: "Being deleted",
    roleLabel: "Your role: {role}",
    errorTitle: "Could not switch workspace",
    dismiss: "Dismiss",
    multiTab: "Switching changes the workspace for every open tab.",
    errors: {
      not_a_member: "You are not a member of that workspace.",
      tenant_blocked: "That workspace is blocked. Ask your platform administrator to unblock it.",
      tenant_deleting: "That workspace is being deleted and can no longer be opened.",
      session_revoked: "Your session is no longer valid for that workspace. Sign in again.",
      two_factor_reauth_required: "That workspace requires two-factor verification. Sign in again with your code.",
      two_factor_setup_required: "That workspace requires two-factor authentication. Set it up first.",
      user_inactive: "Your account is inactive. Contact an administrator.",
      rate_limited: "Too many switches in a short time. Wait a moment and try again.",
      csrf_origin_mismatch: "The request was blocked for security reasons. Reload the page and try again.",
      unknown: "Switching workspace failed. Try again.",
    },
  },

  userMenu: {
    openMenu: "Open user menu",
    profile: "Profile",
    changePassword: "Change password",
    docs: "Docs",
    accountSettings: "Account settings",
    signOut: "Sign out",
  },

  pages: {
    hrDemo: {
      eyebrow: "HR governance demo",
      title: "Safe HR service desk",
      subtitle: "Identity, sources, personal data, policy, review and audit are enforced server-side.",
      tenantLabel: "Tenant",
      projectLabel: "project",
      scenarioGeneral: "General policy",
      scenarioPersonal: "Personal",
      scenarioConfidential: "Confidential",
      scenarioConflict: "Source conflict",
      questionLabel: "Employee question",
      submit: "Submit question",
      submitting: "Processing safely…",
      submitFailed: "The question could not be processed.",
      boardLoadFailed: "The HR board could not be loaded.",
      boardLoading: "Loading the HR board…",
      policyDecision: "Policy decision",
      sources: "Sources",
      sourceVersion: "version {version} · {status}",
      noAuthoritativeSource: "No valid authoritative source.",
      sourceConflict: "Source conflict",
      caseLabel: "Case {id}",
      personalData: "Minimal system data: {hours} leave hours, as of {asOf}.",
      salaryRule: "Salary rule: {rule}.",
      versionLabel: "version {version}",
      reviewWork: "Review work",
      noReviewTasks: "No visible review tasks.",
      auditTimeline: "Privacy-aware audit timeline",
      noAuditEvents: "No visible audit events.",
      casesTitle: "Cases",
      noCases: "No visible cases.",
      answersTitle: "Answers",
      noAnswers: "No visible answers.",
      correctionsTitle: "Open corrections",
      assignedTo: "Assigned to {user}",
      unassigned: "Not assigned yet",
      assignLoad: "Assign…",
      assignAction: "Assign",
      escalateAction: "Escalate",
      resolveAction: "Resolve",
      resolutionPlaceholder: "Resolution (required to resolve)",
      requestApproval: "Request approval",
      approve: "Approve",
      reject: "Reject",
      deliver: "Deliver",
      fourEyesNote: "Four-eyes: a second reviewer must decide this approval.",
      reportIncorrect: "Report incorrect",
      reportReasonPlaceholder: "Why is this answer incorrect?",
      correctAction: "Publish correction",
      retractAction: "Retract",
      correctedTextPlaceholder: "Corrected answer text",
      correctionReasonPlaceholder: "Correction / retraction reason",
      actionDone: "Done — the board below reflects the new state.",
      actionFailed: "The action was not executed.",
      settingsTitle: "HR governance (project setting)",
      settingsEnabled: "Enabled for this project",
      settingsThreshold: "Auto-answer confidence threshold",
      save: "Save",
      settingsSaved: "Settings saved.",
      /**
       * Readable labels for the stored HR vocabulary. The values themselves
       * (case status, sensitivity, classification, policy decision, reason
       * code) stay exactly as the database and the API return them — this map
       * is presentation only. One flat map because the same value means the
       * same thing wherever it appears (a DRAFT source and a DRAFT answer are
       * both a draft); an unknown value falls back to the raw code so a new
       * enum member is visibly untranslated instead of silently mislabelled.
       */
      labels: {
        // Case status
        NEW: "New",
        TRIAGED: "Triaged",
        WAITING_FOR_INFORMATION: "Waiting for information",
        WAITING_FOR_HUMAN_REVIEW: "Waiting for human review",
        IN_REVIEW: "In review",
        ANSWER_PROPOSED: "Answer proposed",
        APPROVAL_REQUIRED: "Approval required",
        RESOLVED: "Resolved",
        ESCALATED: "Escalated",
        BLOCKED: "Blocked",
        CANCELLED: "Cancelled",
        // Answer / approval / correction status
        GENERATED: "Generated",
        DRAFT: "Draft",
        REVIEW_REQUIRED: "Review required",
        APPROVED: "Approved",
        DELIVERED: "Delivered",
        UNDER_REVIEW: "Under review",
        CORRECTED: "Corrected",
        SUPERSEDED: "Superseded",
        RETRACTED: "Retracted",
        PENDING: "Pending",
        REJECTED: "Rejected",
        EXPIRED: "Expired",
        INVALIDATED: "Invalidated",
        CONSUMED: "Used",
        OPEN: "Open",
        ANSWERED: "Answered",
        // Sensitivity
        NORMAL: "Normal",
        SENSITIVE: "Sensitive",
        HIGHLY_RESTRICTED: "Highly restricted",
        // Data classification
        INTERNAL: "Internal",
        PERSONAL: "Personal",
        CONFIDENTIAL_HR: "Confidential HR",
        PAYROLL_CONFIDENTIAL: "Payroll confidential",
        MEDICAL_RESTRICTED: "Medical, restricted",
        LEGAL_RESTRICTED: "Legal, restricted",
        // Question type
        GENERAL_POLICY: "General policy",
        PERSONAL_INFORMATION: "Personal information",
        PERSONAL_CALCULATION: "Personal calculation",
        PROCEDURAL_REQUEST: "Procedural request",
        SENSITIVE_EMPLOYEE_RELATIONS: "Sensitive employee relations",
        MEDICAL_OR_ABSENCE: "Medical or absence",
        PAYROLL: "Payroll",
        LEGAL_OR_COMPLIANCE: "Legal or compliance",
        UNKNOWN: "Unknown",
        // Policy decision
        AUTO_ANSWER_ALLOWED: "Automatic answer allowed",
        DRAFT_ONLY: "Draft only",
        HUMAN_REVIEW_REQUIRED: "Human review required",
        ESCALATION_REQUIRED: "Escalation required",
        DENIED: "Denied",
        CLARIFICATION_REQUIRED: "Clarification required",
        // Review task type
        SENSITIVE_CASE: "Sensitive case",
        ANSWER_APPROVAL: "Answer approval",
        INCORRECT_ANSWER: "Reported as incorrect",
        CONNECTOR_FAILURE: "Connector failure",
        // Source status
        AUTHORITATIVE: "Authoritative",
        SUPPORTING: "Supporting",
        DEPRECATED: "Deprecated",
        // Reason codes
        SOURCE_CONFLICT: "Source conflict",
        SOURCE_NOT_FOUND: "No authoritative source",
        SOURCE_EXPIRED: "Source expired",
        LOW_CONFIDENCE: "Confidence below threshold",
        PERSONAL_IMPACT: "Personal salary impact",
        CURRENT_AUTHORITATIVE_SOURCE: "Current authoritative source",
        SAFE_DEFAULT: "Safe default",
        DATA_SCOPE_VIOLATION: "Outside your own data",
        CONNECTOR_NOT_ALLOWLISTED: "Connector not allowlisted",
        EXTERNAL_CONNECTOR_UNAVAILABLE: "Source system unreachable",
        SOURCE_METADATA_INCOMPLETE: "Incomplete source metadata",
        EXPIRED_OR_INACTIVE_SOURCE_PRESENT: "Expired or inactive source present",
        AUTHORITATIVE_RULE_CONFLICT: "Authoritative sources contradict each other",
        DRAFT_CONTRADICTS_AUTHORITATIVE: "Draft contradicts the authoritative source",
      },
    },
    customers: {
      eyebrow: "Customer context",
      indexTitle: "Contacts",
      indexSubtitle: "The customers and contacts behind governed customer communication.",
      searchPlaceholder: "Search customers…",
      statusActive: "Active",
      statusArchived: "Archived",
      statusAll: "All",
      newCustomer: "New customer",
      createTitle: "New customer",
      nameLabel: "Customer name",
      contactLegend: "First contact (optional)",
      firstName: "First name",
      lastName: "Last name",
      email: "E-mail",
      contactRole: "Role",
      create: "Create",
      cancel: "Cancel",
      save: "Save",
      colName: "Customer",
      colStatus: "Status",
      colContacts: "Contacts",
      colUpdated: "Updated",
      empty: "No customers yet",
      emptyHint: "Create a customer here, or from Slack with /fixcontrol customer create.",
      loadError: "Customers could not be loaded.",
      notFound: "Customer not found.",
      backToList: "All customers",
      detailEyebrow: "Customer",
      rename: "Rename",
      archive: "Archive",
      reactivate: "Reactivate",
      syncAction: "Sync to supportdesk",
      syncOk: "Synced to the supportdesk.",
      syncNotConnected: "No supportdesk connector is configured.",
      contactsTitle: "Contacts",
      contactsEmpty: "No contacts yet.",
      addContact: "Add contact",
      editContact: "Edit",
      contactArchived: "archived",
      communicationsTitle: "Communications",
      communicationsEmpty: "No governed communications for this customer yet.",
      communicationsHint: "Draft one from Slack with /fixcontrol email draft — every send goes through policy and human approval.",
      commSubjectFallback: "(no subject)",
      commStatus: {
        draft: "Draft",
        pending_approval: "Awaiting approval",
        approved: "Approved",
        sent: "Sent",
        failed: "Send failed",
        rejected: "Rejected",
        discarded: "Discarded",
      },
      toastCreated: "Customer created.",
      toastUpdated: "Customer updated.",
      toastContactAdded: "Contact added.",
      toastContactUpdated: "Contact updated.",
    },
    issues: {
      eyebrow: "Decision engine",
      title: "Issues",
      subtitle: "Scan, decide, ship. Actions are inline — detail page only when you need context.",
      newIssue: "New Issue",
      open: "Open",
      resolved: "Resolved",
      all: "All",
      shown: "shown",
      searchPlaceholder: "Search title, key, tenant, impact…",
      empty: "Nothing matches.",
      emptyHint: "Clear filters or search to see the full queue.",
      exportCsv: "Export CSV",
      filters: {
        project: "Project",
        allProjects: "All projects",
        status: "Status",
        allStatuses: "All statuses",
        allPriorities: "All priorities",
      },
      headers: {
        key: "Key",
        issue: "Issue",
        project: "Project",
        status: "Status",
        priority: "Priority",
        actions: "Actions",
      },
    },
    patches: {
      eyebrow: "Work",
      title: "Patches",
      subtitle: "Every code patch produced for {company} — pending review, approved, applied, rejected or archived.",
      subtitleNoTenant: "Every code patch — pending review, approved, applied, rejected or archived.",
      pending: "Pending review",
      approved: "Approved",
      applied: "Applied",
      rejected: "Rejected",
      total: "Total",
      empty: "No patches match the current filter.",
      // The list is empty for two different reasons and only one of them is a
      // filter. Both now say what would put a row here.
      emptyAll: "No patches yet.",
      emptyHint: "A patch appears here when an agent proposes a code change for an issue. Start a mission from an issue to get one.",
      emptyAction: "Open issues",
      filteredEmptyHint: "Clear the filter to see every patch.",
      fileCount: "{count} file",
      fileCountPlural: "{count} files",
      workspaceLink: "workspace",
      untitledWorkspace: "Untitled workspace",
      cols: { patch: "Patch", version: "Version", status: "Status", verification: "Verification" },
      verification: { notRun: "not run" },
      rowAction: {
        approve: "Approve",
        reject: "Reject",
        archive: "Archive",
      },
      searchPlaceholder: "Search patches…",
      columnsMenu: "Columns",
      splitView: {
        enable: "Enable split view",
        disable: "Disable split view",
        detail: "Patch detail",
        resize: "Resize patch detail pane",
        emptyTitle: "Pick a patch to triage",
        emptyHint: "Click any row to load the full summary, files, and decision history here.",
      },
      bulk: {
        approve: "Approve",
        reject: "Reject",
        archive: "Archive",
        rejectTitle: "Reject the selected patches?",
        rejectBodyOne: "1 patch will be rejected.",
        rejectBodyMany: "{count} patches will be rejected.",
        archiveTitle: "Archive the selected patches?",
        archiveBodyOne: "1 patch leaves the queues. It stays under Archive, with its runs and history.",
        archiveBodyMany: "{count} patches leave the queues. They stay under Archive, with their runs and history.",
      },
      // A decision the server refused used to vanish without a word; the row
      // simply did not change. These say what happened.
      actionError: {
        approve: "Could not approve this patch: {reason}",
        reject: "Could not reject this patch: {reason}",
        archive: "Could not archive this patch: {reason}",
        gateOpen: "an approval is still waiting on it. Reject it instead, so that approval is settled.",
        notArchivable: "it is already finished.",
        unknown: "the server refused it.",
      },
      preview: {
        start: "Start preview",
        starting: "Preview starting…",
        open: "Open preview",
        dismiss: "Dismiss",
        startFailed: "Could not start the preview.",
        openFailed: "Could not open the preview.",
        gone: "This preview is no longer running.",
      },
      filter: {
        pending: "Pending",
        approved: "Approved",
        applied: "Applied",
        rejected: "Rejected",
        archived: "Archive",
        all: "All",
      },
      status: {
        draft: "Draft",
        approved: "Approved",
        rejected: "Rejected",
        applied: "Applied",
        changes_requested: "Changes requested",
        archived: "Archived",
        superseded: "Superseded",
      },
      supersededHint: "A newer version of this patch exists in the same workspace.",
    },
    knowledge: {
      eyebrow: "Context",
      title: "Knowledge",
      subtitle: "Documents, FAQs, and references the agents can search when triaging issues.",
      addSource: "Add source",
      empty: "No sources yet.",
      emptyHint: "Add a document, paste a URL, or upload a text file (Markdown, TXT, JSON or CSV) to give the agents context.",
      inspectButton: "Inspect codebase",
      inspectButtonNoCodebase: "Inspect codebase",
      inspectTooltip: "Generate technical docs from a linked codebase",
      scrapeButton: "Scrape URL",
      stats: {
        sources: "Sources",
        sourcesSub: "{count} indexed",
        chunks: "Chunks",
        chunksSub: "Searchable units",
        codebaseDocs: "Codebase docs",
        codebaseDocsSub: "Generated by inspect",
        learnings: "Learnings",
        learningsSub: "Cases recorded",
        windev: "WINDEV",
        windevSub: "Platform sources",
        coverage: "Coverage",
        coverageSub: "Indexed ratio",
      },
      filters: {
        searchPlaceholder: "Search titles, tags, references…",
        allTenants: "All tenants",
        allTypes: "All types",
        allCodebases: "All codebases",
        general: "General",
        windev: "WINDEV",
        codebase: "Codebase docs",
        learning: "System Learning",
        allScopes: "All scopes",
        scopeTenant: "Tenant-wide",
        scopeProject: "Project",
        scopeCodebase: "Codebase",
        allProjects: "All projects",
      },
      scope: {
        column: "Scope",
        tenant: "Tenant-wide",
        project: "Project",
        codebase: "Codebase",
        tenantHint: "Available across the tenant.",
        projectHint: "Available to agents working on this project.",
        codebaseHint: "Generated from or linked to a codebase.",
        tenantWideWarning: "Tenant-wide sources may be available across projects. Use project scope for project-specific docs.",
      },
      sourcesPanelTitle: "Sources",
      sourcesPanelSub: "Click to inspect — use the trash icon to remove",
      table: {
        title: "Title",
        tenant: "Tenant",
        kind: "Kind",
        chunks: "Chunks",
        status: "Status",
        moreTags: "+{count}",
        attemptHint: "Attempt {index} of {total} on this issue. Separate patch runs with their own outcome, not a duplicate.",
      },
      noSources: "No knowledge yet",
      noSourcesHint: "Knowledge is what the agents read before they answer or propose a change: your documentation, a scraped site, or what FixControl learned from a linked codebase. Without it they work from the ticket text alone.",
      noSourcesAsk: "What knowledge should I give FixControl first?",
      filteredEmpty: "Nothing matches these filters",
      filteredEmptyHint: "Clear the search or the type, scope, project and codebase filters to see everything.",
      deleteOne: {
        confirmTitle: "Delete this knowledge source?",
        confirmLabel: "Delete source",
        confirmBody: "The source and its chunks are deleted, so agents stop retrieving from it. Answers that already cited it keep their recorded citation.",
      },
      bulkDelete: {
        confirmTitle: "Delete selected sources?",
        confirmLabel: "Delete sources",
        confirmBody: "This will permanently delete {count} knowledge source(s) and all their chunks. This cannot be undone.",
        resultDeleted: "Deleted {count} source(s).",
        resultSkipped: "{count} skipped (not found in this tenant).",
      },
    },
    memory: {
      eyebrow: "Context",
      title: "AI memory",
      subtitle: "Markdown rules every agent must follow. Global rules apply everywhere; project rules layer on top.",
      tabGlobal: "Global",
      tabProject: "Project",
      saved: "Saved",
      saving: "Saving…",
    },
    workspace: {
      eyebrow: "Work",
      title: "Work sessions",
      subtitle: "AI-assisted code change sessions.",
      patchPanel: "Patch",
      activity: "Activity",
      chat: "Chat",
      noPatch: "No patch yet.",
      runDev: "Run developer",
      reRunDev: "Re-run with feedback",
      newSession: "New code session",
      searchPlaceholder: "Search workspaces…",
      empty: "No workspaces found",
      emptyHint: "A workspace is created when an agent starts working on an issue. Start a mission from an issue to get one.",
      emptyAction: "Open issues",
      shownOf: "Showing {visible} of {total} workspaces",
      headers: {
        title: "Title",
        issue: "Issue",
        status: "Status",
        updated: "Updated",
      },
      rename: "Rename",
      renameTitle: "Rename workspace",
      deleteTitle: "Delete workspace",
      deleteConfirm: "Delete {name}? The workspace is hidden from the list and an admin can restore it. Its patches, approval decisions, sandbox verdicts and audit history are kept.",
      createTitle: "New code session",
      createSubtitle: "Choose a project and describe what you need.",
      createFields: {
        project: "Project",
        sessionTitle: "Session title",
        sessionTitlePlaceholder: "e.g. Fix payment metadata",
        linkedIssue: "Linked issue",
        linkedIssuePlaceholder: "e.g. BIZ-29",
        instruction: "Instruction",
        instructionPlaceholder: "Describe the code change you need…",
      },
      noProjects: "No projects yet.",
      createOneFirst: "Create one first",
      starting: "Starting…",
      startSession: "Start session",
      contextHint: {
        codebase: "Codebase",
        source: "Source",
        repo: "Repo",
        path: "Path",
        mode: "Mode",
      },
      status: {
        drafting: "Drafting",
        ready_for_review: "Review",
        approved: "Approved",
        rejected: "Rejected",
        applied: "Applied",
      },
      bulk: {
        delete: "Delete",
        deleteConfirmTitle: "Delete selected workspaces?",
        deleteConfirmLabel: "Delete work sessions",
        deleteConfirmBody: "This hides {count} workspace(s) from the list. An admin can restore them; their patches, approval decisions, sandbox verdicts and audit history are kept.",
        result: "{ok} deleted",
        resultPartial: "{ok} deleted, {failed} failed",
      },
    },
    sandboxRuns: {
      eyebrow: "Verification",
      title: "Sandbox runs",
      subtitle: "Every time FixControl built and tested a proposed change against a throwaway copy of your project — what it ran, and what it proved.",
      empty: "No sandbox runs yet — they appear once a patch is verified.",
      // A bare "no data" leaves the reader without the next move. Runs appear
      // once a project's environment is verified and a patch is proposed, so
      // the hint names that and the action goes there.
      emptyHint: "A run appears here as soon as a patch is verified against a project's environment. Verify a project's environment under Code & environments to get the first one.",
      emptyAction: "Open projects",
      filteredEmpty: "No runs match these filters.",
      filteredEmptyHint: "Clear the status or project filter to see every run.",
      archivedEmpty: "The archive is empty.",
      archivedEmptyHint: "Archive finished runs from the list (select them and choose Archive) to keep the list tidy. Archived runs stay openable here.",
      row: { archive: "Archive", restore: "Restore" },
      filter: { all: "All", passed: "Passed", failed: "Failed", running: "Running", errored: "Errored/timeout", cancelled: "Cancelled", archived: "Archived" },
      cols: { status: "Status", run: "Run", language: "Language", lane: "Where it ran", steps: "Steps", duration: "Duration", created: "Created" },
      lane: { local: "On FixControl", remote: "In your own cluster" },
      noPatch: "no patch bound",
      patchRef: "patch v{version}",
      patchRefId: "patch {id}",
      stepsSummary: "{passed}/{total} passed",
      // Run detail. The tiles used to be hardcoded English, and the run's
      // identifiers (executor, fingerprint, cluster, namespace, agent,
      // operation) sat next to them at the same weight — six opaque ids
      // competing with the six facts a reader came for. They are evidence:
      // real, worth keeping, and one fold down.
      detail: {
        cancel: "Cancel run",
        // Not "Re-run": pressing it never restarts THIS run — that verdict is
        // evidence the apply gate cites and is never overwritten. It starts a
        // new verification of the same patch, linked back to this one.
        rerun: "Verify again",
        loading: "Loading this run…",
        loadFailed: "Couldn't load this run: {message}",
        cancelled: "Run cancelled.",
        cancelFailed: "Couldn't cancel this run: {message}",
        archive: "Archive",
        restore: "Restore",
        archived: "Run archived. Find it under Archived.",
        archiveFailed: "Couldn't archive this run: {message}",
        restored: "Run restored from the archive.",
        restoreFailed: "Couldn't restore this run: {message}",
        archivedBanner: "This run is archived. Verdict, logs and evidence are unchanged.",
        rerunRequested: "A new verification was started.",
        rerunReused: "A verification of this exact patch version is already running — following that one.",
        rerunFailed: "Couldn't start a new verification: {message}",
        attemptOf: "attempt {index} of {total}",
        // About what a RE-RUN would do, not about this verdict: verifying
        // again re-verifies this run's patch, which is no longer the newest.
        patchSuperseded: "This run verified v{current}. The AI team has since written v{newer} \u2014 verifying again re-verifies v{current}.",
        patchSupersededLink: "Open v{version}",
        newerRunPassed: "A later verification of this patch passed.",
        newerRunFailed: "A later verification of this patch exists; it did not pass.",
        newerRunRunning: "A later verification of this patch is running.",
        newerRunLink: "Open the latest run",
        technicalDetails: "Technical details",
        artifacts: "Artifacts",
        steps: "What the check ran",
        stepLog: "Output of {name}",
        /** Shown over a step's output while the run is still going: the live
         *  stream carries the tail of each finished step, the whole log lands
         *  when the run does. */
        stepLogTail: "Last lines — the full output appears when the check finishes.",
        noSteps: "No steps have reported yet.",
        notExecuted: "This check never ran: {reason}",
        notExecutedTitle: "This check never ran",
        notExecutedTechnical: "Show the technical reason (English)",
        /**
         * The two Windows toolchains. The runtime's own refusal names a
         * TypeScript interface; these say what kind of machine the project
         * needs and whether one exists yet.
         */
        notExecutedReason: {
          dotnetframework: "This project is built with .NET Framework. It compiles against Windows-only assemblies, so FixControl can only verify it on a Windows build machine in your own environment — none is connected yet.",
          windev: "This project is built with WINDEV. It compiles and tests only through a licensed WINDEV installation, so FixControl can only verify it on a Windows machine in your own environment that has one — none is connected yet.",
        },
        notExecutedNext: {
          dotnetframework: "Connect a Windows build machine under Settings → Infrastructure. Until then FixControl reads this project and proposes changes, but never claims they were verified.",
          windev: "Connect a Windows machine with WINDEV under Settings → Infrastructure. Until then FixControl reads this project and proposes changes, but never claims they were verified.",
        },
        runError: "The check stopped with an error: {error}",
        profileNotes: "What the repository scan noticed",
        profileNotesUntranslated: "Written by the scanner while it planned this check. Technical, English only, and kept here for whoever is tuning this project's environment.",
        evidenceTitle: "Validation evidence",
        strategyFromProject: "project setting",
        none: "—",
        runId: "Run id",
        patchValue: "v{version} · {status}",
        tiles: {
          language: "Language",
          workspace: "Workspace",
          patch: "Patch",
          duration: "Duration",
          lane: "Where it ran",
          strategy: "Validation",
        },
        fields: {
          executor: "Executor",
          fingerprint: "Fingerprint",
          cluster: "Cluster",
          namespace: "Namespace",
          agent: "Agent",
          operation: "Operation",
          appStart: "Application start",
        },
        reproduction: {
          title: "What FixControl found in the failed test environment",
          lead: "FixControl looked at this failure from inside the test environment it happened in, ran {n} command(s) there, and concluded:",
          note: "The commands ran inside this run's own sandbox, which was destroyed immediately afterwards. The finding is an observation the next attempt weighs — it is not a change, and nothing was applied.",
          finding: "Finding",
          cause: "Likely cause",
          direction: "Suggested direction",
          confidence: "Confidence",
          /** How much weight the finding carries, as a sentence — the stored
           *  value is high/medium/low and a reader should not have to translate
           *  an enum into a judgement. */
          confidenceValue: {
            high: "The evidence supports this well.",
            medium: "The evidence points this way, but is not conclusive.",
            low: "This is a weak reading of thin evidence — treat it as a lead, not an answer.",
          },
          transcript: "Show the commands it ran",
          noFinding: "FixControl investigated inside the failed test environment and reached no conclusion.",
          commands: "{n} command(s)",
          exit: "exit {code}",
          outputTruncated: "… output cut off at its limit",
          /** Why a command produced nothing. The stored outcomes are
           *  ran/timeout/refused/cancelled/infrastructure. */
          outcome: {
            ran: "ran",
            timeout: "took too long and was stopped",
            refused: "not run — a limit was already reached",
            cancelled: "the run was cancelled",
            infrastructure: "the test environment could not run it",
          },
          // The opt-in, offered where a reader meets the gap it fills: a run
          // that failed and was explained only by the tail of a log. No
          // container, no shell, no capability name — what the person gets is
          // a better next attempt, and what it costs is a little time on a run
          // that already failed.
          optIn: {
            title: "Let FixControl investigate failures inside the test environment",
            body: "When a check fails, FixControl can look around in the same isolated test environment it failed in — read a configuration file, run one failing test again with more output, ask the test database what it contains — and write the next attempt from what it found instead of from the last few lines of a log. The environment has no internet and no access to your systems, it is thrown away straight afterwards, and everything FixControl did there is recorded here for you to read.",
            cost: "It adds a minute or so to a run that already failed, and nothing at all to one that passed.",
            enable: "Switch this on",
            enabling: "Switching on…",
            enabled: "On — the next failed check will be investigated.",
            failed: "Couldn't switch this on ({status}). Nothing was changed.",
            noPermission: "Only admins and managers can switch this on.",
          },
        },
        appStart: {
          title: "Did the application start?",
          verified: "Started and answered",
          failed: "Did not start",
          notAttempted: "Not started",
          unsupported: "Not verified here",
          target: "What was started",
          answered: "Answered",
          budget: "Allowed to take",
          took: "Took",
          note: "The tests can pass while the application no longer boots. This is the check that tells them apart.",
          /**
           * The consequence of each answer, said outright. "Did not start" on
           * its own invites the reader to weigh it against a green test run;
           * it is not a thing you weigh, it is the thing that decides.
           */
          consequence: {
            verified: "The application came up and answered on its own address, with this change in it.",
            failed: "FixControl could not start the application with this change. Passing tests do not prove it works — treat this run as red.",
            notAttempted: "Nobody checked whether the application still starts. The tests say nothing about that.",
            unsupported: "This environment declares nothing to start, so there was nothing to check. The tests say nothing about whether the application runs.",
          },
        },
        preview: "See it live",
        previewFailed: "Couldn't open a preview: {message}",
        previewTitle: "Live preview of this patch",
        previewOpen: "Open the application in a new tab",
        previewNote: "A temporary environment running this patch. It stops by itself, and it is not your production system.",
        previewStarting: "Starting the environment…",
        previewStartingHint: "Pulling images, starting services, waiting for the application to answer. The button to open it appears here as soon as it does.",
        previewElapsed: "{seconds}s elapsed",
        previewExpiresIn: "Stops by itself in {minutes} min.",
        previewStartFailed: "The environment did not start. There is nothing running to look at.",
        // No "start another": the run route hands back the preview that is
        // already booting for this run, so a second press cannot produce a
        // second stack — and telling a reader to ask for one is how this
        // page used to teach the habit.
        previewStartTimeout: "The environment is still starting. It keeps booting on the server — reload the page to pick it up again.",
        previewRetry: "Try again",
        environmentBlock: {
          title: "This run never judged the patch",
          // The plain-language cause comes from the shared failureKind table
          // (projectControl.environment.failureKind) so the run page and the
          // project's environment card cannot tell two different stories.
          body: "{reason}. The environment is what failed, so nothing here is evidence about the patch — and that is why no revision was started.",
          /** Unknown is not evidence that the environment failed; only that
           *  nothing classified this. Name the step and decline to apportion
           *  blame. */
          bodyUnknown: "The cause could not be determined, so this run does not say whether the environment or the patch was at fault — and no revision was started.",
          bodyUnknownStep: "The run stopped at {step}. The cause could not be determined, so this run does not say whether the environment or the patch was at fault — and no revision was started.",
          // Shown INSIDE the regression card, above the id lists, whenever the
          // gate's "new failures" are the environment's failures wearing the
          // patch's name (a new test that cannot reach a database is new AND
          // red, and the gate has no way to tell those apart on its own).
          verdictCaveat: "These ids are new failures, not proven regressions: the environment failed in this run, so a test that never ran to completion counts as new.",
          openProject: "Open the project's environment",
          // The run carried no project, so it never had a verified environment
          // to start in. Replaces the failureKind sentence: naming a database
          // that did not boot sends the reader after a service nothing asked
          // for, while the actual repair is one link away.
          noProjectTitle: "This run had no project",
          noProjectBody: "The issue is not linked to a project, and the project is what carries the verified test environment — so there was nothing to start this change in. Link the issue to a project on its workspace, then run the verification again.",
        },
        regressionVerdict: {
          headPassed: "Tests passed — regression-aware",
          headFailed: "Tests failed — regression-aware",
          // Two different non-verdicts, and they must not share a sentence:
          // one suite was stopped before it could speak, the other spoke
          // without having run anything.
          headNeverRan: "Tests never ran — no verdict",
          headRanNothing: "Tests ran nothing — no verdict",
          headUiUnsupported: "UI tests could not run in this test environment",
          headUiNotRun: "The patch's UI tests did not run",
          note: "Pre-existing failures don't block the patch; only regressions or acceptance misses do.",
          whyPassed: "{preexisting} pre-existing failure(s) carried over, 0 regressions.",
          whyPassedFixed: "{preexisting} pre-existing failure(s) carried over, 0 regressions, {fixed} newly fixed.",
          whyNeverRan: "The test suite never produced a verdict — an earlier required step stopped the run, so the counters below are empty rather than clean.",
          // The bc-13 case: `--addons-path=addons` while the patch lives in
          // `addons_pos/…`. Exit 0 over a test file that was never loaded.
          whyRanNothing: "The test step ran and exited 0, but executed not a single test — the command never saw the module this change lives in, so this run proves nothing about the patch.",
          whyRegressions: "{count} new failure(s) introduced by this patch.",
          whyUnparsed: "A test step failed but produced no parseable test ids — failing closed.",
          unparsedStep: "The step that failed:",
          unparsedExit: "exit {code}",
          whyAcceptanceAbsent: "A declared acceptance test never ran.",
          whyAcceptanceFailed: "A declared acceptance test is still failing.",
          whyFixedNothing: "No new failures, but the patch fixed nothing it was meant to fix.",
          whyGeneric: "The acceptance gate did not pass.",
          whyUiUnsupported: "The patch ships UI tests, and this environment could not run a browser for them: {reason}. That says nothing about the code; it needs a test environment with a browser.",
          whyUiNotRun: "The patch ships UI tests, and none of them executed: {reason}.",
          preexisting: "Pre-existing:",
          regressions: "Regressions:",
          newlyFixed: "Newly fixed:",
          acceptance: "Acceptance:",
          acceptanceValue: "{passed}/{total} passed",
          acceptanceFailed: ", {count} failed",
          acceptanceAbsent: ", {count} absent",
          acceptanceSkipped: ", {count} skipped",
          listRegressions: "Regressions (new)",
          listUnattributed: "New failures (unattributed)",
          listPreexisting: "Pre-existing (not this patch)",
          listNewlyFixed: "Newly fixed",
          acceptanceTargets: "Acceptance targets",
          status: { passed: "passed", failed: "failed", skipped: "skipped", absent: "absent" },
        },
      },
      bulk: {
        cancel: "Cancel",
        cancelConfirmTitle: "Cancel selected runs?",
        cancelConfirmLabel: "Cancel runs",
        cancelConfirmBody: "This stops {count} run(s) that are still in progress. Finished runs are left untouched.",
        result: "{ok} cancelled",
        resultPartial: "{ok} cancelled, {failed} failed",
        archive: "Archive",
        archiveConfirmTitle: "Archive selected runs?",
        archiveConfirmLabel: "Archive runs",
        archiveConfirmBody: "This moves {count} run(s) to the archive. Runs still in progress are skipped. Verdict, logs and evidence are kept and the run stays openable.",
        archiveResult: "{ok} archived",
        archiveResultSkipped: "{ok} archived, {skipped} skipped (still running)",
        archiveResultPartial: "{ok} archived, {failed} failed",
        restore: "Restore",
        restoreResult: "{ok} restored",
        restoreResultPartial: "{ok} restored, {failed} failed",
      },
    },
    projects: {
      eyebrow: "Projects",
      indexEyebrow: "Work",
      indexTitle: "Projects",
      indexSubtitle: "Each project is one area of work — customer support, an app you maintain — with its own connections, approvals and people.",
      newProject: "New project",
      searchPlaceholder: "Search projects…",
      modeFilter: { all: "All" },
      cardLabels: {
        codebases: "Codebases",
        primary: "Primary",
        platforms: "Platforms",
        mode: "Mode",
        updated: "Updated",
      },
      guardrails: "{count} guardrail",
      guardrailsPlural: "{count} guardrails",
      empty: {
        title: "No projects yet",
        hint: "A project is one system FixControl looks after: where its work arrives, which code it lives in, and who approves a change to it. Create one and the rest of the app has something to work on.",
        ask: "What do I need to set up a project?",
      },
      countLabel: "{count} project",
      countLabelPlural: "{count} projects",
      delete: {
        title: "Delete project",
        confirm: "Delete \"{name}\"? The project becomes unavailable for new work and an admin can restore it. Its audit history, approval decisions and deployment evidence are kept, and existing sessions keep their resolved context.",
        cancel: "Cancel",
        confirmButton: "Delete",
      },
      bulk: {
        delete: "Delete",
        deleteConfirmTitle: "Delete selected projects?",
        deleteConfirmLabel: "Delete projects",
        deleteConfirmBody: "This makes {count} project(s) unavailable for new work. An admin can restore them; their audit history, approval decisions and deployment evidence are kept.",
        result: "{ok} deleted",
        resultPartial: "{ok} deleted, {failed} failed",
      },
      create: {
        title: "New project",
        subtitle: "Define a reusable workspace profile with codebases and integrations.",
        nameLabel: "Project name",
        namePlaceholder: "e.g. POS Backend",
        descriptionLabel: "Description",
        descriptionPlaceholder: "Short description…",
        primaryCodebaseLabel: "Primary codebase",
        primaryCodebaseNone: "None (add later)",
        supportDeskLabel: "Support desk",
        supportDeskHint: "Handles support tickets only — new tickets are created and mirrored to the tracker, but no remediation mission auto-starts. Escalate to engineering when needed.",
        helperText: "You can add more codebases, integrations, tags, and guardrails after creation.",
        cancel: "Cancel",
        createButton: "Create project",
        creating: "Creating…",
        failed: "Failed to create project",
      },
      defaultSubtitle: "Project configuration",
      notFound: "Project not found",
      pickWorkspace: "Pick a workspace to open this project.",
      backToProjects: "Back to projects",
      back: "Back",
      patchHistory: "Patch History",
      saveChanges: "Save changes",
      savingChanges: "Saving...",
      changesSaved: "Saved",
      sections: {
        general: "General",
        executionMode: "Execution Mode",
        tags: "Tags",
        codebases: "Codebases",
        codebasesHelp: "Code repositories and folders agents operate on in this project.",
        knowledgeSources: "Knowledge sources",
        knowledgeHelp: "Context sources linked to this project's codebases.",
        guardrails: "Guardrails",
        guardrailsHelp: "Rules that agents must follow when working in this project.",
      },
      fields: {
        name: "Name",
        description: "Description",
        descriptionPlaceholder: "What is this project for?",
        addTag: "Add tag...",
        addGuardrail: "Add guardrail...",
        add: "Add",
        defaultBadge: "default",
        setAsDefault: "Set as default",
        noCodebases: "No codebase is linked to this project yet. Use “Add codebase…” to connect one.",
        analyses: "Analyses",
        technicalDocs: "Technical docs",
        procedures: "Procedures",
        general: "General",
        chunksSuffix: "chunks",
      },
      projectKnowledge: {
        title: "Project Knowledge",
        help: "Available to agents working on this project.",
        empty: "No project-specific knowledge yet. Add docs, scrape a URL or inspect a linked codebase so agents have the right context.",
        addSource: "Add project source",
        scrapeUrl: "Scrape project URL",
        inspect: "Inspect project codebases",
        viewAll: "View all knowledge for this project",
        includeGlobal: "Include tenant-wide",
        readOnly: "You don't have permission to manage knowledge for this project.",
        stats: {
          projectSources: "Project sources",
          codebaseDocs: "Codebase docs",
          scrapedUrls: "Scraped URLs",
          learnings: "Learnings",
          coverage: "Indexed",
        },
        /* The tiles are filters: clicking one narrows the list below to the
         * rows it counted. A number you cannot open is not an answer. */
        filter: {
          showing: "{label} · {count}",
          clear: "Show all",
          none: "No sources in this category.",
          allIndexed: "Every source is indexed.",
          notIndexed: "Not indexed",
          more: "…and {count} more",
        },
        groups: {
          project: "Project sources",
          codebase: "Codebase docs",
          tenant: "Tenant-wide",
        },
      },
    },
  },

  patchDetail: {
    eyebrow: "Patch",
    noFiles: "This patch changed no files.",
    reviewRail: "Review context",
    whatChanged: "What this change does",
    noSummary: "No summary was written for this patch yet.",
    refresh: {
      label: "Check",
      busy: "Checking…",
      tooltip: "Ask the code host what this pull request's state really is. The state is normally pushed by a webhook; this is how you correct it when none arrived.",
      unchanged: "unchanged",
      failed: "The delivery state could not be checked.",
    },
    ask: {
      button: "Why?",
      heading: "Ask about this file",
      close: "Close",
      placeholder: "Why was this changed? What breaks if I drop it?",
      submit: "Ask",
      thinking: "Reading this patch's record…",
      failed: "That question could not be answered right now.",
      empty: "No answer came back.",
      disclaimer:
        "Read from this patch's own record — the request, the engineer's summary, the review rationale and this diff. It explains; it decides nothing, and it is not part of the audit trail.",
      suggestion: {
        why: "Why was this file changed?",
        risk: "What is risky about this change?",
        revert: "What breaks if I drop this file from the patch?",
      },
    },
    discarded: {
      title: "The pull request was closed without being merged",
      body: "Nothing from this patch reached the codebase, even though it reads as delivered. Reject it to record that, or open a new pull request from the workspace.",
      reject: "Reject patch",
      noPermission: "Someone with approval rights has to reject this patch.",
    },
    decision: {
      title: "Decision & delivery",
      prompt: "Approve this patch to deliver it to the codebase, or reject it to send it back.",
      approve: "Approve",
      reject: "Reject",
      failed: "The decision could not be recorded.",
      noPermission: "You don’t have permission to approve or reject patches.",
    },
    browser: {
      reviewProgress: "{done}/{total} reviewed",
      filterPlaceholder: "Filter files…",
      clearFilter: "Clear filter",
      noMatches: "No files match this filter.",
      unified: "Unified",
      sideBySide: "Side by side",
      wrap: "Wrap long lines",
      expand: "Expand",
      collapse: "Exit full screen",
      inOverlay: "This diff is open full screen.",
      prevFile: "Previous file",
      nextFile: "Next file",
      copyPath: "Copy path",
      copied: "Copied",
      markReviewed: "Mark reviewed",
      reviewed: "Reviewed",
      kindFullFile: "whole file",
      kindUnified: "changed parts",
      noDiff: "No diff to display.",
      hintNav: "file",
      hintReviewed: "reviewed",
      hintSplit: "side by side",
      hintExpand: "full screen",
    },
    verify: {
      title: "Verification & preview",
      fact: {
        ok: "verified",
        blocked: "not cleared",
        unknown: "not verified yet",
      },
      gateOk: "The sandbox verified this patch.",
      gateBlocked: "Verification has not cleared this patch.",
      gateUnknown: "This patch has not been verified yet.",
      viewRun: "View the sandbox run ({status})",
      runStatus: {
        pending: "queued",
        provisioning: "provisioning",
        running: "running",
        passed: "passed",
        failed: "failed",
        timeout: "timed out",
        error: "error",
        cancelled: "cancelled",
        unknown: "unknown",
      },
      // The runs card below the facts row — the attempts themselves, not the
      // one word the facts row can hold.
      runsHeading: "Sandbox verifications",
      runsForVersion: "{n} of these verified v{version}, the version on this page.",
      runsNoneForVersion: "None of these verified v{version} \u2014 the version on this page has not been through the sandbox yet.",
      verifyNow: "Verify in the sandbox",
      verifyAgain: "Verify again",
      verifying: "Starting…",
      verifyFailed: "The verification could not be started.",
      runInFlight: "A sandbox run for this patch is in progress.",
      previewHeading: "Try it",
      startPreview: "Start a preview",
      previewOpening: "Starting…",
      previewRetry: "Try again",
      previewStarting: "The preview is starting…",
      previewElapsed: "{seconds}s",
      previewExpires: "Available for another {minutes} min.",
      openPreview: "Open the running change",
      previewHint: "Runs the application with this patch applied, on the environment the sandbox verified.",
      previewNeedsPass: "A preview runs the patch as a live application, so it is offered once the sandbox has verified it.",
      previewFailed: "The preview could not be started.",
      // Shown when this page stops following a boot. The boot itself keeps
      // running on the server, and pressing start again returns the same
      // preview — so the advice is to look again, never to start another.
      previewStillStarting: "Still starting — reload the page to pick it up again.",
      environmentsHeading: "Test environments",
    },
    breadcrumbVersion: "Patch v{version}",
    backToPatches: "Patches",
    loading: "Loading patch…",
    notFound: "Patch not found, or you don’t have access to it.",
    loadFailed: "Failed to load patch history.",
    newerExistsTitle: "A newer patch exists for this issue",
    newerExistsBody: "You're viewing v{current}. The latest in this chain is v{latest}.",
    openLatest: "Open latest patch",
    subtitleSingle: "v{version} · {count} file · created {date}",
    subtitlePlural: "v{version} · {count} files · created {date}",
    openWorkspace: "Open workspace",
    openIssue: "Open issue {key}",
    meta: {
      status: "Status",
      version: "Version",
      cycle: "Cycle",
      created: "Created",
      verification: "Verification",
      delivery: "Delivery",
      notDelivered: "not delivered",
      outcome: {
        none: "not delivered",
        open: "open, not merged yet",
        landed: "merged",
        discarded: "closed without merging",
        manual: "apply by hand",
      },
      churn: "Churn",
      files: "Files",
    },
    rejectedTitle: "This patch was rejected",
    rejectedBody: "Create a follow-up patch using the CTO feedback below.",
    fixAndRetry: "Fix & retry",
    starting: "Starting…",
    retryFailedDefault: "Failed to start retry",
    retryMissingSuffix: " (missing: {fields})",
    latestCtoDecision: "Latest CTO decision: {label}",
    originatingPrompt: "Originating prompt",
    cycleHistory: "Cycle history",
    chainCount: "{count} patches in chain",
    iterationCount: "{count} iteration",
    iterationCountPlural: "{count} iterations",
    inProgress: "In progress",
    qaPrefix: "QA: {id}",
    viewing: "Viewing",
    openLink: "Open →",
    ongoing: "ongoing",
    noCycleHistory: "No cycle history recorded for this patch.",
    filesInPatch: "Files in this patch",
    fileCount: "{count} file",
    fileCountPlural: "{count} files",
    expandAll: "Expand all",
    collapseAll: "Collapse all",
    cto: {
      approved: "Approved",
      changesRequested: "Changes requested",
      rejected: "Rejected",
    },
    delivery: {
      title: "Delivery",
      notDelivered: "Not delivered yet",
      approveFirst: "Approve this patch to deliver it to the codebase.",
      deliveredAt: "Delivered {date}",
      openPr: "Open pull request",
      opening: "Opening…",
      viewPr: "View pull request",
      openFailedDefault: "Could not open the pull request.",
      override: {
        title: "Verification did not pass",
        hint: "You can still open a pull request at your own risk. Nobody has verified this change — the review and CI on the pull request itself are the only checks it will get. Your reason is recorded in the audit trail and written into the pull request.",
        reasonLabel: "Why open it anyway?",
        reasonPlaceholder: "e.g. the sandbox cannot reach the licence server; the change is config-only and reviewed by hand",
        cta: "Open pull request anyway",
        busy: "Opening…",
        cancel: "Cancel",
        start: "Open anyway at my own risk",
        reasonTooShort: "Give a reason of at least 8 characters — it goes into the pull request.",
      },
      branch: "Branch",
      commit: "Commit",
      strategyLocal: "Applied to the local codebase.",
      strategySsh: "Applied on the remote host.",
      strategyManual: "Manual delivery required — apply from the workspace.",
      prStatus: {
        open: "PR open",
        merged: "Merged",
        closed: "PR closed",
      },
      ciState: {
        pending: "CI running",
        success: "CI passed",
        failure: "CI failed",
        neutral: "CI neutral",
        unknown: "CI unknown",
      },
    },
  },

  projectsList: {
    eyebrow: "Work",
    title: "Projects",
    subtitle: "Each project is one area of work — customer support, an app you maintain — with its own connections, approvals and people",
    newProject: "New project",
    searchPlaceholder: "Search projects…",
    metadata: {
      codebases: "Codebases",
      primary: "Primary",
      platforms: "Platforms",
      mode: "Mode",
      updated: "Updated",
      dash: "—",
    },
    guardrailsCount: "{count} guardrail",
    guardrailsCountPlural: "{count} guardrails",
    empty: {
      title: "No projects yet",
      body: "Create a project to define reusable workspace profiles.",
    },
    count: "{count} project",
    countPlural: "{count} projects",
    delete: {
      title: "Delete project",
      body: "Delete {name}? The project becomes unavailable for new work and an admin can restore it. Its audit history, approval decisions and deployment evidence are kept, and existing sessions keep their resolved context.",
      cancel: "Cancel",
      confirm: "Delete",
    },
    create: {
      title: "New project",
      subtitle: "Define a reusable workspace profile with codebases and integrations.",
      nameLabel: "Project name",
      nameRequired: "*",
      namePlaceholder: "e.g. POS Backend",
      descriptionLabel: "Description",
      descriptionPlaceholder: "Short description...",
      primaryCodebase: "Primary codebase",
      noneAddLater: "None (add later)",
      helper: "You can add more codebases, integrations, tags, and guardrails after creation.",
      errorDefault: "Failed to create project",
      cancel: "Cancel",
      submit: "Create project",
      submitting: "Creating...",
    },
  },

  projectsHistory: {
    eyebrow: "Projects",
    eyebrowWithName: "Projects / {name}",
    title: "Patch History",
    subtitle: "All AI-generated code changes in this project",
    backToProject: "Back to project",
    refresh: "Refresh",
    searchPlaceholder: "Search prompt, file or workspace...",
    filter: {
      status: "Status",
      allStatuses: "All statuses",
      agent: "Agent",
      allAgents: "All agents",
      workspace: "Workspace",
      allWorkspaces: "All workspaces",
      group: "Group",
      noGrouping: "No grouping",
      byWorkspace: "By workspace",
      byIssue: "By issue",
    },
    view: {
      list: "List",
      timeline: "Timeline",
    },
    count: "{filtered} of {total} patch",
    countPlural: "{filtered} of {total} patches",
    loadFailedDefault: "Failed to load",
    retry: "Retry",
    empty: {
      totalZeroTitle: "No patches yet",
      filterTitle: "No patches match your filters",
      totalZeroBody: "When agents generate code changes in a workspace, they will appear here as patches.",
      filterBody: "Try clearing your filters to see more patches.",
    },
    unassigned: "Unassigned",
    unknown: "Unknown",
    dash: "—",
    showMore: "Show {count} more",
    remaining: "({count} remaining)",
    loading: "Loading...",
    selectAPatch: "Select a patch to view details",
    noPatchesYet: "No patches yet",
    noFileChanges: "No file changes in this patch",
    fileCount: "{count} file",
    fileCountPlural: "{count} files",
    patchTitleFallback: "Patch v{version}",
    detail: {
      patchVersion: "Patch v{version}",
      originalPrompt: "Original prompt",
      meta: {
        agent: "Agent",
        workspace: "Workspace",
        issue: "Issue",
        created: "Created",
        files: "Files",
        applied: "Applied",
        decidedBy: "Decided by",
      },
      filesValue: "{count} · +{additions} -{deletions}",
    },
    context: {
      empty: "No context was captured for this patch. (Older patches do not have tool-call history.)",
      title: "Context used ({count} tool call)",
      titlePlural: "Context used ({count} tool calls)",
      filesRead: "Files read",
      searches: "Searches",
      other: "Other",
      emptyItem: "(empty)",
    },
    actions: {
      approve: "Approve",
      reject: "Reject",
      markApplied: "Mark applied",
      alreadyStatus: "Already {status}",
      approveFirst: "Approve first",
      rerun: "Re-run",
      rerunTitle: "Re-run this patch's prompt in its workspace",
      compareWithCurrent: "Compare with current",
      viewingVsCurrent: "Viewing vs current",
      compareOffTitle: "Compare against current files on disk",
      compareOnTitle: "Show the original patch diff",
      openInWorkspace: "Open in workspace",
    },
    compareLabel: "Current ↔ Patch",
    compareFailedDefault: "Compare failed",
  },

  time: {
    justNow: "just now",
    minutesAgoShort: "{n}m ago",
    hoursAgoShort: "{n}h ago",
    daysAgoShort: "{n}d ago",
    secondsAgoShort: "{n}s ago",
  },

  pager: {
    label: "Item navigation",
    previous: "Previous",
    next: "Next",
    position: "{current} of {total}",
  },

  /** The bar pinned above every page: breadcrumb trail, record pager, and
   *  the ⋯ menu carrying the standard delete. */
  pageBar: {
    more: "More actions for this item",
    remove: {
      label: "Delete",
      confirmTitle: "Delete {name}?",
      confirmBody: "This removes {name} from active work.",
      confirmKept: "The governance history already recorded — audit events, approval decisions and verification evidence — is kept.",
      confirmReversible: "An admin can restore it.",
      confirm: "Delete",
      error: "Delete failed ({status}).",
      impact: {
        workspace: "Its patches, approval decisions, sandbox verdicts and audit history are kept. An admin can restore the workspace.",
      },
    },
  },


  dashboard: {
    eyebrow: "Overview",
    controlCenter: "Control Center",
    realtimeOverview: "Realtime overview of agents, issues, and the dev pipeline.",
    last7Days: "Last 7 days",
    allSystemsOperational: "All systems operational",
    agents: "Agents",
    agentsHint: "Your AI team: specialised assistants that sort incoming questions and propose fixes. You stay in control — they never send or change anything without your approval.",
    tasksInProgress: "Tasks In Progress",
    pendingApprovals: "Pending Approvals",
    loading: "Loading…",
    awaitingBoardReview: "Awaiting board review",
    agentBreakdown: "{enabled} enabled, {paused} paused, {error} error",
    recentCount: "{count} recent",
    runActivity: "Run Activity",
    issuesByPriority: "Issues by Priority",
    issuesByStatus: "Issues by Status",
    successRate: "Success Rate",
    automatedFixes: "Automated fixes",
    recentActivity: "Recent Activity",
    liveAgentTimeline: "Live agent timeline",
    recentTasks: "Recent Tasks",
    latestIssuesInPipeline: "Latest issues in the pipeline",
    recentlyResolved: "Recently resolved",
    last5IssuesReplied: "Last 5 issues with a customer reply sent",
    legendCritical: "Critical",
    legendHigh: "High",
    legendMedium: "Medium",
    legendLow: "Low",
    legendToDo: "To do",
    legendInProgress: "In progress",
    legendReview: "Review",
    legendDone: "Done",
    noAgentRunsYet: "No agent runs yet",
    noIssuesYet: "No issues yet",
    noAgentActivityYet: "No agent activity yet",
    noTasksYet: "No tasks yet",
    nothingRepliedYet: "Nothing replied to yet",
    empty: {
      runsHint: "Connect a project and run an agent to populate this view.",
      issuesHint: "Forward an email or wire up an integration to start ingesting issues.",
      activityHint: "When agents work, you'll see their progress here in real time.",
      tasksHint: "Triaged issues and patches in flight will surface here.",
      resolvedHint: "When the support reply agent sends a customer reply, it lands here.",
      openAgents: "Open agents",
      openInbox: "Open inbox",
      openIssues: "Open issues",
    },
  },

  tenants: {
    eyebrow: "Account",
    title: "Tenants",
    subtitle: "Each tenant is a separate workspace with its own issues, agents, inbox, and knowledge base.",
    addTenant: "Add tenant",
    addFirstTenant: "Add your first tenant",
    createRequiresPlatformAdmin: "You are not allowed to create a workspace. Ask an administrator of this workspace, or your platform administrator.",
    stats: {
      tenants: "Tenants",
      tenantsSub: "In your account",
      totalIssues: "Total issues",
      totalIssuesSub: "Across all",
      totalAgents: "Total agents",
      totalAgentsSub: "Across all",
      active: "Active",
      activeSub: "Current workspace",
    },
    metrics: {
      issues: "Issues",
      agents: "Agents",
      members: "Members",
    },
    inboxLabel: "Inbox: {mailbox}",
    activeBadge: "Active",
    currentWorkspace: "Current workspace",
    switchTo: "Switch to",
    editTitle: "Edit",
    deleteTitle: "Schedule deletion",
    deleteConfirmTitle: "Schedule deletion of \"{name}\"?",
    deleteConfirm: "Access to \"{name}\" is frozen straight away and all of its data (issues, agents, knowledge, inbox) is permanently removed after 14 days. A platform admin can restore it until then, and the audit trail of the scheduled deletion is kept.",
    deleteConfirmLabel: "Schedule deletion",
    deletedToast: "Deletion scheduled for {name}",
    deleteFailedToast: "Could not schedule deletion for {name}",
    createdToast: "{name} created",
    updatedToast: "{name} updated",
    empty: {
      title: "No tenants in your account",
      hint: "Add your first tenant to create a workspace with its own issues, agents, inbox, and knowledge base.",
    },
    modal: {
      addTitle: "Add tenant",
      editTitle: "Edit {name}",
      addSubtitle: "Create a new workspace with its own issues, agents, and inbox.",
      editSubtitle: "Update tenant details and configuration.",
      createButton: "Create tenant",
      saveButton: "Save",
      submitFailed: "Failed",
      platformAdminRequired: "You are not allowed to create a workspace. Ask an administrator of this workspace, or your platform administrator.",
      twoFactorSetupRequired: "Creating a workspace requires two-factor authentication. Set it up first, then try again.",
      twoFactorReauthRequired: "Creating a workspace requires an active two-factor session. Sign in again with your code.",
      workspaceLimitReached: "You have reached the maximum number of workspaces you can create. Ask your platform administrator to raise the limit.",
      nameInUse: "A workspace with this name already exists. Pick another name.",
      nameOnlyHint: "Only the name is needed. Plan, environment and domain are set by your platform administrator afterwards.",
      switchToCreated: "Switch to it",
    },
    fields: {
      name: "Tenant name",
      namePlaceholder: "FixControl",
      slug: "Slug",
      slugPlaceholder: "fixcontrol",
      domain: "Domain",
      domainPlaceholder: "fixcontrol.nl",
      contactEmail: "Contact email",
      contactEmailPlaceholder: "info@fixcontrol.nl",
      mailbox: "Inbox mailbox",
      mailboxPlaceholder: "support@fixcontrol.nl — emails here become issues",
      mailboxHelp: "Emails sent to this address will be classified and converted to issues for this tenant.",
      plan: "Plan",
      logoLetter: "Logo letter",
      logoLetterPlaceholder: "B",
      members: "Members",
      brandColor: "Brand color",
    },
    plans: {
      free: "Free",
      pro: "Pro",
      enterprise: "Enterprise",
    },
    environments: {
      prod: "Prod",
      staging: "Staging",
      test: "Test",
    },
    environmentField: {
      label: "Environment",
      help: "Tag rendered next to the tenant in the switcher so you can tell sandbox apart from production at a glance.",
    },
    preview: {
      placeholderName: "Tenant name",
      placeholderSlug: "slug",
    },
  },

  inbox: {
    eyebrow: "Intake",
    title: "Inbox",
    subtitle: "Incoming email per tenant — classified by the Ticket Analyst.",
    pollGmail: "Poll Gmail",
    refresh: "Refresh",
    searchPlaceholder: "Search inbox…",
    statusFilterAria: "Status filter",
    emptyFiltered: "No messages match the current filters.",
    clearFilters: "Clear filters",
    emptyTab: {
      active: "No active messages. Incoming email from connected mailboxes appears here automatically.",
      archived: "Nothing archived. Archiving moves a message out of the active queue while keeping it searchable.",
      spam: "No spam. Messages marked as spam land here and are excluded from AI processing.",
      deleted: "Nothing deleted. Deleting is reversible — deleted messages stay here and can always be restored.",
    },
    emptyElsewhereHint: "{count} message(s) are under Archived, Spam or Deleted — see the tabs above.",
    selectHint: "Select a message to view the full conversation.",
    convertToIssue: "Convert to issue",
    ignore: "Ignore",
    all: "All",
    status: {
      new: "New",
      analyzing: "Analyzing",
      triaged: "Triaged",
      answered: "Answered",
      converted: "Converted",
      ignored: "Ignored",
    },
    lifecycle: {
      tabAria: "View",
      tab: {
        active: "Active",
        archived: "Archived",
        spam: "Spam",
        deleted: "Deleted",
      },
      state: {
        active: "Active",
        archived: "Archived",
        spam: "Spam",
        deleted: "Deleted",
      },
      archive: "Archive",
      spam: "Mark as spam",
      delete: "Delete",
      restore: "Restore",
      moreActions: "More actions",
      bulkResult: "{changed} updated · {skipped} skipped",
      syncWarning: "{failed} mailbox sync(s) failed — check the Gmail/Office365 integration.",
      confirmArchiveTitle: "Archive selected messages?",
      confirmArchiveBody: "{count} message(s) move to the Archived tab and are archived in the connected mailbox. They stay searchable and can be restored at any time.",
      confirmSpamTitle: "Mark selected as spam?",
      confirmSpamBody: "{count} message(s) move to the Spam tab, stop triggering AI processing, and are moved to the Spam/Junk folder in the connected mailbox. They can be restored.",
      confirmDeleteTitle: "Delete selected messages?",
      confirmDeleteBody: "{count} message(s) move to the Deleted tab and to Trash in the connected mailbox. Nothing is permanently deleted — everything is kept for audit and can always be restored.",
      confirmRestoreTitle: "Restore selected messages?",
      confirmRestoreBody: "{count} message(s) return to the Active queue and to the inbox in the connected mailbox.",
      archivedBanner: "Archived — hidden from the active inbox and archived in the source mailbox. Restore to bring it back.",
      spamBanner: "Marked as spam — excluded from AI processing and moved to the mailbox Spam/Junk folder.",
      deletedBanner: "Deleted (recoverable) — hidden from the inbox but kept for audit, and moved to Trash in the connected mailbox. Nothing is permanently deleted; use Restore to bring it back.",
      mailboxNote: "Inbox actions also mirror to the connected Gmail/Office365 mailbox (best-effort). FixControl state is the source of truth — a mailbox sync failure never blocks the action.",
    },
    analysis: {
      title: "AI Analysis",
      sub: "Ticket Analyst · confidence {pct}%",
      kind: "Kind",
      priority: "Priority",
      readiness: "Readiness",
      confidence: "Confidence",
      convertedTo: "Converted to",
      flagsLabel: "Flags",
    },
    sort: {
      receivedNew: "Received · newest",
      receivedOld: "Received · oldest",
      status: "Status",
      sender: "Sender",
      subject: "Subject",
      tenant: "Tenant",
    },
    queue: {
      loading: "Loading…",
      range: "{from}-{to} of {total}",
      prev: "Previous page",
      next: "Next page",
    },
    bulk: {
      selectedCount: "{count} selected",
      selectPage: "Select all on this page",
      ignore: "Ignore",
      reanalyse: "Reanalyse",
      assignCategory: "Assign category",
      clear: "Clear selection",
      confirmIgnoreTitle: "Ignore selected messages?",
      confirmIgnoreBody: "{count} message(s) will be marked as ignored. Already-converted messages are skipped.",
      confirmReanalyseTitle: "Reanalyse selected messages?",
      confirmReanalyseBody: "{count} message(s) will be re-queued for classification. Already-converted messages are skipped.",
      ignoredResult: "Ignored {ignored} · skipped {skipped}",
      reanalysedResult: "Reanalysing {reanalysed} · skipped {skipped}",
      assignedResult: "Assigned {assigned} · skipped {skipped}",
      assignToMe: "Assign to me",
      assignToUser: "Assign to user",
      unassign: "Unassign",
      assignedToUserResult: "Assigned {count} · skipped {skipped}",
      unassignedResult: "Unassigned {count} · skipped {skipped}",
    },
    mailbox: {
      all: "All",
      mine: "Mine",
      unassigned: "Unassigned",
      drafts: "Drafts",
      sent: "Sent",
      switcherAria: "Filter by mailbox identity",
    },
    thread: {
      loading: "Loading thread context…",
      noContext: "No thread context yet",
      headerLabel: "Thread",
      unnamed: "(no subject)",
      via: "via",
      lastReply: "last reply",
      unreadCount: "{count} unread",
      siblings: "{count} other message in this thread",
      timelineTitle: "Conversation",
      timelineCount: "{count} entries",
      timeline: {
        inbound: "Inbound",
        replySent: "Sent",
        replySending: "Sending…",
        note: "Note",
      },
      notesTitle: "Internal notes",
      notesHint: "Operator-only · never sent to customer",
      notesPlaceholder: "Add an internal note…",
      notesAdd: "Add note",
      notesEmpty: "No internal notes yet",
    },
    signal: {
      no_assignee: "Unassigned",
      stale: "Stale",
      customer_waiting: "Customer waiting",
      high_risk: "High risk",
      unread: "Has unread",
      low_confidence: "AI low confidence",
      filterAll: "All threads",
      filterAria: "Filter by operator signal",
    },
    attachments: {
      title: "Attachments",
      count: "{count}",
      loading: "Loading attachments…",
    },
    assign: {
      me: "{name} (me)",
      clear: "Clear assignment",
      noUsers: "No active users in this tenant.",
    },
    reply: {
      title: "Reply",
      from: "From:",
      placeholder: "Type your reply…",
      save: "Save draft",
      saving: "Saving…",
      saved: "Saved",
      error: "Save failed",
      discard: "Discard",
      send: "Send",
      sendDisabledHint: "Replies are turned off for this mailbox. Turn on \"Allow replies\" in Settings → Mailboxes.",
      sending: "Sending…",
      sent: "Sent",
      sentEmpty: "This reply has been sent.",
      alreadyAnswered: "Already answered {count}× — last at {time}.",
      confirmAgainTitle: "This e-mail was already answered",
      confirmAgainBody: "{count} reply(ies) already went to this customer, the last at {time}. Send another one?",
      confirmAgain: "Send anyway",
      submitForApproval: "Submit for approval",
      submitForApprovalHint: "This project requires an approval before a reply goes out. Approvers get a notification and an e-mail.",
      awaitingApproval: "Waiting for approval",
      awaitingApprovalHint: "Approvers got a notification and an e-mail. Nothing goes to the customer until someone approves it; if it is rejected, it comes back here with their note.",
      approvalRejected: "Rejected by {name}",
      approvalReturned: "Sent back by {name} — submit it again when it is ready",
      approverFallback: "an approver",
      colleagueAwaitingApproval: "Another reply to this e-mail (from a colleague or the AI) is waiting for approval.",
      queued: "Reply queued for sending",
      queuedHint: "Delivery runs in the background and is retried automatically. The reply shows in the conversation history and flips to Sent once delivered; if it permanently fails, the draft reopens here for editing.",
      sendError: "Send failed",
      sendErrorProvider: "No mail integration is connected for sending.",
      sendErrorThread: "Could not thread this reply on the original message.",
      sendErrorToken: "Mail integration access expired. Reconnect Gmail or Microsoft.",
      sendErrorRetry: "Provider temporarily refused. Try again shortly.",
      sendErrorAlready: "This reply has already been sent.",
      sendErrorRate: "Too many sends in a row — slow down a bit.",
    },
    timeline: {
      title: "History",
      collapse: "Collapse",
      loading: "Loading history…",
      empty: "No activity yet.",
      system: "system",
      event: {
        created: "Message ingested",
        categorized: "Category assigned",
        assigned: "Assigned to user",
        unassigned: "Assignment cleared",
        ignored: "Marked as ignored",
        reanalyzed: "Re-queued for classification",
        converted: "Converted to issue",
        reply_drafted: "Reply drafted",
        reply_sent: "Reply sent",
        reply_discarded: "Reply discarded",
        ai_suggestion_created: "AI suggestion generated",
        ai_policy_matched: "AI policy matched",
        ai_auto_draft_created: "AI auto-drafted reply",
        ai_auto_send_blocked: "AI auto-send blocked by policy",
        ai_auto_reply_sent: "AI auto-reply sent",
        ai_suggestion_accepted: "AI suggestion accepted",
        ai_suggestion_rejected: "AI suggestion marked wrong",
        thread_created: "Thread started",
        thread_reopened: "Thread reopened",
        thread_resolved: "Thread resolved",
        thread_status_changed: "Thread status changed",
        internal_note_added: "Internal note added",
        archived: "Archived",
        spam_marked: "Marked as spam",
        deleted: "Deleted (recoverable)",
        restored: "Restored to active",
        intake_classified: "Intake decided",
        appended_to_issue: "Added to the linked issue",
        intake_failed: "Intake failed",
      },
    },
    categories: {
      manage: "Categories",
      manageTitle: "Manage inbox categories",
      manageSub: "Tenant-scoped tags for organising the queue. One primary category per message.",
      newPlaceholder: "New category name…",
      create: "Create",
      empty: "No categories yet — create the first one above.",
      rename: "Rename",
      archive: "Archive",
      unarchive: "Unarchive",
      delete: "Delete",
      archivedTag: "archived",
      confirmEdit: "Save",
      cancelEdit: "Cancel",
      filter: "Category",
      filterAll: "All categories",
      filterUncategorised: "Uncategorised",
      filterByLabel: "Filter by {name}",
      clearAssignment: "Clear category",
    },
    detail: {
      changeCategory: "Change category",
      uncategorised: "Uncategorised",
      changeAssignment: "Change assignment",
      unassigned: "Unassigned",
      filterBySender: "Filter inbox by this sender",
    },
    filter: {
      clearSender: "Clear sender filter",
    },
    splitter: {
      aria: "Resize queue / detail columns",
    },
    subtitleWithView: "View: {view} · operator workbench",
    workbench: {
      listAria: "Conversations",
      resizeList: "Resize the conversation list",
      resizeComposer: "Resize the reply box",
      conversationAria: "Selected conversation",
      allMailboxes: "All mailboxes",
      filter: "Filter",
      filterTitle: "Filters",
      showLabel: "Show",
      categoryLabel: "Category",
      signalLabel: "Signal",
      sortLabel: "Sort",
      more: "More inbox actions",
      exportCsv: "Export CSV",
      fetchGmail: "Fetch Gmail now",
      mailboxSettings: "Mailbox settings",
      shortcutsHelp: "Keyboard shortcuts",
      selectRow: "Select {subject}",
      selectedCount: "{count} selected",
      unread: "Unread",
      back: "Back to inbox",
      noSelection: "Select a conversation",
      noSelectionMove: "to move",
      confirm: {
        archive: { title: "Archive {count} conversation(s)?", body: "Archived conversations leave the inbox. You find them again under ⋯ → Archive. Nothing changes in the customer’s mailbox.", confirm: "Archive" },
        spam: { title: "Mark {count} conversation(s) as spam?", body: "Spam leaves the inbox and is never analysed by AI. You can restore it from ⋯ → Spam.", confirm: "Mark as spam" },
        delete: { title: "Delete {count} conversation(s)?", body: "Deleted conversations move to ⋯ → Deleted and can be restored. The customer’s mailbox is not touched.", confirm: "Delete" },
        discard: { title: "Discard this reply?", body: "The text in the reply box will be deleted. This cannot be undone.", confirm: "Discard" },
      },
      lifecycleView: {
        archived: "Archive",
        spam: "Spam",
        deleted: "Deleted",
        back: "Back to inbox",
      },
      undo: "Undo",
      deleteTitle: "Delete {count} conversation(s)?",
      deleteBody: "Deleted conversations move to the Deleted view and can be restored. The customer's mailbox is not touched.",
      views: {
        aria: "Inbox views",
        all: "All",
        mine: "Mine",
        unassigned: "Unassigned",
        waiting: "Waiting",
        review: "Needs review",
      },
      empty: {
        all: "Inbox zero. New mail appears here automatically.",
        mine: "Nothing is assigned to you.",
        unassigned: "Every conversation has an owner.",
        waiting: "No conversation is waiting on a customer.",
        review: "Nothing needs review — intake handled everything it could.",
      },
      done: {
        archive: "Archived {count}",
        spam: "Marked {count} as spam",
        delete: "Deleted {count}",
        restore: "Restored {count}",
      },
      status: {
        open: "Open",
        waiting_customer: "Waiting on customer",
        pending_internal: "Pending internal",
        resolved: "Resolved",
        ignored: "Ignored",
        new: "Open",
        ai_drafted: "Open",
        changeFailed: "Could not change the status",
      },
      snooze: {
        laterToday: "Later today",
        tomorrow: "Tomorrow morning",
        nextWeek: "Next week",
        clear: "Unsnooze",
      },
      header: {
        via: "via",
        details: "Details",
        activity: "Activity ({count})",
        createIssue: "Create issue",
        openIssue: "Open issue",
        linkedIssue: "Linked to {key}",
        archive: "Archive",
        restore: "Restore",
        snooze: "Snooze",
        snoozed: "Snoozed until",
        status: "Status",
        assignee: "Assignee",
        assignMe: "Assign to me",
        me: "Me",
        unassigned: "Unassigned",
        more: "More actions",
        markSpam: "Mark as spam",
        delete: "Delete",
        reanalyse: "Run intake again",
        filterSender: "Show all from this sender",
        categoryItem: "Category: {name}",
        fullscreen: "Full screen",
        exitFullscreen: "Exit full screen",
        aiDraftReady: "AI draft ready",
      },
      intake: {
        label: "AI suggests",
        notRun: "Not analysed yet",
        confidence: "{pct}% sure of this call",
        viewAnalysis: "View analysis",
        retry: "Retry",
        run: "Run intake",
        retried: "Intake queued",
        action: {
          create_issue: "Create issue",
          append_to_existing_issue: "Added to the linked issue",
          keep_in_inbox: "Reply to the customer",
          ignore: "No action needed",
          needs_review: "Review manually",
        },
        state: {
          queued: "Waiting for analysis",
          running: "Analysing…",
          linked: "Linked to an issue",
          support: "Support",
          needs_review: "Needs review",
          noise: "No action",
          failed: "Intake failed",
        },
        reason: {
          linked_thread: "Follow-up on an existing issue",
          issue_created: "Issue created automatically",
          noise_detected: "Automatic, empty or acknowledgement-only message",
          classified_noise: "Not actionable",
          support_request: "Support question",
          ambiguous: "Unclear what the sender needs",
          low_confidence: "Low confidence",
          project_unresolved: "No project is linked to this mailbox",
          auto_create_disabled: "Automatic issue creation is off for this mailbox",
          classifier_unavailable: "AI analysis was unavailable",
          intake_error: "Intake could not finish",
        },
        intent: {
          bug: "Bug",
          feature: "Feature request",
          support: "Support",
          operational: "Account or admin request",
          spam_noise: "Noise",
          acknowledgement: "Acknowledgement",
          auto_reply: "Automatic reply",
          unknown: "Unclear",
        },
        actionability: {
          actionable: "Actionable",
          non_actionable: "Not actionable",
          ambiguous: "Ambiguous",
        },
      },
      composer: {
        aria: "Composer mode",
        reply: "Reply",
        note: "Internal note",
        notePlaceholder: "Write an internal note — never sent to the customer…",
        noteAdd: "Add note",
        noteHint: "Team only",
        noteTooLong: "The note is too long",
        noThread: "Internal notes need a conversation thread.",
        sendShortcut: "⌘/Ctrl + Enter",
        aiSeeded: "AI draft filled in — review it before sending.",
        insertAi: "Use AI draft",
      },
      conversation: {
        aria: "Messages",
        loading: "Loading conversation…",
        note: "Internal note",
        teammate: "Teammate",
        replySent: "Reply sent",
        replyQueued: "Reply queued",
        showQuoted: "Show quoted text",
        hideQuoted: "Hide quoted text",
        truncated: "Message shortened for display.",
        unavailable: "The conversation could not be loaded.",
      },
      details: {
        title: "Details",
        tabs: { details: "Details", ai: "AI analysis", activity: "Activity" },
        contact: "Contact",
        project: "Project",
        noProject: "No project",
        mailbox: "Mailbox",
        issue: "Linked issue",
        noIssue: "Not linked",
        assignee: "Assignee",
        category: "Category",
        priority: "Priority",
        received: "Received",
        intake: "Intake",
        decided: "Decided",
        evidence: "Evidence",
        headers: "Intake headers",
        messageId: "Message id",
        intent: "Intent",
        actionability: "Actionability",
        confidence: "Confidence",
        summary: "Summary",
        reason: "Why",
        noAnalysis: "No AI analysis for this message yet.",
        projectSource: {
          thread_issue: "Project from the linked issue",
          mailbox_binding: "Project from the mailbox link",
        },
      },
      createIssue: {
        title: "Create issue",
        subtitle: "From the conversation with {sender}",
        project: "Project",
        noProject: "No project (unassigned)",
        projectHint: "Suggested from the mailbox's linked project.",
        issueTitle: "Title",
        summary: "Summary",
        kind: "Type",
        kinds: { BUG: "Bug", FEATURE: "Feature", SUPPORT: "Support" },
        source: "The conversation stays linked to the issue; later replies are added to it.",
        policyHint: "Whether work starts automatically follows the project's settings.",
        submit: "Create issue",
        created: "Created {key}",
        alreadyLinked: "Already linked to {key}",
        failed: "Could not create the issue",
      },
      shortcuts: {
        next: "Next conversation",
        prev: "Previous conversation",
        open: "Open conversation",
        reply: "Reply",
        note: "Internal note",
        archive: "Archive",
        issue: "Create or open issue",
        assign: "Assign",
        snooze: "Snooze",
        select: "Select conversation",
        search: "Search",
        fullscreen: "Full screen on / off",
        escape: "Leave field / clear selection",
      },
    },
  },

  followUps: {
    eyebrow: "Work",
    title: "Follow-ups",
    subtitle: "Tasks created automatically after patches in {company} are approved or applied. Triage and close them off here.",
    subtitleNoTenant: "Tasks created automatically after patches in your tenant are approved or applied. Triage and close them off here.",
    statsOpen: "Open",
    statsOverdue: "Overdue",
    statsUnassigned: "Unassigned",
    filter: {
      open: "Open",
      done: "Done",
      all: "All",
      anyone: "Anyone",
      mine: "Mine",
      unassigned: "Unassigned",
    },
    empty: "No follow-ups match the current filters.",
    // Distinguishes rows that are otherwise identical: the same task raised
    // by two patches on one issue differs only by which patch shipped.
    patchVersion: "patch v{version}",
    overdue: "overdue",
    due: "due {when}",
    actions: {
      assignToMe: "Assign to me",
      start: "Start",
      markDone: "Mark done",
      cancel: "Cancel",
    },
    confirmCancel: "Cancel this follow-up?",
    toast: {
      cancelled: "Cancelled",
      updateFailed: "Update failed ({status})",
      updateFailedGeneric: "Update failed",
      deleteFailed: "Delete failed ({status})",
      deleteFailedGeneric: "Delete failed",
    },
    status: {
      new: "New",
      assigned: "Assigned",
      in_progress: "In progress",
      done: "Done",
      blocked: "Blocked",
    },
    priority: {
      low: "Low",
      medium: "Medium",
      high: "High",
      critical: "Critical",
    },
  },

  runs: {
    eyebrow: "System",
    title: "Agent runs",
    subtitle: "Every recorded agent execution for {company} — last 100, newest first.",
    subtitleNoTenant: "Every recorded agent execution for your tenant — last 100, newest first.",
    statsRuns: "Runs",
    statsSuccessRate: "Success rate",
    statsAvgLatency: "Avg latency",
    statsSpend: "Spend",
    statsSpendUnpriced: "Priced runs only. {n} run(s) in this view have no known price — a CLI run reports no tokens, and the catalogue prices Anthropic models only.",
    statsAction: {
      clearFilters: "Show all runs",
      showFailed: "Show the {n} failed run(s)",
      sortLatency: "Sort by slowest first",
      sortCost: "Sort by most expensive first",
    },
    filter: {
      all: "All ({n})",
      success: "Success ({n})",
      failed: "Failed ({n})",
    },
    allAgents: "All agents",
    empty: "No work sessions match the current filter.",
    emptyHint: "A work session is one thing an agent did — reading a ticket, drafting a reply, proposing a change. They appear here as soon as an agent starts working.",
    emptyAsk: "How do I get an agent working on something?",
    searchPlaceholder: "Search runs…",
    columns: {
      status: "Status",
      run: "Run",
      agent: "Agent",
      issue: "Issue",
      latency: "Latency",
      cost: "Cost",
      when: "When",
    },
    runLabel: {
      defaultVerb: "Process",
      fallback: "Run {id}",
    },
    emptyNeverRecords:
      "This agent calls no model, so it never records a run here. Its work is "
      + "recorded on the mission it ran in — and, where it opens one, on that "
      + "gate's approval record.",
    detail: {
      titleFallback: "Run",
      subtitle: "{issue} · {when}",
      noIssue: "No issue",
      agentRuns: "Runs by this agent",
      gone: "This run is no longer recorded.",
      goneHint:
        "Runs are pruned on the maintenance retention window (7 days by default), "
        + "so a link can outlive the run it points at. The mission it ran in, and "
        + "its audit trail, are kept.",
      backToRuns: "Back to runs",
      failed: "Could not load this run.",
    },
  },

  orchestrationConsole: {
    page: {
      title: "Orchestration & context",
      subtitle: "How FixControl selects agents, what they see, and what you can tune",
      pickTenant: "Pick an organisation first.",
      intro:
        "FixControl runs a fixed set of specialist agents coordinated by an orchestrator. "
        + "This area shows which agents exist, why the orchestrator picks one, what context "
        + "it receives, and which settings you can safely change. Core security and execution "
        + "boundaries are deliberately not adjustable here.",
      loadFailed: "Could not load the orchestration overview.",
      retry: "Try again",
    },
    tabs: {
      overview: "Overview",
      agents: "Agents",
      routing: "Routing",
      context: "Context & search",
      simulator: "Simulator",
    },
    common: {
      systemAgent: "System agent",
      systemAgentHint:
        "Delivered by FixControl. You can tune supported settings; core security and "
        + "execution boundaries cannot be overridden, and it cannot be removed.",
      enabled: "Enabled",
      disabled: "Paused",
      cannotDisable: "Cannot be paused",
      open: "Open",
      none: "None",
      loading: "Loading…",
      advancedOnly: "Shown to administrators only",
    },
    source: {
      systemDefault: "FixControl default",
      tenantOverride: "Set for this organisation",
      projectOverride: "Set for this project",
      locked: "Frozen at {scope}",
      lockedReason: "Frozen at {scope}: {reason}",
      platformDefault: "Platform default",
    },
    tiers: {
      safe: "Adjustable",
      guarded: "Needs confirmation",
      fixed: "Set elsewhere",
      invariant: "Protected",
      safeHint: "Affects cost, speed or thoroughness. It never changes what the agent may do.",
      guardedHint:
        "Changes what the system is allowed to do, or who has to sign off. "
        + "Each field has to be acknowledged individually.",
      fixedHint:
        "Written somewhere this screen does not reach — a fixed runtime value, a "
        + "platform-wide setting, or the FixControl administrator console. Shown so you "
        + "can see it. It is not a security boundary.",
      invariantHint: "A security boundary. Not adjustable at any level, by anyone.",
    },
    overview: {
      rosterTitle: "The agents",
      rosterBody:
        "{total} agents, {enabled} enabled. {planner} are scheduled by the mission planner; "
        + "the rest are driven by intake and workflow events.",
      modelTitle: "Which run a model",
      modelBody:
        "{llm} agents call a language model. The other {deterministic} run deterministic "
        + "code — they are shown without a model rather than with one they never use.",
      findingsTitle: "Configuration check",
      findingsNone: "Nothing blocking. The current configuration can run every mission stage.",
      protectedTitle: "What you cannot change here",
      protectedBody:
        "Tenant isolation, the approval chokepoint, the run-state contract, outbound writes "
        + "through the outbox, and pull-request-only code delivery are enforced in code. They "
        + "are listed on each agent so you can see they are deliberate, not missing.",
      severityBlocking: "Blocking",
      severityWarning: "Warning",
      severityInfo: "Note",
    },
    agents: {
      role: "Role",
      model: "Model",
      deterministic: "No model — deterministic",
      capabilities: "{n} capabilities",
      responsibilities: "Responsibilities",
      lastUsed: "Last used",
      never: "Never",
      configState: "Configuration",
      stateDefault: "FixControl defaults",
      stateTenant: "Organisation overrides",
      stateProject: "Project overrides",
      warningsTitle: "While resolving this configuration",
      warnings: {
        agentRowUnreadable:
          "The agent row could not be read; the fail-open default (available) is shown.",
        behaviorChainFallback:
          "The governance behavior chain could not be resolved; the built-in balanced "
          + "profile is shown, which is what the runtime uses too.",
        modelRoutingUnresolved:
          "Model routing could not be resolved; the shipped task default applies. No "
          + "configured override is shown because none could be read.",
        budgetCeilingsUnread:
          "One or more budget ceilings could not be read; the resolved value is shown.",
        scopedBehaviorOff:
          "Per-agent behaviour settings are not armed for this organisation "
          + "(ORCH_SCOPED_BEHAVIOR_RESOLUTION is off), so the values below are the "
          + "ORGANISATION-WIDE ones — changing one here changes it for every agent.",
        projectOverridesNotEnforced:
          "This project's bindings are stored but NOT applied: the organisation has not "
          + "armed ORCH_SCOPED_BEHAVIOR_RESOLUTION, so agents still resolve their behavior at "
          + "the organisation scope. Arming it applies only the bindings that were reviewed "
          + "and activated.",
        projectBindingsPendingReview:
          "A behaviour binding on this project waits for review: it is stored but not "
          + "applied until someone activates it under Policy & scope, Agent behaviour.",
      },
      deterministicReason: {
        codebase:
          "The architect brief is assembled from the codebase list and indexed knowledge "
          + "search. No provider call is made on the headless path; the async path hands work "
          + "to the implementation agent instead.",
        cto:
          "The technical gate is derived, not generated: it composes the latest QA "
          + "verification report with the issue's intrinsic risk flags.",
        security:
          "Engine-inline. Reads the CTO risk flags and the QA report and returns a verdict; "
          + "no provider is involved.",
        release:
          "Engine-inline. Drives the gated apply two-phase commit. Delivery is a "
          + "human-signed step, not a generated one.",
        ux:
          "Engine-inline. Runs a UX assessment over the real run, issue and routes, "
          + "including screenshot capture.",
        default: "This agent's runtime makes no provider call.",
      },
    },
    governance: {
      gates: {
        humanSignOff: "Human sign-off before this role's work proceeds",
        technical: "Technical gate (approve / request changes / reject)",
        deployment: "Deployment gate — delivery is never automatic",
        riskyPatch: "Risky-patch gate when the change touches a flagged area",
        communications: "Communications approval before a reply reaches a customer",
      },
      validation: {
        sandboxBeforeReview: "Sandbox verification before hand-off to review",
        verificationBeforeClosure:
          "Verification report required before the closure gate clears",
        qaReportRequired:
          "A verification report must exist; the gate runs verification first if it does not",
      },
      productionRestrictions: {
        pullRequestOnlyDelivery:
          "Code delivery is pull/merge request only — never a push to a default or "
          + "production branch.",
        armedDeploymentCapabilities:
          "Carrying a verdict to a CI/CD host requires the tenant deployment capabilities to "
          + "be armed.",
      },
      policySources: {
        behaviorProfileChain: "Agent behavior profile chain",
        governanceScopeHierarchy: "Governance scope hierarchy",
      },
      enforcementNotes: {
        enforced:
          "Enforced: this agent's completions resolve the tenant routing policy before "
          + "dispatch.",
        notEnforced:
          "This agent's completions are made without measurement context, so the tenant "
          + "retry budget, deadline and cost ceiling are not applied on this path. The values "
          + "are shown because they are configured, not because they are currently enforced.",
        notEnforcedStreaming:
          "This agent streams the model provider directly instead of going through the "
          + "routing layer, so the retry budget, deadline and cost ceiling are not applied "
          + "on this path — its spend is measured after the fact, never capped before the "
          + "call. The values are shown because they are configured, not because they are "
          + "currently enforced.",
      },
      retryLoops: {
        qaJsonRetry:
          "Verification retries a malformed JSON verdict as well as a provider failure. "
          + "Giving up drops it to a content-blind needs-changes verdict, so the loop is "
          + "deliberate. Configured by QA_LLM_MAX_ATTEMPTS.",
      },
    },
    routing: {
      title: "How work reaches an agent",
      intro:
        "A mission is planned as an ordered set of stages. Stages run in order; tasks inside "
        + "one stage run in parallel. Each task names a role, and each role dispatches to one "
        + "agent. Nothing below is drawn for illustration — it is the live mapping.",
      shapesTitle: "The three plans the orchestrator can produce",
      shapesIntro:
        "These are mutually exclusive — a mission gets exactly one of them. The "
        + "checks run in this order.",
      shapes: {
        ux: {
          name: "UI/UX assessment",
          when:
            "Selected first, when the request text or the linked issue reads as a "
            + "UI/UX change.",
        },
        support: {
          name: "Support reply",
          when: "Selected when the issue is classified as a support question.",
        },
        fix: {
          name: "Fix pipeline",
          when:
            "Everything else — bugs, features and free-form instructions all take "
            + "this one.",
        },
      },
      stagesTitle: "Mission stages",
      stage: "Stage {n}",
      notPlanned: "Not planned for this organisation",
      rolesTitle: "Roles and the agent each one dispatches to",
      roleColumn: "Role",
      agentColumn: "Agent",
      delegates: "Plans and delegates",
      engineInline: "Runs in the engine — no model",
      requiresApproval: "Gated behind a human by default",
      entryPointsTitle: "Other ways this agent is reached",
      entryPointLabels: {
        inbound_email: "Inbound email",
        intake_form: "Intake form",
        webhook_ingest: "Webhook ingest",
        agents_run_api: "The agent-run API endpoint",
        issue_state_change: "Issue state change",
        workspace_event: "Workspace event",
        patch_orchestrator: "Patch orchestrator",
        issue_resolved: "Issue resolved",
        planner_root: "Mission root",
        planner_architect: "Mission planner (architect stage)",
        planner_implementation: "Mission planner (implementation stage)",
        planner_technical_gate: "Mission planner (technical gate stage)",
        planner_verification: "Mission planner (verification stage)",
        planner_support: "Mission planner (support missions)",
        planner_delivery: "Mission planner (delivery stage, when enabled)",
        planner_ux: "Mission planner (UX missions)",
      },
      prerequisiteTitle: "Runs first when it is missing",
      fallbackTitle: "If this agent is paused",
      fallbackDropped:
        "Its tasks are dropped from the plan. There is no substitute agent — this runtime has "
        + "no generalist to reroute to.",
      fallbackRefused: "It cannot be paused: the pipeline structurally depends on it.",
    },
    simulator: {
      title: "Predict a mission",
      intro:
        "Describe what you would ask FixControl to do. Nothing runs: no mission is "
        + "created, no agent is dispatched, no gate is opened and no external system "
        + "is touched.",
      instructionLabel: "What would you ask for?",
      instructionPlaceholder: "Fix the checkout timeout on the payments service",
      issueLabel: "Against an existing issue (optional)",
      issuePlaceholder: "FIX-123",
      kindLabel: "Issue kind",
      kindAuto: "Take it from the issue",
      run: "Predict",
      running: "Predicting…",
      failed: "Could not run the prediction.",
      simulationOnly: "Simulation — nothing was executed",
      planTitle: "Plan the orchestrator would produce",
      whyTitle: "Why this plan",
      stepStage: "Stage {n}",
      stepWhy: "Why this agent",
      stepContext: "Context it would read",
      stepCapabilities: "What it could do",
      stepModel: "Model",
      stepDeterministic: "No model",
      stepSkipped: "Skipped",
      approvalsTitle: "Human decisions required",
      approvalsNone: "None — this plan runs to completion without a gate.",
      validationTitle: "Verification the work must pass",
      riskTitle: "Risk flags on the issue",
      limitsTitle: "What this prediction does not cover",
      unusedTitle: "Agents this plan would not use",
      empty: "Describe a task to see how it would be orchestrated.",
    },
    inspector: {
      // Read by /runs/[id] via <RunInspectionPanels>. The block keeps its
      // historical name: the orchestration console's Run-inspector tab was
      // removed once /runs grew a real drill-down.
      runTitle: "The run",
      agent: "Agent",
      outcome: "Outcome",
      succeeded: "Succeeded",
      failedOutcome: "Failed",
      duration: "Duration",
      model: "Model",
      tokens: "Tokens",
      cost: "Cost",
      unknown: "Not known",
      unknownCostHint:
        "Not known is not zero: either the model has no catalogue price, or the "
        + "call went through the CLI subscription, which reports no tokens.",
      configTitle: "Configuration it ran under",
      configVersion: "Version",
      configUnstamped: "Not stamped",
      configUnstampedHint:
        "Not stamped is not \u2018default’: this run predates the stamp, is a flow "
        + "rather than one of the ten agents, or the resolve failed at the time.",
      contextTitle: "Context that fed the prompt",
      contextNone: "No context manifest is linked to this run.",
      contextTokens: "Cacheable {cacheable} / dynamic {dynamic} tokens",
      contextFiles: "Files included",
      contextSources: "Sources used",
      contextExcluded: "Kept out",
      snapshotHash: "Snapshot hash",
      timelineTitle: "Configuration changes around this run",
      gapsTitle: "What could not be shown",
    },
    context: {
      title: "What each agent sees",
      intro:
        "Only sources the runtime actually reads are listed. Retrieval is a hybrid of vector, "
        + "full-text and trigram search combined into one ranking, restricted to the issue's "
        + "project plus organisation-wide sources.",
      sourcesTitle: "Sources",
      depth: "Top {n}",
      evidence: "Read at",
      configurable: "Adjustable",
      notConfigurable: "Fixed",
      sources: {
        intake: {
          request: {
            label: "The inbound message",
            evidence: "The intake step, on the inbound payload",
          },
          memory: {
            label: "Tenant and project memory rules",
            evidence: "The classification call, from the tenant and project memory lookup",
          },
          caller_context: {
            label: "Context blocks supplied by the caller",
            evidence: "The classification call, from the context blocks the caller supplies",
          },
        },
        workflow: {
          current_task: {
            label: "The issue and its current state",
            evidence: "The workflow agent, when it decides the next step",
          },
          history: {
            label: "Recent workspace events",
            evidence: "The workflow agent, while it assembles its prompt",
          },
          memory: {
            label: "Tenant and project memory rules",
            evidence: "The workflow agent, from the tenant and project memory lookup",
          },
        },
        codebase: {
          codebases: {
            label: "Registered codebases",
            evidence: "The codebase agent, from the registered-codebase list",
          },
          knowledge: {
            label: "Indexed knowledge (hybrid vector + full-text + trigram search)",
            evidence: "The codebase agent, through the project-scoped knowledge search",
          },
          project_scope: {
            label: "The issue's project boundary",
            evidence: "The retrieval context loaded for the issue",
          },
        },
        dev: {
          repository: {
            label: "Working-tree files in the issue workspace",
            evidence: "The workspace job that runs the implementation agent",
          },
          current_task: {
            label: "The issue and the architect brief",
            evidence:
              "The implementation agent, from the issue and the architect brief it starts "
              + "with",
          },
          evidence: {
            label: "Sandbox failures from the previous attempt",
            evidence: "The sandbox revision feedback from the previous attempt",
          },
          history: {
            label: "Reviewer feedback on the previous patch version",
            evidence: "The reviewer feedback recorded on the previous patch version",
          },
        },
        cto: {
          evidence: {
            label: "The latest QA verification report",
            evidence: "The technical gate, from the latest verification report",
          },
          governance: {
            label: "The issue's intrinsic risk flags",
            evidence: "The technical gate, from the issue's risk flags",
          },
        },
        qa: {
          current_task: {
            label: "The issue and the patch under review",
            evidence: "The QA agent, when it runs a verification",
          },
          evidence: {
            label: "Sandbox verification results",
            evidence: "The sandbox gate that verifies the patch",
          },
          context_assembly: {
            label: "The assembled context snapshot for this phase",
            evidence: "The context assembly step for this phase",
          },
        },
        support: {
          support_context: {
            label: "Previously resolved tickets",
            evidence: "The support step, from the resolved-ticket history it is given",
          },
          knowledge: {
            label: "Tenant knowledge sources",
            evidence: "The support reply agent, from its knowledge context",
          },
          memory: {
            label: "Tenant and project memory rules",
            evidence: "The support reply agent, from the tenant and project memory lookup",
          },
        },
        security: {
          governance: {
            label: "Risk flags raised by the technical gate",
            evidence: "The security review step, from the technical gate's risk flags",
          },
          evidence: {
            label: "The QA verification report",
            evidence: "The stored QA verification report",
          },
        },
        release: {
          current_task: {
            label: "The approved patch",
            evidence: "The release step, on the approved patch",
          },
          governance: {
            label: "The project's deployment binding and gate state",
            evidence: "The deployment binding resolved for the project",
          },
        },
        ux: {
          current_task: {
            label: "The UI mission and its bound issue",
            evidence: "The UX assessment step, from the mission and its bound issue",
          },
          live_connector: {
            label: "Rendered routes and captured screenshots",
            evidence: "The route check that renders routes and captures screenshots",
          },
        },
      },
      capabilityLabels: {
        intake_classify: "Classify an inbound message",
        intake_duplicates: "Detect duplicate issues",
        repo_write: "Modify code",
        deployment_production_release: "Production release",
        workflow_transition: "Move an issue to its next state",
        workflow_assign: "Assign work",
        workflow_escalate: "Escalate to a human",
        repo_read: "Read repository code",
        knowledge_search: "Search indexed knowledge",
        patch_write_virtual: "Propose code changes",
        sandbox_run: "Run sandbox validation",
        sandbox_debug_exec: "Investigate a failed verification in its own sandbox",
        git_pull_request: "Open a pull or merge request",
        git_push_default: "Push to a default or production branch",
        gate_technical: "Approve or request changes on a patch",
        orchestration_delegate: "Delegate work to other agents",
        patch_read: "Read the patch under review",
        verification_write: "Record a verification verdict",
        reply_draft: "Draft a customer reply",
        reply_send: "Send a reply to a customer",
        support_context_read: "Read prior resolved tickets",
        risk_read: "Read risk flags and verification evidence",
        gate_security: "Open a security gate",
        patch_apply: "Apply an approved patch",
        gate_deployment: "Open a deployment gate",
        devops_carry_verdict: "Carry a verdict back to a CI/CD host",
        deployment_unattended: "Deploy without a human decision",
        ux_assess: "Assess an operator workflow",
        ux_screenshot: "Capture screenshots of real routes",
        preview_browser_verify: "Open the patched application in a browser",
      },
      capabilityReasons: {
        intake: {
          intake_classify: "Its whole responsibility.",
          intake_duplicates: "Runs over existing issues for this tenant.",
          repo_write: "Intake never touches a repository.",
          deployment_production_release:
            "Delivery is a separate, human-signed gate owned by the Release Manager.",
        },
        workflow: {
          workflow_transition: "Through the run-state contract only; no direct row writes.",
          workflow_assign: "Within the tenant.",
          workflow_escalate: "Raises the mission to an operator.",
          repo_write: "The flow controller does not implement changes.",
        },
        codebase: {
          repo_read: "Reads registered codebases.",
          knowledge_search: "Project-scoped hybrid retrieval plus organisation-wide sources.",
          repo_write: "The navigator produces a brief, never a patch.",
          deployment_production_release:
            "Delivery is a separate, human-signed gate owned by the Release Manager.",
        },
        dev: {
          repo_read: "Inside the issue workspace.",
          patch_write_virtual: "Writes a patch for review; it does not land it.",
          sandbox_run: "Verifies its own patch before handing off.",
          sandbox_debug_exec:
            "A bounded shell in the failed run's container — no host, no network, destroyed with the run.",
          preview_browser_verify:
            "The agent that wrote a change does not certify it in a browser; that is the QA agent's.",
          git_pull_request: "Pull/merge request only, after the approval gate.",
          git_push_default: "Not a reachable state: delivery is pull-request only.",
          deployment_production_release:
            "Delivery is a separate, human-signed gate owned by the Release Manager.",
        },
        cto: {
          repo_read: "For patch inspection.",
          gate_technical: "The technical gate; the verdict is frozen on the approval row.",
          orchestration_delegate: "The only agent that plans a mission and delegates.",
          repo_write: "The reviewer never edits.",
        },
        qa: {
          patch_read: "Reads the patch and its sandbox evidence.",
          verification_write: "Writes the verification report the closure gate reads.",
          preview_browser_verify:
            "Through the sandbox preview gateway only, inside that preview's own origin.",
          repo_write: "Verification does not edit.",
        },
        support: {
          reply_draft: "Drafts only.",
          reply_send: "Sending goes through the communications approval gate.",
          support_context_read: "Tracker-agnostic support history.",
        },
        security: {
          risk_read: "Its inputs.",
          gate_security: "Work by this role is gated behind a human by default.",
          repo_write: "Review only.",
        },
        release: {
          patch_apply: "Two-phase commit, after a human sign-off.",
          gate_deployment: "Records the decision; carrying it outward is armed separately.",
          devops_carry_verdict: "Requires the tenant deployment capabilities to be armed.",
          deployment_unattended: "Delivery is never automatic.",
        },
        ux: {
          ux_assess: "Its responsibility.",
          ux_screenshot: "Against the running app.",
          repo_write: "Produces an assessment, not a patch.",
        },
      },
      boundaryTitle: "The boundary",
      boundaryBody:
        "An agent answering about an issue sees that issue's project sources plus "
        + "organisation-wide ones, never another project's. That boundary is enforced in the "
        + "query and in the database, and is not a setting.",
      untrustedTitle: "Retrieved content is data, not instructions",
      untrustedBody:
        "Text pulled from a repository or a knowledge source can never grant an agent a "
        + "capability or lift an approval requirement. Permissions come from policy, not from "
        + "anything a document says.",
      capabilitiesTitle: "What this agent may do",
      granted: "Allowed",
      withheld: "Not allowed",
    },
  },
  orchestration: {
    panel: {
      title: "AI Operations Control Center",
      subtitle: "CTO-led agent mission orchestration",
      live: "live",
      reconnecting: "reconnecting",
      controlCentre: "Open mission cockpit",
      newMission: "New mission",
      checking: "Checking for an active mission…",
      startError: "Could not start the mission — {error}",
      loading: "Loading mission…",
      loadError: "Could not load the mission — {error}",
      noData: "No mission data.",
      tasksProgress: "{done}/{total} tasks",
      approvalsCount: "{n} approval(s)",
      approvalsPending: "{n} approval(s) pending",
      blockers: "{n} blocker(s)",
      blocks: {
        approvalCenter: "Approval Center",
        delegationTree: "Delegation tree",
        tasks: "Tasks",
        timeline: "Agent activity",
        approvals: "Approvals",
      },
      ledger: {
        approver: "approver",
        unknownActor: "Unknown actor",
        auditEvent: "Audit event",
        via: {
          ui: "via FixControl",
          slack: "via Slack",
          email_token: "via email link",
          system: "via system",
          command_center: "via Command Center",
          unknown: "via FixControl",
        },
        status: {
          approved: "Approved",
          rejected: "Rejected",
          changes_requested: "Changes requested",
          expired: "Expired",
        },
        result: {
          execStarted: "Execution started.",
          deployPromoted: "Deployment promoted.",
          retryAuthorized: "Retry authorized.",
          overrideApplied: "Override applied.",
          changeAuthorized: "Change authorized.",
          approved: "Approved.",
          missionStopped: "Mission stopped.",
          rejected: "Rejected.",
          changesRequested: "Changes requested.",
        },
      },
    },
  },
  skillsPage: {
    eyebrow: "Agents",
    title: "Skills",
    subtitle: "Capabilities your AI Team can use.",
    pickTenant: "Pick an organisation to see its skills.",
    loading: "Loading skills…",
    loadFailed: "Could not load skills.",
    empty: "No skills match this filter.",
    addSkill: "Add skill",
    noAgents: "Not assigned",
    neverChanged: "Never changed",
    unsatisfied: "An assigned agent cannot run this skill",
    searchPlaceholder: "Search skills, agents, capabilities…",
    attentionBanner:
      "{count} skill(s) are assigned to an agent that cannot run them. They are active and "
      + "will never be selected until the assignment or the requirement changes.",
    columns: {
      skill: "Skill",
      type: "Type",
      usedBy: "Used by",
      status: "Status",
      capabilities: "Required capabilities",
      lastChanged: "Last changed",
      source: "Source",
      category: "Category",
      trust: "Trust",
      risk: "Risk",
      version: "Version",
      lastReviewed: "Last reviewed",
      platforms: "Platforms",
      languages: "Languages",
      frameworks: "Frameworks",
      lastUpdated: "Last updated",
      checksum: "Checksum",
      license: "License",
      ref: "Version / ref",
      repository: "Source",
    },
    filters: {
      all: "All",
      system: "FixControl",
      custom: "Custom",
      attention: "Needs attention",
      quick: {
        all: "All",
        installed: "Installed",
        active: "Active",
        needs_review: "Needs review",
        attention: "Attention",
      },
      source: "Source",
      trust: "Trust",
      category: "Category",
      risk: "Risk",
      agent: "Used by agent",
      platform: "Platform",
      attentionKind: "Attention",
      anySource: "All sources",
      anyTrust: "All trust levels",
      anyCategory: "All categories",
      anyRisk: "All risk levels",
      anyAgent: "All agents",
      anyPlatform: "All platforms",
      anyAttention: "Any attention",
      sourceOption: {
        fixcontrol: "FixControl",
        custom: "Custom",
        verified: "Verified external",
        community: "Community",
      },
    },
    status: {
      active: "Active",
      draft: "Draft",
      disabled: "Disabled",
      archived: "Archived",
    },
    type: {
      system: "FixControl",
      custom: "Custom",
      edited: "edited",
      external: "External",
    },
    procedure: {
      descriptive: "Describes work the agent already does",
      browser_route_check: "Opens the changed routes in a real browser",
      preview_browser_check: "Opens the patched application in a real browser",
      guidance: "Advisory guidance handed to the agent",
    },
    costClass: {
      low: "Low",
      medium: "Medium",
      high: "High",
    },
    events: {
      "skill.created": "Skill created",
      "skill.updated": "Skill changed",
      "skill.restored": "FixControl defaults restored",
      "skill.status_changed": "Status changed",
      "skill.assigned": "Assigned to an agent",
      "skill.unassigned": "Removed from an agent",
      "skill.deleted": "Draft deleted",
      "skill.imported": "Imported",
      "skill.review_started": "Review started",
      "skill.approved": "Approved",
      "skill.rejected": "Rejected",
      "skill.re_review_requested": "Re-review requested",
      "skill.update_available": "Update available",
      "skill.version_switched": "Switched version",
      "skill.rolled_back": "Rolled back",
      "skill.quarantined": "Quarantined",
    },
    triggers: {
      none: "No triggers yet. Without one, nothing would ever select this skill.",
      kind: "When",
      matcher: "Matches",
      priority: "Priority",
      pick: "Choose…",
      add: "Add trigger",
      remove: "Remove trigger",
      priorityNote:
        "Priority orders skills when several fire on one task. It breaks ties; it grants "
        + "nothing.",
      kinds: {
        always: "Always, for its agents",
        agent_role: "The task runs as a role",
        issue_kind: "The issue is of a kind",
        issue_flag: "The issue carries a flag",
        changed_file_pattern: "A changed file matches",
        ux_mission: "The request is a UI/UX mission",
        failing_test: "A test or sandbox check failed",
        explicit_request: "The request says so",
      },
      placeholder: {
        changed_file_pattern: "src/app/**",
        explicit_request: "browser verification",
        agent_role: "frontend",
        issue_kind: "BUG",
        issue_flag: "PAYMENT",
        always: "",
        ux_mission: "",
        failing_test: "",
      },
    },
    simulator: {
      title: "Test skill",
      intro:
        "Describe a task. Nothing runs — this shows which agent would pick the skill up, why, "
        + "and what it would be allowed to use.",
      instruction: "What would you ask for?",
      instructionPlaceholder: "Check the issue list renders after the refactor",
      issueKind: "Issue kind",
      flags: "Risk flags",
      changedFiles: "Changed files",
      run: "Run test",
      failed: "The simulation could not run.",
      noSelection: "Nothing would select this skill for that task.",
      wouldRun: "Would run",
      wouldNotRun: "Would not run",
      matched: "Trigger",
      capabilities: "Capabilities it may use",
      context: "Context in reach",
      procedure: "How it runs",
      version: "Version",
    },
    runs: {
      title: "Where this skill ran",
      loading: "Loading run history…",
      empty: "This skill has not appeared on a run yet.",
      olderVersion: "· an earlier version of this skill",
      matched: "Trigger: {triggers}",
      evidence: "{count} evidence artifact(s)",
      routesTested: "Routes: {routes}",
      assertions: "{passed} checks passed, {failed} failed",
      consoleErrors: "{count} console error(s)",
      status: {
        selected: "Selected",
        executed: "Ran",
        refused: "Refused",
        failed: "Failed",
      },
    },
    detail: {
      loading: "Loading skill…",
      loadFailed: "Could not load this skill.",
      backToList: "All skills",
      saved: "Saved.",
      requestFailed: "The request did not complete.",
      save: "Save",
      revert: "Revert",
      unsatisfiedTitle: "An assigned agent cannot run this skill",
      setStatus: {
        active: "Activate",
        disabled: "Disable",
        draft: "Back to draft",
        archived: "Archive",
      },
      tabs: {
        overview: "Overview",
        instructions: "Instructions",
        triggers: "Triggers",
        context: "Context",
        capabilities: "Capabilities",
        agents: "Agents",
        verification: "Verification",
        versions: "Versions",
        provenance: "Provenance",
        review: "Review",
      },
      overview: {
        purpose: "Purpose",
        type: "Type",
        status: "Status",
        agents: "Assigned agents",
        howItRuns: "How it runs",
        version: "Current version",
        lastChanged: "Last changed",
      },
      instructions: {
        reopensReview: "Saving a change to this approved skill sends it back to review; agents stop using it until it is approved again.",
        intro:
          "What this skill tells the agent. Instructions steer how work is done; they cannot "
          + "change what the agent is allowed to do.",
        bounds: "{count} characters",
      },
      triggers: {
        intro: "When this skill is relevant. Several can fire; the highest priority goes first.",
      },
      context: {
        intro:
          "What this skill asks to read. The agent's own context policy still decides what it "
          + "actually sees — a skill can never open a source the agent is not allowed to read.",
        none: "This skill asks for no particular context.",
        required: "Required",
        optional: "Helpful",
        boundedNote:
          "Effective context is the overlap of this list with what the agent's runtime reads, "
          + "inside the project and tenant boundary.",
      },
      capabilities: {
        boundary:
          "Read-only. A skill never grants a capability: it states what the agent must already "
          + "have, and stays unavailable when the agent does not have it.",
        required: "Required",
        optional: "Helpful when available",
        none: "None.",
        satisfiedBy: "Held by {agents}",
        missingOn: "Missing on {agents}",
        noAgentsYet: "No agent assigned yet.",
      },
      agents: {
        intro:
          "Which agents carry this skill. An agent that does not hold the required capabilities "
          + "cannot be assigned — the skill would be active and silently absent.",
        assigned: "Assigned",
        assign: "Assign",
        unassign: "Remove",
      },
      verification: {
        expectations: "What must be true for a run to count",
        noExpectations: "No expectations recorded for this skill.",
      },
      versions: {
        restore: "Restore FixControl defaults",
        restoreAvailable:
          "This tenant has changed this skill. Restoring drops those changes and returns to the "
          + "shipped version. History stays intact.",
        alreadyDefault: "This skill is unchanged from what FixControl ships.",
        timeline: "What changed, when",
        definitions: "Versions",
        noChanges: "No changes recorded.",
        noVersions: "No version has been recorded yet.",
        versionN: "Version {n}",
        current: "Current",
        system: "system",
        changedFields: "Changed: {fields}",
      },
    },
    registry: {
      seedOff: "{count} FixControl engineering skills are available but switched off for this organisation. Once enabled, they add advisory guidance to the briefs of the agents they are assigned to.",
      seedOn: "FixControl engineering skills are on for this organisation ({count}).",
      seedEnable: "Enable FixControl engineering skills ({count})",
      seedDisable: "Disable",
      seedKeepOwn: "{count} of them keep their own status: they were switched on or off one by one, and this switch leaves them as they are.",
      seedFailed: "The engineering skills setting could not be changed.",
      meta: "{installed} installed · {active} active",
      addOrDiscover: "Add / Discover skill",
      menuLabel: "Ways to add a skill",
      menuDiscover: "Discover skills",
      menuImport: "Import from a source",
      menuCustom: "Write a custom skill",
      tabInstalled: "Installed",
      tabDiscover: "Discover",
      viewLabel: "Registry view",
      label: "Skills registry",
      rowActions: "Actions",
      openFullPage: "Open full page",
      openDetails: "Open details",
      resetFilters: "Reset filters",
      emptyTitle: "No skills installed yet",
      emptyHint: "Add a skill from the discovery catalog, import one from git, or write your own.",
      filteredEmpty: "No skills match these filters",
      filteredEmptyHint: "Widen or reset the filters.",
      moreAgents: "+{count}",
      attentionMarker: "Needs attention",
      reviewMarker: "Review needed",
      updateMarker: "Update available",
      anyPlatform: "Any",
      checksumTitle: "Checksum {value}",
      shipped: "Shipped",
      neverReviewed: "Never",
      columns: "Columns",
      quickLabel: "Quick filter",
      externalSkill: "External skill",
      retry: "Retry",
    },
    attention: {
      title: "Needs attention",
      kind: {
        unsatisfied: "Assigned agent cannot run it",
        quarantined: "Quarantined",
        checksum_mismatch: "Checksum mismatch",
        update_available: "Update available",
      },
      count: {
        unsatisfied: "{count} assigned to an agent that cannot run them",
        quarantined: "{count} in quarantine",
        checksum_mismatch: "{count} with a checksum mismatch",
        update_available: "{count} with an update available",
      },
    },
    trust: {
      internal: "Internal",
      verified: "Verified",
      community: "Community",
      quarantined: "Quarantined",
    },
    review: {
      pending_review: "Pending review",
      approved: "Approved",
      rejected: "Rejected",
      needs_re_review: "Needs re-review",
    },
    risk: {
      low: "Low",
      medium: "Medium",
      high: "High",
      restricted: "Restricted",
    },
    source: {
      fixcontrol: "FixControl",
      custom: "Custom",
      verified: "Verified",
      community: "Community",
      quarantined: "Quarantined",
    },
    sourceType: {
      internal: "FixControl",
      manual: "Manual",
      git: "Git repository",
      openclaw: "SKILL.md (git)",
      catalog: "Discovery catalog",
    },
    health: {
      ok: "OK",
      update_available: "Update available",
      checksum_mismatch: "Checksum mismatch",
      quarantined: "Quarantined",
    },
    category: {
      debugging: "Debugging",
      planning: "Planning",
      implementation: "Implementation",
      testing: "Testing",
      verification: "Verification",
      code_review: "Code review",
      security: "Security",
      architecture: "Architecture",
      frontend: "Frontend",
      backend: "Backend",
      api: "API",
      database: "Database",
      performance: "Performance",
      browser: "Browser",
      git: "Git",
      devops: "DevOps",
      containers: "Containers",
      kubernetes: "Kubernetes",
      cloud: "Cloud",
      incident_response: "Incident response",
      support: "Support",
      release: "Release",
      windows: "Windows",
      dotnet: ".NET",
      windev: "WinDev",
      documentation: "Documentation",
      intake: "Intake",
    },
    platform: {
      linux: "Linux",
      windows: "Windows",
      browser: "Browser",
    },
    scan: {
      severity: {
        critical: "Critical",
        high: "High",
        medium: "Medium",
        low: "Low",
      },
      category: {
        prompt_hierarchy_override: "Overrides system instructions",
        secret_exfiltration: "Secret exfiltration",
        approval_bypass: "Approval bypass",
        production_push: "Push to production",
        arbitrary_shell: "Arbitrary shell commands",
        destructive_fs: "Destructive file operations",
        network_exfiltration: "Network exfiltration",
        permission_escalation: "Permission escalation",
        persistence: "Persistence",
        hidden_instructions: "Hidden instructions",
        scope_override: "Scope override",
        unknown_capability: "Unknown capability",
      },
      summary: "{critical} critical · {high} high · {medium} medium · {low} low",
      clean: "No findings",
    },
    reasons: {
      missing_capability: "Cannot assign: agent lacks capability {missing}",
      forbidden_capability: "Asks for a capability no skill may use",
      role_not_allowed: "Not meant for this agent's role",
      agent_disabled: "The agent is paused",
      skill_not_active: "The skill is switched off",
      context_unavailable: "The agent cannot read the context this skill needs",
      runtime_unavailable: "The runtime this skill needs is not available",
      not_assigned: "Not assigned",
      trust_blocked: "Its trust level does not allow use",
      review_pending: "Waiting for review approval",
      quarantined: "In quarantine",
      platform_mismatch: "Platform does not match this environment",
      runtime_mismatch: "Runtime does not match this environment",
      checksum_mismatch: "Content changed since it was reviewed (checksum mismatch)",
      no_capabilities: "Requires no capability, so it would reach every agent",
    },
    refusals: {
      guidance_text_locked: "A shipped guidance skill's text is reviewed by FixControl and cannot be edited here.",
      skill_no_capabilities: "This skill declares no required capability, so it cannot be assigned or enabled.",
      guidance_too_long: "The guidance text is longer than the 4,000 characters an agent is handed.",
      reason_reserved: "That reason prefix is reserved for quarantines the system sets itself.",
      ssrf_blocked: "That source address is not allowed.",
      invalid_source: "The source is not a valid repository file.",
      source_too_large: "The skill file is too large (limit 256 KB).",
      already_installed: "This catalog entry is already installed.",
      import_failed: "The import did not complete.",
      not_external: "Only an imported skill has a source.",
      not_updatable: "This skill has no upstream to check.",
      external_content_pinned: "Imported content is pinned; change it by importing a new version.",
      already_pinned: "This version is already pinned.",
      review_pending: "This skill has not been approved yet. Review and approve it before enabling it.",
      trust_blocked: "This skill's trust level does not allow it to be enabled.",
      quarantined: "This skill is in quarantine and cannot be enabled or assigned.",
      assignment_authority_unsatisfied: "This agent cannot run this skill, so it cannot be assigned.",
      findings_not_acknowledged: "Acknowledge every high and critical finding before approving.",
      acknowledgement_required: "Acknowledge every high and critical finding before approving.",
      snapshot_not_approved: "Only an approved version can be pinned.",
      snapshot_not_found: "That version no longer exists.",
      skill_version_conflict: "Someone else changed this skill first. It has been reloaded; try again.",
      skill_not_found: "This skill no longer exists.",
      checksum_mismatch: "The fetched content does not match its checksum.",
      source_unavailable: "The source could not be reached.",
      reason_required: "A reason is required.",
      skill_exists: "A skill with this id is already installed.",
      forbidden: "You do not have permission for this action.",
    },
    drawer: {
      acknowledgeFinding: "Acknowledge finding: {finding}",
      hiddenCharacters: "{count} hidden characters removed",
      guidanceNotRead: "This agent's runtime does not read guidance",
      externalTriggersNote: "The content of an imported skill is pinned to its source version. When it is considered is this organisation's configuration.",
      snapshotFindingsNotice: "This version has high or critical findings, but the server did not send them. It can be approved once they are shown here, or rejected now.",
      approveVersion: "Approve this version",
      rejectVersion: "Reject this version",
      loading: "Loading skill…",
      loadFailed: "Could not load this skill.",
      sections: {
        triggers: "Triggers",
        provenance: "Provenance",
        instructions: "Instructions",
        review: "Security review",
        assigned: "Assigned agents",
        available: "Other agents",
        platforms: "Platforms, languages and frameworks",
        versions: "Source versions",
        audit: "Audit history",
      },
      fields: {
        source: "Source",
        sourceType: "Source type",
        repository: "Repository",
        path: "Path",
        version: "Version / commit",
        imported: "Imported",
        lastReviewed: "Last reviewed",
        checksum: "Checksum",
        license: "License",
        inspiredBy: "Inspired by",
        trust: "Trust",
        review: "Review",
        risk: "Risk",
        status: "Status",
        category: "Category",
        quarantineReason: "Quarantine reason",
        platforms: "Platforms",
        languages: "Languages",
        frameworks: "Frameworks",
        runtimes: "Runtimes",
      },
      shippedWithFixControl: "Shipped with FixControl",
      never: "Never",
      reviewedBy: "{when} by {name}",
      copy: "Copy",
      any: "Any",
      instructionsNote: "Shown as plain text, exactly as stored. Nothing here is rendered or executed.",
      guidanceNote: "Handed to the agent as advisory guidance only. It cannot override system, governance or approval rules.",
      scanNone: "The scan found nothing on the pinned version.",
      scanNotApplicable: "Shipped with FixControl — no source scan applies.",
      acknowledge: "I have read this finding",
      line: "line {line}",
      noneAssigned: "Not assigned to any agent.",
      noneAvailable: "No other agents.",
      canRun: "Can run here",
      cannotRun: "Cannot run here",
      compatible: "Compatible",
      incompatible: "Not compatible",
      reviewCapabilities: "Review this agent's capabilities",
      assign: "Assign",
      remove: "Remove from agent",
      snapshotsNone: "No source versions — this skill was not imported from a source.",
      pinned: "Pinned",
      fetched: "Fetched {when}",
      viewDiff: "View diff",
      hideDiff: "Hide diff",
      diffLoading: "Loading diff…",
      diffFailed: "Could not load the diff.",
      diffEmpty: "No differences in the instructions.",
      diffLabel: "Changes against the pinned version",
      switchTo: "Switch to this version",
      rollBack: "Roll back",
      checkUpdate: "Check for update",
      updateFound: "A newer version was fetched and waits for review (+{added} / -{removed} lines). The pinned version is unchanged.",
      noUpdate: "The source has no newer version.",
      auditNone: "No history recorded yet.",
      system: "system",
      done: "Done.",
      requestFailed: "The request did not complete.",
      actions: {
        label: "Skill actions",
        enable: "Enable",
        disable: "Disable",
        review: "Review",
        approve: "Approve",
        reject: "Reject",
        requestReReview: "Request re-review",
        openFullPage: "Open full page",
        reason: "Reason",
        reasonPlaceholder: "Why? This is recorded in the audit history.",
        ackRequired: "Acknowledge every high or critical finding to approve.",
        reasonRequired: "A reason is required.",
        cancel: "Cancel",
        confirmApprove: "Approve skill",
        confirmReject: "Reject skill",
        confirmReReview: "Request re-review",
        enableBlocked: "Can be enabled once the skill is approved.",
      },
      state: {
        pending_review: "Waiting for review. No agent can use this skill until it is approved.",
        needs_re_review: "Changed since it was approved. It needs a new review before agents can use it again.",
        rejected: "Rejected in review. It cannot be enabled.",
        quarantined: "In quarantine. It cannot be enabled or assigned.",
      },
    },
    import: {
      title: "Add a skill",
      stepOf: "Step {current} of {total}",
      chooseSource: "Where does the skill come from?",
      sources: {
        catalog: "From the discovery catalog",
        git: "Git repository (GitHub/GitLab)",
        openclaw: "SKILL.md from git",
        manual: "Paste manually",
      },
      hints: {
        catalog: "A curated entry, resolved to a pinned commit on import.",
        git: "A FixControl skill file in a repository you name.",
        openclaw: "A SKILL.md file; its text becomes advisory guidance.",
        manual: "Paste a name and instructions.",
      },
      customWizard: "Or write a custom skill with the wizard",
      catalogEntry: "Catalog entry",
      pickEntry: "Choose an entry…",
      repository: "Repository",
      repositoryPlaceholder: "owner/repo",
      path: "Path in the repository",
      pathPlaceholder: "skills/debugging/SKILL.md",
      ref: "Tag or commit",
      refPlaceholder: "v1.2.0 or a 40-character commit",
      refHint: "A branch name is resolved once, to a pinned commit. FixControl never follows 'latest': a newer version is only fetched when you check for an update, and then waits for review.",
      name: "Name",
      instructions: "Instructions",
      instructionsHint: "Stored and shown as plain text. It is scanned and must be reviewed before any agent can use it.",
      required: "Fill in the required fields.",
      back: "Back",
      next: "Next",
      submit: "Import",
      importing: "Importing…",
      failed: "The import did not complete.",
      close: "Close",
      resultTitle: "Imported — disabled until approved",
      pinnedCommit: "Pinned commit",
      checksum: "Checksum",
      scan: "Scan",
      trust: "Trust",
      review: "Review",
      status: "Status",
      disabledNote: "The skill is installed DISABLED. It must be reviewed and approved before any agent can use it. Nothing was assigned or enabled.",
      openSkill: "Open the skill",
    },
    discover: {
      intro: "A curated index of external skills. Only metadata is shown — nothing is fetched until you import, and an import lands disabled and pending review.",
      label: "Discovery catalog",
      import: "Import",
      installed: "Installed",
      notInstalled: "Not installed",
      openInstalled: "Open installed skill",
      empty: "The discovery catalog is empty.",
      filteredEmpty: "No catalog entries match these filters.",
      loadFailed: "Could not load the discovery catalog.",
    },
    wizard: {
      title: "New skill",
      close: "Close",
      stepOf: "Step {current} of {total} — {name}",
      back: "Back",
      next: "Next",
      create: "Create skill",
      failed: "The skill could not be created.",
      notValid: "This skill is not valid yet",
      name: "Name",
      namePlaceholder: "API regression check",
      id: "Id",
      idHint: "Lowercase words joined by hyphens. It never changes once created.",
      description: "What it does",
      descriptionPlaceholder: "One sentence an operator can read in a list.",
      triggersIntro: "When should this skill be considered?",
      contextIntro:
        "What should it be able to read? The agent's own policy still decides what it actually "
        + "sees.",
      capabilitiesBoundary:
        "A skill states what the agent must already be allowed to do. Picking a capability here "
        + "grants nothing — it decides which agents can carry this skill at all.",
      capabilityForbidden: "Decides workflow or delivery — not available to a skill.",
      heldBy: "Held by {agents}",
      heldByNobody: "No agent holds this today.",
      readBy: "Read by {agents}",
      eligibleAgents: "Agents that could run this: {agents}",
      noEligibleAgents:
        "No agent holds all of these capabilities, so this skill could not be assigned to "
        + "anything. Remove one, or pick capabilities one agent has.",
      instructionsIntro:
        "Write the steps, the goal and what a good result looks like. No commands, no URLs, no "
        + "credentials — a skill describes work, it does not execute it.",
      instructionsPlaceholder:
        "Compare the API surface before and after the change. Report any removed or renamed "
        + "endpoint as a breaking change, with the route named.",
      outputIntro: "How is this skill carried out, and what does it record?",
      procedure: "How it runs",
      procedureHint: {
        descriptive:
          "The agent already does this as part of its normal work. The skill describes it and "
          + "is recorded on the run; it adds no step.",
        browser_route_check:
          "Opens the routes this change touches in a real browser, checks their critical "
          + "elements, and records screenshots and console errors as evidence.",
        preview_browser_check:
          "Opens the patched application through its sandbox preview, inside that preview's own "
          + "origin, and records the page status, console errors, failed requests and a screenshot "
          + "as evidence.",
      },
      verificationIntro: "What must be true for a run of this skill to count? One per line.",
      expectationsPlaceholder: "Every tested route has a stored screenshot.",
      costClass: "Expected cost",
      budgetCeiling:
        "The agent's own budget and retry policy remain the ceiling. A skill can ask for less, "
        + "never more.",
      draftNote:
        "The skill is created as a draft. Assign it to an agent and activate it to make it run.",
      steps: {
        identity: "Name and purpose",
        triggers: "Triggers",
        context: "Context",
        capabilities: "Capabilities",
        instructions: "Instructions",
        output: "Output",
        verification: "Verification",
        review: "Review",
      },
    },
  },
  /**
   * AI Team overview (/agents). Header, summary, filters, cards, flow, live
   * activity, workload, running-now, empty/degraded states. Agent NAMES come
   * from the per-tenant row and are not translated; the role subtitles are.
   */
  aiTeam: {
    eyebrow: "AI Operations",
    title: "AI Team",
    subtitle: "Your autonomous workforce across support, engineering, verification and release.",
    headline: {
      agents: "{count} agents",
      agentsOne: "1 agent",
      active: "{count} active",
      waiting: "{count} waiting for approval",
      attention: "{count} needs attention",
      attentionPlural: "{count} need attention",
    },
    actions: {
      assignTask: "Assign task",
      viewAllRuns: "View all runs",
      orgChart: "Org chart",
      openAgent: "Open agent",
      openAgentNamed: "Open {name}",
      assignTo: "Assign task to {name}",
    },
    ask: {
      label: "Ask the team",
      placeholder: "What do you want the AI Team to work on?",
      submit: "Ask",
      hint: "Opens the Command Center with your request — the same governed flow, nothing runs unasked.",
    },
    summary: {
      total: "Agents",
      running: "Active",
      idle: "Idle",
      waiting: "Waiting",
      attention: "Attention needed",
      pausedNote: "{count} paused",
      enabledNote: "{count} enabled",
      windowNote: "Metrics cover the last {days} days",
      updated: "Updated {ago}",
    },
    status: {
      running: "Working",
      waiting: "Waiting",
      attention: "Attention",
      idle: "Idle",
      paused: "Paused",
      liveLabel: "Working right now",
    },
    filters: {
      label: "Filter agents",
      all: "All",
      active: "Active",
      waiting: "Waiting",
      attention: "Attention",
      idle: "Idle",
      searchPlaceholder: "Search agents or current work…",
      searchLabel: "Search agents or current work",
      clear: "Clear filters",
      noResults: "No agents match this filter.",
      noResultsHint: "Try another status or clear the search.",
    },
    view: {
      label: "View",
      team: "Team",
      flow: "Flow",
    },
    roles: {
      intake: "Triage & classification",
      workflow: "Flow controller",
      codebase: "Repository intelligence",
      dev: "Implementation",
      cto: "Technical review",
      qa: "Validation & evidence",
      support: "Customer communication",
      security: "Security review",
      release: "Delivery coordination",
      ux: "Workflow UX",
    },
    card: {
      currentWork: "Current work",
      nothingInProgress: "Nothing in progress",
      pausedHint: "Deactivated — the runtime skips this agent until it is resumed.",
      waitingOn: "Waiting on approval",
      queuedShort: "{count} queued",
      runs: "{count} runs",
      runsOne: "1 run",
      success: "{rate}% success",
      noRunsYet: "No runs yet",
      noRunsWindow: "No runs in the last {days} days",
      noRunLedger: "Runs are not recorded for this agent",
      metricsUnavailable: "Metrics unavailable",
      lastRun: "Last run {ago}",
      lastRunFailed: "Last run failed {ago}",
      sparklineLabel: "Runs per day over the last {days} days: {values}",
      engineInline: "Engine-inline",
    },
    attention: {
      agent_error: "The agent reported an error",
      failed_run: "The last run failed",
      job_error: "A work session failed",
      failed_task: "A task failed",
      blocked_task: "A task is blocked",
    },
    flow: {
      title: "How work flows through the team",
      intro: "The shipped support-to-engineering pipeline, with what is in flight at each stage. The CTO plans at the start and reviews at the end; the UX architect joins for UI missions.",
      stages: {
        intake: "Intake",
        workflow: "Workflow",
        plan: "Plan",
        codebase: "Codebase",
        dev: "Developer",
        qa: "QA",
        review: "CTO review",
        security: "Security",
        release: "Release",
        support: "Support reply",
        ux: "UX architect",
      },
      branch: "Joins after {stage} on UI missions",
      running: "{count} running",
      waiting: "{count} waiting",
      blocked: "{count} blocked",
      quiet: "Nothing in flight",
      stageLabel: "Stage {index} of {total}: {stage}",
    },
    activity: {
      title: "Live activity",
      hint: "The last few things the team did, newest first.",
      empty: "No recent activity",
      emptyHint: "Events appear here as soon as an agent starts work.",
      unavailable: "Recent activity could not be loaded.",
      system: "System",
      kinds: {
        run_succeeded: "Run completed",
        run_failed: "Run failed",
        task_started: "Started: {title}",
        task_completed: "Completed: {title}",
        task_failed: "Failed: {title}",
        approval_requested: "Waiting for approval: {title}",
        approval_approved: "Approved: {title}",
        approval_rejected: "Rejected: {title}",
        approval_changes_requested: "Changes requested: {title}",
        mission_started: "Mission started: {title}",
        mission_completed: "Mission completed: {title}",
        mission_failed: "Mission failed: {title}",
        mission_blocked: "Mission blocked: {title}",
        mission_awaiting_approval: "Mission waiting for approval: {title}",
      },
    },
    workload: {
      title: "Workload",
      hint: "Where work is accumulating.",
      columns: {
        agent: "Agent",
        active: "Active",
        queued: "Queued",
        waiting: "Waiting",
        success: "Success",
        lastRun: "Last run",
      },
      empty: "No work recorded yet.",
      notRecorded: "n/a",
    },
    running: {
      title: "Running now",
      hint: "Work that is executing at this moment.",
      empty: "No agents are currently running",
      emptyHint: "Assign a task or start a mission to put the team to work.",
      startedAgo: "Started {ago}",
      more: "+{count} more",
    },
    empty: {
      noAgents: "No agents are configured yet",
      noAgentsHint: "The AI team is provisioned automatically for every workspace. If this list stays empty, ask your administrator to re-seed the agents for this organisation.",
    },
    error: {
      title: "The AI Team overview could not be loaded",
      degraded: "Some sections are unavailable right now: {sections}. The rest of the page is live.",
      sections: {
        metrics: "run metrics",
        work: "current work",
        approvals: "pending approvals",
        activity: "recent activity",
        queue: "queued issues",
      },
    },
  },
  agentsPage: {
    orchestration: {
      editor: {
        title: "Orchestration settings",
        intro:
          "The resolved configuration this agent runs under, and the parts of it you "
          + "can change. Each value shows where it came from and whether it is yours "
          + "to move.",
        groupLifecycle: "Availability",
        groupModel: "Model",
        groupBudget: "Budget",
        groupRetry: "Retries and timeouts",
        groupContext: "Context",
        groupRouting: "Routing",
        groupCapabilities: "Capabilities",
        groupGovernance: "Governance",
        historyTitle: "Configuration history",
        historyNone: "No configuration changes recorded.",
        versionsTitle: "Configurations this agent has had",
        currentVersion: "Current version",
        edit: "Change",
        cancel: "Cancel",
        review: "Review change",
        apply: "Apply",
        applying: "Applying…",
        restore: "Restore FixControl defaults",
        restoreConfirm:
          "Remove this organisation's overrides for {name} and return to the "
          + "FixControl defaults? History is kept — this records a new version rather "
          + "than erasing the ones before it.",
        previewTitle: "What would change",
        previewFrom: "Now",
        previewTo: "After",
        previewRisk: "Risk",
        riskLow: "Low",
        riskMedium: "Medium",
        riskHigh: "High",
        guardedTitle: "These need an explicit acknowledgement",
        guardedAck: "I understand what {field} changes",
        refusedTitle: "Refused",
        graphTitle: "Effect on the orchestration graph",
        noChanges: "Nothing to apply — the values already match.",
        conflict:
          "This agent's configuration changed while you were editing. The current "
          + "values have been reloaded; review them and try again.",
        applied: "Applied. New version {version}.",
        restored: "Restored FixControl defaults.",
        failed: "Could not apply the change.",
        readOnly: "You can see this configuration but not change it.",
        alsoAffects: "Also changes: {agents}. These share this setting, and it has no per-agent form.",
        restoreSkipped: "Left alone (change these deliberately): {fields}.",
        fields: {
          enabled: "Available to the orchestrator",
          preferredModel: "Preferred model",
          platformTaskModel: "Platform default model",
          anthropicOnlyClamp: "Provider restriction",
          maxCostUsd: "Cost ceiling per request (USD)",
          maxOutputTokens: "Answer length ceiling (tokens)",
          retryCap: "Retries on the same task",
          retryBudget: "Provider attempts before the fallback",
          deadlineMs: "Per-attempt time limit (ms)",
          sandboxAutoRevision: "Self-heal after a failed sandbox run",
          sandboxAutoReviseMaxDepth: "Self-heal rounds",
          qaLlmMaxAttempts: "Verification retry loop",
          contextExpansion: "How much context to gather",
          planningDepth: "How far ahead to plan",
          confidenceThreshold: "Confidence needed to act unprompted",
          retrievalLimit: "Knowledge hits in the brief",
          hybridWeights: "Search ranking weights",
          rerankEnabled: "Re-ranking pass",
          supportHistoryLimit: "Previous tickets offered as context",
          retrievalProjectBoundary: "Project boundary on retrieval",
          includeReleaseStep: "Plan a delivery stage",
          planApprovalPolicy: "When a plan needs sign-off",
          haltDevOnGate: "Stop work while a gate is open",
          ctoPrerequisiteQa: "Verification required before the gate",
          protectedAgent: "Load-bearing agent",
          agentSetFixed: "Fixed agent set",
          scopedBehaviorResolution: "Honour project and per-agent overrides",
          deploymentCapabilities: "Deployment capabilities",
          externalWritesViaOutbox: "External writes through the outbox",
          gitDeliveryPrOnly: "Pull-request-only delivery",
          gateApproveSemantics: "What approving a change request does",
          rejectGateAborts: "A rejection ends the mission",
          resumeReviseOnComplete: "Re-run after a revision request",
          verificationStrictness: "How strict verification is",
          governanceBlockedEnforcement: "When policy blocks a plan",
          approvalChokepoint: "Single approval chokepoint",
          tenantIsolation: "Organisation isolation",
          runStateContract: "Run-state contract",
        },
        impacts: {
          enabled:
            "A deactivated agent is refused at every dispatch chokepoint, and the planner "
            + "drops its tasks. Where that agent owns an approval step (security review, gated "
            + "delivery), the step disappears from the plan rather than blocking it.",
          preferredModel:
            "Changes which model answers for THIS tenant, and therefore cost and answer "
            + "quality. It never changes what the agent is permitted to do.",
          platformTaskModel:
            "The shipped default model for this agent's task. One value for the whole "
            + "platform, changed only by a FixControl platform administrator in Settings -> "
            + "AI. A tenant tunes its own model with the preferred-model setting above, which "
            + "overrides this one for that tenant only.",
          anthropicOnlyClamp:
            "The code agents drive an Anthropic tool-use loop. A non-Anthropic override is "
            + "ignored rather than handed to them.",
          maxCostUsd:
            "Estimated USD ceiling per request, checked before the call. 0 = no ceiling. "
            + "Over the ceiling the request is refused, not truncated.",
          maxOutputTokens:
            "Clamps the answer length DOWN only. Too low truncates structured output and "
            + "drops the agent to its deterministic fallback.",
          retryCap:
            "Max consecutive orchestration retries on the same task before the mission fails "
            + "instead of looping.",
          retryBudget:
            "Provider attempts on the primary model before the fallback arm. Multiplies with "
            + "an agent's own retry loop where it has one.",
          deadlineMs:
            "Per-attempt wall clock. 0 = the shared default. An expired deadline counts as a "
            + "retriable failure and spends the retry budget.",
          sandboxAutoRevision:
            "Off means every failed sandbox verification goes to a human instead of the "
            + "agent self-healing.",
          sandboxAutoReviseMaxDepth:
            "How many self-heal rounds a sandbox failure may chain before it stops.",
          qaLlmMaxAttempts:
            "QA retries its own parse failures, which the routing layer cannot see. Env-only "
            + "today — not on the tenant surface.",
          contextExpansion:
            "How much surrounding context the agent gathers before acting. Stored on the "
            + "agent-behaviour profile and resolved into the effective profile, but NO runtime "
            + "callsite reads it today. Offering it as a control would be a setting that "
            + "silently does nothing — the exact defect this surface exists to expose. Shown "
            + "so the stored value is visible; it becomes adjustable when a callsite reads it.",
          planningDepth:
            "How far ahead the agent plans before it starts work. Stored on the "
            + "agent-behaviour profile and resolved into the effective profile, but NO runtime "
            + "callsite reads it today. Offering it as a control would be a setting that "
            + "silently does nothing — the exact defect this surface exists to expose. Shown "
            + "so the stored value is visible; it becomes adjustable when a callsite reads it.",
          confidenceThreshold:
            "Minimum confidence before the agent acts without asking. Stored on the "
            + "agent-behaviour profile and resolved into the effective profile, but NO runtime "
            + "callsite reads it today. Offering it as a control would be a setting that "
            + "silently does nothing — the exact defect this surface exists to expose. Shown "
            + "so the stored value is visible; it becomes adjustable when a callsite reads it.",
          retrievalLimit: "Knowledge hits the architect brief carries. Fixed in the handler.",
          hybridWeights:
            "Vector / full-text / trigram weighting in the RRF combine. A central setting: "
            + "one value for every tenant, so it is not a per-tenant knob.",
          rerankEnabled: "Cross-encoder rerank pass over the candidate pool. Central setting.",
          supportHistoryLimit: "Previously resolved tickets offered as reply context.",
          retrievalProjectBoundary:
            "An agent answering about an issue sees that issue's project sources plus "
            + "organisation-wide ones, never another project's. Not a toggle.",
          includeReleaseStep:
            "Adds a delivery stage to every planned mission. Delivery stays gated behind a "
            + "human, but the stage exists where it did not before.",
          scopedBehaviorResolution:
            "Off (default), every agent resolves its behavior at the organisation scope, so "
            + "a project-level or per-agent binding is stored but changes nothing. Turning it "
            + "on makes every per-agent binding at the organisation take effect at once, and "
            + "every project binding that was reviewed and activated — review them before "
            + "arming it. A project binding that was never activated keeps waiting.",
          planApprovalPolicy:
            "Whether a mission plan needs a human sign-off before any work starts. It is "
            + "derived from this organisation's governance header, so it is changed on the "
            + "governance console — changing it here would leave two writers on one gate.",
          haltDevOnGate:
            "Off lets the implementation agent keep working while an approval gate is open, "
            + "so work accumulates ahead of a decision.",
          ctoPrerequisiteQa:
            "A CTO sign-off with no QA report on file runs QA first. The gate cannot be "
            + "reached without verification evidence.",
          protectedAgent:
            "The technical gate and the closure gate are load-bearing. Deactivating them is "
            + "refused by the API and the runtime.",
          agentSetFixed:
            "Agents cannot be created or deleted: every id must have a real executor behind "
            + "it. The ten are the set.",
          deploymentCapabilities:
            "Three of the four arm an external write — a verdict leaving FixControl. Armed "
            + "on the governance surface, not here.",
          externalWritesViaOutbox:
            "Every side effect on an external system carries an idempotency key, a retry "
            + "policy and a dead-letter path. No direct provider calls.",
          gitDeliveryPrOnly:
            "Code delivery is pull/merge request only. Pushing to a default or production "
            + "branch is not a reachable state.",
          gateApproveSemantics:
            "`accept` means approving a 'changes requested' gate overrides the CTO verdict "
            + "and takes the version as-is, instead of asking for a revision.",
          rejectGateAborts:
            "Whether a reject ends the mission or lets it continue on another path.",
          resumeReviseOnComplete:
            "Whether the agent re-runs automatically once a revision request completes.",
          verificationStrictness:
            "How much evidence verification demands before it will pass a change. Stored on "
            + "the agent-behaviour profile and resolved into the effective profile, but NO "
            + "runtime callsite reads it today. Offering it as a control would be a setting "
            + "that silently does nothing — the exact defect this surface exists to expose. "
            + "Shown so the stored value is visible; it becomes adjustable when a callsite "
            + "reads it.",
          governanceBlockedEnforcement:
            "`observe` records a policy block without stopping the mission; `enforce` "
            + "refuses to plan it at all.",
          approvalChokepoint:
            "Every gate verdict freezes its decided role, route, evidence, policy version, "
            + "consequence and channel on the approval row. No side door.",
          tenantIsolation:
            "Tenant isolation is enforced in the database and fails closed. It is not a "
            + "setting at any scope or role.",
          runStateContract:
            "Run-state changes go through one transition function. No direct writes.",
        },
        refusals: {
          unknownField: "Unknown setting \"{key}\".",
          notApplicable: "\"{key}\" does not apply to this agent.",
          fixed:
            "\"{key}\" is fixed by the runtime ({handle}). It is shown for inspection and is "
            + "not a security boundary, but it cannot be changed here.",
          platformInvariant:
            "\"{key}\" is a platform security boundary and is never configurable.",
          unknownModel:
            "\"{value}\" is not in the model catalogue for this deployment. An id the "
            + "catalogue does not know is ignored at dispatch, so accepting it would record a "
            + "change that never takes effect.",
          outOfBoundsNumber: "\"{key}\" must be between {min} and {max}.",
          outOfBoundsEnum: "\"{key}\" must be one of: {values}.",
          outOfBoundsBoolean: "\"{key}\" expects on or off.",
          outOfBoundsModel: "\"{key}\" expects a model id from the catalogue.",
          outOfBoundsReadonly: "\"{key}\" is read-only.",
          outOfBounds: "\"{key}\" is outside the allowed range.",
        },
        enums: {
          contextExpansion: {
            narrow: "Narrow",
            moderate: "Moderate",
            wide: "Wide",
          },
          planningDepth: {
            shallow: "Shallow",
            normal: "Normal",
            deep: "Deep",
          },
          gateApproveSemantics: {
            revise: "Ask for a revision",
            accept: "Take the version as-is",
          },
          verificationStrictness: {
            light: "Light",
            normal: "Normal",
            strict: "Strict",
          },
          governanceBlockedEnforcement: {
            observe: "Observe (record only)",
            enforce: "Enforce (refuse to plan)",
          },
        },
        values: {
          on: "on",
          off: "off",
          unset: "—",
        },
        modelNotInCatalog: "{model} — not in this deployment's catalogue",
      },
    },
    list: {
      protectedAgent: "Load-bearing — cannot be deactivated",
      engineInline: "Engine-inline",
    },
    detail: {
      loadingAgent: "Loading agent...",
      breadcrumbAgents: "Agents",
      assignTask: "Assign Task",
      runHeartbeat: "Check its work queue",
      pause: "Pause",
      resume: "Resume",
      heartbeatFailed: "The work queue could not be checked. Nothing changed — try again.",
      heartbeatComplete: "Work queue checked.",
      heartbeatIdle: "Nothing is waiting for this agent right now.",
      heartbeatOpen: "{n} task(s) are waiting for this agent.",
      assignedFlash: "Created {key} and assigned to {name}.",
      tabs: {
        dashboard: "Dashboard",
        instructions: "Instructions",
        skills: "Skills",
        configuration: "Configuration",
        orchestration: "Orchestration",
        stats: "Stats",
      },
      state: {
        running: "running",
        idle: "idle",
        paused: "paused",
        error: "error",
      },
      dashboard: {
        loading: "Loading dashboard…",
        latestRun: "Latest Run",
        viewDetails: "View details →",
        succeeded: "succeeded",
        failed: "failed",
        noRunsYet: "This agent has not done any work yet. Assign it a task, or check its work queue, to get started.",
        runActivity: "Run Activity",
        last14Days: "Last 14 days",
        legendSucceeded: "Succeeded",
        legendFailed: "Failed",
        issuesByPriority: "Issues by Priority",
        legendCritical: "Critical",
        legendHigh: "High",
        legendMedium: "Medium",
        legendLow: "Low",
        issuesByStatus: "Issues by Status",
        legendDone: "Done",
        legendActive: "Active",
        legendBlocked: "Blocked",
        successRate: "Success Rate",
        successRateNoRuns: "No runs yet",
        successRateSummary: "{rate}% over {count} run",
        successRateSummaryPlural: "{rate}% over {count} runs",
        recentIssues: "Recent Issues",
        seeAll: "See All →",
        noAssignedIssues: "No issues are currently assigned to this agent.",
      },
      skills: {
        emptyPrefix: "No skills are assigned to this agent yet. Assign them from ",
        emptySuffix: ".",
        emptyConfigurationLink: "Skills",
        granted: "Assigned",
        manageLink: "Manage skills →",
        activeTitle: "Active skills",
        activeEmpty: "No active skills on this agent.",
        inactiveTitle: "Assigned, switched off",
        gapsTitle: "Required capability gaps",
        gapsHint: "Assigned, but this agent cannot run them. A skill never grants a capability.",
        availableTitle: "Available compatible skills",
        availableEmpty: "No other compatible skills.",
        availableUnknown: "Compatibility for unassigned skills is not available from this server yet.",
        missing: "Missing: {caps}",
        loadFailed: "Could not load skills.",
      },
      configuration: {
        title: "Configuration",
        edit: "Edit",
        rows: {
          name: "Name",
          role: "Role",
          model: "Model",
          state: "State",
          tenant: "Tenant",
          reportsTo: "Reports to",
          description: "Description",
        },
        topOfTree: "— top of tree",
        emptyDash: "—",
      },
      stats: {
        rows: {
          runs14d: "Runs (last 14 days)",
          successRate14d: "Success rate (14d)",
          avgLatencyRecent: "Avg latency (recent runs)",
          openIssuesAssigned: "Open issues assigned",
          lastRun: "Last run",
        },
      },
    },
    org: {
      eyebrow: "Platform / Agents",
      title: "Org chart",
      subtitle: "Hierarchy of your AI workforce — who reports to whom.",
      backToList: "Back to list",
      refresh: "Refresh",
      loading: "Loading…",
      retry: "Retry",
      loadFailed: "Failed to load agents",
      emptyTitle: "No agents yet",
      emptyBody: "The canonical 7 agents are provisioned automatically per tenant. If you see this, the database bootstrap probably hasn't finished — try Refresh.",
    },
  },

  shared: {
    newIssue: {
      title: "New Issue",
      sub: "Create an issue manually — Ticket Analyst will enrich it.",
      createIssue: "Create issue",
      titleLabel: "Title",
      titlePlaceholder: "Short description of the problem…",
      kind: "Kind",
      priority: "Priority",
      customer: "Customer",
      customerPlaceholder: "Customer name (optional)",
      project: "Project",
      projectNone: "No project",
      projectHint: "Optional — leave empty to let FixControl resolve the project.",
      reporter: "Reporter",
      flagsLabel: "Flags (comma separated)",
      flagsPlaceholder: "CRASH, PAYMENT, HARDWARE",
      description: "Description",
      descriptionPlaceholder: "What happened? Steps to reproduce, expected vs actual…",
      kindBug: "Bug",
      kindFeature: "Feature",
      kindSupport: "Support",
      priorityCritical: "Critical",
      priorityHigh: "High",
      priorityMedium: "Medium",
      priorityLow: "Low",
      submitFailed: "Failed",
    },
    attachments: {
      label: "Attachments",
      hint: "Paste a screenshot (Ctrl+V), drop files here, or browse. They go to the AI agents as context.",
      dropHint: "Drop files here, paste a screenshot, or",
      browse: "browse files",
      limits: "Up to 10 files, 10 MB each.",
      uploading: "Uploading…",
      uploadFailed: "Upload failed",
      tooMany: "At most {max} attachments per issue.",
      remove: "Remove {name}",
    },
    notifications: {
      title: "Notifications",
      unread: "{count} unread",
      markAllRead: "Mark all read",
      empty: "No notifications yet",
      bellAria: "Notifications",
      elsewhere: "{count} unread in {workspace}",
      elsewhereSwitch: "Switch",
      elsewhereSwitchAria: "Switch to {workspace} to see those notifications",
      viewAll: "View all notifications",
      eyebrow: "Overview",
      pageSubtitle: "Everything that needs your attention, newest first.",
      refresh: "Refresh",
      filterAll: "All",
      filterUnread: "Unread",
      filterType: "Filter by type",
      allTypes: "All types",
      loading: "Loading notifications…",
      emptyUnread: "You're all caught up",
      emptyHint: "New notifications about approvals, runs, and reviews show up here.",
      markReadOne: "Mark as read",
      markUnread: "Mark as unread",
      // Action-needed rail + bulk/selection controls + sidebar indicator.
      actionNeeded: "Action needed",
      actionNeededHint: "These are paused, waiting on you.",
      review: "Review",
      reviewAll: "Review all",
      balloonDismiss: "Dismiss",
      deleteOne: "Delete notification",
      deleteAction: "Delete",
      selectedCount: "{count} selected",
      bulkMarkRead: "Mark read",
      clearSelection: "Clear",
      selectAll: "Select all",
      loadMore: "Load more",
      deleteConfirmTitle: "Delete notifications?",
      deleteConfirmBody: "This permanently removes the selected notifications. This can't be undone.",
      waitingOnYou: "{count} waiting on you",
      waitingOnYouHint: "Missions paused for your approval",
      bucket: {
        today: "Today",
        yesterday: "Yesterday",
        thisWeek: "This week",
        earlier: "Earlier",
      },
      types: {
        action_assigned: "Assigned",
        action_overdue: "Overdue",
        action_escalated: "Escalated",
        issue_needs_review: "Needs review",
        patch_ready: "Patch ready",
        workspace_update: "Workspace update",
        system_reminder: "Reminder",
        inbox_mention: "Mention",
        approval_required: "Approval required",
        run_completed: "Run completed",
        run_failed: "Run failed",
        integration_health: "Integration",
        integration_recovered: "Integration recovered",
        preview_ready: "Preview ready",
        preview_failed: "Preview failed",
      },
    },
    commandCenter: {
      title: "Command Center",
      collapse: "Collapse",
      expand: "Expand",
      thinking: "Thinking...",
      thinkingNl: "Aan het nadenken...",
      listening: "Listening...",
      pleaseWait: "Please wait...",
      askPlaceholder: "Ask a question or tell FixControl what to do…",
      stopListening: "Stop listening",
      voiceInput: "Voice input",
      hintToggle: "Ctrl+Shift+K to toggle · Enter to send",
      hintBrowse: " · ↑↓ to browse commands",
      messages: "{count} messages",
      tipExploring: "Tip: {company}'s core agents are already set up — from intake to QA. Try \"what do the agents do?\" or \"change the Developer agent's model\".",
      welcomeIntro: "I'll help you set up {company}. Ask anything or pick a suggestion below.",
      onPageHint: "You're on {path}. I can explain what you see here and help with actions on this page.",
      paletteHeading: "Suggestions for this page. Click to run, or type a command.",
      reasoningModes: "Reasoning modes",
      sectionExplain: "Explain",
      sectionNavigate: "Navigate",
      sectionAct: "Act",
      confirmBadge: "confirm",
      nextSteps: "Next steps",
      statusSuccess: "Success",
      statusPartial: "Partial",
      statusError: "Error",
      statusRefused: "Refused",
      statusUnsupported: "Unsupported",
      actionsDetected: "{count} action detected",
      actionsDetectedPlural: "{count} actions detected",
      actionsUrgent: "{count} urgent",
      actionsTrigger: "Trigger:",
      strategyLabel: "Strategy",
      strategyHint: "Product thinking, scope, simplification",
      decideLabel: "Decide",
      decideHint: "What to do next, where to focus",
    },
    firstRun: {
      skip: "Set this up later",
      back: "Back",
      next: "Next",
      finish: "Get started",
      stepOf: "Step {n} of {total}",
      goal: {
        title: "What do you want to use FixControl for first?",
        why: "Your answer decides which parts we show — the rest stays hidden until you need it.",
        support: "Answer support questions",
        tickets: "Manage tickets",
        code: "Fix code problems",
        approvals: "Approve AI actions",
        website: "Handle website questions",
        explore: "Just look around first",
      },
      channel: {
        title: "Where does your team work now?",
        why: "This is where your questions arrive and reviews go back. Pick one to start — you can connect more later.",
        slack: "Slack",
        teams: "Microsoft Teams",
        email: "Email (Gmail)",
        o365: "Office 365 / Outlook",
        widget: "Chat on my website",
        later: "Do this later",
      },
      autonomy: {
        title: "What may FixControl do on its own, and what do you approve?",
        why: "This is the key safety choice. We suggest the safe default; you can fine-tune it later under AI & approval.",
        safe: "Safe — prepare everything, I approve every external message and fix",
        balanced: "Balanced — routine runs automatically, risky work comes to me",
        observe: "Observe only — FixControl proposes, never sends anything",
        recommended: "Recommended",
        approver: "Approvals come to you for now. You can invite teammates later.",
      },
      summary: {
        title: "Your proposed configuration",
        proposalNotice: "This is your proposed configuration. The next steps still need to be completed — nothing has been connected or activated yet.",
        may: "MAY — once you finish setting up the safe default",
        mayNot: "MAY NOT without you",
        mayReceive: "Receive and sort questions",
        mayDraft: "Draft replies",
        mayRoutine: "Send routine replies automatically",
        mayNotSend: "Send a reply to a customer",
        mayNotDeploy: "Put code live",
        mayNotRefund: "Issue a refund or a risky action",
        nextTitle: "Your first recommended step",
        nextConnect: "Connect {channel} — that's where your support channel arrives",
        next_support: "Set up your support mailbox or channel",
        next_tickets: "Connect your ticket system (Jira, Freshdesk or Linear)",
        next_code: "Connect your first codebase",
        next_approvals: "Review what AI may prepare and what you approve",
        next_website: "Turn on website chat for your site",
        next_explore: "Load the safe sample environment and click around",
        nextExplore: "Look at the sample data and click around",
        goToSetup: "Continue setup",
        adjustLater: "You can change all of this later under Settings; you can always resume setup from the dashboard.",
      },
    },
    onboarding: {
      dismiss: "Dismiss",
      title: "Get started",
      progress: "{completed} of {total} steps complete",
      /** Every checklist item is done. The first-run WIZARD may still be
       *  unfinished, which is a different thing and gets its own label —
       *  "2 of 2 steps complete" beside "Resume setup" read as a bug. */
      allDone: "Every setup step is done",
      resume: "Resume setup",
      revisitWizard: "Open the setup guide again",
      steps: {
        createProjectLabel: "Create a project",
        createProjectDesc: "Define a reusable workspace profile with repo, branch, and guardrails",
        inviteTeamLabel: "Invite a colleague",
        inviteTeamDesc: "Done once your team has at least 2 accounts — approvals no longer all depend on you",
        connectMailboxLabel: "Connect your support mailbox",
        connectMailboxDesc: "Connect Gmail or Office 365, or set up a mailbox — customer questions arrive there",
        chooseInboxAiModeLabel: "Choose what AI may do in the inbox",
        chooseInboxAiModeDesc: "Suggestions only, drafts for approval, or limited automatic replies",
        connectTrackerLabel: "Connect your ticket system",
        connectTrackerDesc: "Connect Jira, Freshdesk or Linear so tickets show up in FixControl",
        firstIssueLabel: "Look at your first issue",
        firstIssueDesc: "As soon as a question or ticket arrives, you'll find it here",
        connectCodebaseLabel: "Connect a codebase",
        connectCodebaseDesc: "Pick your provider and repository — AI reads along; writes always go through review",
        reviewGovernanceLabel: "Set your approval rules",
        reviewGovernanceDesc: "Decide what AI may prepare and which actions need your approval",
        createWidgetLabel: "Turn on website chat",
        createWidgetDesc: "Create a chat key for your website and choose which knowledge may be answered publicly",
        loadDemoLabel: "Load the practice environment",
        loadDemoDesc: "Safe sample data to click through — removable later with one click",
        verifyCodebaseLabel: "Test the repository connection",
        verifyCodebaseDesc: "Confirm FixControl can actually read your repository — via Test connection on the codebase",
        runDemoMissionLabel: "Run a practice mission",
        runDemoMissionDesc: "Start the sample scenario and watch the whole flow — from issue to approved fix",
      },
    },
    agentModal: {
      editTitle: "Edit {name}",
      sub: "Update agent configuration.",
      name: "Name",
      namePlaceholder: "e.g. QA Tester",
      role: "Role",
      rolePlaceholder: "e.g. Test Automation",
      model: "Model",
      state: "State",
      stateRunning: "Enabled",
      statePaused: "Paused",
      stateIdle: "Idle",
      stateError: "Error",
      skillsLabel: "Skills (comma separated)",
      skillsPlaceholder: "Search repo, Generate code, Run tests",
      description: "Description",
      descriptionPlaceholder: "What does this agent do?",
      submitFailed: "Failed",
    },
    assignTask: {
      title: "Assign task to {name}",
      sub: "Creates a new issue in {tenant} assigned to this agent.",
      assignTask: "Assign task",
      agent: "Agent",
      titleLabel: "Title",
      titlePlaceholder: "Short description of the task…",
      kind: "Kind",
      priority: "Priority",
      description: "Description",
      descriptionPlaceholder: "Context, expected outcome, any constraints…",
      kindBug: "Bug",
      kindFeature: "Feature",
      kindSupport: "Support",
      priorityCritical: "Critical",
      priorityHigh: "High",
      priorityMedium: "Medium",
      priorityLow: "Low",
      submitFailed: "Failed to create task",
    },
    instructions: {
      files: "Files",
      addFile: "Add file",
      entry: "entry",
      filenamePlaceholder: "NOTES.md",
      filenameInvalid: "Filename must be a simple *.md name (e.g. NOTES.md)",
      loading: "Loading instructions…",
      loadFailed: "Failed to load instructions",
      saveFailed: "Failed to save",
      createFailed: "Failed to create file",
      deleteFailed: "Failed to delete",
      deleteConfirm: "Delete {filename}? This cannot be undone.",
      deleteTitle: "Delete {filename}",
      empty: "No instruction files yet. Click + to add one.",
      emptyContent: "(empty)",
      markdownMeta: "markdown file · {size} · updated {when}",
    },
    knowledgeSource: {
      sub: "{tenant} · added by {addedBy} · {when}",
      deleteSource: "Delete source",
      deleteConfirmOne: "Delete {count} chunk?",
      deleteConfirmMany: "Delete {count} chunks?",
      confirmDelete: "Confirm delete",
      kind: "Kind",
      chunks: "Chunks",
      size: "Size",
      status: "Status",
      tags: "Tags",
      contentHeader: "Content ({count} chunks)",
      contentHelper: "Used by agents for context retrieval",
      loadingChunks: "Loading chunks…",
      noChunks: "No chunks found. The source may still be processing.",
      chunkLabel: "chunk {index} / {total}",
      chunkChars: "{count} chars",
    },
    folderPicker: {
      selectFolder: "Select a folder",
      select: "Select",
      noSubfolders: "No subfolders",
    },
  },

  issuesDetail: {
    breadcrumb: {
      issues: "Issues",
    },
    attachments: {
      title: "Attachments",
      add: "Add attachment",
      empty: "No attachments on this issue.",
      download: "Download {name}",
    },
    // Top-level option labels used by the issues TABLE (issues-table-columns.tsx)
    // — distinct from editModal.* which the edit form uses. Without these the
    // Priority/Status column rendered raw keys (issuesDetail.priorityOptions.*).
    priorityOptions: {
      critical: "Critical",
      high: "High",
      medium: "Medium",
      low: "Low",
    },
    statusOptions: {
      intake: "Intake",
      review: "Review",
      ready: "Ready",
      in_progress: "In progress",
      blocked: "Blocked",
      done: "Done",
    },
    readinessOptions: {
      ready: "Dev ready",
      investigate: "Investigate",
      blocked: "Blocked",
    },
    header: {
      reportedBy: "{tenant} · reported by {reporter} · updated {when}",
    },
    panels: {
      lastVerification: "Last verification",
      lastVerificationSub: "From the most recent run",
      possibleDuplicates: "Possible duplicates",
      possibleDuplicatesSub: "Auto-detected",
      details: "Details",
      detailsSub: "Full issue body",
      metadata: "Metadata",
      verificationSkipped: "(skipped)",
    },
    metadata: {
      tenant: "Tenant",
      project: "Project",
      noProject: "Not linked to a project",
      noProjectHint: "The project is what carries the verified test environment — link one on this issue’s workspace.",
      assignee: "Assignee",
      reporter: "Reporter",
      created: "Created",
      updated: "Updated",
      unassigned: "Unassigned",
    },
    actions: {
      approve: "Approve",
      approved: "Approved",
      reject: "Reject",
      markDuplicate: "Mark duplicate",
      split: "Split",
      dryRun: "Dry run",
      runFix: "Run fix",
      done: "Done",
      pending: "…",
      pr: "PR →",
      criticalWarning: "Critical / payment flow — dry run recommended. CTO gate may be required before full run.",
      supportNote: "Customer-facing reply — review and send from the Auto-reply panel above.",
    },
    actionFeedback: {
      approved: "{key} approved → dev pipeline",
      updated: "{key} updated",
      rejected: "{key} rejected",
      markedDuplicate: "Marked duplicate of {of}",
      splitInto: "Split into {n} issues",
      dryRunPatches: "Dry run: {n} patches proposed",
      prOpened: "PR #{n} opened",
      runStarted: "Run started",
      failed: "Failed",
      approveFailed: "Approve failed",
      rejectFailed: "Reject failed",
      duplicateFailed: "Duplicate failed",
      splitFailed: "Split failed",
      runFailed: "Run failed",
      deleted: "{key} deleted",
      deleteFailed: "Delete failed",
    },
    prompts: {
      duplicate: "Mark {key} as duplicate of which issue? (e.g. BIZ-22)",
      split: "Split into child titles (one per line):",
    },
    aiAnalysis: {
      title: "AI Analysis",
      author: "Ticket Analyst",
      flaggedAs: "Flagged as {flag}",
      summary: "Summary",
      impact: "Impact",
      likelyCause: "Likely cause",
      confidence: "Confidence",
      causeFallback: "Not enough signal — investigation required.",
      impactFallback: "To be assessed.",
      confidenceHigh: "High — safe to act.",
      confidenceMedium: "Medium — review before approving.",
      confidenceLow: "Low — investigate further.",
    },
    risks: {
      title: "Risks & Impact",
      sub: "Operational summary",
      affects: "Affects",
      blocks: "Blocks",
      approval: "Approval",
      deploy: "Deploy",
      affectsPayment: "Customers using payment flow",
      affectsTenant: "Users in tenant {tenant}",
      blocksCrash: "Customer-facing flow is down",
      blocksPayment: "Payment processing affected",
      blocksHardware: "In-store hardware operation",
      blocksDefault: "Feature unavailable to affected users",
      approvalGate: "CTO gate before merge",
      approvalStandard: "Standard review",
      ctoApprovalRequired: "CTO approval required before merge",
      paymentDryRun: "Payment-flow change — use dry run first",
      securityReview: "Security review gate",
      datalossBackup: "Data-loss risk — backup required before deploy",
      noConstraints: "No special constraints",
    },
    reply: {
      title: "Auto-reply",
      to: "To {reporter}",
      tones: {
        friendly: "friendly",
        formal: "formal",
        concise: "concise",
        professional: "professional",
        technical: "technical",
      },
      drafting: "Drafting…",
      draftWithAI: "Draft with AI",
      regenerate: "Regenerate",
      placeholder: "Write a reply or let the Support Agent draft one…",
      draftSaved: "Draft saved.",
      sentTo: "✓ Sent to {reporter}",
      failed: "Failed",
      draftFailed: "Draft failed",
      saveFailed: "Save failed",
      sendFailed: "Send failed",
      charCount: "{n} chars",
      saving: "Saving…",
      saveDraft: "Save draft",
      sending: "Sending…",
      send: "Send",
      signaturePreview: {
        label: "Added automatically on send",
        showFull: "Show the full e-mail",
        hideFull: "Hide the full e-mail",
        fullLabel: "What the customer receives",
      },
      templates: {
        pickerLabel: "Insert template…",
      },
    },
    governedReply: {
      title: "Governed customer reply",
      pending: "Awaiting approval",
      approved: "Approved",
      sent: "Sent to customer",
      rejected: "Rejected",
      escalated: "Escalated",
      revise_requested: "Changes requested",
      toCustomer: "To {email}",
      decidedBy: "by {who}",
      edited: "edited before sending",
      pendingHint: "This reply is waiting for approval. Approving it sends it to the customer through the channel it was drafted for.",
      sentHint: "Posted as a public reply on the linked Freshdesk ticket.",
      showMessage: "Show message",
      hideMessage: "Hide message",
      approve: "Approve and send",
      reject: "Reject",
      confirmApproveTitle: "Send this reply to the customer?",
      confirmApproveBody: "Approving sends the message above to the customer. This cannot be undone.",
      confirmRejectTitle: "Reject this reply?",
      confirmRejectBody: "The customer receives nothing. The draft stays on record with your decision.",
      cancel: "Cancel",
      failed: "Couldn't record the decision ({code}). Nothing was sent.",
      heldUnsafe: "Held back: the reply contains something that must not be sent ({reasons}). Change the draft first.",
    },
    actionItems: {
      title: "Action items",
      subLoading: "Operations Coordinator · loading…",
      sub: "Operations Coordinator · {open} open · {total} total",
      // BUG issues show only QA-handoff rows, so the Operations Coordinator
      // byline would credit an agent that no longer writes here.
      subHandoff: "QA handoff · {open} open · {total} total",
      generating: "Generating…",
      regenerate: "Regenerate",
      generate: "Generate actions",
      empty: "No actions yet. Click Generate to have the Operations Coordinator extract follow-up actions.",
      risky: "risky",
      starting: "Starting…",
      start: "Start",
      inProgress: "in progress",
      remove: "Remove",
      removeConfirmTitle: "Remove this action item?",
      removeConfirm: "It disappears from the follow-up list. The mission timeline and audit history keep the record that it existed.",
      removeConfirmLabel: "Remove",
      markOpen: "Mark open",
      markDone: "Mark done",
      dispatchTitle: "Dispatch this sub-task to the {role} agent (if one exists)",
      priority: {
        urgent: "urgent",
        high: "high",
        normal: "normal",
        low: "low",
      },
      type: {
        support: "support",
        commercial: "commercial",
        operational: "operational",
        configuration: "configuration",
      },
      result: {
        started: "started",
        startedFallback: "started (fallback)",
        manualTask: "manual task",
        limited: "limited",
        error: "error",
        noMessage: "no message",
        openWorkspace: "Open workspace →",
      },
      requestFailed: "request failed",
    },
    editModal: {
      title: "Edit {key}",
      sub: "Update issue fields.",
      labels: {
        title: "Title",
        kind: "Kind",
        priority: "Priority",
        status: "Status",
        readiness: "Readiness",
        customer: "Customer",
        assignee: "Assignee",
        flags: "Flags (comma separated)",
        summary: "Summary",
        impact: "Impact",
        description: "Description",
        retryNote: "What went wrong, or what should the AI know now?",
      },
      kindOptions: {
        bug: "Bug",
        feature: "Feature",
        support: "Support",
      },
      priorityOptions: {
        critical: "Critical",
        high: "High",
        medium: "Medium",
        low: "Low",
      },
      statusOptions: {
        intake: "Intake",
        review: "Review",
        ready: "Ready",
        in_progress: "In progress",
        blocked: "Blocked",
        done: "Done",
      },
      readinessOptions: {
        ready: "Dev ready",
        investigate: "Investigate",
        blocked: "Blocked",
      },
      customerPlaceholder: "Customer name (optional)",
      failed: "Failed",
      saveAndRetry: "Save and try again",
      retryNoteHint: "Goes to the new attempt as instruction. Not stored on the issue.",
      retryNotePlaceholder: "E.g.: the previous patch changed the POS module, but the bundling happens in the invoice service.",
      standing: {
        running: {
          title: "An attempt is running right now",
          body: "This edit changes the text the AI team is working from. The running attempt reads the old description and will be marked outdated once it finishes.",
        },
        "pending-approval": {
          title: "An approval is open",
          body: "Someone still has to decide on work based on the current description. That gate stays open and decidable — after you save it will say it covers an earlier version.",
        },
        "approved-patch": {
          title: "An approved patch is waiting",
          body: "That approval was really given and stays on the record; FixControl does not remove it. What goes is the one-click Apply: the patch was approved against the text as it stands now, and will be marked outdated after you save.",
        },
      },
    },
    focus: {
      title: "Needs attention",
      clearFilter: "Clear filter",
      allClearTitle: "Nothing needs your attention",
      allClearBody: "All issues are triaged, reviewed, or shipped.",
      criticalSingular: "Critical issue blocking customers",
      criticalPlural: "{n} critical issues blocking customers",
      reviewSingular: "Issue needs review",
      reviewPlural: "{n} issues need review",
      blockedSingular: "Blocked issue",
      blockedPlural: "{n} blocked issues",
      emptyCritical: "No critical issues",
      emptyReview: "Review queue empty",
      emptyBlocked: "Nothing blocked",
    },
    summary: {
      critical: "Critical",
      review: "Review",
      blocked: "Blocked",
      openTotal: "Open",
    },
    row: {
      replied: "replied · {when}",
      repliedTooltip: "Customer reply sent {when}",
      readinessStatusTooltip: "Readiness: {readiness} · Status: {status} · Confidence {pct}%",
    },
    bulk: {
      approve: "Approve",
      reject: "Reject",
      delete: "Delete",
      deleteConfirmTitle: "Delete selected issues?",
      deleteConfirmLabel: "Delete issues",
      deleteConfirmBody: "This removes {count} issue(s) from active work. An admin can restore them; their audit history, approval decisions and verification results are kept.",
    },
    pane: {
      title: "Issue detail",
      open: "Open",
      openFullPage: "Open full page",
      close: "Close detail",
      enableSplit: "Open detail pane",
      disableSplit: "Close detail pane",
      resizeHandle: "Resize detail pane",
      customer: "Customer",
      reporter: "Reporter",
      assignee: "Assignee",
      replied: "Last reply",
      impact: "Impact",
      summary: "Summary",
      emptyTitle: "No issue selected",
      emptyBody: "Click any row in the table to preview it here.",
    },
    rowActions: {
      approve: "Approve",
      runFix: "Run fix",
      open: "Open",
      edit: "Edit",
      duplicate: "Mark duplicate",
      reject: "Reject",
      delete: "Delete",
      more: "More actions",
      pr: "PR",
      titles: {
        approve: "Approve → mark dev ready",
        runFix: "Run implementation agent (dry run)",
        duplicate: "Mark duplicate",
        edit: "Edit issue",
        reject: "Reject → close as irrelevant",
        delete: "Delete issue",
      },
      deleteConfirm: "Delete {key}? It disappears from active work and can be restored by an admin. Its audit history, approval decisions and verification results are kept.",
      duplicatePrompt: "Mark {key} as duplicate of which issue? (e.g. BIZ-22)",
    },
    why: {
      possibleDuplicate: "Possible duplicate of {key}",
      breaksPayment: "Breaks payment flow",
      blocksHardware: "Blocks in-store operations",
      crash: "Customer-facing crash",
      dataloss: "Risk of data loss",
      security: "Security concern",
      touchesPayment: "Touches payment flow",
      hardwareIssue: "Hardware issue",
    },
  },

  issueDetailV2: {
    primaryAction: {
      label: {
        none: "No action needed",
        reviewApproval: "Review",
        dryRun: "Test in sandbox",
        applyPatch: "Apply",
        viewPatch: "View patch",
        viewSandboxRun: "View sandbox run",
        runFix: "Run fix",
        reply: "Reply to customer",
      },
      reason: {
        issueDone: "This issue is resolved.",
        approvalPending: "The AI team is waiting for your approval to continue.",
        patchNeedsDryRun: "A patch is ready — test it safely in the sandbox first.",
        readyToApply: "Sandbox passed and the patch is approved — ready to apply.",
        sandboxFailed: "The sandbox run failed — open the run to see which step failed.",
        patchChangesRequested: "The AI sent this patch back for changes and stopped — review it and decide: ship, drop, or revise with your guidance.",
        patchReady: "A patch is ready to review.",
        noRun: "No AI mission started for this issue yet.",
        replyReady: "A draft reply is ready for the customer.",
        aiWorking: "The AI team is working — nothing is needed from you right now.",
        nothingNeeded: "No action is needed from you right now.",
        issueChangedSinceRun: "This issue was rewritten after this patch was approved — the approval covers the older text. Review the patch before applying it, or start a fresh attempt.",
      },
    },
    missionStart: {
      alreadyActive: "An attempt is already running on this issue. Wait for it to finish, or cancel it in the mission, then try again.",
      capExceeded: "{active} of at most {cap} concurrent AI missions are already running in this organisation. Finish or cancel one and this attempt can start.",
      quotaExceeded: "This plan's AI actions are spent for the current period ({used} of {limit} used). Raise the limit or wait for the next period.",
      httpFailed: "Could not start the attempt (error {status}). Try again, or contact an administrator.",
      unknown: "Could not start the attempt.",
    },
    status: {
      resolved: "Resolved",
      awaitingApproval: "Waiting for your approval",
      investigating: "AI team is investigating",
      working: "AI team is working on a fix",
      blocked: "Mission blocked",
      missionFailed: "Mission failed",
      verificationFailed: "Verification failed",
      verifying: "Patch is being tested",
      readyToApply: "Ready to apply",
      patchChangesRequested: "Patch sent back — needs your review",
      patchReadyDryRun: "Patch ready for sandbox test",
      patchReady: "Patch ready for review",
      missionDone: "AI mission complete",
      notStarted: "Not started yet",
      issueChangedSinceRun: "Issue rewritten after this attempt",
    },
    commandBar: {
      updated: "Updated",
      statusLabel: "Status",
      overflow: "More actions",
      edit: "Edit",
      reject: "Reject",
      markDuplicate: "Mark as duplicate",
      split: "Split",
      rerun: "Edit and try again",
      openDetails: "Open details",
      delete: "Delete",
      runFixExplain: {
        title: "What happens when you start a fix?",
        body: "FixControl has an AI agent investigate this issue and propose a code change. Every proposal is tested first in an isolated sandbox.",
        guarantee: "Nothing reaches a customer or production without your approval.",
        eta: "Usually takes a few minutes. You can keep working in the meantime.",
        confirm: "Start the fix",
        cancel: "Cancel",
      },
    },
    summary: {
      title: "What's going on?",
      empty: "No description available.",
      expected: "Expected behaviour",
      actual: "Actual behaviour",
      environment: "Environment",
      impact: "Impact",
      impactUnknown: "Not yet assessed.",
      supportTicket: "Support ticket",
      createTicket: "Create support ticket",
      ticketCreateError: "Could not create the support ticket.",
      ticketKindUnsupported: "Only bug and feature issues are mirrored to the tracker.",
    },
    run: {
      title: "AI execution",
      openFull: "Open full operation",
      loading: "Loading…",
      loadError: "Could not load the mission.",
      noRun: "No AI mission started yet.",
      noRunHint: "Start one with the action button above.",
      tasksDone: "{done}/{total} tasks",
      currentStep: "Current step",
      idleStep: "Waiting for the next step",
      blocker: "Blocker",
      blockerCount: "{n} blocked step(s)",
      result: "Produced",
      patchVersion: "Patch v{v}",
      patchVersions: "{n} versions",
      lastEvent: "Last update",
    },
    execution: {
      eyebrow: "Governed execution",
      trackerLabel: "Execution progress",
      live: "Live",
      currentActivity: "Current execution",
      finished: "No step is running — the mission has finished.",
      assignedTo: "Handled by {role}",
      activeAgent: "Working on it now",
      noActiveAgent: "Nobody is working on this right now.",
      missionOwner: "Mission owner: {role}",
      pipelineTitle: "Execution pipeline",
      sideActivityTitle: "Live side activity",
      sideActivityHint: "Workspace agents running alongside the mission (chat / codebase research).",
      sideActivityEmpty: "No side activity right now.",
      sideActivityAgent: { dev: "Dev agent", codebase: "Codebase agent" },
      sideActivityHeartbeat: "Last signal {age}",
      failureReason: "Failure reason",
      auditTitle: "Execution summary",
      auditNote: "Hash-chained execution log",
      viewLog: "View detailed operational log ({n})",
      hideLog: "Hide operational log",
      outputTitle: "Output",
      patchLabel: "Patch v{version}",
      metricsTitle: "Operational telemetry",
      metricDuration: "Duration",
      metricElapsed: "Elapsed",
      metricAffected: "Affected files",
      metricBlocked: "Blocked deps",
      metricChecks: "QA checks",
      checksValue: "{passed}/{total} · {pct}%",
      notAssessed: "Not assessed",
      metricRollback: "Rollback",
      metricAudit: "Execution events",
      metricCost: "Execution cost",
      costRuns: "{n} metered execution event(s)",
      notAvailable: "Not available",
      notMetered: "No execution has been metered for this issue yet.",
      rollback: {
        available: "Available",
        unavailable: "Unavailable",
        rolled_back: "Rolled back",
        na: "N/A",
      },
      header: {
        title: "Operational status",
        label: {
          overall: "State",
          stage: "Stage",
          risk: "Risk",
          validation: "Validation",
          approval: "Approval",
          deployment: "Deployment",
        },
        overall: {
          executing: "Executing",
          awaiting_approval: "Awaiting approval",
          blocked: "Blocked",
          resolved: "Resolved",
          failed: "Failed",
          halted: "Halted",
          idle: "Not started",
        },
        risk: {
          low: "Low",
          medium: "Medium",
          high: "High",
          critical: "Critical",
          unassessed: "Unassessed",
        },
        validation: {
          passed: "Passed",
          failed: "Failed",
          running: "In progress",
          pending: "Pending",
        },
        approval: {
          required: "{n} required",
          clear: "Clear",
        },
        deployment: {
          deployed: "Deployed",
          rolled_back: "Rolled back",
          ready: "Safe to deploy",
          awaiting: "Awaiting approval",
          blocked: "Blocked",
          not_ready: "Not ready",
        },
      },
      summary: {
        tasksDispatched: "{n} task(s) dispatched",
        tasksDispatchedNone: "No tasks dispatched yet",
        tasksCompleted: "{done}/{total} task(s) completed",
        validation: {
          passed: "Validation passed",
          failed: "Validation failed",
          running: "Validation in progress",
          pending: "Validation not run",
        },
        review: {
          accepted: "Review accepted",
          rejected: "Review rejected",
          changes: "Changes requested",
          in_progress: "Review in progress",
          pending: "Review pending",
        },
        security: {
          pending: "Security review pending",
          in_progress: "Security review in progress",
          cleared: "Security review cleared",
        },
        deployment: {
          deployed: "Deployed",
          rolled_back: "Deployment rolled back",
          ready: "Deployment ready",
          awaiting: "Deployment awaiting approval",
          blocked: "Deployment blocked",
          not_ready: "Deployment not ready",
        },
        rollback: {
          available: "Rollback available",
          unavailable: "Rollback unavailable",
          rolled_back: "Rolled back",
          na: "Rollback not applicable",
        },
      },
      phase: {
        intake: "Intake",
        analysis: "Analysis",
        plan_review: "Plan review",
        implementation: "Implementation",
        validation: "Validation",
        final_review: "Final review",
      },
      state: {
        completed: "Completed",
        active: "Running",
        attention: "Needs review",
        pending: "Pending",
        failed: "Failed",
        skipped: "Skipped",
      },
    },
    planReview: {
      title: "Execution plan",
      awaitingTitle: "Awaiting plan approval",
      version: "Plan v{n}",
      riskLevel: "Risk",
      requiredApprover: "Suggested routing",
      approvalStages: "Approval stages",
      requestedBy: "Requested by",
      generatedAt: "Generated",
      approvalStatus: "Status",
      notAvailable: "Not available",
      blockedByGovernance: "Implementation is blocked by governance policy until this plan is approved.",
      readOnlyNote: "Only an administrator or manager can decide this execution plan.",
      readOnlyNoteStaged: "This plan is approved in stages. The current stage is decided by {role}.",
      anyApproverDecides: "The routing is a suggestion: any one administrator or manager can decide this plan, unless staged approvals put it in stages.",
      stagedNote: "This plan is approved in stages, in order, one person per stage. Anyone who can decide can reject it.",
      anApprover: "an approver",
      reviewerFeedback: "Requested changes",
      approve: "Approve plan",
      requestChanges: "Request changes",
      reject: "Reject plan",
      commentPlaceholder: "What needs to change? (required, recorded in the audit trail)",
      commentRequired: "A comment is required to request changes.",
      status: {
        draft: "Draft",
        awaiting_approval: "Awaiting approval",
        approved: "Approved",
        changes_requested: "Changes requested",
        rejected: "Rejected",
        superseded: "Superseded",
      },
      risk: { low: "Low", medium: "Medium", high: "High", critical: "Critical" },
      fields: {
        problemInterpretation: "Problem interpretation",
        proposedApproach: "Proposed approach",
        affectedSystems: "Affected systems",
        affectedFiles: "Affected files",
        riskLevel: "Risk level",
        riskReasons: "Risk reasons",
        validationStrategy: "Validation strategy",
        rollbackPlan: "Rollback plan",
        customerImpact: "Customer impact",
        estimatedExecutionTime: "Estimated execution time",
        estimatedCost: "Estimated cost",
        requiredPermissions: "Required permissions",
        requiredApprovals: "Suggested approvers",
        openQuestions: "Open questions",
        confidence: "Confidence",
      },
    },
    nextAction: {
      title: "Your action",
      why: "Why this is needed:",
      nonePending: "Nothing is waiting on you.",
      noneHint: "When the AI team needs you, it shows up here.",
      openCta: "Open the decision",
      moreWaiting: "{count} more decision also waiting",
      moreWaitingPlural: "{count} more decisions also waiting",
    },
    reply: {
      title: "Customer reply",
      draftReady: "A draft reply is ready — open to send it",
      none: "No draft reply yet — click to write one",
    },
    verification: {
      title: "Verification",
      sandbox: "Sandbox",
      patch: "Patch",
      qa: "QA review",
      notRun: "Not run yet",
      // Shown when the CURRENT patch version has no run of its own and an
      // earlier one does. Never a substitute for the current version's
      // verdict — it names the version the verdict belongs to.
      onOlderVersion: "on v{version}",
      currentVersionNotRun: "Patch v{version} has not been verified yet \u2014 see the Sandbox tab for every attempt.",
      qaBlocking: "{n} blocking item open",
      qaAccept: "Accepted",
      qaReject: "Rejected",
      qaNeedsChanges: "Changes needed",
      qaPending: "Pending",
      sandboxStatus: {
        pending: "Queued",
        provisioning: "Provisioning",
        running: "Running",
        passed: "Passed",
        failed: "Failed",
        timeout: "Timed out",
        error: "Error",
        cancelled: "Cancelled",
      },
      patchStatus: {
        draft: "Draft",
        approved: "Approved",
        rejected: "Rejected",
        applied: "Applied",
        changes_requested: "Changes requested",
      },
    },
    tabs: {
      operation: "Operation",
      patch: "Patch",
      sandbox: "Sandbox",
      analysis: "Analysis",
      details: "Details",
      history: "History",
      operationEmpty:
        "No AI mission for this issue yet. Start one with the action button above.",
      patchEmpty: "No patch generated yet.",
      sandboxIntro:
        "Every time FixControl built and tested a proposed fix for this issue \u2014 including attempts on earlier patch versions.",
      sandboxEmpty: "No verification has run for this issue yet.",
      historyEmpty: "No activity recorded yet.",
      delegation: "AI team",
      tasks: "Tasks",
      approvals: "Approvals",
      patchFiles: "{n} files",
      openPatch: "Open patch",
    },
  },

  workspaceDetail: {
    loading: "Loading workspace...",
    notFound: "Workspace not found",
    backToList: "Back to list",
    allWorkspaces: "← All workspaces",
    mobileTabs: {
      details: "Details",
      chat: "Chat",
      patch: "Patch",
    },
    meta: {
      issue: "Issue",
      project: "Project",
      branch: "Branch",
      created: "Created",
      updated: "Updated",
      runs: "runs",
      patch: "patch",
      patches: "patches",
      decision: "decision",
      decisions: "decisions",
    },
    project: {
      eyebrow: "Project",
      editProject: "Edit project",
      name: "Name",
      codebases: "Codebases",
      mode: "Mode",
      guardrailActive: "{count} guardrail active",
      guardrailsActive: "{count} guardrails active",
      missingEyebrow: "No project",
      missingTitle: "This workspace is not linked to a project",
      missingBody: "The project is what carries the verified test environment. Without one the sandbox has nowhere to start the change, and a patch reaches the approval unverified. Link a project, then run the verification again.",
      missingNoIssue: "Linking becomes available once this workspace is attached to an issue.",
      link: "Link",
      linking: "Linking…",
      linkChoose: "Choose a project",
      linkFailed: "Could not link the project. Try again.",
      change: "Change",
      changing: "Changing…",
      changeFailed: "Could not change the project. Try again.",
      changeNoOthers: "This tenant has no other project to move the issue to.",
    },
    codeContext: {
      eyebrow: "Code context",
      changeContext: "Change context",
      repository: "Repository",
      sshUrl: "SSH URL",
      path: "Path",
      branch: "Branch",
      apply: "Apply",
      cancel: "Cancel",
      platform: "Platform",
      source: "Source",
      sourceGithub: "GitHub",
      sourceSsh: "SSH",
      sourceLocal: "Local",
      repo: "Repo",
      snapshot: "Snapshot",
      projectFile: "Project file",
      projectRoot: "Project root",
      scm: "SCM",
      scmPcsoft: "PCSoft SCM",
      scmGit: "Git",
      scmLocal: "Local",
      analyses: "Analyses",
      analysisFile: "{count} .wda file",
      analysisFiles: "{count} .wda files",
      techDocs: "Tech docs",
      techDocsAvailable: "{count} available",
      techDocsNone: "None",
      mode2: "Mode",
      loaded: "Loaded",
    },
    contextPreview: {
      eyebrow: "Agent context",
      collapse: "collapse",
      show: "show",
      generic: "Generic",
      memoryActive: "Memory active",
      guardrail: "{count} guardrail",
      guardrails: "{count} guardrails",
      source: "{count} source",
      sources: "{count} sources",
      project: "Project",
      analyses: "Analyses",
      techDocs: "Tech docs",
      procedures: "Procedures",
      guardrailsList: "Guardrails",
      contextSections: "Context sections ({count})",
      cached: "(cached)",
    },
    windev: {
      eyebrow: "WINDEV context",
      analyses: "Analyses",
      technicalDocs: "Technical docs",
      procedures: "Procedures",
      chunks: "{count} chunks",
    },
    history: {
      eyebrow: "History",
      empty: "No activity yet",
    },
    timeline: {
      you: "You",
      agent: "Agent",
      codebaseAnalyst: "Codebase Analyst",
      developer: "Developer",
      system: "System",
      patchVersion: "Patch v{version}",
      file: "file",
      files: "files",
      noteBy: "by {name}",
      noteWith: "{name}: {note}",
    },
    chat: {
      title: "Chat",
      messages: "{count} messages",
      compactingNow: "Compacting…",
      compact: "Compact",
      compactedTooltip: "Manually compacted thread — click to view recap",
      summaryTooltip: "Show the condensed recap of earlier turns",
      compactedLabel: "{count} messages compacted",
      summarizedLabel: "{count} earlier messages summarized",
      compactDisabledShort: "Thread is too short to compact",
      compactTooltip: "Fold this thread into a recap so future turns start fresh with context preserved",
      newChat: "+ New",
      newChatTooltip: "New chat",
      deleteChat: "Delete",
      deleteChatTooltip: "Delete this chat",
      switchChat: "Switch chat",
      agentRunning: "Agent running",
      stop: "Stop",
      stopping: "Stopping…",
      stopTooltip: "Stop the running agent",
      compactedThread: "Compacted thread",
      earlierInConversation: "Earlier in this conversation",
      summaryCount: "({count} messages)",
      compactConfirm: "Compact this thread? The agent will only see a condensed recap of the messages so far, plus any new messages from this point on. Earlier messages stay visible in the chat — only the agent's view changes.",
      compactFailed: "Compact failed: {error}",
      deleteChatConfirm: "Delete \"{title}\"? All messages in this chat will be removed.",
      deleteChatDefaultName: "this chat",
      sendPlaceholder: "Describe the change…",
      sendBtn: "Send",
      thinkingBtn: "Thinking…",
      steerBtn: "Send instruction",
      steerTooltip: "Send this to the running agent — it picks it up after its current step",
      steerPlaceholder: "Add an instruction for the running agent…",
      steerDelivered: "added during run",
      steerQueued: "sent as the next turn",
      emptyHint: "Send a message to request code changes.",
      dropFilesHint: "Drop files here to attach",
      uploadTooltip: "Upload files (images, PDF, text)",
      micStart: "Voice input",
      micStop: "Stop recording",
      removeAttachment: "Remove",
      seeAttachment: "(see attachment)",
      moreActions: "More chat actions",
      sendHint: "Enter sends · Shift+Enter for a new line",
      activityStep: "1 agent step",
      activitySteps: "{count} agent steps",
      activityCollapse: "Collapse steps",
      activityExpand: "Show steps",
      fileTypeUnsupported: "File type not supported: {type}",
      fileTooLarge: "File too large (max 10 MB): {name}",
      codeBlock: "Code block · {count} lines",
      filterLabel: "Filter",
      filterAgent: "Agent",
      filterUser: "You",
      filterReview: "CTO/Review",
      filterActivity: "Activity",
      filterSystem: "System",
      filterShow: "Show {type}",
      filterHide: "Hide {type}",
      filterEmpty: "No messages match this filter.",
      expand: "Expand",
      expandTooltip: "Open chat fullscreen",
      collapse: "Close",
      collapseTooltip: "Close fullscreen view (Esc)",
    },
    patch: {
      title: "Patch",
      titleVersion: "Patch v{version}",
      cycleTooltip: "Cycle iteration {n}",
      cycleLabel: "cycle {n}",
      ctoApproved: "CTO: Approved",
      ctoRejected: "CTO: Rejected",
      ctoChangesRequested: "CTO: Changes requested",
      file: "file",
      files: "files",
      versions: {
        heading: "Versions in this work session",
        tooltip: "View the patch versions of this work session",
        projectHistory: "All patches in this project →",
        viewPr: "View pull request #{number}",
        status: {
          draft: "Draft",
          approved: "Approved",
          rejected: "Rejected",
          applied: "Applied",
          changes_requested: "Changes requested",
        },
      },
      historyLink: "History",
      openPatch: "Open patch",
      openPatchTooltip: "Open this patch on its own page — the full diff, the verification and the decision",
      historyTooltip: "View all patches in this project",
      selectFile: "Select a file to view its diff",
      expandDiffTooltip: "Open diff full screen",
      collapseDiff: "Close",
      collapseDiffTooltip: "Close full-screen view (Esc)",
      revisionInProgress: "Revision in progress",
      revisionInProgressShort: "Revision in progress…",
      revisionDescription: "A new patch is being generated from the CTO feedback. The previous patch is locked until the new version is ready.",
      revisionRunRef: "run {id}",
      ctoChangesRequestedLabel: "CTO: changes requested",
      autoRetryLabel: "auto-retry {current}/{max}",
      devAutoRevising: "Dev is auto-revising. Watch the chat for the next attempt.",
      autoReviseCapReached: "Auto-revise cap reached. Manual revision required.",
      reRunDevWithFeedback: "Re-run dev with feedback",
      startingRetry: "Starting retry…",
      finalApplied: "✓ Applied",
      finalRejected: "✕ Rejected",
      finalAppliedPr: "Pull request opened on GitHub.",
      finalAppliedNoPr: "Files written to the codebase.",
      finalRejectedNote: "This patch will not be shipped.",
      viewPr: "View PR",
      prStatus: {
        open: "PR open",
        merged: "Merged",
        closed: "PR closed",
      },
      ciState: {
        pending: "CI running",
        success: "CI passed",
        failure: "CI failed",
        neutral: "CI neutral",
      },
      safetyWarning: "This patch touches a sensitive area. Validate with a dry run before applying.",
      followUpsCreated: "Follow-up actions created",
      followUpOverdue: "overdue",
      followUpDue: "due {when}",
      followUpUnassigned: "UNASSIGNED",
      decisions: "Decisions",
      noPatchYet: "No patch yet — send an instruction to start.",
      previousRejected: "Previous patch rejected",
      previousRejectedHint: "Use the CTO feedback to draft the next patch.",
      retryInProgressShort: "Revision already in progress",
      fixRejectedPatch: "Fix rejected patch",
      startingShort: "Starting…",
      viewPatchInHistory: "View patch v{version}",
      noActivePatchApplied: "No active patch — the previous patch was applied.",
      sendNewInstruction: "Send a new instruction to draft the next patch.",
    },
    review: {
      recommended: "Recommended",
      overrideApprove: "Approve anyway",
      overrideApproveHint: "Approve despite the open warnings",
      missionGate: {
        title: "Waiting on mission approval",
        body: "An approval is pending for this issue in the mission. Decide there — the patch updates to match automatically.",
        cta: "Go to approval",
      },
      fallback: {
        title: "Rule-based review (no LLM)",
        body: "No LLM provider was active, so this is an automated rule-based check — not a real code review. Configure a model under Settings → AI for a real review.",
      },
      confidence: {
        high: "high confidence",
        medium: "medium confidence",
        low: "low confidence",
      },
      status: {
        inProgress: "Agent working…",
        revising: "Revising",
        awaitingReview: "Revision complete — re-review needed",
        changesRequested: "Changes requested",
        verificationFailed: "Verification failed",
        readyToApprove: "Ready to approve",
        approvalDiscouraged: "Approval not advised",
        approved: "Approved — awaiting apply",
        approvedBlocked: "Approved — apply blocked",
        approvedBlockedRunning: "Approved — verification running",
        verifyingAfterReview: "Review positive — verification running",
        reviewOkGateBlocked: "Review positive — sandbox not green yet",
        approvedBlockedStale: "Approved — verification out of date",
        approvedBlockedMissing: "Approved — not verified yet",
        approvedBlockedEnvironment: "Approved — environment changed",
        approvedBlockedRuntime: "Approved — application start unproven",
        applied: "Applied",
        rejected: "Rejected",
      },
      conclusion: {
        inProgress: "The agent is still generating and reviewing this change — no verdict yet.",
        revising: "A new version of this patch is being generated.",
        awaitingReview: "This revision hasn't been verified yet. Run verification before approving.",
        changesRequested: "Not all requested changes are resolved — review the open items.",
        changesCapped: "Auto-revision limit reached. Give new feedback or re-run the revision manually.",
        verificationFailed: "Sandbox validation failed. Approval is not advised until this is resolved.",
        safeToApprove: "No open review comments or structural changes beyond the requested fixes.",
        discouraged: "This change needs verification or CTO sign-off first. Approval is not advised.",
        approved: "The patch is approved and ready to apply.",
        approvedBlocked: "The approval stands, but this patch cannot be applied yet: sandbox validation has not passed for this version of it. Re-run the verification.",
        approvedBlockedRunning: "The approval stands. A verification for this version of the patch is running right now — wait for it to finish; applying becomes possible as soon as it passes.",
        verifyingAfterReview: "The AI review found no problems. The sandbox is testing this version right now; approving makes sense once it passes. Nothing is needed from you until then.",
        approvedBlockedStale: "The approval stands, but the patch changed after the last verification. Re-run the verification for this version.",
        approvedBlockedMissing: "The approval stands, but this version of the patch has not been verified yet. Run a verification before applying.",
        approvedBlockedEnvironment: "The approval stands and the verification passed — but in an environment this project no longer uses. Re-run the verification in the current one.",
        approvedBlockedRuntime: "The approval stands and the tests passed — but nothing proved the application itself still starts. Re-run the verification so its start target is exercised.",
        applied: "The patch has been applied.",
        rejected: "The patch was rejected and won't be shipped.",
      },
      viewSandboxRun: "View sandbox run →",
      viewStartLog: "View the start log →",
      // Why the apply is refused, per gate code — the gate's own `message` is
      // an English log sentence and never reaches the panel.
      gateBlock: {
        sandbox_required: "This version of the patch has not been verified, so it cannot be applied yet.",
        sandbox_stale: "The patch changed after it was last verified; the green result is about different code.",
        sandbox_in_progress: "A verification for this version is still running. Applying is possible once it passes.",
        sandbox_not_passed: "The verification of this version did not pass. Look at the run before applying.",
        sandbox_environment_required: "This workspace requires evidence from a full multi-service environment, and this run produced none.",
        sandbox_remote_required: "This project requires validation in your own environment, and no such run has passed for this version.",
        environment_contract_stale: "The project's verified environment changed after the verification passed; the result is about a different environment.",
        runtime_verification_required: "The tests passed, but nothing proved the application itself still starts.",
        unknown: "The sandbox gate refuses to apply this version of the patch. Open the run for the details.",
      },
      // Why the start of the application is unproven — decided by
      // lib/patch-review/start-proof.ts. The gate blocks the same in every case;
      // only the explanation and the offered actions differ.
      startProof: {
        lane: {
          aws_fargate: "AWS Fargate",
          customer_runner: "the runner in your own environment",
          unknown: "the environment that ran it",
        },
        laneCannotStart: {
          status: "Approved — this test environment cannot start the application",
          block: "The tests passed on {lane}, but that test environment does not start the application, so its start target was never run.",
          conclusion: "Running the verification again lands on {lane} again and proves nothing new. What proves it is a run that starts the application and gets an answer from its start target — this test environment cannot do that yet. Until it can, a project admin can waive the start check for this project on its Environments page; the waiver is recorded with a name and a date.",
        },
        startFailed: {
          status: "Approved — the application did not start",
          block: "The tests passed, but the application did not start: {reason}.",
          conclusion: "A new verification would fail the same way. Read the start log, then reject the patch so a revision can fix it.",
          conclusionEnvironment: "This is about how the environment starts the application, not about the patch. Correct the start target on the project's Environments page, then verify again.",
        },
        failedReason: {
          launch_failed: "the start command itself failed",
          process_exited: "it started and then stopped",
          readiness_error: "it runs, but answers with an error (HTTP {status})",
          readiness_timeout: "it did not answer within {seconds}s",
          start_refused: "the environment's start target cannot be run here",
        },
        noStartTarget: {
          status: "Approved — no start target set up",
          block: "The project's environment does not say how the application starts, so nothing could prove that it does.",
          conclusion: "Set up how the application starts on the project's Environments page; the next verification will then start it.",
        },
        proofMissing: {
          status: "Approved — start of application unproven",
          block: {
            legacy: "The tests passed, but this run predates start verification, so it carries no proof that the application starts.",
            not_attempted: "The tests passed, but the start of the application was never reached in this run.",
            infrastructure: "The tests passed, but the sandbox could not carry out the start check; that says nothing about the patch.",
            stale_proof: "The run proved a start that the project's environment no longer describes.",
            lane_now_starts: "This run did not start the application, but the test environment has been able to since. A new verification can prove it.",
            unknown: "The tests passed, but nothing proved the application itself still starts.",
          },
          conclusion: "Run the verification again, so the application is started and its start target answers.",
        },
      },
      conclusionLabel: "AI conclusion",
      conclusionDetail: {
        allResolved: "All requested changes have been processed.",
        openItems: "{n} review point(s) still open.",
        openVerification: "{n} verification step(s) have not been carried out.",
        partialResolved: "{resolved}/{total} points resolved.",
        verifyOk: "No regressions detected — verification passed.",
        verifyFailed: "Verification failed ({n} check(s)).",
        sandboxVerified: "The sandbox verified this version.",
        sandboxBlocked: "The sandbox has not cleared this version — see the run.",
        notVerified: "Not verified yet.",
      },
      recommendedAction: {
        approve: "Approve",
        apply: "Apply",
        newFeedback: "Give new feedback",
        runVerification: "Run verification",
        openEnvironments: "Open the project's Environments",
        rerunRevision: "Re-run revision",
        wait: "Wait for the revision",
      },
      resolution: {
        heading: "Requested changes",
        verificationHeading: "Still to verify",
        resolved: "resolved",
        partial: "partial",
        unresolved: "unresolved",
        unverified: "not carried out",
        count: "{resolved}/{total} resolved",
        verifiedCount: "{resolved}/{total} verified",
        unverifiedNote: "{n} of these steps have not been carried out — approving does not perform them.",
        unverifiedAfterRunNote: "{n} of these steps were not carried out by any step of the sandbox run: they are checks for a person or a UI test. Approving or verifying again does not perform them.",
        verifiedByStep: "verified by sandbox",
        proofInstall: "Sandbox step \"{step}\" did this.",
        proofTests: "Sandbox step \"{step}\" ran {n} tests, all passed.",
        proofLink: "View run →",
        // A UI goal is verified only by a BROWSER test of this patch version
        // that covers it (lib/patch-review/goal-evidence.ts).
        proofUi: "Browser test \"{test}\" in sandbox step \"{step}\" passed.",
        // Said plainly when the patch's UI tests did not run (gate reasons
        // ui_tests_unsupported / ui_tests_not_run).
        uiTestsUnsupported: "UI tests could not run in this test environment: {reason}",
        uiTestsNotRun: "The UI tests this patch ships did not run: {reason}",
        coarseNote: "Per-item status not available yet — verify manually against the diff.",
      },
      verification: {
        heading: "Verification",
        notRun: "Not verified yet — run verification to be sure.",
        summary: "{ok}/{total} ok",
        failedCount: "{n} failed",
        passed: "passed",
        failed: "failed",
        skipped: "skipped",
      },
      testEnvironment: {
        heading: "Test environment",
        manage: "All test environments",
      },
      diffSection: "Diff & files",
      decisionsSection: "Decisions",
    },
    gate: {
      heading: "Approval review",
      status: "Awaiting approval",
      purpose: {
        plan: "Approve the CTO's mission plan before any work starts.",
        risky_patch: "The CTO gate flagged this patch as risky — a human sign-off is required.",
        deployment: "Approve applying / deploying the reviewed patch.",
        retry: "A task failed — approve a retry before the mission continues.",
        security_override: "Security risk — a manual sign-off is required to proceed.",
        ui_change_approval: "A UI change that restructures the interface — approve before it is applied.",
        ux_regression_override: "A UX regression the architect could not clear itself — approve to proceed.",
      },
      riskLabel: "Risk",
      risk: { low: "Low risk", medium: "Medium risk", high: "High risk", critical: "Critical risk" },
      riskReason: {
        planOnly: "A plan only — nothing has been changed yet.",
        ctoFlagged: "The CTO gate flagged this patch as risky.",
        deploy: "Changes the live environment when applied.",
        taskFailed: "An earlier task failed.",
        securityFlags: "Touches security-sensitive, risk-flagged areas.",
        uiChange: "Changes the interface (section / primary action / navigation).",
        uxRegression: "A UX regression could not be resolved automatically.",
      },
      checks: {
        heading: "Verification & progress",
        qa: "QA verification",
        security: "Security review",
        progress: "Tasks completed",
        blockers: "Blocked tasks",
        ci_status: "CI on this deploy",
        validation: "Sandbox validation",
        app_start: "Application start",
        ok: "passed",
        warn: "needs attention",
        /** A waived application-start check. Its own word, so a reader who
         *  skims the state column can never take it for a pass. */
        waived: "waived",
        fail: "failed",
        pending: "in progress",
      },
      pipelineRun: {
        heading: "Pipeline run",
        phase: "Phase",
        target: "Target",
        artifact: "Artifact",
        commit: "Commit",
        actor: "Actor",
        run: "Run id",
        resumeCapability: {
          direct: "Resume: direct",
          reverse_webhook: "Resume: in-cluster",
          git_promote: "Resume: git commit",
          agent: "Cluster agent (outbound)",
          // Generic CI/CD hosts: nothing "resumes" — a deployment the host is
          // holding for its required reviewers gets released.
          environment_review: "Release: GitHub environment",
          // GitLab: one label for every lane (a blocked deployment — held by an
          // approval rule or by its own manual job — and a job-lane pause). It
          // deliberately does not say "approval": deployment approval rules are
          // Premium/Ultimate, and on every other plan the same gate is released
          // by running the held job. See ResumeCapabilityProfile.
          gitlab_release: "Release: GitLab",
          // Jenkins: the pause is an `input` step, and it is released — an
          // approve proceeds it, a rejection aborts it.
          jenkins_input: "Release: Jenkins input step",
          // Host-neutral on purpose: only the Argo lanes resume a "rollout", and
          // the chip is shown for GitHub, GitLab and Jenkins too.
          tooltip: "FixControl can carry this gate's verdict to the host holding the pause.",
        },
      },
      conclusionLabel: "AI conclusion",
      conclusion: {
        planReady: "The plan is drawn up and ready for approval.",
        readyApprove: "No open blockers detected. Appears safe to approve.",
        readyDeploy: "The patch was reviewed and verified. Ready to roll out.",
        manualReview: "This touches risk areas. Manual review is advised before approval.",
        ctoChangesRequested: "The CTO requested the changes listed above. Approving lets the agent address them in a new version; rejecting drops the patch.",
        ctoChangesAccept: "The CTO requested the changes listed above. Approving accepts this version as-is (ready to apply); rejecting drops the patch.",
        taskFailed: "A task failed. Review the error before retrying.",
        verifyFirst: "Verification has not finished yet. Review carefully.",
      },
      recommendedLabel: "Recommended action",
      recommended: {
        approve: "Ready for approval",
        reviewCarefully: "Review carefully before approving",
        approveRevises: "Approve → the agent addresses the requested changes in a new version",
        approveAccepts: "Approve → accept this version as-is (ready to apply)",
        reviewError: "Review the error, then approve a retry",
        wait: "Wait for the running tasks to finish",
      },
      cta: {
        approve: "Approve",
        approvePlan: "Approve plan",
        approveDeploy: "Approve deployment",
        approveRetry: "Retry",
        approveOverride: "Approve anyway",
        approveRevise: "Approve & revise",
        approveAccept: "Approve as-is",
        // Tier-3: approving a deployment gate whose rollout is paused and
        // whose cluster has an Argo capability wired actually CALLS BACK
        // to promote the rollout. The verb tracks the side-effect.
        resumeRollout: "Resume rollout",
        // Non-Argo hosts: approving RELEASES a deployment the host is holding
        // for its required reviewers. "Resume rollout" would be Argo
        // vocabulary and would misdescribe the click.
        releaseDeployment: "Approve & release deployment",
        requestChanges: "Request changes",
        reject: "Reject",
        rejectAbort: "Reject — stop mission",
        rejectDrop: "Reject — drop patch",
      },
      resume: {
        promoted: "Rollout resumed",
        failed: "Rollout resume failed: {reason}",
        skipped: "Approval recorded; rollout not resumed ({reason}).",
      },
      changesNoteRequired: "Add a comment describing the changes to request.",
      brief: {
        decisionRequired: "Decision required",
        recommendationLabel: "FixControl recommends",
        ifApproveLabel: "If approved",
        ifRejectLabel: "If rejected",
        insufficientHeading: "Insufficient evidence to recommend",
        insufficientLeadIn: "Inspect the evidence before deciding.",
        priorIncidentsLabel: "Similar prior incidents",
        headline: {
          plan: "Approve the mission plan before any work starts.",
          risky_patch: "Sign off on a CTO-flagged change.",
          deployment: "Authorise a deployment.",
          retry: "Authorise a retry after a task failure.",
          security_override: "Override the security gate.",
          ui_change_approval: "Approve a UI change.",
          ux_regression_override: "Override a UX regression.",
          commit: "Authorise landing a commit.",
          pr_merge: "Authorise merging the pull request.",
          pipeline: {
            deployment: "Authorise a {changeKind} to {environment}.",
            pr_merge: "Authorise merging a {changeKind} to {environment}.",
            commit: "Authorise committing a {changeKind} to {environment}.",
            retry: "Authorise retrying a {changeKind} on {environment}.",
            plan: "Approve the mission plan (targeting {environment}).",
            risky_patch: "Sign off on a CTO-flagged change targeting {environment}.",
            security_override: "Override the security gate on {environment}.",
            ui_change_approval: "Approve a UI change on {environment}.",
            ux_regression_override: "Override a UX regression on {environment}.",
          },
        },
        whatChanges: {
          blast: "Touches {fileCount} file(s) across {blastTagKeys}.",
          files: "Touches {fileCount} file(s) in the codebase.",
          pipeline: "Targets {target} on {environment} ({changeKind}).",
          plan: "Plan stage — no code changes yet; the proposed steps are listed above.",
          unknown: "No structural evidence is available for this gate yet.",
        },
        governance: {
          posture: "{statement}",
          planOnly: "A plan-only gate — governance reviews the steps before any change is made.",
          ctoFlagged: "The CTO gate flagged this change as risky — a team member must approve it first.",
          deploy: "Governance routes live-environment changes through manual approval.",
          taskFailed: "Governance pauses the mission after a task failure so a human can authorise the retry.",
          securityFlags: "Touches security-sensitive areas; governance requires a manual override.",
          uiChange: "UI changes that alter primary actions or navigation route to UX review.",
          uxRegression: "A UX regression could not be cleared automatically — governance asks for an override.",
        },
        autonomy: {
          posture: "Autonomy withdrawn ({severity}): {statement}",
        },
        escalation: {
          posture: "Approver chain expanded — adds {requiredApprovers} (blast radius: {appliesAt}).",
        },
        validation: {
          passed: "Validation passed ({ok}/{total} checks); {taskDone}/{taskTotal} mission tasks complete.",
          pending: "{pending} check(s) still in progress; {taskDone}/{taskTotal} mission tasks complete.",
          warned: "{warned} check(s) flagged for attention alongside {ok} passed; {taskDone}/{taskTotal} mission tasks complete.",
          failed: "{failed} check(s) failed out of {total}; {taskDone}/{taskTotal} mission tasks complete.",
        },
        ifApprove: {
          plan: "Execution starts under the approved plan; agents begin working immediately.",
          risky_patch: "The agent revises the patch to address the listed feedback.",
          risky_patch_accept: "The patch is accepted as-is and moves on to apply.",
          deployment: "The deployment proceeds and the live environment changes.",
          retry: "The failed task retries and the mission continues.",
          security_override: "Execution continues despite the flagged security risk.",
          ui_change_approval: "The UI change is applied and rolled out to operators.",
          ux_regression_override: "Execution proceeds despite the regression the UX agent could not clear.",
          commit: "The commit lands on the repository branch.",
          pr_merge: "The pull request is merged and downstream CI/deploy continue.",
          pipeline: {
            deployment: "The {environment} deployment to {target} proceeds.",
            pr_merge: "The pull request merges to {target} on {environment}.",
            commit: "The commit lands on {target} ({environment}).",
            retry: "The failed pipeline step retries on {target} ({environment}).",
            plan: "Execution starts on {environment} under the approved plan.",
            risky_patch: "The agent revises the patch (targeting {environment}).",
            security_override: "Execution continues on {environment} despite the flagged risk.",
            ui_change_approval: "The UI change is applied on {environment}.",
            ux_regression_override: "Execution proceeds on {environment} despite the regression.",
          },
        },
        ifReject: {
          plan: "The mission aborts; no work starts.",
          risky_patch: "The patch is dropped and the mission halts.",
          deployment: "The deployment is blocked and the mission halts.",
          retry: "The mission aborts without retrying the failed task.",
          security_override: "Execution remains blocked on the flagged security risk.",
          ui_change_approval: "The UI change is discarded.",
          ux_regression_override: "Execution remains blocked on the regression.",
          commit: "The commit is dropped and the mission halts.",
          pr_merge: "The pull request is not merged; the underlying patch can be revised.",
        },
        recommendation: {
          approve: {
            approve: "Validation passed and risk is contained.",
            approveRevises: "Approving sends the review's requested changes back so the agent revises the patch.",
            approveAccepts: "Accept the patch as-is; the review notes are addressed.",
          },
          hold: {
            insufficient: "Operational posture is insufficient — decide manually.",
            autonomyBlocked: "A critical autonomy constraint is active ({constraintKind} on {surface}) — review before approving.",
            reviewCarefully: "Risk-flagged area — review the evidence before deciding.",
            reviewError: "A check failed — inspect the error before approving.",
            wait: "Running tasks have not finished yet.",
            default: "Not enough confidence to recommend approval.",
          },
        },
        insufficient: {
          posture: "FixControl could not gather enough operational signals to recommend.",
          noEvidence: "No patch, plan or pipeline run is attached to this gate yet.",
          noChain: "The approver chain is not resolved — accountability is unclear.",
        },
        insufficientHint: {
          posture: "Run a fresh assessment from Knowledge → Codebase health, or proceed manually if you accept the gap.",
          noEvidence: "Wait for the agent to produce a patch or plan, or inspect the run timeline for what it is doing.",
          noChain: "Configure an approver chain for this gate type in Settings → Governance → Approvals.",
        },
        modeExecutive: "Executive",
        modeTechnical: "Technical",
        verdict: {
          approve: "Approve",
          hold: "Hold for review",
          reject: "Reject",
        },
        chipValidationPassed: "Validation passed",
        chipValidationWarn: "Needs attention",
        chipValidationFailed: "Validation failed",
        chipValidationPending: "Validation in progress",
        chipScopeFiles: "{fileCount} file(s) touched",
        evidenceFiles: "Affected systems & files",
        evidencePipeline: "Pipeline & CI",
        evidenceVerification: "Verification & progress",
        evidenceCi: "Your own CI, on the delivered commit",
        /**
         * What the CUSTOMER'S pipeline said about the commit this patch was
         * delivered as. A separate answer from FixControl's own verification,
         * allowed to disagree with it, so it never hides inside "Validation".
         * The provider's rollup token (`succeeded`/`failed`/…) is never shown.
         */
        ci: {
          outcome: {
            succeeded: "Your CI passed on this commit.",
            failed: "Your CI failed on this commit.",
            running: "Your CI was still running when this was captured.",
            cancelled: "Your CI run was cancelled before it finished.",
            unknown: "Your CI has not reported a result for this commit yet.",
          },
          commit: "Commit",
          commitUnknown: "the run did not name a commit",
          captured: "Read at",
          tests: "Tests",
          testsValue: "{failed} failed of {total}",
          failedJobs: "Show the {count} job(s) that failed",
          moreFailedJobs: "and {count} more",
          truncated: "The capture hit its size limit, so this is part of what your CI reported — not all of it.",
          open: "Open the run in your CI",
          note: "Read once and frozen with this decision, so the record says what your CI said at the moment it was decided — not what it says now.",
        },
        inspect: "Inspect",
        productionBannerDeploy: "Production change — human approval is required before this deployment proceeds.",
        productionBannerSurface: "This change touches a regulated surface (payments / auth / database / external integration). Human approval is required for this change.",
        stages: {
          progress: "Stage {step} of {total}",
          waitingOn: "Waiting on: {labels}",
          done: "approved",
          active: "now",
          waiting: "next",
          skipped: "not needed",
          rejected: "said no",
          overdue: "overdue",
          recorded: "Your approval of stage {step} of {total} ({label}) is recorded. The approval stays open until every stage has approved.",
          recordedNext: "Next: {labels}.",
          note: "This approval has stages. Each stage is approved by its own people, one person per stage; anyone who can decide can say no.",
        },
        postDecision: {
          approve: "Decision recorded — approval submitted. The mission continues to its next step.",
          reject: "Decision recorded — gate rejected. The mission is halted.",
          request_changes: "Decision recorded — changes requested. The agent will revise and re-submit.",
        },
        queueHeader: "Decision queue · {count} pending",
        queueOfType: "#{current} of {total}",
        queueNextCta: "Jump to the next gate of this type",
        routesTo: "Suggested routing:",
        routesToStaged: "Approval stages:",
        routesToAnyDecider: "any administrator or manager decides",
        chainQuorum: "{count} approvers required",
        postureExpansionTag: "posture",
        postureExpansionNote: "{count} approver added by posture",
        postureExpansionTooltip: "Added by posture interpretation — driven by: {derivedFrom}",
        postureExpansionTooltipUnknownSource: "operational evidence",
        refusalAction: {
          openPosture: "Open Codebase Posture",
          openOperations: "Open Operations",
        },
        // The patch sign-off gate refuses an approve while the project's own
        // validation evidence is not green. The server's sentence carries the
        // verdict code and the strategy enum verbatim, because that text is
        // what the ledger and the chat cards record; a reviewer gets the same
        // fact in their own language, with the original one fold down.
        // Codes: `SandboxGateBlockCode` (src/server/patches/sandbox-gate.ts).
        validationRefusal: {
          sandbox_required: "This patch has not been verified yet. Start a verification run, then approve.",
          sandbox_stale: "The patch changed after it was last verified, so the green result is about different code. Re-run the verification.",
          sandbox_in_progress: "The verification for this patch is still running. It has to finish before the patch can be approved.",
          sandbox_not_passed: "The verification of this patch did not pass. Look at the run before approving.",
          sandbox_environment_required: "This workspace requires evidence from a full multi-service environment, and this run produced none. Re-run the verification with the environment enabled.",
          sandbox_remote_required: "This project requires validation in your own environment, and no such run has passed for this version of the patch. A run on FixControl's own infrastructure does not satisfy it.",
          environment_contract_stale: "This project's verified environment changed after the verification passed, so the result is about a different environment. Re-run it.",
          runtime_verification_required: "The tests passed, but nothing proved the application itself still starts in this environment. The patch page explains why, and what would prove it.",
        },
        /**
         * Every reason the approval chokepoint can refuse a decision, as a
         * sentence. Each says WHAT stopped it and whether there is anything to
         * do — the previous behaviour was the server's own English prose,
         * written for a log. Guarded by the closed vocabulary in
         * `lib/approval-refusal-copy.ts`.
         */
        refusal: {
          not_found: "This approval no longer exists. It was probably withdrawn or the mission was archived while this page was open.",
          already_decided: "Someone already decided this one. Reload to see who, and what they chose.",
          mission_terminal: "This mission has already finished, so its gates can no longer be answered.",
          proposal_changed: "What was proposed changed after this page loaded. Reload and read the current proposal before deciding.",
          unsupported_decision: "Requesting changes is not something this kind of gate accepts — it can only be approved or rejected.",
          note_required: "Say what should change. Requesting changes without a comment leaves the agent nothing to work from.",
          ci_unknown: "Your CI has not reported on the delivered commit yet, so there is nothing to approve against. Give it a moment, or look at the run.",
          ci_not_green: "Your own CI failed on the delivered commit. Approving would ship a change your pipeline rejected.",
          posture_insufficient: "This codebase has not been assessed deeply enough for a change of this risk. Run the assessment first.",
          human_decision_required: "This one has to be decided by a person, in this screen — it cannot be answered automatically.",
          execution_missing: "The work this gate is about is not there any more, so there is nothing to approve.",
          reviewer_is_initiator: "You started this work, so you cannot also be the one who approves it. Someone else has to look.",
          not_authorized_for_review: "Your role does not cover this kind of approval. The people who can are listed above.",
          validation_not_green: "The change has not been proven yet, so there is nothing green to approve.",
          validation_state_unavailable: "We could not read the proof for this change, so there is nothing to approve against yet. Nothing was decided and the gate is still open — try again in a moment.",
        },
        validationRefusalDetails: "What the system reported",
        rolloutPhase: {
          queued: "Rollout queued on {environment}",
          running: "Rollout in progress on {environment}",
          succeeded: "Rollout completed on {environment}",
          failed: "Rollout failed on {environment} — inspect and roll back if needed",
          cancelled: "Rollout cancelled on {environment}",
          awaiting_approval: "Rollout paused on {environment} — another gate is waiting",
        },
        rolloutBlocked: "Rollout to {environment} is blocked.",
        rolloutResumed: "Rollout promotion triggered via Argo Rollouts ({profile}).",
        rolloutResumeFailed: "Automatic rollout promotion did not complete ({profile}) — an operator is following up.",
        // Host-neutral wording for CI/CD hosts that have no rollout to promote
        // (GitHub Actions, GitLab, Jenkins): the gate RELEASES a deployment the
        // host was holding. Argo copy above must never be shown for those.
        deploymentReleased: "The paused deployment was released on the CI/CD host ({profile}).",
        deploymentReleaseFailed: "The paused deployment could not be released ({profile}) — an operator is following up.",
        // LABEL only. The sentence after it is the resume handler's
        // server-authored operator text (which credential to connect, and
        // why), rendered verbatim — it is host vocabulary, not an i18n key.
        hostNotTold: "Your decision is recorded, but the CI/CD host was not told:",
        viewDiffCta: "View patch diff",
        rolloutDetailsCta: "View details",
        rolloutDrawerTitle: "Rollout details",
        rolloutDrawerNote: "Live deployment events stream into the operations surface. Open it for the full history, including rollback controls when available.",
        openOperationsCta: "Open operations",
        openTimelineCta: "Open issue timeline",
        notePlaceholder: "Decision note (optional, recorded in the audit trail)…",
        confirmHighRisk: "High-risk change — click Approve again to confirm.",
        confirmApproveCta: "Confirm approve",
      },
    },
    actions: {
      approve: "Approve",
      reject: "Reject",
      requestChanges: "Request changes",
      dryRun: "Dry run",
      apply: "Apply",
      export: "Export",
      exportTooltip: "Write the patch as a unified-diff file the user can git apply",
      alreadyApproved: "Already approved",
      approveBeforeApplying: "Approve the patch before applying",
      labels: {
        approve: "Approve",
        reject: "Reject",
        request_changes: "Request changes",
        dry_run: "Dry run",
        apply: "Apply",
      },
    },
    patchApply: {
      label: {
        applyLocally: "Apply locally",
        openPullRequest: "Open pull request",
        openMergeRequest: "Open merge request",
        openReviewRequest: "Open review request",
        applyOnRemote: "Apply on remote server",
        manualFallback: "Download patch / open in desktop",
      },
      manualFallback: {
        title: "Apply this patch yourself",
        body: "Hosted FixControl cannot reach this codebase to land the change for you. Pick how you'd like to take it from here:",
        downloadPatch: "Download patch",
        copyPatch: "Copy patch to clipboard",
        openInDesktop: "Open in desktop app",
        connectGitProvider: "Connect a Git provider",
        copied: "Patch copied to clipboard",
        copyFailed: "Could not copy patch — try the download instead",
      },
      remoteSsh: {
        confirmTitle: "Apply on remote server",
        confirmBody: "FixControl will SSH into {host} and write the patch under {path}. Existing files are backed up; tests (if configured) run automatically and trigger rollback on failure.",
        host: "Host",
        repoPath: "Repo path",
        files: "Files to write",
        rollbackNote: "If anything goes wrong, FixControl restores from the backup directory it just created.",
      },
    },
    requestChangesModal: {
      title: "Request changes",
      subtitle: "Describe what still went wrong when you tested it. A new version that builds on this patch starts right away.",
      placeholder: "E.g.: I still hear the error sound and land straight on the floor plan; the Order button does not light up when printable products are added…",
      cancel: "Cancel",
      submit: "Request new version",
    },
    toasts: {
      revisionAlreadyInProgress: "Revision already in progress — watch the chat for progress",
      retryStartedDeduped: "Revision already in progress — watch the chat for progress",
      retryStarted: "Drafting a follow-up patch — watch the chat for progress",
      changesRequestedRevising: "Changes requested — a new version is being drafted, watch the chat for progress",
      retryFailedMissing: "Could not start retry: {error} (missing: {missing})",
      retryFailed: "Could not start retry: {error}",
      retryFailedNetwork: "Could not start retry: {error}",
      networkError: "Network error",
      actionFailed: "{action} failed: {error}",
      patchApproved: "Patch approved — follow-up actions created",
      patchRejected: "Patch rejected",
      changesRequested: "Changes requested",
      dryRunPassed: "Dry run passed — sandbox checks green",
      dryRunFailed: "Dry run failed — sandbox reported issues",
      dryRunCompleted: "Dry run completed",
      patchApplied: "Patch applied — follow-up actions created",
      patchAppliedPr: "Patch applied — PR opened: {url}",
      exportFailedStatus: "Export failed ({status})",
      verifyStarted: "Verification started — the sandbox run is queued.",
      verifyAlreadyRunning: "A verification for this patch version is already running.",
      verifyFailed: "Could not start the verification.",
      verifyFailedStatus: "Could not start the verification ({status})",
      exportFailed: "Export failed",
      exported: "Exported: {path} ({fileCount} file)",
      exportedPlural: "Exported: {path} ({fileCount} files)",
      connectionError: "Connection error: {message}",
      cancelledFallback: "Agent stopped by user.",
      errorPrefix: "Error: {error}",
    },
    impact: {
      customerAffected: "{count} customer affected",
      customersAffected: "{count} customers affected",
      ifNotFixed: "If not fixed",
    },
    risk: {
      eyebrow: "Risk",
      confidence: "{pct}% confidence",
      dryRunRecommended: "Dry run recommended",
      ctoApprovalRequired: "CTO approval required",
      blast: {
        label: "Changes",
        payments: "payments",
        authFlow: "auth flow",
        dbSchema: "database schema",
        webhook: "webhook handling",
        externalApi: "external integration",
        frontendOnly: "frontend only",
      },
    },
    followUp: {
      eyebrow: "Follow-up",
      doneCount: "{done}/{total} done",
      overdue: "{count} overdue",
      unassigned: "{count} unassigned",
      ownerUnassigned: "unassigned",
    },
    busy: {
      pondering: "Pondering",
      reasoning: "Reasoning",
      reflecting: "Reflecting",
      cogitating: "Cogitating",
      synthesising: "Synthesising",
      investigating: "Investigating",
    },
    chatBubble: {
      agentLabel: "Agent",
      patchMoved: "Patch content moved to Patch panel.",
      softErrorTitle: "Agent's exploratory probe didn't match — the run continues with the next tool call.",
    },
    runFix: {
      runFix: "Run fix",
      dryRun: "Dry run",
      failed: "Failed",
      patchesReady: "{count} patches ready",
      pr: "PR #{number}",
      skipped: "(skipped)",
    },
    replyDrafter: {
      to: "To:",
      toneFriendly: "friendly",
      toneFormal: "formal",
      toneConcise: "concise",
      draftWithAi: "Draft reply with AI",
      drafting: "Drafting…",
      confidence: "Confidence",
      confidencePct: "{pct}%",
      editBeforeSending: " · edit before sending",
      regenerate: "Regenerate",
      copy: "Copy",
      copied: "Copied",
    },
  },

  intake: {
    headerTitle: "Report an issue",
    inputPlaceholder: "Describe the problem…",
    greeting:
      "Hi 👋 — ask a question, report a bug, or request a feature. I'll try to help straight away, and only file a ticket if we can't solve it here.",
    pageSubtitle: "Report a problem or ask a question — we'll turn it into a ticket.",
    pageBody:
      "Click the bubble in the bottom-right and describe what's going on. The intake assistant will ask a few short questions and forward the report to the {company} dev team.",
    pageProductLabel: "Product:",
    bubbleAria: "Open intake widget",
    closeAria: "Close",
    sendAria: "Send",
    thinking: "thinking…",
    contactPlaceholder: "Email for follow-up (optional)",
    askAnother: "Ask another question",
    reportAnother: "Report another issue",
    yesHelped: "Yes, that helped",
    noStuck: "No, still stuck",
    yesCreateTicket: "Yes, create a ticket",
    noThanks: "No thanks",
    addDetail: "…or add detail and press Enter",
    createIssue: "Create issue",
    tellMeChange: "or tell me what to change…",
    issueCreated: "Issue {key} created",
    typeStatus: "Type: {kind} · Status: Under review",
    sortedOut: "Glad we sorted it out",
    sortedOutDetail: "No ticket created — come back if anything else comes up.",
    issueCreatedThanks:
      "Thanks — issue **{key}** has been created. The team will pick it up from here.",
    didThisSolve: "Did this solve your problem?",
    inputPlaceholderShort: "Describe the problem…",
    openInFixControl: "Open in FixControl",
    preview: {
      ticketPreview: "Ticket preview",
      steps: "Steps",
      expected: "Expected",
      actual: "Actual",
      environment: "Environment",
      impact: "Impact",
    },
  },

  twoFactor: {
    header: "Two-factor authentication",
    badgeEnabled: "Enabled",
    badgeRequired: "Required",
    description: "Protect your account with a one-time code from an authenticator app (Google Authenticator, 1Password, Microsoft Authenticator, …) in addition to your password. SMS is not used.",
    ssoNote: "You sign in through your organization's identity provider. Multi-factor authentication is managed there; enabling native 2FA here is optional.",
    enableButton: "Enable two-factor authentication",
    errors: {
      loadFailed: "Could not load two-factor status",
      network: "Network error",
      badCode: "That code is not valid — check your authenticator app",
      badPassword: "Current password is incorrect",
      passwordRequired: "Enter your current password to replace the existing authenticator",
      requiredByPolicy: "Two-factor authentication is required for this account and cannot be disabled",
      noPending: "No enrollment in progress — start again",
      rateLimited: "Too many attempts — wait a moment and try again",
      generic: "Something went wrong. Try again.",
    },
    enroll: {
      scanHint: "Scan the QR code with your authenticator app, or enter the setup key manually. Then confirm with the 6-digit code the app shows.",
      qrAlt: "QR code for authenticator app setup",
      manualKey: "Setup key",
      manualHint: "Time-based (TOTP), 6 digits, 30-second period, SHA-1.",
      codeLabel: "Code from the app",
      verify: "Verify & enable",
    },
    recovery: {
      showOnceWarning: "Store these recovery codes now — they are shown only once. Each code signs you in exactly once if you lose your authenticator.",
      copy: "Copy",
      copied: "Copied",
      download: "Download",
      savedThem: "I saved my recovery codes",
      fileNote: "Each code can be used once. Store this file somewhere safe.",
    },
    stepUp: {
      passwordLabel: "Current password",
      codeLabel: "Authenticator or recovery code",
      codePlaceholder: "123456 or XXXX-XXXX-XXXX-XXXX",
    },
    disable: {
      button: "Disable",
      hint: "Disabling two-factor authentication makes your account password-only. Confirm with your current password and a valid code.",
      confirm: "Disable two-factor authentication",
    },
    regenerate: {
      button: "New recovery codes",
      hint: "Generate a fresh set of recovery codes. Every previously issued code stops working immediately.",
      confirm: "Regenerate recovery codes",
    },
    reenroll: {
      button: "New authenticator",
      start: "Start setup",
    },
    status: {
      enabledSince: "Enabled since {date}.",
      recoveryRemaining: "{count} unused recovery codes remaining.",
      requiredNote: "Two-factor authentication is required for this account by policy and cannot be disabled.",
    },
    setupPage: {
      title: "Set up two-factor authentication to continue",
      explanation: "Your organization requires two-factor authentication for this account. Set it up with an authenticator app below — it takes about a minute.",
      continue: "Continue to the app",
      signOut: "Sign out",
    },
    policy: {
      header: "Workspace two-factor policy",
      description: "Require every member of this workspace to enable two-factor authentication. Members without 2FA are taken to setup at their next sign-in and cannot use the app until it is enabled.",
      requireAllLabel: "Require 2FA for all members",
      adminsAlwaysNote: "Turning this on includes this workspace's own admins. FixControl platform staff always require 2FA, regardless of this setting.",
      ownSetupFirst: "Enable two-factor authentication on your own account first, then manage the workspace policy.",
      saved: "Saved",
    },
  },

  login: {
    metaTitle: "Sign in — FixControl",
    backToHomeAria: "Back to home",
    subtitle: "Sign in to your account",
    signInRequired: "Sign in to open that page.",
    sessionExpired: "Your session expired. Please sign in again.",
    emailLabel: "Email",
    emailPlaceholder: "you@company.com",
    passwordLabel: "Password",
    passwordPlaceholder: "Enter your password",
    signingIn: "Signing in…",
    signIn: "Sign in",
    loginFailed: "Login failed",
    forgotPassword: "Forgot password?",
    twoFactor: {
      title: "Two-factor authentication",
      codeHint: "Enter the 6-digit code from your authenticator app.",
      recoveryHint: "Enter one of your single-use recovery codes.",
      codeLabel: "Authentication code",
      recoveryLabel: "Recovery code",
      verifying: "Verifying…",
      verify: "Verify",
      useRecovery: "Use a recovery code",
      useAuthenticator: "Use your authenticator app",
      back: "Back",
      expired: "That sign-in attempt expired. Enter your password again.",
      badCode: "That code is not valid",
    },
    demoAccounts: "Demo accounts",
    demoPasswordLabel: "Password:",
    noAccountPrompt: "No account yet?",
    signUpFree: "Sign up — it's free",
    roles: {
      admin: "Admin",
      adminAccess: "Full access",
      manager: "Manager",
      managerAccess: "Assign & approve",
      operator: "Operator",
      operatorAccess: "View & work",
    },
  },

  forgotPassword: {
    metaTitle: "Reset your password — FixControl",
    backToHomeAria: "Back to home",
    title: "Reset your password",
    subtitle: "Enter your email and we'll send a link to choose a new password.",
    emailLabel: "Email",
    emailPlaceholder: "you@company.com",
    submit: "Send reset link",
    sending: "Sending…",
    sentTitle: "Check your email",
    sentBody:
      "If that address has an account, a reset link is on its way. It expires in one hour.",
    backToLogin: "Back to sign in",
  },

  resetPassword: {
    metaTitle: "Choose a new password — FixControl",
    backToHomeAria: "Back to home",
    title: "Choose a new password",
    subtitle: "Enter a new password for your account.",
    passwordLabel: "New password",
    passwordPlaceholder: "At least 8 characters",
    confirmLabel: "Confirm new password",
    confirmPlaceholder: "Re-enter your password",
    submit: "Reset password",
    saving: "Saving…",
    mismatch: "Passwords don't match.",
    tooShort: "Password must be at least 8 characters.",
    missingToken: "This reset link is invalid. Request a new one.",
    invalidToken: "This reset link is invalid or has expired. Request a new one.",
    genericError: "Something went wrong. Please try again.",
    successTitle: "Password reset",
    successBody:
      "Your password has been changed and you've been signed out everywhere. Sign in with your new password.",
    goToLogin: "Go to sign in",
    requestNew: "Request a new link",
  },

  signup: {
    alreadyHaveAccount: "Already have an account?",
    signIn: "Sign in",
    eyebrowPlan: "{plan} plan",
    eyebrowCodebase: "Codebase setup",
    eyebrowIssues: "Issue pilot",
    eyebrowDefault: "Sign up",
    headlinePlan: "Start your {plan} workspace",
    headlineCodebase: "Connect your codebase",
    headlineIssues: "Try with your own issue",
    headlineDefault: "Create your workspace",
    sublinePlan: "Free to start — your workspace is ready in seconds. You'll only be billed when you upgrade.",
    sublineCodebase: "Spin up an isolated workspace for your team. Connect your codebase and ship your first reviewed fix.",
    sublineIssues: "Spin up an isolated workspace for your team. Point us at one ticket and watch the triage → patch → approve loop.",
    sublineDefault: "Spin up your own isolated workspace. You'll be the admin — invite teammates whenever you're ready.",
    selectedPlan: "Selected plan",
    changePlan: "Change plan",
    perkIsolatedTitle: "Your own isolated workspace",
    perkIsolatedBody: "Your data, your codebase, your guardrails. Nothing shared with other tenants.",
    perkApprovalTitle: "Approval-first by default",
    perkApprovalBody: "You hold the final call — by default every patch waits for your green light, and any autonomy is explicit and opt-in.",
    perkAuditTitle: "Audit trail by default",
    perkAuditBody: "Every governed decision — who approved it, over which evidence — is recorded against the issue, and stays when the issue is deleted.",
    freeBlurb: "Free to start. No credit card. Cancel any time. You can invite teammates from inside your workspace once you're in.",
    fullName: "Full name",
    fullNamePlaceholder: "Ada Lovelace",
    workEmail: "Work email",
    workEmailPlaceholder: "you@company.com",
    company: "Company",
    companyPlaceholder: "Acme Logistics",
    password: "Password",
    passwordPlaceholder: "At least 8 characters",
    fairUseNote: "Creating an account means you agree to fair use of FixControl. We'll never spam you.",
    creatingWorkspace: "Creating workspace…",
    createAccount: "Create account",
    genericError: "Something went wrong. Please try again.",
    networkError: "Network error. Please try again.",
    createWorkspace: "Create workspace",
    checkEmail: "Check your email for a confirmation code.",
    verifyTitle: "Confirm your email",
    verifySubtitle: "We sent a 6-digit code to {email}. Enter it to finish setting up your workspace.",
    codeLabel: "Verification code",
    verifyCta: "Confirm email",
    verifying: "Confirming…",
    resend: "Resend code",
    resendIn: "Resend in {sec}s",
    resendSent: "A new code is on its way.",
    codeInvalid: "Code expired or invalid.",
    verifiedTitle: "Email confirmed",
    verifiedBody: "Your workspace is being prepared…",
    reviewTitle: "Request received",
    reviewBody: "We've received your request. We'll review it manually and get back to you shortly.",
    useDifferentEmail: "Use a different email",
    loadDemoTitle: "Include sample company 'Northwind Bank'",
    loadDemoBody: "Explore FixControl immediately with issues, codebases, workspaces, and knowledge. Removable with one click.",
    plans: {
      starter: "Starter",
      starterPrice: "€29 / month",
      growth: "Growth",
      growthPrice: "€99 / month",
      pro: "Pro",
      proPrice: "€299+ / month",
    },
  },

  contact: {
    metaTitle: "Contact — FixControl",
    metaDescription: "Contact FixControl directly with a question, use case or demo request.",
    eyebrow: "Contact",
    titlePart1: "Send us a question about",
    titleHighlight: "fit, security, or rollout.",
    body: "Use the form for first questions, evaluation fit, and integration questions. Use email for longer technical or commercial discussions. Book a live demo to walk through Slack, Jira, Freshdesk, or engineering remediation flows.",
    walkthroughLabel: "Want a live walkthrough?",
    walkthroughCta: "Book a demo →",
    emailLabel: "Email — longer technical or procurement questions",
    trust:
      "FixControl governs AI-assisted support and engineering work — and, where you configure them, deployment gates — with human approval by default, per-project scope, and a recorded decision trail.",
    whoReplies:
      "Your message is read and answered directly by the team building the product — typically within one business day. Security and procurement materials (architecture overview, subprocessor list, data-processing documentation) are provided during onboarding.",
    submitLabel: "Send message",
    successTitle: "Message received.",
    successBody: "Thanks. We'll reply within one business day.",
    fields: {
      name: "Your name",
      namePlaceholder: "Alex Jansen",
      email: "Email",
      emailPlaceholder: "alex@yourcompany.com",
      company: "Company (optional)",
      companyPlaceholder: "Acme B.V.",
      message: "What would you like to ask?",
      messagePlaceholder: "First question, demo fit, or an integration question (Slack, Jira, Freshdesk, GitHub…). The more concrete, the better.",
    },
  },

  demo: {
    metaTitle: "Book a demo — FixControl",
    metaDescription: "A 15-minute guided demo: see how FixControl takes a real production issue from intake to root cause, drafted patch and approval.",
    eyebrow: "Book a demo",
    titlePart1: "See FixControl handle a real issue —",
    titleHighlight: "end to end.",
    body: "A 15-minute guided walkthrough. We take one real production issue end-to-end — intake, root cause, drafted patch, your approval, and the applied fix — so you can see exactly how the workflow and guardrails behave. No slides, no sales team.",
    bullets: {
      codebase: "Shown on a real codebase",
      noDeploys: "No automatic deployments",
      approval: "Every change in this walkthrough requires approval",
      sandbox: "Evaluate with demo data before connecting any production system",
    },
    afterSubmit:
      "What happens next: we reply within one business day to schedule. Pilots start with demo data and one integration and define the approval gates and success criteria for expansion; security and procurement materials are provided during onboarding.",
    preferEmail: "Prefer to email instead?",
    contactLink: "Use the contact form →",
    submitLabel: "Request demo",
    successTitle: "Demo request received.",
    successBody: "We'll get back to you within one business day to schedule your 15-minute walkthrough.",
    fields: {
      name: "Your name",
      namePlaceholder: "Alex Jansen",
      email: "Work email",
      emailPlaceholder: "alex@yourcompany.com",
      company: "Company",
      companyPlaceholder: "Acme B.V.",
      codebase: "Stack or repo (optional)",
      codebasePlaceholder: "Next.js + Postgres · github.com/acme/api",
      teamSize: "Engineering team size",
      teamSizePlaceholder: "How big is the engineering team?",
      message: "What would you like to see?",
      messagePlaceholder: "e.g. the kind of production issue you'd like to see FixControl handle.",
    },
  },

  adminWebhookOutbox: {
    eyebrow: "FixControl admin",
    title: "Webhook DLQ",
    subtitle: "Cross-tenant dead-letter queue for outbound webhook deliveries. Replay individual rows, or select several to retry in bulk.",
    accessRequired: "Platform admin access required",
    accessRequiredDetail: "This page is reserved for FixControl staff.",
    empty: "No dead-letter rows match the current filter.",
    searchPlaceholder: "Search integration / idempotency-key / error…",
    filter: {
      allProviders: "All providers",
      includeReplayed: "Show replayed",
    },
    /**
     * Why a delivery is stuck, expressed as what the operator does about it.
     * `transient` / `permanent_auth` / `permanent_other` are the stored kinds
     * (src/server/webhook-outbox/types.ts).
     */
    errorKind: {
      transient: "Will retry",
      permanent_auth: "Credentials rejected",
      permanent_other: "Refused for good",
    },
    columns: {
      diedAt: "Died",
      provider: "Provider",
      integration: "Integration",
      attempts: "Attempts",
      errorKind: "Error kind",
      lastError: "Last error",
      replayed: "Replayed",
      notReplayed: "—",
    },
    actions: {
      replay: "Replay",
    },
    bulk: {
      replaySelected: "Replay selected",
      exportSelected: "Export selected",
      confirmTitle: "Replay {n} dead-letter row(s)?",
      confirmBody: "Each row will be re-enqueued with a fresh attempt count. Already-replayed rows are skipped.",
      confirmLabel: "Replay",
    },
  },

  adminScimTokens: {
    eyebrow: "FixControl admin",
    title: "SCIM tokens",
    subtitle: "Cross-tenant inventory of SCIM bearer tokens. Plaintext is never displayed; minting and rotation live on the per-tenant settings page.",
    accessRequired: "Platform admin access required",
    accessRequiredDetail: "This page is reserved for FixControl staff.",
    empty: "No SCIM tokens match the current filter.",
    searchPlaceholder: "Search label / tenant / created-by…",
    statusEnabled: "Enabled",
    statusDisabled: "Disabled",
    confirmDisable: "Disable SCIM token \"{label}\" for tenant \"{tenant}\"? Subsequent SCIM calls with this token will be rejected.",
    filter: { all: "All status" },
    columns: {
      tenant: "Tenant",
      label: "Label",
      status: "Status",
      lastUsed: "Last used",
      expires: "Expires",
      created: "Created",
    },
    actions: { disable: "Disable" },
    bulk: {
      disableSelected: "Disable selected",
      confirmTitle: "Disable {n} SCIM token(s)?",
      confirmBody: "Each selected token will be disabled immediately. Already-disabled tokens are skipped.",
      confirmLabel: "Disable",
    },
  },

  adminOidcProviders: {
    eyebrow: "FixControl admin",
    title: "OIDC providers",
    subtitle: "Cross-tenant inventory of OIDC providers. Refresh metadata in bulk; rotate-secret stays per-provider on the per-tenant page where the secret-once flow is wired.",
    accessRequired: "Platform admin access required",
    accessRequiredDetail: "This page is reserved for FixControl staff.",
    empty: "No OIDC providers match the current filter.",
    searchPlaceholder: "Search name / slug / tenant / issuer…",
    statusEnabled: "Enabled",
    statusDisabled: "Disabled",
    allDomains: "all domains",
    confirmDelete: "Delete OIDC provider \"{name}\" for tenant \"{tenant}\"? Users authenticating via this IdP will lose access immediately.",
    filter: { all: "All status" },
    columns: {
      tenant: "Tenant",
      provider: "Provider",
      status: "Status",
      domains: "Domains",
      updated: "Updated",
    },
    actions: {
      refresh: "Refresh metadata",
      delete: "Delete",
    },
    bulk: {
      refreshSelected: "Refresh metadata",
    },
  },

  adminCustomers: {
    loading: "Loading…",
    deniedTitle: "FixControl support only",
    deniedBody: "This page is reserved for FixControl staff. If you reached it by accident, head back to your",
    deniedWorkspace: "workspace",
    eyebrow: "FixControl support",
    title: "Customers",
    subtitleOps: "Operations view of every customer workspace — health, onboarding, integrations and activity. Click a row for detail; support-login keeps you signed in as FixControl.",
    platformAdmin: "Platform admin",
    updated: "Updated {ago}",
    refresh: "Refresh",
    healthError: "Could not load health data. Try refreshing.",
    never: "never",
    searchPlaceholderOps: "Search name, domain, contact or integration",
    countOf: "{shown} of {total}",
    cardActive: "Active (7d)",
    cardOnboarding: "Onboarding incomplete",
    cardAtRisk: "At risk",
    cardReconnect: "Reconnect required",
    cardInactive: "Inactive",
    cardAiToday: "AI runs today",
    headerCustomer: "Customer",
    headerHealth: "Health",
    headerOnboarding: "Onboarding",
    headerIntegrations: "Integrations",
    headerLastActivity: "Last activity",
    headerUsage: "Usage 7d",
    headerActions: "Actions",
    activeBadge: "Active",
    openAction: "Open",
    supportLogin: "Support login",
    supportLoginTitle: "Open {name} as support",
    statusBlocked: "Blocked",
    blockTitle: "Block {name}",
    unblockTitle: "Unblock {name}",
    confirmBlock: "Block this customer? Their users won't be able to sign in until you unblock them.",
    blockError: "Failed to change status. Please try again.",
    wipeTitle: "Wipe all data for {name}",
    confirmWipePrompt: "This wipes all issues, workspaces, integrations, and knowledge for {name}. Account + members remain. Type the company name '{name}' to confirm:",
    confirmWipeMismatch: "Name doesn't match — action cancelled.",
    wipeError: "Failed to wipe data. Please try again.",
    wipeOk: "Customer data wiped ({total} rows removed).",
    deleteTitle: "Delete environment {name}",
    confirmDeletePrompt: "This schedules {name} for deletion. Access is frozen now and everything is permanently removed after a 2-week restore window — you can undo it until then. Type the exact name '{name}' to confirm:",
    confirmDeleteMismatch: "Name doesn't match — deletion cancelled.",
    deleteError: "Failed to schedule deletion. Please try again.",
    deleteScheduled: "Scheduled for deletion. Frozen now; permanently removed on {date} unless restored.",
    restoreTitle: "Restore {name}",
    restoreError: "Failed to restore. Please try again.",
    pendingDeletionBadge: "Deletes {date}",
    pendingDeletionTitle: "Scheduled for permanent deletion on {date}. Restore to keep it.",
    downloadTitle: "Download all data for {name}",
    emptyNone: "No customers yet",
    emptyFiltered: "No customers match the filter",
    footerNote: "Need to edit a customer's plan, mailbox, or domain? Use the",
    footerLink: "Tenants",
    footerLinkSuffix: "page.",
    lastActivity: "Last activity",
    lastLogin: "Last login",
    lastInbox: "Last inbox intake",
    lastIssue: "Last issue created",
    lastPatch: "Last patch reviewed",
    drawerClose: "Close",
    supportContext: "Support context",
    drawerBlockedNote: "This tenant is blocked — its users cannot sign in.",
    drawerNoSignals: "No activity signals yet.",
    onboardingTitle: "Onboarding",
    usageTitle: "Usage",
    usageInbox7: "Inbox 7d",
    usageIssues7: "Issues 7d",
    usagePatches7: "Patches 7d",
    usageAi7: "AI runs 7d",
    usageInbox30: "Inbox 30d",
    usageIssues30: "Issues 30d",
    usageActiveUsers: "Active users 7d",
    usageAiToday: "AI runs today",
    integrationsTitle: "Integrations",
    drawerNoIntegrations: "No integrations connected.",
    mailboxes: "Mailboxes",
    mailboxIngestOff: "ingest off",
    recentIssues: "Recent issues",
    drawerNoIssues: "No issues yet.",
    recentInbox: "Recent inbox",
    drawerNoInbox: "No inbox activity yet.",
    recentPatches: "Recent patches",
    drawerNoPatches: "No patches yet.",
    activityTimeline: "Activity timeline",
    drawerNoActivity: "No recorded activity.",
    supportHistory: "Support session history",
    drawerNoSupport: "No support sessions recorded.",
    supportOngoing: "ongoing",
    supportActions: "{n} actions",
    drawerLoadError: "Could not load tenant detail.",
    health: {
      healthy: "Healthy",
      onboarding: "Onboarding",
      at_risk: "At risk",
      integration_problem: "Integration issue",
      inactive: "Inactive",
      abandoned: "Abandoned",
    },
    filter: {
      all: "All",
      healthy: "Healthy",
      onboarding: "Onboarding",
      at_risk: "At risk",
      integration_problem: "Integration issues",
      inactive: "Inactive",
    },
    integrationState: {
      active: "Connected",
      reconnect: "Reconnect required",
      error: "Error",
      pending: "Pending",
      disabled: "Disabled",
    },
    onboarding: {
      workspace: "Workspace created",
      team: "Team member invited",
      channel: "Intake channel connected",
      intake: "First intake received",
      issue: "First issue created",
      patch: "First patch reviewed",
      optional: "optional",
    },
    usage: {
      inbox: "Inbox",
      issues: "Issues",
      ai: "AI runs",
    },
    reason: {
      never_logged_in: "Never logged in",
      no_login_long: "No login in {days} days",
      no_login: "No login in {days} days",
      recent_login: "Recently logged in",
      no_inbox_ever: "No inbox intake yet",
      no_inbox_activity: "No inbox activity in {days} days",
      inbox_active: "Inbox active ({count} in 7d)",
      integration_reconnect: "{name} needs reconnect",
      integration_error: "{name} integration error",
      no_conversions: "{count} intake items, no conversions",
      onboarding_incomplete: "Onboarding {done}/{total} complete",
      ai_active: "AI flows in use ({count} in 7d)",
    },
  },

  leadForm: {
    backToHome: "← Back to home",
    sending: "Sending…",
    networkError: "Network error. Please try again.",
    genericError: "Something went wrong. Please try again.",
    selectPlaceholder: "Select...",
  },

  landingNav: {
    example: "Example",
    useCases: "Use cases",
    howItWorks: "How it works",
    control: "Control",
    compare: "Compare",
    pricing: "Pricing",
    docs: "Docs",
    openApp: "Open app",
    talkToFounder: "Contact us",
    tryYourFirstIssue: "Try your first issue",
    integrations: "Integrations",
    safety: "Security",
    review: "Review",
    contact: "Contact",
    tryFree: "Try for free",
    // Grouped-nav (dropdown) labels
    product: "Product",
    exampleWorkflow: "Example workflow",
    approvalLedger: "Approval ledger",
    integrationHealth: "Integration health",
    ucTicketQuestions: "Ticket questions in Slack/Jira",
    ucApprovedReplies: "AI replies for Freshdesk/Jira",
    ucRemediation: "Support to engineering",
    ucDeployment: "Deployment governance",
    openMenu: "Open menu",
    closeMenu: "Close menu",
    // Mega-menu (mm) — the teaching nav. Every desc is one sentence ≤80
    // chars, claims-safe (docs/ai/fixcontrol-website-claims.md).
    mm: {
      product: {
        eyebrow: "Platform",
        tagline: "The governance layer for AI-assisted engineering.",
        supportIntelligence: {
          title: "Support Intelligence",
          desc: "Understand incidents using docs, past tickets and operational context.",
        },
        operationalGovernance: {
          title: "Operational Governance",
          desc: "AI operates within policies, approvals and audit requirements.",
        },
        evidenceEngine: {
          title: "Evidence Engine",
          desc: "Every recommendation carries the evidence it rests on.",
        },
        changeValidation: {
          title: "Change validation",
          desc: "A change is only green when its tests pass and the application still starts.",
        },
        commandCenter: {
          title: "Command Center",
          desc: "Ask in your own words; every change still stops at a confirmation.",
        },
        missionTimeline: {
          title: "Mission Timeline",
          desc: "Replay every decision from intake to deployment.",
        },
        whyTitle: "What FixControl enforces",
        why: {
          humanApproval: "Human approval by default",
          explainable: "Evidence-backed proposals",
          auditTrail: "Audit trail",
          multiTenant: "Multi-tenant",
          kubernetes: "Kubernetes ready",
        },
        cta: "Explore the product overview",
      },
      useCases: {
        supportEmail: {
          title: "Support & Email",
          desc: "From your customer’s question to a checked reply — with demo video.",
        },
        engineeringBugs: {
          title: "Engineering & Bugs",
          desc: "From bug report to a tested change proposal — with demo video.",
        },
        customerSupport: {
          title: "Customer Support",
          desc: "Slack and Freshdesk become the entry point for engineering work.",
        },
        supportToEngineering: {
          title: "Support → Engineering",
          desc: "Turn support incidents into structured engineering missions.",
        },
        deploymentGovernance: {
          title: "Deployment Governance",
          desc: "Approval gates over deployments in Kubernetes and Argo CD, where configured.",
        },
        operationalAi: {
          title: "Operational AI",
          desc: "AI that assists engineers without bypassing governance.",
        },
        hrQuestions: {
          title: "HR questions",
          desc: "Your employees’ questions, answered under a permission model HR can explain.",
        },
      },
      integrations: {
        colSupport: "Support",
        colHr: "HR",
        colDevelopment: "Development",
        colInfrastructure: "Infrastructure",
        slack: { title: "Slack", desc: "Use Slack as your intake and approval surface." },
        teams: { title: "Microsoft Teams", desc: "Approve gates and reply without leaving Teams." },
        freshdesk: { title: "Freshdesk", desc: "Keep Freshdesk as your support system of record." },
        odoo: { title: "Odoo Helpdesk", desc: "Tickets linked, stages read back, replies after approval." },
        topdesk: { title: "TOPdesk", desc: "Incidents created and answered after approval, caller linked." },
        afas: { title: "AFAS Profit", desc: "Read-only HR fields for a governed answer." },
        youforce: { title: "Visma Raet Youforce", desc: "The same read-only HR contract on your Youforce endpoints." },
        jira: { title: "Jira", desc: "Sync missions with Jira issues and transitions." },
        github: { title: "GitHub", desc: "Generate reviewable patches and pull requests." },
        gitlab: { title: "GitLab", desc: "Deliver governed merge requests in GitLab." },
        kubernetes: { title: "Kubernetes", desc: "Govern a paused rollout through an approval, where configured." },
        argo: { title: "Argo Rollouts", desc: "Tie rollout promotions to approval gates, where configured." },
        fcagent: { title: "FC Agent", desc: "Connect private clusters and CI — outbound only." },
      },
      security: {
        tenantIsolation: {
          title: "Tenant Isolation",
          desc: "Every organization operates in an isolated environment.",
        },
        governance: { title: "Governance", desc: "Human approval before high-risk actions, by default." },
        evidence: { title: "Evidence", desc: "Every recommendation carries its evidence." },
        auditability: { title: "Auditability", desc: "Replay every operational decision." },
        cta: "Read security documentation",
      },
      about: {
        label: "About",
        mission: { title: "Mission", desc: "Why FixControl exists." },
        principles: { title: "Design Principles", desc: "Human control over AI operations." },
        architecture: { title: "Architecture", desc: "Built around existing enterprise tooling." },
        company: { title: "Company", desc: "About the company and vision." },
      },
    },
  },

  landing: {
    // NOTE: the NL copy is canonical (V5 plain-language pass, 2026-07-18).
    // This EN version is a direct translation kept in lockstep; editorial EN
    // polish is a planned follow-up after the Gate-4 NL panel test.
    meta: {
      title: "FixControl — From customer issue or engineering bug to tested fix",
      description:
        "FixControl starts from a customer's support ticket or a bug your team already knows about, investigates it with your documentation and code, proposes a code change and tests it in an isolated sandbox. The change arrives as a pull request — by default only after your team approves.",
    },
    hero: {
      eyebrow: "A customer reports a bug — and development gets a tested fix to review.",
      title: "From issue to tested fix.",
      sub:
        "FixControl connects your support desk, documentation and codebase. Start from a customer report or an engineering issue: FixControl investigates the cause, proposes a code change and tests it in an isolated sandbox — then hands it to your developers for review.",
      second:
        "By default, nothing goes to your customers, repositories or production without a recorded approval from your team. Code changes arrive as a pull request, never straight to your main branch.",
      ctaPrimary: "See how it works",
      ctaSecondary: "Start with a project",
      underCtas: "FixControl works on top of Freshdesk, Jira, GitHub, GitLab, Slack, Teams and e-mail — and replaces none of them.",
      screenshotAlt:
        "A proposal in FixControl: the project's used information on the left, the prepared code change waiting for approval on the right.",
    },
    flow: {
      title: "Two starting points. One path to a tested fix.",
      intro:
        "Support can start it. Engineering can start it. What happens after that runs through the same steps: FixControl investigates and tests, your team decides.",
      entriesLabel: "Two starting points",
      entries: {
        support: {
          title: "Start from support",
          body: "A customer reports a problem. FixControl combines the ticket with your documentation and code, investigates the likely cause and proposes a fix.",
          path: { p1: "Customer report", p2: "Investigation", p3: "Proposed fix" },
          note: "For customer-reported bugs and escalations from support to development.",
        },
        engineering: {
          title: "Start from engineering",
          body: "Your team already knows about the bug. File it in FixControl or open a work session on the codebase, and it goes straight to the code: analysis, a patch, tests.",
          path: { p1: "Known bug", p2: "Code analysis", p3: "Tested change" },
          note: "No support ticket needed — for defects, backlog items and internal issues.",
        },
      },
      converge: "Different starting points. The same eight steps to a tested change:",
      listLabel: "The eight steps from issue to pull request",
      phases: {
        investigate: { title: "Investigate", note: "Read-only, inside the project" },
        propose: { title: "Propose and test", note: "Tested in isolation — nothing pushed to your repository yet" },
        decide: { title: "Your team decides", note: "By default nothing moves on without a recorded approval" },
      },
      steps: {
        s1: {
          title: "An issue comes in",
          body: "From a customer through your support desk, e-mail, Slack or Teams — or filed directly by your team.",
        },
        s2: {
          title: "FixControl collects tickets, docs and code",
          body: "The report, earlier tickets, the documentation you connected and the project's repository.",
        },
        s3: {
          title: "It investigates the code",
          body: "It follows the report into the relevant code path and recent changes.",
        },
        s4: {
          title: "It names the likely cause",
          body: "Which part of the code is probably responsible, and why.",
        },
        s5: {
          title: "It proposes a code change",
          body: "A concrete patch. Nothing has been changed or sent yet.",
        },
        s6: {
          title: "It tests the change in a sandbox",
          body: "Build and tests run in an isolated environment. Where FixControl knows how to start your application, it also has to come up.",
        },
        s7: {
          title: "A developer reviews",
          body: "They see the change and the test results, then approve, reject or ask for a revision.",
        },
        s8: {
          title: "Pull request after approval",
          body: "The change arrives as a pull request, never straight to your main branch. A deployment is promoted only where you configured that, after its own approval.",
        },
      },
      principle: "First the right information. Then a proposal. Only after checks, an action.",
      cta: "Follow one report through every step",
    },
    different: {
      title: "Where FixControl fits",
      items: {
        support: {
          title: "More than support AI",
          body: "Support tools answer questions. FixControl can follow a customer's report into the code and come back with a tested change.",
        },
        coding: {
          title: "More than a coding assistant",
          body: "A coding assistant starts from a developer's prompt. FixControl starts from the issue itself — a customer report or a known bug — with the project's earlier tickets, documentation and code, and tests the change before anyone reviews it.",
        },
        unchecked: {
          title: "Not AI that acts on its own",
          body: "By default, every change is tested, reviewed by your team and recorded before it moves on — and it arrives as a pull request, never straight to your main branch.",
        },
      },
    },
    sources: {
      title: "The knowledge is already there. It is just everywhere.",
      intro:
        "The answer to a customer question rarely lives in one place. It sits in earlier tickets, in the code, in a Slack conversation from last month and in documentation nobody can find anymore.",
      items: {
        tickets: { title: "Tickets", body: "Earlier reports and how they were resolved — in Freshdesk, Jira, Odoo or TOPdesk." },
        code: { title: "Code", body: "The repositories where the work happens, including recent changes." },
        conversations: { title: "Conversations", body: "Slack and Teams channels where agreements and decisions pass by." },
        mail: { title: "E-mail", body: "Customer questions and agreements in Gmail or Office 365." },
        docs: { title: "Documentation", body: "Manuals, procedures and project documents you connect or upload." },
        deployments: { title: "Deployments", body: "What was rolled out and when — where a connected system supports it." },
        decisions: { title: "Recorded decisions", body: "And afterwards: who approved what and why, recorded next to the work so you can look it back up." },
      },
      outro:
        "AI without this information guesses. FixControl gives AI the right context per project — from tickets, code and documentation — before it proposes anything, and records the decision that follows.",
    },
    project: {
      title: "You decide which information belongs to a project",
      intro:
        "A project in FixControl is a bounded working environment. You connect exactly the sources that are relevant for that task: a mailbox, a Slack or Teams channel, one or more repositories, documentation.",
      exampleATitle: "Project: invoicing",
      exampleABody:
        "The support mailbox, the invoicing repository and the documentation about invoices. Questions about invoices are answered with this information — not with everything your organization has ever written down.",
      exampleBTitle: "Project: mobile app",
      exampleBBody:
        "The app channel in Slack, the app repository and the release documentation. An outage in the app is investigated with the app's code and reports — not with those of invoicing.",
      outro:
        "This way AI works with the right information per project: not all of your organization's information, but only the information that is relevant and permitted for that task.",
    },
    layers: {
      title: "This is how FixControl sits between your systems and your people",
      note: "From bottom to top: your systems stay the source, your people keep the final say.",
      l1: "Your existing systems — Freshdesk, Jira, Odoo, TOPdesk, GitHub, Slack, Teams, e-mail",
      l2: "The information that belongs to a project",
      l3: "Rules, checks and approval (governance)",
      l4: "What FixControl may do: observe · investigate · prepare proposals · execute approved actions",
      l5: "Tasks: support, engineering, deployment",
      l6: "Your people — they decide",
      stepsTitle: "What FixControl does, in seven steps",
      s1: "You connect the systems you already use.",
      s2: "Per project you choose which information belongs to it.",
      s3: "FixControl investigates with that information: it reads the ticket, the code and earlier reports.",
      s4: "Before an action, FixControl checks which rules apply.",
      s5: "Someone on your team decides — by default about everything that goes to your customers, repositories or production.",
      s6: "Only then does FixControl execute the approved action: an answer, a pull request, a deployment step.",
      s7: "Everything is traceable: who decided, why, and based on which information.",
    },
    example: {
      label: "A fictional but representative example — not a real customer",
      title: "A customer of your organization reports that the invoice export keeps failing. What happens then?",
      moments: {
        m1: {
          tag: "Report",
          title: "The report comes in",
          body:
            "The customer mails your service desk: “The invoice export crashes on large files.” The ticket is in Freshdesk and simply stays there.",
        },
        m2: {
          tag: "Project",
          title: "FixControl recognizes the project",
          body:
            "The mailbox is linked to the Invoicing project. FixControl sees which project and which customer the report belongs to.",
        },
        m3: {
          tag: "Investigation",
          title: "FixControl investigates with project information",
          body:
            "It reads earlier reports about the export, the documentation on invoices, the code of the export function and last week's change — and points to the likely cause.",
        },
        m4: {
          tag: "Proposal",
          title: "AI prepares two things",
          body:
            "A draft reply to the customer and a code change that addresses the likely cause. Nothing has been sent or changed yet.",
        },
        m5: {
          tag: "Test",
          title: "The change is tested first",
          body:
            "The code change runs in an isolated test environment — and where that environment knows how to start the application, the application has to come up and answer before the run counts as green. The draft reply is checked for sensitive information.",
        },
        m6: {
          tag: "Decision",
          title: "Someone on your team decides",
          body:
            "The reviewer — a member of your own team — sees the proposal and the test results and approves, rejects or requests a revision. By default nothing leaves for your customer or production without this recorded decision.",
        },
        m7: {
          tag: "Execution",
          title: "Only now does something happen",
          body:
            "The answer goes to the customer. The code change leaves as a pull request — not straight to your main branch. And everything is traceable: who decided, why, based on what.",
        },
      },
      close: "Freshdesk stayed Freshdesk, and the change arrived in GitHub as an ordinary pull request. All that was added is checks and oversight.",
      engineering: {
        label: "A second example — this one starts in engineering",
        title: "A developer notices that the retry logic sends some webhooks twice.",
        steps: {
          s1: "The developer files the bug in FixControl. No support ticket is involved.",
          s2: "FixControl finds the retry code path and the recent changes to it.",
          s3: "It proposes a patch for the likely cause.",
          s4: "The project's build and tests run in the isolated sandbox.",
          s5: "The developer sees the patch and the test results, approves — and the change arrives as a pull request.",
        },
      },
    },
    integrations: {
      label: "No migration",
      title: "FixControl works on top of the systems you already use — and replaces none of them.",
      names: "Freshdesk · Jira · Odoo · TOPdesk · GitHub · GitLab · Slack · Teams · Gmail · Office 365",
      linkLabel: "See all integrations",
    },
    video: {
      eyebrow: "In one minute",
      title: "See how FixControl works",
      intro:
        "From scattered information to a prepared proposal, checks on rules and tests, and someone on your team who decides.",
      posterCaption: "Watch FixControl in 60 seconds",
      points: {
        p1: "Only the tickets, code and documents that belong to the project",
        p2: "Specialized AI helpers (agents) for tickets, replies and code changes",
        p3: "Rules and checks before anything goes to your customers or repositories",
        p4: "Someone on your team decides where that is needed",
      },
    },
    appShot: {
      title: "This is the real product",
      body: "The flow above, in the actual interface: the FixControl control center on demo data — a real screenshot, not a mockup.",
      caption: "Home — today's work",
      alt: "The FixControl home screen with connected sources, open work and project status",
      cta: "See more screens of the product",
    },
    valueBand: {
      title: "What is a shorter path from report to fix worth to your team?",
      body: "Less searching in support, faster fixes in engineering, and a recorded decision behind every action. Calculate what that means for your team.",
      ctaCalc: "Calculate your potential value",
      ctaDemo: "Watch the 60-second demo",
    },
    demos: {
      title: "See FixControl for your team",
      intro: "Two demos: one for customer questions and email, one for bugs and code.",
      support: {
        title: "Support and email automation",
        body: "From customer question to a checked reply, with the right tickets, conversations and documentation.",
        cta: "Watch the support demo",
      },
      engineering: {
        title: "Bug and code automation",
        body: "From bug report to a tested change proposal and a pull request after approval.",
        cta: "Watch the engineering demo",
      },
    },
    useCases: {
      title: "Bug fixes are one example. The same rules apply to more.",
      intro:
        "Every project gets a goal. Customer replies, code changes, releases and questions about your documentation run through the same checks, approvals and records. These are the tasks FixControl handles today.",
      items: {
        support: {
          title: "Support",
          body:
            "Answer questions about tickets from Slack or Teams and prepare draft replies to your customers, in your house style. Someone on your team reviews by default what goes to the customer.",
        },
        engineering: {
          title: "Engineering",
          body:
            "From a customer report or a known bug to a tested code change, delivered as a pull request — never straight to your main branch.",
        },
        deployment: {
          title: "Deployment",
          body:
            "An approval promotes a paused rollout, with a record of who decided — only available for connected systems that support this.",
        },
        security: {
          title: "Security",
          body:
            "Investigation is read-only and sits behind approval; every proposed change gets a risk class before anyone decides.",
        },
        knowledge: {
          title: "Knowledge base",
          body:
            "Answers to employee questions, prepared with the documentation and project sources you connect.",
        },
      },
      linkLabel: "Explore all use cases",
    },
    control: {
      title: "AI now has the right information. But is it allowed to act?",
      intro:
        "You decide what AI may do on its own and when a person has to decide — with rules, checks and approval (governance).",
      rules: {
        r1: "A reply to your customer leaves, by default, only after approval by someone on your team.",
        r2: "A risky code change requires a passing test in an isolated environment, an application that still starts, and a senior reviewer.",
        r3: "Promoting a deployment is only possible after a recorded approval — where this is configured.",
        r4: "Read-only investigation within the project is something AI may do without approval; outward actions are not.",
      },
      outro:
        "FixControl checks these rules before an action is executed and records every decision.",
      ladder: {
        title: "Start safe and expand later",
        steps: {
          observe: {
            title: "Observe only",
            body: "FixControl reads along with tickets and code and summarizes what is going on. Nothing changes.",
          },
          analyze: {
            title: "Investigate",
            body: "FixControl looks for causes: it connects a report to earlier tickets and recent changes in the code.",
          },
          prepare: {
            title: "Prepare proposals",
            body: "FixControl writes draft replies and prepares code changes — ready for review, not sent anywhere yet.",
          },
          external: {
            title: "Execute approved actions",
            body: "Only after approval does an answer, pull request or deployment step leave. This step sits behind a human decision by default; exceptions are something you configure explicitly yourself.",
          },
        },
        outro:
          "You do not have to switch everything on at once. Start with observing and investigating, and add the next step only when your team trusts it.",
      },
    },
    boundaries: {
      title: "FixControl does not replace your existing systems",
      items: {
        workflow: {
          title: "Not a workflow builder",
          body:
            "A workflow tool executes steps someone built in advance. FixControl first gathers the project's information and then applies the rules and the approval to the proposed work — from ticket to pull request.",
          link: "Compare with workflow automation",
        },
        search: {
          title: "Not a general search engine",
          body:
            "A search layer or AI assistant searches everything it can reach and formulates an answer. FixControl uses only the sources that belong to the project — and ties an answer or change to checks and approval.",
          link: "Compare with enterprise search",
        },
        platform: {
          title: "Not a new ticket system or code platform",
          body:
            "Your tickets stay in Freshdesk, Jira, Odoo or TOPdesk, and changes arrive in GitHub or GitLab as ordinary pull requests. There is no new system to migrate to; your team keeps working where it already works.",
          link: "Compare with ServiceNow",
        },
      },
      outro:
        "FixControl connects the information that is needed, prepares the work and checks what may happen with it.",
    },
    trust: {
      title: "Verify us before you trust us",
      checkTitle: "What you can verify yourself today",
      checks: {
        c1: "How FixControl is built and secured is on the security page: isolation per organization, encryption, two-factor authentication, SSO (OIDC) and SCIM — built as scalable multi-tenant SaaS.",
        c2: "What is finished, what is in preview and what is planned is open on the product page — without “coming soon” promises.",
        c3: "Every approval is recorded with who decided, why, and what they based it on, in a hash-chained log the database itself will not let anyone edit. On Pro and Enterprise you export it as a signed file that proves it was not altered after export; on the hosted service audit records are kept for a year.",
        c4: "Prices are public on the pricing page.",
      },
      notYetTitle: "What is not there yet — and how we handle that",
      notYet: {
        n1: "Deployment gates are pilot functionality: where you configure one, FixControl records a verdict on a pause your pipeline already holds — it never starts, creates or rolls back a deployment itself. Onboarding is controlled and step by step, because we connect to sensitive systems: every rollout is validated first.",
        n2: "SOC 2 or ISO certifications are not yet in place. Customers assess the architecture, the checks and the recorded approvals directly.",
      },
      faqTitle: "Three questions readers ask us",
      faq: {
        q1: "How does all that information come together?",
        a1: "Per project you connect the sources that belong to it: a mailbox, a Slack or Teams channel, a ticket system, a repository, documentation. No source is mandatory. FixControl reads along from them; something only goes back — a reply, a status update — after the approval your rules require.",
        q2: "Who gets to see and do what?",
        a2: "Access follows your own structure: permissions per project and per role, and sensitive categories — such as HR questions — are additionally restricted to assigned reviewers. The AI falls under those boundaries too: what FixControl may do in a project (observe, investigate, prepare proposals, execute approved actions) is set per project, and every action is recorded.",
        q3: "What if the AI gets it wrong?",
        a3: "We count on that — which is why, by default, nothing leaves without review by someone on your team: reject or request a revision and nothing has happened. If a change turns out to be wrong after all — say your team rolled the production deploy back — FixControl can reopen the linked Freshdesk ticket, where you switch that on, so support knows the problem is back. And if a connection or check fails, FixControl would rather do nothing than guess: failures are visible per part, not hidden behind one green checkmark.",
      },
      partner: {
        title: "A guided start, validated per connection",
        body: "Every new team starts with guided onboarding: we validate each connection on your own data before your team relies on it. Start now and you work directly with us during rollout — and what your team needs next lands on the roadmap.",
        cta: "Talk to us about a guided start",
      },
    },
    finalCta: {
      title: "Give AI the context of one real project.",
      body:
        "Connect your tickets, code or documentation, start with observing and investigating, and see for yourself what FixControl sees. Proposals and approved actions are something you switch on only when your team is ready. Guided onboarding proves each connection before you rely on it.",
      ctaPrimary: "Create your first project",
      ctaSecondary: "Plan a pilot on your own code",
      pilotNote:
        "A guided pilot starts small: one integration, repository access scoped with you, and the approval rules and success criteria for expanding agreed up front. It is credited toward an annual contract if you continue.",
    },
    footer: {
      tagline: "AI prepares. Your people decide — that is the default. Every decision is recorded.",
      product: "Product",
      resources: "Resources",
      app: "App",
      contact: "Contact",
      links: {
        product: "Product overview",
        howItWorks: "How it works",
        example: "Example",
        integrations: "Integrations",
        safety: "Rules and approval",
        review: "Use cases",
        pricing: "Pricing",
        docs: "Documentation",
        security: "Security",
        api: "API",
        login: "Log in",
        tryFree: "Start free",
        intake: "Try with your own issue",
        demo: "Book a demo",
        contactUs: "Contact us",
        privacy: "Privacy",
        terms: "Terms",
      },
      copyright: "© {year} FixControl. All rights reserved.",
      built: "For organizations that want to use AI — with the right information, and by default an approval before an action.",
      legal: "FixControl is a trade name of {legal} · KvK {kvk} · BTW {vat}",
      legalPreRegistration: "FixControl is a trade name of {legal}",
    },
  },



  // Localized twin of the effective-decision summary sentences that
  // `resolveEffectivePolicy` builds in English for server-side surfaces.
  // Consumed via src/lib/governance/summary-format.ts (novice-audit D-12).
  governanceSummary: {
    autoExecute: "Auto-executes ({risk} risk, no approval gate).",
    draftOnly: "Prepared for human send ({risk} risk).",
    approvalRequired: "Approval required: {roles} ({risk} risk).",
    humanSignOff: "human sign-off",
    blocked: "Blocked by policy ({risk} risk).",
    risk: { low: "low", medium: "medium", high: "high", critical: "critical" },
    outcome: {
      auto_execute: "Auto-execute",
      draft_only: "Draft only",
      approval_required: "Approval required",
      blocked: "Blocked by policy",
    },
    signals: {
      escalates: "Escalates after {minutes}m",
      requires: "Requires {list}",
      recordsOne: "Records 1 audit event",
      recordsMany: "Records {count} audit events",
      conflict: "Conflict on {field}: {winner} wins",
      verificationTip: "Verification gates not yet satisfied",
    },
    gates: {
      dryRunRecommended: "Dry run recommended",
      approvalRequired: "Approval required",
    },
    inboxOutcome: {
      suggestOnly: "Suggestion only",
      draft: "Auto-drafted",
      send: "Auto-sent",
      blocked: "Blocked by policy",
    },
    requestApproval: "Request {label} approval",
    checkFailed: "Governance check failed — action blocked",
    checkFailedShort: "Governance check failed",
    whyCount: "Why? ({count})",
    resolving: "Resolving policy…",
    approvers: "Approvers:",
  },

  settingsLayout: {
    sectionLabel: "Settings",
    groups: {
      getStarted: "Get started",
      peopleAccess: "People & access",
      supportComms: "Support & communication",
      aiApproval: "AI & approvals",
      engineering: "Engineering",
      security: "Security",
      advanced: "Advanced",
      platform: "Platform admin",
    },
    tabs: {
      overview: "Overview & progress",
      profile: "Profile",
      account: "Account",
      infrastructure: "Infrastructure",
      integrations: "Channels",
      codebases: "Codebases",
      ai: "AI providers",
      agentBehavior: "Agent behavior",
      governance: "AI & approvals",
      billing: "Plan & billing",
      navigationPolicy: "Navigation policy",
      inboxMailboxes: "Mailboxes",
      outboundEmail: "Outbound email",
      websiteChat: "Website chat",
      replyStyle: "Customer communication",
      replyTemplates: "Reply templates",
      devops: "DevOps",
      users: "Users",
      system: "System",
      security: "My security",
      securityOrganization: "Organization security",
      securityPlatform: "Platform security",
      audit: "Audit",
      demo: "Practice environment",
    },
    advancedToggle: {
      show: "Show advanced settings",
      hide: "Hide advanced settings",
      hint: "Hidden sections stay reachable via a direct link.",
    },
    status: {
      completed: "Completed",
      action_needed: "Action needed",
      optional: "Optional",
      not_used: "Not used",
      blocked_prerequisite: "Available after {prerequisite} is set up",
      connection_error: "Connection not working",
      advanced: "Advanced",
    },
  },

  settingsOverview: {
    eyebrow: "Settings",
    title: "Overview & progress",
    subtitleWithGoal: "Your goal: {goal}. Below you can see what already works, what's still missing and what you deliberately skipped.",
    subtitleNoGoal: "Below you can see what already works, what's still missing and what you deliberately skipped. Pick a goal via Resume setup for targeted steps.",
    progressTitle: "Setup",
    nextStep: "Next recommended step",
    workingTitle: "Already working",
    workingEmpty: "Nothing set up yet — start with the recommended step above.",
    missingTitle: "Still missing or needs attention",
    missingEmpty: "Nothing — everything your goal needs is set up.",
    skippedTitle: "Deliberately skipped or not used",
    skippedHint: "Optional parts. You can always set them up later.",
    onboarding: {
      started: "setup not finished yet",
      postponed: "setup postponed",
      completed: "setup finished",
    },
  },

  /**
   * Google Cloud connector (settings/integrations/gcp).
   *
   * The operator is connecting THEIR Google Cloud project so FixControl can
   * read logs and errors while diagnosing a bug. Everything here is said in
   * those terms; the only machine vocabulary that survives is the IAM role
   * ids, which the operator has to type into Google Cloud verbatim.
   */
  /**
   * The platform-admin AI-capacity cockpit (/admin/ai-capacity).
   *
   * An expert screen for FixControl staff, so the numbers keep their precise
   * names (p50, context window, output budget) — but it is still a screen with
   * a language, and a page that only exists in English is a page half the
   * people who run this product cannot read. The provider's own refusal
   * strings stay verbatim: they are evidence, not copy.
   */
  adminAiCapacity: {
    eyebrow: "Admin",
    title: "AI capacity",
    subtitle: "Every customer's LLM calls against what the models and the account allow: refusals, context pressure, output budgets.",
    windowLabel: "Time window",
    windowOption: "Last {label}",
    platformAdmin: "Platform admin",
    deniedTitle: "Platform admin required",
    deniedBody: "This cockpit is for FixControl staff only.",
    loading: "Loading…",
    loadingAnnounce: "Loading AI capacity figures",
    loadedAnnounce: "AI capacity figures updated",
    error: {
      titleStale: "These numbers could not be refreshed",
      titleEmpty: "The capacity numbers could not be loaded",
      bodyStale: "What you see below is the last reading that arrived. Nothing about the AI layer changed because of this — only this page failed to read it.",
      bodyEmpty: "The reading itself failed, so nothing can be said yet about refusals or context pressure. This is a problem with this page, not with the AI layer.",
      retry: "Try again",
      retrying: "Retrying…",
    },
    noConditions: "No condition is standing in the last {hours}h.",
    seenIn: "Seen in:",
    severity: { critical: "Critical", warn: "Warning" },
    stats: {
      calls: "LLM calls",
      failed: "Failed",
      inputTokens: "Input tokens",
      outputTokens: "Output tokens",
      cost: "Cost (USD)",
      conditions: "Open conditions",
    },
    models: {
      title: "Per model",
      sub: "Input against the model's context window; completions stopped at the output budget.",
      model: "Model",
      calls: "Calls",
      failed: "Failed",
      inputP50: "Input p50",
      inputP95: "Input p95",
      inputMax: "Input max",
      context: "Context",
      pressure: "Pressure",
      truncated: "Truncated",
      outputMax: "Output max",
      cost: "Cost",
      empty: "No calls in this window.",
    },
    tenants: {
      title: "Per customer",
      sub: "Who is calling, and who is being refused.",
      tenant: "Customer",
      calls: "Calls",
      failed: "Failed",
      input: "Input",
      inputMax: "Input max",
      cost: "Cost",
    },
    kinds: {
      title: "Refusals by kind",
      sub: "Status, provider error type and the diagnostic slug — never provider prose.",
      empty: "No failures in this window.",
    },
    largest: {
      title: "Largest prompts",
      sub: "The calls closest to a model's context window.",
      when: "When",
      tenant: "Customer",
      flow: "Flow",
      model: "Model",
      input: "Input",
      context: "Context",
      pressure: "Pressure",
    },
    failures: {
      title: "Recent failures",
      sub: "Newest first, at most 50.",
      when: "When",
      tenant: "Customer",
      flow: "Flow",
      model: "Model",
      error: "Refusal, as the provider wrote it",
      empty: "No failures in this window.",
    },
  },

  /**
   * "What would FixControl read to answer this?" — the retrieval preview on
   * the knowledge page. Written for someone checking whether the agents will
   * find the right material, not for someone reading a ranking function.
   */
  /**
   * The stored AI suggestion on an inbox row, as a support agent reads it
   * between tickets. The classifier's own values (bug / feature / support,
   * critical…low) get a word; the tenant's own policy and guardrail names
   * cannot be translated by anyone but the tenant, so they are labelled as
   * rule identifiers rather than dressed up as prose.
   */
  inboxAiSuggestion: {
    title: "What FixControl suggests",
    subtitle: "Drafted against your reply policy — nothing is sent without you",
    grounded: "Answered from {count} source(s) in your knowledge",
    notGrounded: "Not backed by anything in your knowledge",
    /** What the reply's confidence stands on (server/reply-grounding.ts). */
    groundingKnowledge: "Confidence is based on {count} matching knowledge source(s), best match {best}%.",
    groundingNoMatch: "Confidence is low: nothing in your knowledge clearly matches this question (best match {best}%).",
    groundingEarlierResolution: "Confidence is based on an earlier resolution only, so it stays at most medium.",
    groundingUnscored: "Confidence is based on context that was not ranked for this question, so it stays at most medium.",
    groundingNone: "Confidence is low: nothing in your knowledge matches this question.",
    groundingLowered: "Lowered because {reasons}.",
    groundingReason: {
      single_supporting_source: "only one source backs it",
      single_retriever: "only one search method found the best match",
      weak_semantic_match: "the best match is only loosely related in meaning",
      top_hit_stale: "the best match was indexed by an older model",
      retriever_agreement_unknown: "the search methods could not be cross-checked",
    },
    intent: "Looks like: {value}",
    intentValue: {
      bug: "something is broken",
      feature: "a request for something new",
      support: "a question",
    },
    priority: "Urgency: {value}",
    priorityValue: {
      critical: "critical",
      high: "high",
      medium: "normal",
      low: "low",
    },
    reasonsTitle: "Why the policy decided this",
    reasonsLead: "What the AI could and could not do here, and why.",
    reason: {
      policy_off: "The AI is off for this mail.",
      mode_suggest_only: "The AI may only suggest a reply here.",
      mode_auto_draft: "The AI may only prepare a draft here.",
      mode_auto_send_low_risk: "The AI may send low-risk replies itself here.",
      low_confidence: "Confidence is below your minimum for sending on its own.",
      risk_not_low: "The risk is not low enough to send on its own.",
      no_kb_grounding: "The reply does not rest on a knowledge base source.",
      intent_blocked_global: "This kind of message is never sent automatically (complaint, bug, refund, legal, security).",
      intent_blocked_policy: "Your policy excludes this kind of message from sending on its own.",
      intent_not_allowed: "This kind of message is not on the list that may be sent on its own.",
      intent_not_autosendable: "Only questions and how-to questions are sent automatically.",
      category_blocked: "This category is never sent automatically.",
      angry_sentiment: "The customer sounds upset; a person replies.",
      human_requested: "The customer asks for a person.",
      max_autoreplies_reached: "The daily limit of automatic replies in this conversation is reached.",
      empty_body: "The suggested reply is empty.",
      missing_recipient: "There is no sender to reply to.",
      provider_unavailable: "The mailbox cannot send right now.",
      human_sends: "A person sends this reply.",
      workspace_ai_disabled: "AI is off for this organization.",
      out_of_office_hours: "Outside the office hours in your policy.",
      disclosure_missing: "The required AI notice is missing from the reply.",
      defers_to_team: "The reply leaves part to the team. That is never sent automatically: finish it or send it yourself.",
      internal_knowledge: "The reply partly rests on internal documentation generated from the code. Read it before it goes to the customer.",
      awaiting_approval: "The reply is waiting for approval.",
      already_sending: "A reply is already being sent.",
      draft_unavailable: "The draft was no longer available.",
      approval_stage_failed: "The reply could not be submitted for approval.",
    },
    replyTitle: "Suggested reply",
    insert: "Put this in my draft",
    accept: "Good suggestion",
    reject: "Wrong suggestion",
  },

  knowledgeRetrievalPreview: {
    title: "Check what FixControl would read",
    sub: "Ask a question the way a customer would. This runs the agents' own search and shows what they would find — nothing is changed or sent.",
    inputLabel: "The question to try",
    placeholder: "e.g. Why is my invoice not being sent?",
    ask: "Try it",
    asking: "Searching…",
    resultCount: "{n} result(s) found",
    failedTitle: "The search could not be run",
    failedBody: "Nothing changed — this is a problem reading your knowledge, not with the knowledge itself. Try again in a moment.",
    failed: "Could not run the search ({status}).",
    excerptTruncated: "…",
    staleEmbedding: "This passage was indexed by an older model, so it ranks lower than it otherwise would. It will move up the next time knowledge is re-indexed.",
    /** Why a result ranked where it did. The stored reason is
     *  primaryCodebase / project / tenantGlobal / …, which explains nothing to
     *  the person asking whether their documentation is reachable. */
    reason: {
      primaryCodebase: "Ranked first because it belongs to this project's main codebase.",
      projectCodebase: "Ranked high because it belongs to a codebase linked to this project.",
      project: "Ranked high because it belongs to this project.",
      tenantGlobal: "Included because it is shared across your whole workspace.",
      outOfScope: "Included, but ranked low: it belongs to neither this project nor your shared knowledge.",
      unscoped: "Ranked purely on how well it matches — the question was not tied to a project.",
    },
    idle: {
      title: "Try a real question",
      hint: "Type something a customer would actually ask. You will see exactly which documents the agents would read, in which order, and why — before it matters on a real ticket.",
      ask: "What knowledge do the agents use to answer a question?",
    },
    noHits: {
      title: "Nothing in your knowledge answers this",
      hint: "The agents would have nothing to work from for this question. Add the document that answers it, or widen what this project may read.",
      ask: "Help me add knowledge that answers: {query}",
    },
    excluded: {
      title: "Not reachable for this question",
      /** Counted, never asserted — the preview reports what it could count. */
      rule: {
        tombstoned_owner: "{count} source(s) whose owner was deleted.",
        another_project: "{count} source(s) that belong to a different project.",
        tenant_global_not_requested: "{count} workspace-wide source(s), because this question was pinned to one project.",
        not_in_selected_sources: "{count} source(s) outside the selection you asked about.",
      },
      /** The count itself could not be read. "Nothing was excluded" and "we
       *  did not look" are different statements. */
      unknown: {
        tombstoned_owner: "Sources whose owner was deleted (the number could not be read).",
        another_project: "Sources belonging to a different project (the number could not be read).",
        tenant_global_not_requested: "Workspace-wide sources, because this question was pinned to one project (the number could not be read).",
        not_in_selected_sources: "Sources outside the selection you asked about (the number could not be read).",
      },
    },
  },

  /**
   * The settings a WORKSPACE may change for itself — the tenant half of the
   * registry, closed over a reviewed allowlist
   * (`TENANT_WRITABLE_SETTING_KEYS`, server/config/registry.ts).
   *
   * Two states this copy has to keep apart, because they are different facts:
   * a workspace FOLLOWING the deployment default, and a workspace that has a
   * value OF ITS OWN which happens to equal it. Only the second stops tracking
   * the default when an operator changes it.
   */
  tenantSettings: {
    title: "Settings for this workspace",
    subtitle: "A handful of choices your workspace makes for itself. Everything else is set for the whole FixControl deployment by whoever runs it.",
    loading: "Reading this workspace's settings\u2026",
    loadFailed: "These settings could not be read. Nothing changed — try again in a moment.",
    on: "On",
    off: "Off",
    /** The provenance of the value in force. */
    inherited: "Following the default",
    overridden: "Set by this workspace",
    inheritedIs: "The default is {value}.",
    useDefault: "Use the default again",
    resetting: "Switching back\u2026",
    /** The Command Center button beside the reproduction switch. It is labelled
     *  with the QUESTION, not with the setting's title — a button that repeats
     *  the heading above it says nothing about what pressing it does. */
    askReproduction: "What does FixControl do when it investigates a failed check?",
    saving: "Saving\u2026",
    /** The two refusals the tenant settings route can answer with. */
    refusal: {
      setting_not_tenant_writable: "This is not a setting a workspace can change for itself. Ask whoever runs this FixControl deployment to change it.",
      invalid_value: "That value is not one this setting accepts. Nothing was changed.",
      insufficient_role: "Only admins and managers can change a workspace setting.",
      unknown: "The setting could not be saved. Nothing was changed.",
    },
    /** What each key means, in the reader's terms. The registry's own
     *  description is written for an operator reading a configuration table. */
    key: {
      SANDBOX_REPRODUCTION_SESSIONS: {
        label: "Investigate failures inside the test environment",
        help: "When a check fails, let FixControl look around in the environment it failed in before writing the next attempt. Adds about a minute to a run that already failed.",
      },
      SANDBOX_PREVIEW_ENABLED: {
        label: "Let people open a running preview",
        help: "A private, expiring link to a running copy of the application with a proposed change in it, so a reviewer can click through before approving.",
      },
      SANDBOX_AI_DISCOVERY: {
        label: "Work out how an application runs",
        help: "Let FixControl read a repository and find out how to build and start it, instead of waiting for someone to write that down.",
      },
      TENANT_DEFAULT_LOCALE: {
        label: "Language for messages sent outside the app",
        help: "What FixControl writes in when it composes a message outside the app: approval cards in Slack or Teams, approval emails, mission status posts. A ticket that arrives in another language keeps that language, and in the app everyone follows their own language picker.",
      },
    },
  },

  settingsGcp: {
    eyebrow: "Connected systems",
    title: "Google Cloud (read-only)",
    subtitle: "Let FixControl read your Google Cloud logs and errors while it diagnoses a bug. Read-only, off until you turn it on, and every read is capped, masked and written to the audit trail.",
    add: "Connect a Google Cloud project",
    loading: "Looking up your connected Google Cloud projects…",
    loadFailed: {
      title: "Your connected Google Cloud projects could not be listed",
      body: "Nothing changed — this page failed to read the list. Your connectors and their settings are untouched.",
    },
    empty: {
      title: "No Google Cloud project is connected",
      hint: "Connect one and FixControl can look at the real logs and errors behind a bug report instead of guessing from the description. You keep control of which datasets and logs it may read.",
      ask: "What does connecting Google Cloud let FixControl do?",
    },
    /**
     * What the connector is ACTUALLY used for, stated rather than implied by
     * the fields. Cloud Logging and Error Reporting have a caller; BigQuery
     * does not yet, and an operator who fills in a dataset allowlist has every
     * right to assume something reads it.
     */
    whatIsRead: {
      title: "What FixControl reads",
      and: "and",
      logs: "— read automatically on a ticket flagged as a bug, once this connector is allowed to be read while diagnosing, and attached to the note the reviewer sees. Capped, masked and audited on every read.",
      notUsedYet: "not used yet",
      bigQuery: "Nothing in FixControl runs a query today. The dataset list and the scan limit below are stored and enforced, and the connection check confirms the credential can reach BigQuery — but no feature asks it a question, so setting them changes nothing right now.",
    },
    status: {
      pending: "Not verified yet",
      connected: "Working",
      reconnect_required: "Needs a new key",
      error: "Not working",
      disabled: "Turned off",
    },
    form: {
      name: "Name",
      nameHint: "How you will recognise this project in FixControl.",
      projectId: "Google Cloud project id",
      projectIdHint: "Optional — taken from the key you paste below.",
      datasets: "BigQuery datasets FixControl may read",
      /** The allowlist and the ceiling are stored and enforced, but no
       *  feature queries BigQuery yet — an operator filling one in has
       *  every right to assume something reads it, so the label says. */
      datasetsNotUsedYet: "BigQuery datasets FixControl may read — not used yet",
      datasetsHint: "Comma-separated. Leave empty and BigQuery stays closed — nothing is queryable.",
      logs: "Logs FixControl may read",
      logsHint: "Comma-separated. Leave empty to allow any log in this project.",
      scanCeiling: "Largest BigQuery scan (MiB)",
      scanCeilingNotUsedYet: "Largest BigQuery scan (MiB) — not used yet",
      scanCeilingHint: "A query that would scan more than this is refused before it runs, so a diagnosis can never become a big bill.",
      key: "Service account key (JSON)",
      keyHint: "Pasted once and stored encrypted. It is never shown again, here or anywhere else.",
      iamIntro: "In Google Cloud, give this service account read-only roles only:",
      iamAnd: ", and for BigQuery",
      iamBigQueryOptional: "The BigQuery roles are optional today — nothing in FixControl runs a query yet; grant them only if you want the connection check to verify BigQuery access.",
      iamOutro: "FixControl only ever asks for read-only access.",
      saving: "Connecting…",
      saveFailed: "Could not connect this Google Cloud project ({status}). Nothing was stored — check the key and try again.",
    },
    row: {
      datasets: "BigQuery datasets:",
      datasetsNotUsedYet: "BigQuery datasets (not used yet):",
      datasetsNone: "none — BigQuery stays closed",
      logs: "Logs:",
      logsAny: "any log in this project",
      scanCeiling: "Largest BigQuery scan: {mib} MiB — a query that would scan more is refused before it runs.",
      scanCeilingNotUsedYet: "Largest BigQuery scan: {mib} MiB — the limit a query would be refused above, once something queries.",
      noKey: "No key is stored for this project, so nothing can be read. Connect it again with a fresh service account key.",
      lastErrorLead: "Google Cloud last refused this connector with:",
      allowAi: "Let FixControl read this while diagnosing",
      allowAiHint: "Off means the agent sees nothing from this project, whatever the allowlists say.",
      disconnect: "Delete",
      disconnecting: "Deleting…",
      confirmTitle: "Delete {name}?",
      confirmBody: "The stored service account key is destroyed and FixControl stops reading this Google Cloud project. Nothing in Google Cloud changes. To stop reads but keep the settings, disable it instead.",
      /**
       * What each stored state MEANS for the operator, said where the badge
       * cannot: a connector is stored unverified, and every read refuses a row
       * that has not passed its test, so "pending" is a to-do, not a detail.
       */
      notVerified: "Not checked yet. Run \"Check the connection\" — until it passes, FixControl reads nothing from this project.",
      reconnectRequired: "Google refused the stored key. Paste a new service account key under Change settings, then check again.",
      disabledNote: "Turned off — nothing is read and nothing is probed. Your settings are kept.",
      noKeyEdit: "No key is stored for this project, so nothing can be read. Paste a new one under Change settings.",
      test: "Check the connection",
      testing: "Checking…",
      edit: "Change settings",
      editClose: "Close",
      disable: "Turn off",
      enable: "Turn on",
      saving: "Saving…",
    },
    /** The per-capability result of a connection check. Each Google product is
     *  probed on its own, so an operator who granted Logging but not Error
     *  Reporting is told exactly that instead of one undifferentiated red. */
    test: {
      capability: {
        connection: "Reaching Google Cloud",
        read_logs: "Reading Cloud Logging",
        read_errors: "Reading Error Reporting",
        query_bigquery: "BigQuery reachable (not used yet)",
      },
    },
    edit: {
      name: "Name",
      projectId: "Google Cloud project id",
      scanCeiling: "Largest BigQuery scan (MiB, at most {max})",
      scanCeilingNotUsedYet: "Largest BigQuery scan (MiB, at most {max}) — not used yet",
      replaceKey: "Replace the service account key",
      replaceKeyHint: "Leave this empty to keep the key that is already stored. A new key — or a different project — has not been proven against Google yet, so the connector goes back to unchecked until you check it again.",
      save: "Save changes",
      saving: "Saving…",
      saveFailed: "Could not save these settings ({status}). Nothing was changed.",
    },
  },

  settingsDevops: {
    eyebrow: "AI operations governance",
    title: "DevOps & CI/CD",
    subtitle: "Connect your CI/CD providers so FixControl ingests deployment and pipeline events, scores them for risk, and records them immutably. Phase 1 is observe-and-govern: events are governed, never executed.",
    loading: "Loading…",
    loadError: "Could not load DevOps connections",
    observeOnlyNote: "Observe-and-govern: FixControl scores and logs deployments, but never triggers or rolls anything back itself.",
    providersTitle: "Connections",
    providersSub: "Connect a provider, then paste its webhook URL into the host. GitHub & GitLab use a real OAuth/App handshake; Jenkins is a manual legacy connection.",
    status: {
      notConnected: "Not connected",
      connecting: "Connecting",
      connected: "Connected",
      degraded: "Degraded",
      invalidCredentials: "Invalid credentials",
      revoked: "Revoked",
      comingSoon: "Coming soon",
      // Health demotions of a "connected" card. Credential presence is not a
      // working integration, so a card whose capability health is failing,
      // needs a reconnect, or has verified nothing says so.
      failing: "Not working",
      requiresReconnect: "Reconnect needed",
      notVerified: "Connected — not verified",
    },
    kind: {
      githubApp: "GitHub App",
      oauthApp: "OAuth app",
      manual: "Manual",
    },
    legacyNote: "Legacy manual connection — Jenkins has no signed-webhook standard, so FixControl verifies an HMAC you configure on a Notification webhook. Prefer GitHub or GitLab where available.",
    comingSoonNote: "This provider is not yet supported for governed deployments. You'll be able to connect it once ingestion is available.",
    connectedAccount: "Connected account",
    scopesLabel: "Scopes",
    lastEvent: "Last event",
    never: "Never",
    eventsLabel: "Events",
    webhookHealth: "Webhook health",
    lastErrorLabel: "Last error",
    webhookUrl: "Webhook URL",
    webhookUrlHelp: "Paste this URL into the host's webhook settings.",
    webhookUrlPending: "Available once a public origin is configured.",
    projectIdLabel: "GitLab project ID",
    projectIdPlaceholder: "e.g. 12345678",
    projectIdHelp: "The numeric project ID whose pipeline/deployment webhooks you want governed.",
    accountIdLabel: "Instance / job identifier",
    accountIdPlaceholder: "e.g. deploy-prod",
    accountIdHelp: "An identifier carried by the webhook (the job name, or an X-FixControl-Account header) so we can bind it to this connection.",
    apiTokenLabel: "API token (optional)",
    apiTokenPlaceholder: "Stored encrypted — never shown again",
    apiTokenHelp: "Optional API token, encrypted at rest. Stored for future enrichment; nothing calls back out with it yet.",
    apiTokenPlaceholderJenkins: "username:apiToken — stored encrypted, never shown again",
    apiTokenHelpJenkins: "Needed only to let FixControl proceed or abort an approved input step. Jenkins authenticates with a user AND a token, so enter both as \"username:apiToken\" — create the token in Jenkins at your name (top right) → Security. Encrypted at rest; the account needs Job/Build permission on the job.",
    baseUrlLabel: "Instance URL (optional)",
    baseUrlPlaceholderGitlab: "https://gitlab.example.com",
    baseUrlPlaceholderJenkins: "https://ci.example.com",
    baseUrlHelpGitlab: "Only for a self-managed GitLab. Leave empty for gitlab.com. Must be reachable from the internet over HTTPS.",
    baseUrlHelpJenkins: "The HTTPS address of your Jenkins, reachable from the internet. Private and localhost addresses are refused.",
    carriageLabel: "Write carriage",
    carriageDirect: "Direct — FixControl calls the host",
    carriageHelpDirect: "FixControl calls this host itself when a deployment gate is decided, so the host has to be reachable from the internet.",
    carriageHelpAgent: "The fc-agent performs the write from inside your network, so FixControl never dials this host — and the instance URL is not used. The agent only ever calls addresses from its own configuration.",
    carriageNoAgents: "No fc-agents are registered yet. Enrol one on the Kubernetes connection first; it can then carry writes for this host too.",
    carriageMissingCapability: "This agent does not advertise ci.approve_deployment yet, so it cannot release a paused deployment. Grant that capability on its registration.",
    carriageAgentGone: "Agent no longer available",
    carriageEditHelp: "Change where an approved deployment verdict is delivered. This does not touch the webhook secret — rotating that stays a separate, deliberate action.",
    carriageEditSecretNote: "The webhook secret this host signs with is untouched by this change.",
    recentRuns: "Recent governed runs",
    noRuns: "No governed runs yet.",
    showEvents: "Recent events",
    hideEvents: "Hide events",
    secretReveal: {
      title: "Copy your webhook secret now",
      note: "This is shown only once. Set it as the host's webhook secret (GitLab: “Secret token”; Jenkins: the HMAC key for the X-FixControl-Signature header). We store only an encrypted copy.",
      copy: "Copy secret",
      copied: "Copied",
      done: "I've saved it",
    },
    cta: {
      connect: "Connect",
      connecting: "Connecting…",
      disconnect: "Disconnect",
      disconnecting: "Disconnecting…",
      rotateSecret: "Rotate secret",
      rotating: "Rotating…",
      installGithub: "Install GitHub App",
      connectGitlab: "Connect GitLab",
      retry: "Reconnect",
      addAnother: "Add another connection",
      editCarriage: "Change delivery",
      saveCarriage: "Save delivery",
      savingCarriage: "Saving…",
      copy: "Copy",
      copied: "Copied",
    },
    disconnectConfirm: "Disconnect {provider}? Stored credentials are cleared and inbound webhooks will be refused.",
    needsGithubInstall: "Install the FixControl GitHub App first, then come back to finish connecting Actions governance.",
    needsGitlabOAuth: "Connect GitLab (OAuth) first, then enter a project ID to govern its pipelines.",
    agents: {
      title: "fc-agents",
      sub: "An fc-agent runs inside your own network and dials out to FixControl — nothing dials in. Enrol one on a Kubernetes connection, then name it as the write carrier on a GitLab CI or Jenkins connection above.",
      loading: "Loading agents…",
      loadError: "Could not load fc-agents",
      empty: "No fc-agents enrolled yet.",
      // Two prerequisites the enrolment form used to reveal only by failing:
      // the capability has to be armed first, and an agent is enrolled ON a
      // Kubernetes connection that must already exist.
      emptyPrereqIntro: "Before you can enrol one:",
      emptyPrereqCapability: "Arm the fc-agent capability in Settings → Governance → deployment capabilities.",
      emptyPrereqConnection: "Connect a Kubernetes provider above — an agent is enrolled on that connection.",
      governanceCta: "Open Governance settings",
      enrolRefusal: {
        agent_lane_disabled: "The fc-agent capability is not armed for this workspace yet. Arm it in Settings → Governance → deployment capabilities, then enrol the agent.",
      },
      state: {
        live: "Live",
        paused: "Paused",
        revoked: "Revoked",
      },
      // Enrolment state and connectivity are different facts: an agent can be
      // enrolled and enabled (Live) while it has stopped talking to us. The
      // health model records connectivity as ONE worst-of row per tenant (see
      // runAgentHealthCheck), so it is reported for the fleet, with the silent
      // agent named in its own message — never as a per-agent green.
      connectivityLabel: "Cluster connectivity",
      connectivityNeverConnected: "Never connected",
      clusterLabel: "Cluster",
      lastSeenLabel: "Last heartbeat",
      neverSeen: "Never — the agent has not called home yet",
      fingerprintLabel: "Secret fingerprint",
      enrolledLabel: "Enrolled",
      capabilitiesLabel: "Advertised capabilities",
      noCapabilities: "None — this agent may be handed no work.",
      revokedNote: "Revoked. Its shared secret no longer authenticates and nothing will be signed for it. Enrol a new agent to restore this cluster's carriage.",
      signingKeysLabel: "Signing keys to pin",
      noSigningKeys: "This deployment has no platform signing key yet, so operations are signed with the agent's own shared secret and there is nothing to pin.",
      // No repo command here. This screen is a tenant admin's; the export
      // command is an internal FixControl operator's tool, and printing it in
      // a customer console reads as an instruction the reader cannot follow.
      signingKeysHelp: "Public keys — they cannot sign anything. Put them in the agent's ConfigMap so it refuses any operation FixControl did not authorize. FixControl generates and rotates these keys; contact support if a rotation is needed.",
      keyPrimary: "Primary",
      keyStaged: "Staged",
      package: {
        label: "Install package",
        // The truth while no public release exists. A link to a release that
        // 404s at this point in a setup reads as a broken product.
        unpublished: "The install package is provided by your onboarding contact. It contains the manifests, an idempotent install.sh driven by one values file, and verify.sh — plus rotate, revoke and uninstall.",
        download: "Download fc-agent {version} (.tar.gz)",
        releaseNotes: "Release notes",
        checksum: "Checksum",
        imagesHint: "Images are digest-pinned; install.sh refuses a tag. Paste the two lines from",
      },
      values: {
        label: "values.env — paste into the package",
        hint: "This is the identity half of the file. The allowlists, egress destinations and promotion mode are yours to decide and are documented in values.example.env — FixControl does not choose them and cannot widen them.",
      },
      rotateConfirm: "Rotate the secret for {name}? The old secret stops working immediately, so the running agent is deaf until you update its Kubernetes Secret and restart it.",
      revokeConfirm: "Revoke {name}? This is final: the shared secret dies, any connection carried by this agent stops releasing deployments, and a revoked agent cannot be resumed — only replaced.",
      reveal: {
        title: "Copy the install values now",
        titleRotated: "Copy the new secret now",
        note: "The secret is shown only once and is never readable again. Everything else below stays visible in the list — but the agent needs all of it together to start.",
        secretLabel: "Shared secret",
        agentIdLabel: "Agent ID",
        clusterLabel: "Cluster ID",
        fcUrlLabel: "FixControl URL",
        noFcUrl: "No public origin is configured for this deployment, so there is no URL to give the agent yet. Set one before installing.",
        done: "I've saved it",
      },
      form: {
        nameLabel: "Agent name",
        namePlaceholder: "e.g. prod-eu-agent",
        nameHelp: "Letters, digits and hyphens. Also names the in-cluster Deployment. Re-using an existing name rotates that agent's secret instead of creating a second one.",
        nameRequired: "Give the agent a name.",
        clusterLabel: "Cluster ID",
        clusterPlaceholder: "e.g. prod-eu-1",
        clusterHelp: "Your own identifier for the cluster. Every authorization is signed for this exact value, and an agent refuses work signed for another cluster.",
        clusterRequired: "Give the cluster an identifier.",
        connectionLabel: "Kubernetes connection",
        connectionPlaceholder: "Choose a connection",
        connectionHelp: "The agent registration lives on a Kubernetes connection — that is where the cluster binding and the shared secret belong.",
        capabilitiesLabel: "Capabilities",
        capabilitiesHelp: "Nothing is granted by default. Each capability is an authority this agent will hold; releasing and refusing a deployment are deliberately separate grants.",
        capabilityRequired: "Grant at least one capability.",
        noK8s: "Connect the Kubernetes integration first — an fc-agent is enrolled on a Kubernetes connection.",
      },
      cta: {
        enrol: "Enrol an agent",
        enrolling: "Enrolling…",
        rotate: "Rotate secret",
        revoke: "Revoke",
        pause: "Pause",
        resume: "Resume",
      },
    },
    agentOps: {
      title: "Signed operations",
      sub: "The signed, single-use authorizations the fc-agent redeems — the hop between a decision here and a write inside your network. Settled entries are purged after ~24 hours.",
      empty: "No operations in the last 24 hours.",
      loadError: "Could not load operations",
      loading: "Loading recent operations…",
      refusal: "Refused: {reason}",
      pinnedAny: "any capable agent",
      cols: { operation: "Operation", state: "State", agent: "Agent", cluster: "Cluster", created: "Created" },
      copyId: "Copy id",
      copied: "Copied",
      // `AgentOperationState` (src/server/integrations/devops/agent/store.ts).
      states: {
        registered: "Waiting for the agent",
        claimed: "In progress",
        succeeded: "Done",
        failed: "Failed",
        refused: "Refused by the agent",
        expired: "Expired before the agent picked it up",
      },
      // `AGENT_CAPABILITIES` (…/agent/envelope.ts). The keys carry `_` where
      // the capability id carries `.` — a dot is a path separator to the
      // translator, so `rollout.promote` would be read as two levels.
      capabilities: {
        rollout_promote: "Promote a rollout",
        rollout_abort: "Abort a rollout",
        rollout_status: "Read rollout status",
        test_run: "Run validation",
        test_cancel: "Cancel validation",
        ci_approve_deployment: "Approve a deployment",
        ci_reject_deployment: "Reject a deployment",
      },
      // `AGENT_REFUSAL_REASONS` (…/agent/envelope.ts), plus the two an agent
      // may report outside that list. Every one of these is a settled answer,
      // not an error to retry — except `host_unreachable`.
      refusalReason: {
        tenant_mismatch: "the authorization was not for this workspace",
        cluster_mismatch: "the authorization was not for this cluster",
        bad_signature: "the agent could not verify FixControl's signature",
        expired: "the authorization had already expired",
        replay: "the authorization had already been used",
        not_allowlisted: "the agent's own allowlist does not permit this address",
        revision_drift: "what is deployed changed after the decision was made",
        capability_denied: "this agent was not granted that permission",
        no_longer_pending: "the deployment was no longer waiting",
        parameterized_input: "the paused build expects parameters the agent cannot supply",
        tier_unavailable: "the host's plan does not offer this action",
        host_unreachable: "the agent could not reach the host",
        host_refused: "the host refused the request",
        no_refusal_primitive: "the host offers no way to refuse this",
        run_cancelled: "the run was cancelled",
        unspecified: "no reason was given",
      },
    },
  },

  operations: {
    eyebrow: "Operations",
    title: "AI Operations Governance",
    subtitle: "Governed CI/CD activity, deployment risk, and the approval queue — observe-and-govern.",
    loadError: "Could not load the dashboard",
    stats: {
      pipelineRuns: "Pipeline runs",
      awaitingApproval: "Awaiting approval",
      blocked: "Blocked by policy",
      pendingApprovals: "Pending approvals",
    },
    riskDistribution: "Risk distribution",
    noGovernedRuns: "No governed runs yet.",
    heatmap: "Infrastructure risk heatmap",
    noData: "No data.",
    recentDeployments: "Recent governed deployments",
    emptyDeployments: "No governed deployments yet",
    emptyDeploymentsHint: "Connect a CI/CD provider in Settings → DevOps to start ingesting events.",
    cols: {
      provider: "Provider",
      change: "Change",
      environment: "Environment",
      phase: "Phase",
      risk: "Risk",
      outcome: "Outcome",
      when: "When",
    },
    risk: {
      low: "Low", medium: "Medium", high: "High", critical: "Critical", unknown: "Unknown",
    },
    phase: {
      queued: "Queued", running: "Running", succeeded: "Succeeded",
      failed: "Failed", cancelled: "Cancelled", awaiting_approval: "Awaiting approval",
    },
    outcome: {
      auto_execute: "Auto-execute", draft_only: "Draft only",
      approval_required: "Approval required", blocked: "Blocked",
    },
    environment: {
      production: "Production", staging: "Staging", development: "Development",
    },
    changeKind: {
      production_deployment: "Production deployment",
      staging_deployment: "Staging deployment",
      rollback: "Rollback",
      infra_modification: "Infrastructure change",
      terraform_apply: "Terraform apply",
      kubernetes_change: "Kubernetes change",
      secret_rotation: "Secret rotation",
      dns_change: "DNS change",
      database_migration: "Database migration",
      feature_flag: "Feature flag",
      unknown: "Unknown",
    },
    provider: {
      github_actions: "GitHub Actions",
      gitlab_ci: "GitLab CI",
      jenkins: "Jenkins",
      azure_devops: "Azure DevOps",
      argo_cd: "Argo CD",
      vercel: "Vercel",
      terraform_cloud: "Terraform Cloud",
      kubernetes: "Kubernetes",
      circleci: "CircleCI",
      buildkite: "Buildkite",
    },
  },

  operationsMetrics: {
    eyebrow: "AI Operations",
    title: "Operational metrics",
    subtitle: "Leading indicators for the support → remediation loop. A metric reads n/a when there is no data in the window, and withholds a verdict below {min} measurements.",
    loadError: "Couldn't load metrics",
    na: "n/a",
    window: { "7": "7d", "30": "30d", "90": "90d" },
    band: {
      healthy: "healthy",
      watch: "watch",
      attention: "attention",
      noData: "no data",
      notConnected: "not connected",
      lowSample: "too few measurements",
    },
    retrieval: {
      label: "Support retrieval hit rate",
      sub: "{n}/{total} queries returned a hit",
      none: "No retrieval queries recorded",
      notConnected: "Needs a helpdesk with retrieval (Freshdesk, Jira, Odoo, TOPdesk)",
    },
    kb: {
      label: "KB suggestion approval rate",
      sub: "{n}/{total} reviewed drafts approved",
      none: "No reviewed drafts yet",
    },
    latency: {
      label: "Approval latency (p50 / p90)",
      sub: "{count} decisions · avg {avg} · max {max}",
      none: "No decided approvals",
    },
    rollback: {
      label: "Rollback frequency",
      sub: "{n}/{total} deployments rolled back",
      none: "No deployment outcomes recorded",
      notConnected: "Needs a CI/CD integration that reports deployments",
    },
    repeated: {
      label: "Repeated incidents",
      sub: "{repeated} repeats over {total} incidents · {distinct} distinct",
      none: "No incidents in window",
    },
    rejectedRemediation: {
      label: "Rejected remediations",
      sub: "{n}/{total} evaluated runs rejected the patch",
      none: "No evaluated remediation runs",
    },
    unusableRuns: {
      label: "Runs without a verdict",
      sub: "{n}/{total} runs never reached a verdict",
      none: "No remediation runs recorded",
      hint: "Sandbox failures (timeout / error) — infrastructure, not patch quality",
    },
  },

  missionTimeline: {
    title: "Operational timeline",
    subtitle: "Replayable operational milestones for this mission.",
    empty: "No operational milestones yet for this mission.",
    openLink: "link",
    milestone: {
      intake_received: "Intake received",
      clarification_requested: "Clarification requested",
      issue_created: "Issue created",
      freshdesk_ticket_created: "Freshdesk ticket created",
      support_ticket_resolved: "Support ticket resolved",
      support_ticket_reopened: "Support ticket reopened",
      support_context_loaded: "Support context loaded",
      governance_check_started: "Governance check started",
      plan_generated: "Plan generated",
      approval_requested: "Approval requested",
      approval_granted: "Approval granted",
      execution_started: "Execution started",
      task_completed: "Task completed",
      patch_generated: "Patch generated",
      qa_completed: "QA verification completed",
      security_review_completed: "Security review completed",
      cto_gate_approved: "CTO gate approved",
      security_override: "Security override applied",
      deployment_approval_requested: "Deployment approval requested",
      deployment_approved: "Deployment approved",
      argo_sync_started: "Argo sync started",
      deployment_completed: "Deployment completed",
      kb_suggestion_created: "KB suggestion created",
      mission_completed: "Mission completed",
      mission_failed: "Mission failed",
      mission_cancelled: "Mission cancelled",
      approval_rejected: "Approval rejected",
      changes_requested: "Changes requested",
      approval_expired: "Approval expired",
      verification_passed: "Verification passed",
      verification_failed: "Verification failed",
      verification_blocked: "Verification blocked by its environment",
    },
  },

  settingsProfile: {
    eyebrow: "Settings",
    title: "Profile",
    subtitle: "Your name, language, and display size. Email and access live on the Account tab.",
    networkError: "Network error — please retry.",
    identity: {
      header: "Identity",
      nameLabel: "Display name",
      emailLabel: "Email",
      emailReadOnly: "Email is your sign-in identity. Ask an admin to change it.",
      save: "Save",
      saving: "Saving…",
      saved: "Saved",
    },
    language: {
      header: "Language",
      description: "Switches the interface immediately. Your choice is remembered on this device.",
    },
    display: {
      header: "Display size",
      description: "Adjust text size and spacing across the app. Remembered on this device.",
      compact: "Compact",
      default: "Default",
      comfortable: "Comfortable",
      large: "Large",
    },
    managedByAdmin: "Managed by your workspace admin.",
    layoutGroup: {
      header: "Layout & navigation",
      toggleHint: "click to open",
      description: "Personal choices for how FixControl is arranged for you — they never change what you can access. Admins set the boundaries under Settings → Navigation policy.",
    },
    workProfile: {
      header: "Work profile",
      description: "Choose how FixControl is arranged for the way you work. A preset, not a permission — it never changes what you can access.",
    },
    startPage: {
      header: "Start page",
      description: "Choose which page opens after you sign in. Only pages you can see are listed.",
    },
    navigation: {
      header: "Navigation",
      description: "Choose which sections appear in your sidebar. You can only show sections you have access to; some may be set by your admin.",
    },
    profiles: {
      starter: "Starter",
      support: "Support",
      engineering: "Engineering",
      release_devops: "Release / DevOps",
      manager_reviewer: "Manager / Reviewer",
      admin: "Admin",
      custom: "Custom",
    },
    profileHints: {
      starter: "Only where you start: projects, issues and what comes in. The rest appears once you use it.",
      support: "Inbox, issues and knowledge up front.",
      engineering: "Issues, missions, workspaces and patches.",
      release_devops: "Missions, governance, patches and operational metrics.",
      manager_reviewer: "Dashboard, approvals, governance and progress.",
      admin: "The full operator navigation, still limited by your permissions.",
      custom: "Pick exactly which sections you want to see.",
    },
  },

  settingsBilling: {
    eyebrow: "Workspace settings",
    title: "Plan & billing",
    subtitle: "What this workspace is subscribed to, and how to change it.",
    noAccess: "Only a workspace admin can view billing.",
    loadError: "Could not load billing details — please retry.",
    notConfigured:
      "Self-service billing is not set up for this deployment. Your plan is arranged with us directly — contact us to change it.",
    currentPlan: "Current plan",
    status: {
      label: "Status",
      trialing: "Trial",
      active: "Active",
      past_due: "Payment failed",
      grace: "Payment overdue",
      suspended: "Suspended",
      canceled: "Cancelled",
    },
    renewsOn: "Renews on",
    endsOn: "Access ends on",
    pilot: {
      header: "No subscription",
      body: "This workspace runs without a billing relationship — a pilot or an agreement made with us directly. Nothing here is limited by payment.",
    },
    dunning: {
      pastDue: "A payment failed. Everything keeps working until {date}, so nothing is interrupted while you sort it out.",
      grace: "Payment is overdue. Access continues until {date}.",
      suspended: "Access is suspended because payment was not completed. Your data is intact — updating your payment method restores it.",
    },
    usage: {
      header: "This month's usage",
      body: "What this workspace has consumed in {month}, against the ceilings its plan sets.",
      aiActions: "AI actions",
      tokens: "Model tokens",
      spend: "Model spend (USD)",
      of: "of",
      unlimited: "No plan ceiling",
      footnote:
        "Counting continues whether or not a ceiling is set, so the figure stays honest. Spend covers the models we have a price for; anything unpriced adds tokens but no money, which makes this a floor.",
    },
    portal: {
      header: "Payment & invoices",
      body: "Update your payment method, download invoices, or cancel. Opens our payment provider's secure page.",
      action: "Manage billing",
      opening: "Opening…",
      unavailable: "There is nothing to manage yet — this workspace has no self-service billing relationship.",
    },
    choosePlan: {
      header: "Change plan",
      body: "Prices exclude VAT. You are taken to our payment provider's secure checkout; nothing changes until payment completes.",
      action: "Choose {plan}",
      current: "Current plan",
      perMonth: "per month",
      contactOnly: "Contact us",
    },
    checkout: {
      success: "Payment received. Your plan updates as soon as the confirmation arrives — usually within seconds.",
      cancelled: "Checkout cancelled. Nothing changed.",
      failed: "Could not reach the payment provider. Please try again.",
    },
  },
  settingsNavigationPolicy: {
    eyebrow: "Workspace settings",
    title: "Navigation policy",
    subtitle: "Set the default work profile and decide what users may show, hide, or customise. This shapes navigation only — it never grants or removes a permission.",
    noAccess: "Only a workspace admin can change the navigation policy.",
    save: "Save policy",
    saving: "Saving…",
    saved: "Saved",
    saveError: "Could not save — please retry.",
    profiles: {
      header: "Work profiles",
      description: "Which work profiles users may select, and the default for new users.",
      allowedLabel: "Selectable profiles",
      defaultLabel: "Default profile",
    },
    customization: {
      header: "User customisation",
      description: "Turn off a control to enforce the profile/policy defaults for everyone.",
      nav: "Let users show/hide navigation items",
      startPage: "Let users choose their start page",
      displaySize: "Let users change display size",
    },
    items: {
      header: "Forced & hidden navigation",
      description: "Force an item always visible (for users who have permission) or hide it for everyone. Hidden always wins over forced.",
      itemCol: "Navigation item",
      lockedCol: "Always show",
      hiddenCol: "Hide",
      note: "Hiding an item only removes it from the sidebar — it never changes who may reach the route directly. Routes stay protected server-side.",
    },
  },

  settingsAccount: {
    eyebrow: "Settings",
    title: "Account",
    subtitle: "Where you belong, what you can do and where approval e-mails go. Role and organization are managed by admins on the Users tab.",
    workspace: {
      header: "Organization",
      nameLabel: "Active organization",
      idLabel: "Technical organization id",
      noWorkspace: "No organization assigned",
      technicalDetails: "Technical details",
    },
    access: {
      header: "Access",
      roleLabel: "Role",
      roleDesc: {
        admin: "Full access: users, settings and all approvals",
        manager: "View, assign and approve",
        operator: "View and work on assigned items",
      },
      platformLabel: "Platform operations",
      platformYes: "Yes — FixControl staff",
      platformNo: "No",
      changeNote: "Role and access are managed by admins. Contact an admin to change them.",
    },
    approvalEmail: {
      header: "Approval e-mails",
      description: "When a customer reply needs your approval, FixControl e-mails you the reply with approve and reject buttons. By default that goes to your account e-mail ({email}). Use a different address here, for example a shared approvals inbox.",
      label: "Address for approval e-mails",
      placeholder: "approvals@company.com",
      save: "Send confirmation",
      saving: "Sending…",
      remove: "Use my account e-mail",
      verified: "Confirmed — approval e-mails go to this address.",
      pending: "Waiting for confirmation — open the link we sent to {email}. Until then approval e-mails go to your account e-mail.",
      expired: "Not confirmed — the link expired. Send a new confirmation.",
      none: "Approval e-mails go to your account e-mail.",
      sent: "Confirmation e-mail sent to {email}.",
      notSent: "The confirmation e-mail could not be sent. Check the organization's outgoing e-mail.",
      confirmedNow: "Address confirmed. Approval e-mails now go to {email}.",
      confirmFailed: "This confirmation link is invalid or has expired. Send a new confirmation.",
      invalid: "That is not a valid e-mail address.",
      error: "Saving failed. Try again.",
      onlyApprovers: "Only admins and managers receive approval e-mails.",
    },
    session: {
      header: "Session",
      description: "Change your password or sign out of this browser.",
      openSecurity: "Change password",
      signOut: "Sign out",
    },
  },

  demoSettings: {
    eyebrow: "Settings",
    title: "Demo data",
    subtitle: "Load a fictional 'Northwind Bank' company into your workspace to explore FixControl — issues, codebases, workspaces, patches, Slack threads. Removable in one click.",
    loadingStatus: "Loading status…",
    statusActive: "Demo data active",
    statusActiveSub: "Northwind Bank is loaded into this workspace.",
    statusInactive: "No demo data",
    statusInactiveSub: "Click load to add Northwind Bank.",
    loadCta: "Load demo data",
    loading: "Loading…",
    loadOk: "Demo data loaded.",
    loadError: "Load failed. Please try again.",
    resetCta: "Reset demo",
    resetting: "Resetting…",
    confirmReset: "This wipes the current demo data and recreates it from scratch. Any notes or edits on demo issues will be lost.",
    resetOk: "Demo data reset.",
    resetError: "Reset failed. Please try again.",
    removeCta: "Remove demo data",
    removing: "Removing…",
    confirmRemove: "Remove all demo data? This action cannot be undone.",
    removeOk: "Demo data removed ({total} rows).",
    removeError: "Removal failed. Please try again.",
    helpLoad: "Load adds 15 issues, 3 workspaces with real patches, a Slack thread, and supporting knowledge/audit — all tagged as demo.",
    helpReset: "Reset wipes the existing demo data and seeds a clean copy. Useful after an upgrade or if data is corrupt.",
    helpRemove: "Remove deletes only the demo data — your own issues, projects, and integrations are untouched.",
  },

  integrationHealth: {
    panelTitle: "Integration health",
    panelSubtitle: "Capability-level status, retries and failed deliveries for this connection.",
    rollupLabel: "Overall",
    status: {
      healthy: "Healthy",
      degraded: "Limited",
      failing: "Error",
      requires_reconnect: "Reconnect needed",
      disabled: "Disabled",
      unknown: "Not checked yet",
    },
    capability: {
      connection: "Connection",
      create_ticket: "Create ticket",
      post_public_reply: "Public reply",
      post_private_note: "Private note",
      push_status: "Status push",
      read_ticket: "Read ticket",
      sync_webhook: "Webhook sync",
      create_issue: "Create issue",
      post_comment: "Comment",
      transition_status: "Status transition",
      read_issue: "Read issue",
      post_message: "Post message",
      update_message: "Update message",
      approval_actions: "Approval actions",
      create_branch: "Create branch",
      create_pr: "Create PR",
      read_checks: "Read checks",
      send_reply: "Send reply",
      ingest: "Mail ingest",
      token_refresh: "Mailbox authorization",
      watch_renewal: "Watch renewal (live sync)",
      subscription_renewal: "Subscription renewal (live sync)",
      webhook_receive: "Webhook delivery",
      // DevOps capabilities — the health panel now runs on /settings/devops
      // too, where an unmapped key would render the raw capability id.
      read_rollout: "Read rollout",
      promote_rollout: "Promote rollout",
      abort_rollout: "Abort rollout",
      read_status: "Read status",
      approve_deployment: "Approve deployment",
      play_job: "Start held job",
      proceed_input: "Continue paused build",
      read_repo: "Read repository",
      read_file: "Read file",
      write_patch: "Write patch",
      customer_directory: "Customer directory",
      jsm_customer_reply: "Service-desk reply",
      receive_actions: "Receive actions",
      receive_commands: "Receive commands",
      open_modal_or_task_module: "Open dialog",
      connectivity: "Agent connectivity",
      // Read-only Google Cloud connector. The labels name the Google product,
      // never the internal capability key.
      read_logs: "Cloud Logging read",
      read_errors: "Error Reporting read",
      query_bigquery: "BigQuery reachable (not used yet)",
      read_only_verified: "Read-only role verified",
    },
    jobs: {
      heading: "Delivery jobs",
      open: "Open",
      failed: "Failed",
      deadLetter: "Dead-letter",
      succeeded: "Succeeded",
      none: "No outbound jobs need attention.",
      attempt: "Attempt {n}/{max}",
      nextAttempt: "Next try {when}",
      retrying: "Retrying",
    },
    lastSuccess: "Last success",
    lastFailure: "Last failure",
    lastErrorLabel: "Last error",
    healthy: "All capabilities are working.",
    noChecksYet: "Nothing has been checked yet. Once this system is connected, use “Test connection” to check it.",
    actions: {
      retry: "Retry",
      test: "Test connection",
      reconnect: "Reconnect",
      requeue: "Requeue",
      retrying: "Retrying…",
      retried: "Retry started",
      retryFailed: "Retry failed",
    },
    banner: {
      pendingTitle: "External ticket not created yet",
      pendingBody: "FixControl is retrying to push this issue to {provider}. Last attempt: {last}. Next attempt: {next}.",
      failedTitle: "Action required",
      failedBody: "{provider} is not accepting tickets right now. Reconnect or fix the integration, then retry.",
      lastError: "Last error: {error}",
      openIntegration: "Open integration settings",
      retry: "Retry now",
    },
  },
  settingsIntegrations: {
    eyebrow: "Settings",
    title: "Channels & connections",
    subtitle: "Connect the channels your team already works in. Every channel runs through the same intake, review and patch flow.",
    engineCallout: {
      title: "Connect once. Use everywhere.",
      body: "FixControl normalises messages from Slack, Teams, email and the widget into one workflow. Reviews and updates flow back through the same channel.",
    },
    capabilities: {
      intake: "Intake",
      review: "Reviews",
      notification: "Notifications",
      code: "Code",
      tracker: "Mirroring",
      webhook: "Webhook",
      api: "API",
      diagnostics: "Diagnostics",
      deploy: "Build & deploy",
    },
    capabilityTooltips: {
      intake: "Receive issues and requests through this channel.",
      review: "Approve patches or request changes from this channel.",
      notification: "Receive workflow updates through this channel.",
      code: "Open pull or merge requests with patches prepared by agents.",
      tracker: "Mirror BUG and FEATURE issues to this tracker.",
      webhook: "Send or receive signed webhook payloads.",
      api: "Programmatic access through API keys.",
      diagnostics: "Run read-only, approval-gated diagnostics against this data source.",
      deploy: "Watch what this system built or released, and gate a promotion behind a person.",
    },
    compact: {
      sameConnection: "Uses the same {name} connection — set up under Intake channels.",
    },
    sections: {
      intake: {
        title: "Intake channels",
        description: "Receive support questions, bugs and feature requests through any channel your team uses.",
        hint: "Each channel can be used for intake, reviews and notifications depending on its capabilities.",
      },
      reviewNotify: {
        title: "Review & notifications",
        description: "Approve patches, request changes, and get updates without losing context.",
      },
      codeDelivery: {
        title: "Code & patch delivery",
        description: "Let agents prepare patches and ship them as pull or merge requests.",
      },
      issueTracking: {
        title: "Issue tracker mirroring",
        description: "Mirror BUG and FEATURE issues to Jira or Linear and link delivered pull requests back to the ticket.",
      },
      webhooksApi: {
        title: "Webhooks & API",
        description: "Layer your own systems on top of the same workflow engine.",
      },
      data: {
        title: "Data sources",
        description: "Read-only connectors that give the AI extra context when fixing BUG tickets. Default off; tenants opt in per integration.",
      },
      buildRun: {
        title: "Build, deploy & run",
        description: "Where your code is built, released and runs. FixControl watches these, records what happened, and asks a person before anything is promoted.",
      },
    },
    status: {
      connected: "Connected",
      not_connected: "Not connected",
      setup_required: "Setup required",
      coming_soon: "Coming soon",
      per_project: "Set up per project",
      // Health demotions — credentials exist but the integration isn't working.
      failing: "Failing",
      requires_reconnect: "Reconnect required",
    },
    /**
     * Strings for the shared `IntegrationStatusCard` (rendered on the
     * Slack and Teams settings pages). Uses generic provider-agnostic
     * wording so we don't need to fork copy per provider.
     */
    statusCard: {
      derivedStatus: {
        connected: "Connected",
        // Credentials in place, nothing verified. Never rendered as green —
        // see foldHealthIntoStatus in components/integration-status-card.tsx.
        configured: "Configured — not verified",
        sync_error: "Sync error",
        reconnect_required: "Reconnect required",
        disconnected: "Disconnected",
      },
      configuredHint: "Credentials are stored, but no successful call has confirmed this connection works. Use “Test connection” below to verify it.",
      lastDelivery: "Last delivery",
      failures: "Failures",
      lastError: "Last error",
      never: "never",
      reconnectCta: "I've reconnected — clear the warning",
      slackReconnectHelp: "Click \"Reinstall with Slack\" above to re-do the OAuth handshake. Once Slack returns a fresh token the warning clears automatically. Use this button only if you fixed the credential another way.",
      slackEnvReconnectHelp: "Rotate SLACK_BOT_TOKEN or paste a new bot token below, save, then click this button.",
      teamsReconnectHelp: "Teams doesn't use OAuth. To recover, rotate the Bot Framework App Password in the Azure portal (App registrations → your bot → Certificates & secrets), paste the new password into the form below, save, then click this button. The pin only clears when you confirm — saving credentials alone does not.",
      relativeJustNow: "just now",
      relativeSeconds: "{n}s ago",
      relativeMinutes: "{n}m ago",
      relativeHours: "{n}h ago",
      relativeDays: "{n}d ago",
    },
    health: {
      healthy: "Healthy",
      warning: "Warning",
      error: "Error",
    },
    meta: {
      usage: "{count} runs",
      deadLetter: "{count} failed deliveries",
    },
    actions: {
      connect: "Connect",
      configure: "Configure",
      completeSetup: "Complete setup",
      notifyMe: "Notify me",
      chooseProject: "Choose a project",
      notifyMeRegistered: "We'll let you know",
    },
    cards: {
      widget: {
        name: "Web widget",
        description: "Drop the FixControl widget into your product or help centre. Visitors file issues without leaving the page.",
      },
      slack: {
        name: "Slack",
        description: "Send issue and patch updates to Slack, and approve or request changes from chat.",
      },
      teams: {
        name: "Microsoft Teams",
        description: "Use Microsoft Teams for issue intake, patch reviews, and workflow notifications.",
      },
      gmail: {
        name: "Gmail",
        description: "Import support emails and turn them into issues.",
      },
      office365: {
        name: "Office 365 / Outlook",
        description: "Import Outlook support mail and Microsoft 365 context.",
      },
      emailNotifications: {
        name: "E-mail",
        description: "Receive review prompts and patch updates by e-mail through the mailboxes you've already connected.",
      },
      github: {
        name: "GitHub",
        description: "One connection, multiple capabilities: repository access, pull request delivery, and webhook events. Activate repos as codebases to let agents work on them.",
      },
      gitlab: {
        name: "GitLab",
        description: "Connect GitLab projects, push patches, and open merge requests.",
      },
      bitbucket: {
        name: "Bitbucket",
        description: "Connect Bitbucket repositories, push patches, and open pull requests.",
      },
      jira: {
        name: "Jira",
        description: "Mirror BUG and FEATURE issues to a Jira Cloud project and link delivered pull requests back to the ticket.",
      },
      linear: {
        name: "Linear",
        description: "Mirror BUG and FEATURE issues to a Linear team and attach delivered pull requests to the issue.",
      },
      freshdesk: {
        name: "Freshdesk",
        description: "Create and link Freshdesk support tickets while FixControl handles governance and remediation. Freshdesk stays the support system of record.",
      },
      odoo: {
        name: "Odoo",
        description: "Create and link Odoo Helpdesk tickets while FixControl handles governance and remediation. Odoo stays the support system of record.",
      },
      topdesk: {
        name: "TOPdesk",
        description: "Create and link TOPdesk incidents while FixControl handles governance and remediation. TOPdesk stays the service desk of record.",
      },
      outboundWebhooks: {
        name: "Outbound webhooks",
        description: "Forward FixControl events to any HTTPS endpoint your team owns.",
      },
      publicApi: {
        name: "Public API",
        description: "Mint API keys for external tools to read issues, manage patches, and configure webhooks.",
      },
      database: {
        name: "Database (read-only)",
        description: "Give the patch agent read-only access to a Postgres database to gather diagnostic context when fixing BUG tickets. Off by default; every query is approved per shape and audited.",
      },
      gcp: {
        name: "Google Cloud (read-only)",
        description: "Give the patch agent read-only access to Cloud Logging, Error Reporting and BigQuery for diagnostic context on BUG tickets. Off by default; reads are capped, secrets are masked and BigQuery scans are refused above a cost ceiling.",
      },
      "github-actions": {
        name: "GitHub Actions",
        description: "See what your GitHub workflows did to a commit, and freeze that verdict onto the approval.",
      },
      "gitlab-ci": {
        name: "GitLab CI",
        description: "See what your GitLab pipelines did to a commit, and freeze that verdict onto the approval.",
      },
      jenkins: {
        name: "Jenkins",
        description: "Take in Jenkins build and deployment events, score them for risk, and record them in the audit trail.",
      },
      "azure-devops": {
        name: "Azure DevOps",
        description: "Take in Azure Pipelines build and release events, score them for risk, and record them in the audit trail.",
      },
      argocd: {
        name: "Argo CD",
        description: "Watch your Argo rollouts, and let a paused one be promoted only after a person approves it.",
      },
      vercel: {
        name: "Vercel",
        description: "Take in Vercel deployment events so a release shows up on the timeline with everything else.",
      },
      "terraform-cloud": {
        name: "Terraform Cloud",
        description: "Take in Terraform Cloud run events so an infrastructure change is recorded like any other release.",
      },
      kubernetes: {
        name: "Kubernetes",
        description: "Connect a cluster so FixControl can read what is running there, and prove a change against it.",
      },
      "fc-agent": {
        name: "FC Agent",
        description: "A small service you run in your own environment. It calls out to FixControl, never the other way round, so FixControl can reach systems that are not open to the internet.",
      },
    },
    gmail: {
      pageTitle: "Gmail",
      pageSubtitle: "Connect a Google account so {company} agents can ingest inbound support email.",
      addHeading: "Add another Gmail account",
      emptyHeading: "Connect Gmail to import support email",
    },
    github: {
      pageTitle: "GitHub",
      pageSubtitle: "Install the FixControl GitHub App on the repositories {company} agents can patch.",
      addHeading: "Add another GitHub connection",
      emptyHeading: "Connect GitHub to enable patches and pull requests",
      capabilitiesTitle: "Capabilities",
      capabilityRepoAccess: "Repository access",
      capabilityPrDelivery: "Pull request delivery",
      capabilityWebhooks: "Webhook events",
      capabilitiesHint: "GitHub is one connection with multiple capabilities. The connection provides access; a repository becomes a codebase agents work on; PR delivery and webhooks handle status and intake.",
      manageConnection: "Manage connection",
      addRepoAsCodebase: "Add repository as codebase",
      codebasesHint: "You can also add a codebase directly from a project via Project → Add codebase.",
    },
    gitlab: {
      pageTitle: "GitLab",
      pageSubtitle: "Connect a GitLab Cloud or self-hosted instance so {company} agents can push patches and open merge requests.",
      addHeading: "Add another GitLab connection",
      emptyHeading: "Connect GitLab to enable patches and merge requests",
    },
    bitbucket: {
      pageTitle: "Bitbucket",
      pageSubtitle: "Connect a Bitbucket workspace so {company} agents can push patches and open pull requests.",
      addHeading: "Add another Bitbucket connection",
      emptyHeading: "Connect Bitbucket to enable patches and pull requests",
    },
    jira: {
      pageTitle: "Jira",
      pageSubtitle: "Connect a Jira Cloud site so {company} agents can mirror BUG and FEATURE issues and link delivered pull requests back to the ticket.",
      addHeading: "Add another Jira connection",
      emptyHeading: "Connect Jira to mirror issues and link pull requests",
      webhook: {
        heading: "Inbound webhook",
        body: "Add this URL as a Jira webhook to sync issue-status changes back to FixControl. The token in the URL authenticates your workspace — treat it like a secret and rotate it if it leaks.",
        urlLabel: "Webhook URL",
        notMinted: "No webhook token yet. Generate one to get the URL.",
        generate: "Generate webhook URL",
        rotate: "Rotate token",
        rotating: "Rotating…",
        copy: "Copy",
        copied: "Copied",
        rotateWarning: "Rotating invalidates the current URL immediately — update your Jira webhook with the new one.",
      },
    },
    linear: {
      pageTitle: "Linear",
      pageSubtitle: "Connect a Linear workspace so {company} agents can mirror BUG and FEATURE issues and attach delivered pull requests.",
      addHeading: "Add another Linear connection",
      emptyHeading: "Connect Linear to mirror issues and attach pull requests",
    },
    freshdesk: {
      pageTitle: "Freshdesk",
      pageSubtitle: "Connect a Freshdesk domain so {company} can create and link support tickets while FixControl handles governance and remediation.",
      addHeading: "Add another Freshdesk connection",
      emptyHeading: "Connect Freshdesk to create and link support tickets",
      behaviorHeading: "Behaviour",
      autoCreateLabel: "Automatically create a Freshdesk ticket on Slack intake",
      autoCreateHint: "On: a report via Slack immediately creates a linked Freshdesk ticket. Off (default): the ticket is only created when someone deliberately chooses to — via the button on the intake card or approval-gated.",
      sync: {
        intakeHeading: "Intake",
        inboundHeading: "Inbound sync — Freshdesk informs FixControl",
        inboundIntro: "Everything below is read-direction or data-only: a support event can update issue data or notify a human, but never drives the governed workflow or runs agents. All off by default.",
        outboundHeading: "Outbound sync — FixControl updates the ticket",
        outboundIntro: "Pushes are best-effort and one-way: a Freshdesk hiccup never blocks a FixControl decision, status pushes never close the ticket on support's behalf, and the customer reply only ever goes out after human approval.",
        autoCreateLabel: "Automatically create a Freshdesk ticket on Slack intake",
        autoCreateHint: "On: a report via Slack immediately creates a linked Freshdesk ticket. Off (default): the ticket is only created when someone deliberately chooses to — via the button on the intake card or approval-gated.",
        ingestConversationLabel: "Feed the ticket conversation into the AI context",
        ingestConversationHint: "The linked ticket's recent customer replies and agent notes (emails and phone numbers redacted) appear as context on the issue, so the agent understands symptoms and what support already tried.",
        priorityInboundLabel: "Sync ticket priority onto the issue",
        priorityInboundHint: "A priority change in Freshdesk updates the FixControl issue's priority. Data-only — it influences routing, never execution.",
        tagsInboundLabel: "Map ticket tags to issue flags (allowlist only)",
        tagsInboundHint: "Only tag=FLAG pairs you list below ever map. A support-side tag can never set a governance flag you didn't explicitly allow.",
        tagAllowlistLabel: "Tag → flag allowlist (comma-separated tag=FLAG pairs)",
        tagAllowlistPlaceholder: "vip=VIP, urgent-customer=ESCALATED",
        csatResurfaceLabel: "Re-surface on negative customer satisfaction",
        csatResurfaceHint: "A negative CSAT rating on the linked ticket notifies your managers to review whether the fix actually landed for the customer.",
        mergeInboundLabel: "Mark merged tickets as duplicates",
        mergeInboundHint: "When support merges the linked ticket into another, the FixControl issue is marked as a duplicate of the primary ticket's issue (same tenant only).",
        assignmentInboundLabel: "Sync ticket assignment onto the issue",
        assignmentInboundHint: "An explicit responder email becomes the issue assignee; otherwise your group → assignee map below decides. Unmapped groups do nothing.",
        groupMapLabel: "Group → assignee map (comma-separated groupId=assignee pairs)",
        groupMapPlaceholder: "42=ops@acme.com, 7=platform@acme.com",
        slaResurfaceLabel: "Re-surface on (approaching) SLA breach",
        slaResurfaceHint: "An SLA-breach signal from Freshdesk notifies your managers — like reopen: it asks a human to look, it never re-executes anything.",
        captureLearningLabel: "Capture resolved tickets as project learning",
        captureLearningHint: "When support resolves the linked ticket, the case is stored as project-scoped knowledge (PII-redacted) so future work on the same project can surface it. Tickets without a project are never captured.",
        progressNotesLabel: "Post mission progress as private notes",
        progressNotesHint: "Plan approved, patch ready, fix applied and deployment approved appear as agent-only notes on the linked ticket (with a FixControl deep-link and deploy changelog). Never visible to the customer.",
        statusPushLabel: "Move the ticket to Pending when the issue resolves",
        statusPushHint: "Resolving the FixControl issue sets the ticket to Pending (awaiting customer). Never Resolved/Closed — support owns closing the loop; tickets support already settled are left alone.",
        regressionReopenLabel: "Reopen the ticket when a shipped fix regresses",
        regressionReopenHint: "A rollback or failed production deployment of the fix reopens the linked ticket with a private note, so support knows the customer's issue is not resolved after all.",
        priorityEscalationLabel: "Raise ticket priority on critical escalation",
        priorityEscalationHint: "When the issue escalates (priority critical, or a DATALOSS/SECURITY governance flag), the ticket is raised to Urgent with a private note. Raise-only — never lowers a priority.",
        customerReplyLabel: "Draft a customer reply when the issue resolves",
        customerReplyHint: "Resolving queues a reply draft in the communications review queue. Nothing is ever posted automatically: a human approves (and usually edits) the draft, and only then is it posted as a public reply.",
      },
      autoCreateLoadError: "Could not load the setting.",
      autoCreateSaveError: "Could not save the setting.",
    },
    odoo: {
      pageTitle: "Odoo",
      pageSubtitle: "Connect an Odoo instance so {company} can create and link Helpdesk tickets while FixControl handles governance and remediation.",
      addHeading: "Add another Odoo connection",
      emptyHeading: "Connect Odoo to create and link helpdesk tickets",
      settingsLoadError: "Could not load the settings.",
      settingsSaveError: "Could not save the setting.",
      sync: {
        intakeHeading: "Intake",
        inboundHeading: "Inbound sync — Odoo informs FixControl",
        inboundIntro: "Read-direction only: a support event can add context for the AI, but never drives the governed workflow or runs agents. Off by default.",
        outboundHeading: "Outbound sync — FixControl updates the ticket",
        outboundIntro: "Pushes are best-effort and one-way: an Odoo hiccup never blocks a FixControl decision, the stage push only moves tickets to a stage you name yourself, and the customer reply only ever goes out after human approval.",
        ingestConversationLabel: "Feed the ticket conversation into the AI context",
        ingestConversationHint: "The linked ticket's recent chatter (emails and phone numbers redacted; internal notes marked as notes) appears as context on the issue, so the agent understands symptoms and what support already tried.",
        captureLearningLabel: "Capture settled tickets as project learning",
        captureLearningHint: "When the linked ticket reaches a settled stage, the case is stored as project-scoped knowledge (PII-redacted) so future work on the same project can surface it. Tickets without a project are never captured.",
        autoCreateLabel: "Automatically create an Odoo ticket on Slack intake",
        autoCreateHint: "On: a report via Slack immediately creates a linked Odoo ticket. Off (default): the ticket is only created when someone deliberately chooses to — via the button on the intake card or approval-gated.",
        progressNotesLabel: "Post mission progress as internal notes",
        progressNotesHint: "Patch ready, PR opened and fix applied appear as internal chatter notes on the linked ticket. Never visible to the customer.",
        statusPushLabel: "Move the ticket to a stage you name when the issue resolves",
        statusPushHint: "Resolving the FixControl issue moves the ticket to the stage below (matched against the ticket's team stages). With no stage named nothing is pushed; tickets support already settled are left alone.",
        resolveStageLabel: "Target stage name",
        resolveStagePlaceholder: "Awaiting confirmation",
        customerReplyLabel: "Draft a customer reply when the issue resolves",
        customerReplyHint: "Resolving queues a reply draft in the communications review queue. Nothing is ever posted automatically: a human approves (and usually edits) the draft, and only then is it posted as a customer-visible message.",
      },
    },
    topdesk: {
      pageTitle: "TOPdesk",
      pageSubtitle: "Connect a TOPdesk environment so {company} can create and link incidents while FixControl handles governance and remediation.",
      addHeading: "Add another TOPdesk connection",
      emptyHeading: "Connect TOPdesk to create and link incidents",
      settingsLoadError: "Could not load the settings.",
      settingsSaveError: "Could not save the setting.",
      sync: {
        intakeHeading: "Intake",
        inboundHeading: "Inbound sync — TOPdesk informs FixControl",
        inboundIntro: "Read-direction only: a service desk event can add context for the AI, but never drives the governed workflow or runs agents. Off by default.",
        outboundHeading: "Outbound sync — FixControl updates the incident",
        outboundIntro: "Pushes are best-effort and one-way: a TOPdesk hiccup never blocks a FixControl decision, the status push only uses a status you name yourself, and the reply to the caller only ever goes out after human approval.",
        ingestConversationLabel: "Feed the incident's actions into the AI context",
        ingestConversationHint: "The linked incident's recent actions (emails and phone numbers redacted; actions hidden from the caller marked as internal) appear as context on the issue, so the agent understands the symptoms and what the service desk already tried.",
        captureLearningLabel: "Capture settled incidents as project learning",
        captureLearningHint: "When the linked incident is completed or closed, the case is stored as project-scoped knowledge (PII-redacted) so future work on the same project can surface it. Incidents without a project are never captured.",
        autoCreateLabel: "Automatically create a TOPdesk incident on Slack intake",
        autoCreateHint: "On: a report via Slack immediately creates a linked TOPdesk incident. Off (default): the incident is only created when someone deliberately chooses to — via the button on the intake card or approval-gated.",
        progressNotesLabel: "Post mission progress as internal actions",
        progressNotesHint: "Patch ready, PR opened and fix applied are added to the linked incident as actions marked invisible to the caller. Never visible to the customer.",
        statusPushLabel: "Complete the incident when the issue resolves",
        statusPushHint: "Resolving the FixControl issue marks the linked incident completed. Name a processing status below and that status is set too — status names are yours to define in TOPdesk, so nothing is guessed. Leave it empty and the incident is only marked completed; incidents the service desk already settled are left alone.",
        resolveStatusLabel: "Processing status name",
        resolveStatusPlaceholder: "Awaiting confirmation",
        customerReplyLabel: "Draft a reply to the caller when the issue resolves",
        customerReplyHint: "Resolving queues a reply draft in the communications review queue. Nothing is ever posted automatically: a human approves (and usually edits) the draft, and only then is it added to the incident as an action the caller can see.",
      },
    },
    slack: {
      pageTitle: "Slack",
      pageSubtitle: "Send issues, patches, and decisions into Slack. Connect a workspace, paste a bot token + signing secret, and try /fix from any channel where the app is installed.",
      statusTitle: "Status",
      healthTitle: "Delivery status",
      statusConnected: "Connected",
      statusTeamSuffix: "— team {team}",
      statusConfiguredMissingCreds: "Configured but missing credentials",
      statusNotConnected: "Not connected",
      reinstallCta: "Reinstall with Slack",
      connectCta: "Connect with Slack",
      oauthHint: "Recommended — Slack issues the bot token directly to FixControl.",
      envFallbackPrefix: "Environment fallback active:",
      envFallbackTeamSuffix: "team {team}",
      connectionTitle: "Connection",
      connectionSub: "Slack workspace identifiers and credentials. Tokens are stored encrypted; an empty field means leave the existing value untouched.",
      fields: {
        teamId: "Slack team ID",
        teamIdHint: "The team_id Slack sends with every request. Visible in your Slack URL or by running /fix and inspecting the request.",
        defaultChannel: "Default channel ID",
        defaultChannelHint: "Channel ID (not name) where notifications land when no thread mapping exists.",
        botToken: "Bot token",
        botTokenHint: "From your Slack app → OAuth & Permissions → Bot User OAuth Token.",
        signingSecret: "Signing secret",
        signingSecretHint: "From your Slack app → Basic Information → Signing Secret. Used to verify every inbound request.",
        storedPlaceholder: "•••••• (stored)",
      },
      saving: "Saving…",
      saved: "Saved",
      sending: "Sending…",
      sendTest: "Send test",
      sendTestTitleReady: "Post a test notification to the default channel",
      sendTestTitleUnready: "Save credentials first",
      disable: "Disable",
      disableConfirm: "Disable Slack integration? Notifications will stop until you re-enable it.",
      testSent: "Sent — check the Slack channel.",
      testFailed: "Failed: {reason}",
      testNetworkError: "Network error.",
      appSetupTitle: "Slack app setup",
      appSetupSub: "Configure these endpoints in your Slack app. All three must be reachable from Slack's servers — use a Cloudflare Tunnel or similar in dev.",
      endpoints: {
        commands: "Slash command request URL",
        actions: "Interactivity request URL",
        events: "Events API request URL",
      },
      scopesPrefix: "Required bot scopes:",
      slashCommandPrefix: "The slash command should be",
    },
    teams: {
      pageTitle: "Microsoft Teams",
      pageSubtitle: "Send issues, patches, and decisions into Teams via the Bot Framework. Configure your Entra tenant + bot credentials and pick a default channel.",
      statusTitle: "Status",
      statusConnected: "Connected",
      statusTenantSuffix: "— tenant {tenant}",
      statusConfiguredMissingCreds: "Configured but missing credentials",
      statusNotConnected: "Not connected",
      envFallbackPrefix: "Environment fallback active:",
      devInsecureWarning: "Dev insecure mode is on — request signature verification is bypassed. Never enable this in production.",
      connectionTitle: "Connection",
      connectionSub: "Bot Framework credentials and the Teams conversation to post into. Secrets are stored encrypted; leave a field blank to keep the stored value.",
      fields: {
        tenantId: "Microsoft Entra tenant ID",
        tenantIdHint: "The Azure AD tenant id that owns the bot registration.",
        conversation: "Default conversation/channel ID",
        conversationHint: "Where notifications land when no thread mapping exists. Looks like 19:…@thread.tacv2.",
        serviceUrl: "Service URL",
        serviceUrlHint: "Bot Framework regional endpoint, e.g. https://smba.trafficmanager.net/emea/.",
        botAppId: "Bot App ID",
        botAppIdHint: "Bot Framework MicrosoftAppId from the Azure Bot resource.",
        botAppPassword: "Bot App password",
        botAppPasswordHint: "Bot Framework client secret. Stored encrypted.",
        storedPlaceholder: "•••••• (stored)",
      },
      saving: "Saving…",
      saved: "Saved",
      sending: "Sending…",
      sendTest: "Send test",
      sendTestTitleReady: "Post a test notification to the default conversation",
      sendTestTitleUnready: "Save credentials first",
      disable: "Disable",
      disableConfirm: "Disable Teams integration? Notifications will stop until you re-enable it.",
      testSent: "Sent — check the Teams channel.",
      testFailed: "Failed: {reason}",
      testNetworkError: "Network error.",
      appSetupTitle: "Bot Framework setup",
      appSetupSub: "What Microsoft needs from you to deliver messages.",
      appSetupBody: "Register an Azure Bot resource, install the manifest in your Teams tenant, and point the Messaging endpoint at /api/integrations/teams. Copy the App ID and a client secret into the fields above.",
      wizard: {
        title: "Connection wizard",
        subtitle: "Follow the steps to connect Teams. Each step checks itself off once it's done.",
        healthTitle: "Delivery status",
        connectedBanner: "Teams is connected — notifications and gate approvals can now flow to your channel.",
        stepDone: "Done",
        stepNow: "Now",
        stepTodo: "To do",
        nextStep: "Done — next step",
        open: "Open",
        copy: "Copy endpoint",
        copied: "Copied",
        serviceUrlInvalid: "This doesn't look like a URL — it should start with https://",
        modeQuestion: "How do you want to connect?",
        modeShared: "FixControl's Teams app",
        modeSharedHint: "Recommended — no Azure setup",
        modeOwn: "Your own Azure bot",
        modeOwnHint: "Enterprise — full control",
        sharedUnavailable: "FixControl hasn't configured a shared Teams app on this server yet. Use your own bot below, or ask your FixControl admin to set TEAMS_BOT_APP_ID and TEAMS_BOT_APP_PASSWORD.",
        code: {
          label: "Connection code from the bot",
          hint: "Type \"link\" to the FixControl bot in your Teams channel, then paste the code it replies with — no tenant id to look up.",
          button: "Connect with code",
          linking: "Connecting…",
          ok: "Connected — your Teams workspace and channel are now linked.",
          failed: "Couldn't connect: {reason}",
          orManual: "No code? Enter the details by hand below.",
          reasonExpired: "the code expired — type \"link\" again for a fresh one",
          reasonInvalid: "the code wasn't recognised — type \"link\" again in Teams",
          reasonAlreadyLinked: "this Teams workspace is already linked to another FixControl workspace",
          connectedTitle: "Workspace connected",
          connectedBody: "Your Teams workspace is connected — no code needed here. From now on, typing \"link\" in Teams links each person's individual account (a different thing): they type \"link\" to the FixControl bot and confirm in the browser, which lets them approve gates from Teams.",
          editManual: "Change the tenant / default channel manually",
        },
        pkg: {
          title: "Download the Teams app",
          hint: "Hand this package to a Teams admin to install the FixControl app in your workspace.",
          download: "Download Teams app package",
          uploadTitle: "How to install it in Teams",
          step1: "In Teams: Apps → Manage your apps → Upload an app.",
          step2: "Choose \"Upload a custom app\" and pick the downloaded .zip.",
          step3: "Add it to the channel for notifications, then type \"link\" to connect.",
          unavailable: "The app package isn't configured on this server yet (TEAMS_APP_ID missing).",
        },
        steps: {
          sharedIntroTitle: "You're using FixControl's Teams app",
          sharedIntroBody: "No Azure setup needed. FixControl operates a shared, verified Teams bot — you only install it in your workspace and pick a channel.",
          sharedInstallTitle: "Install the app and pick a channel",
          sharedInstallBody: "Install the FixControl app in your Teams tenant and @-mention the bot once in the channel where notifications should land — FixControl captures your tenant ID, conversation ID and service URL automatically. If anything is missing, fill it in below.",
          prepTitle: "Before you start",
          prepBody: "You'll link FixControl to Teams through an Azure Bot. You need two things: access to your organisation's Azure / Entra portal, and a Teams channel where notifications may land. Allow about 10 minutes.",
          createBotTitle: "Create an Azure Bot",
          createBotBody: "In the Azure portal, create an 'Azure Bot' resource (Multi Tenant is fine). Azure creates an app registration for you. Write down two values: the App ID (MicrosoftAppId) and your Entra tenant ID — you'll paste them in step 5.",
          createBotLinkBot: "Create Azure Bot",
          createBotLinkEntra: "App registrations (your tenant ID is here)",
          secretTitle: "Create a client secret",
          secretBody: "Open your app registration → Certificates & secrets → New client secret (pick 12+ months). Copy the Value immediately — it's hidden after you leave the page. This is the Bot App password.",
          secretLink: "Open app registrations",
          endpointTitle: "Set the messaging endpoint",
          endpointBody: "In your Azure Bot → Configuration → Messaging endpoint, paste the URL below. Microsoft must be able to reach it from the internet.",
          endpointLocalNote: "Running locally? Put a public tunnel (e.g. ngrok or a VS Code dev tunnel) in front and use that URL instead. For local testing without signature verification you can set TEAMS_DEV_INSECURE_WEBHOOKS=true — never in production.",
          credentialsTitle: "Enter the credentials in FixControl",
          credentialsBody: "Paste the tenant ID, App ID and client secret from Azure, then save. Secrets are stored encrypted.",
          installTitle: "Add the bot in Teams and pick a channel",
          installBody: "In your Azure Bot → Channels, add 'Microsoft Teams'. Install the bot in your team and @-mention it once in the channel where notifications should land — FixControl then captures the conversation ID and service URL automatically. If that doesn't work, paste them below by hand.",
          testTitle: "Test the connection",
          testBody: "Send a test notification to the chosen channel. If it shows up in Teams, you're connected.",
        },
      },
    },
    office365: {
      pageTitle: "Office 365 / Outlook",
      pageSubtitle: "Import support emails from Outlook and turn them into issues.",
      statusTitle: "Status",
      statusConnected: "Connected",
      statusAccountSuffix: "— {email}",
      statusDisabled: "Disabled — reconnect to resume",
      statusNotConnected: "Not connected",
      connectionsCount: "{count} account(s) connected",
      connectAnother: "Connect another account",
      oauthMissing: "Microsoft OAuth is not configured on this server. Set MICROSOFT_OAUTH_CLIENT_ID, MICROSOFT_OAUTH_CLIENT_SECRET, and MICROSOFT_OAUTH_REDIRECT_URI before connecting.",
      envClientId: "Client ID:",
      envRedirectUri: "Redirect URI:",
      envTenant: "Tenant:",
      envScopes: "Scopes:",
      connect: "Connect with Microsoft",
      reconnect: "Reconnect Microsoft",
      disable: "Disable",
      disableConfirm: "Disable Office 365 integration? Email intake will stop until you reconnect.",
      connectOk: "Microsoft account connected.",
      connectErr: "Connect failed: {reason}",
      mailboxTitle: "Mailbox",
      mailboxSub: "The Outlook mailbox we ingest from. Defaults to the connected user; switch to a shared mailbox UPN if the connected account has delegate access.",
      fields: {
        mailbox: "Mailbox address",
        mailboxHint: "Email address (UPN) of the mailbox to read. Use a shared mailbox UPN when delegated.",
      },
      saving: "Saving…",
      testing: "Testing…",
      testConnection: "Test connection",
      testOk: "Connected as {email}, mailbox {mailbox}.",
      testFailed: "Failed: {reason}",
      testNetworkError: "Network error.",
      recentTitle: "Recent messages",
      recentSub: "The last few emails Microsoft Graph would deliver. Click Import to turn one into an issue.",
      loadRecent: "Load recent messages",
      recentEmpty: "No messages returned. (Empty mailbox or filter applied.)",
      noSubject: "(no subject)",
      import: "Import",
      importQueued: "Imported into the inbox. Intake decides whether it becomes an issue.",
      importDeduped: "Already imported. No duplicate created.",
      importFailed: "Import failed: {reason}",
      syncTitle: "Sync inbox",
      syncSub: "Pull the last 10 messages from the connected mailbox and run them through intake. Same email never produces two issues.",
      syncNow: "Sync now",
      syncing: "Syncing…",
      syncDone: "Fetched {fetched}: {queued} new in the inbox (intake decides issue or support), {duplicates} already imported.",
      syncFailed: "Sync failed: {reason}",
      syncNetworkError: "Network error during sync.",
      liveSyncTitle: "Live sync (Microsoft Graph)",
      liveSyncSub: "When live sync is on, FixControl receives a Microsoft Graph notification each time a new email lands in the connected mailbox and processes it through intake immediately. Subscriptions expire every ~3 days and are renewed automatically.",
      liveSyncEnabled: "Live sync is on",
      liveSyncDisabled: "Live sync is off",
      liveSyncSubscriptionId: "Subscription ID:",
      liveSyncExpiresAt: "Expires at:",
      liveSyncLastWebhookAt: "Last webhook delivered:",
      liveSyncLastError: "Last delivery issue: {reason}",
      liveSyncEnable: "Enable live sync",
      liveSyncEnabling: "Enabling…",
      liveSyncEnableOk: "Live sync enabled.",
      liveSyncEnableFailed: "Could not enable live sync: {reason}",
      liveSyncDisable: "Disable live sync",
      liveSyncDisabling: "Disabling…",
      liveSyncDisableOk: "Live sync disabled.",
      liveSyncDisableFailed: "Could not disable live sync: {reason}",
      liveSyncDisableConfirm: "Disable live sync? Outlook emails will only be picked up by manual Sync until you re-enable it.",
      liveSyncRenew: "Renew now",
      liveSyncRenewing: "Renewing…",
      liveSyncRenewOk: "Subscription renewed.",
      liveSyncRenewFailed: "Could not renew: {reason}",
      liveSyncNetworkError: "Network error.",
      scopesTitle: "Microsoft Graph scopes",
      scopesSub: "Granted at consent time. Least-privilege MVP — no send, no write.",
      scopesNote: "Adding scopes requires a re-consent: disconnect and reconnect once new scopes ship.",
      // Derived-status banner copy. Driven by the GET /api/settings/office365
      // `derivedStatus` field; one banner at a time.
      derivedConnected: "Office365 is configured. Live capability health is shown below.",
      derivedRenewalExpiring: "Live sync subscription expires soon. Renewal should run automatically.",
      derivedRenewalFailed: "Live sync renewal failed repeatedly. Check cron and Microsoft permissions.",
      derivedSyncError: "Mail sync encountered an error. FixControl will retry where possible.",
      derivedReconnectRequired: "Reconnect your Microsoft account to restore sync.",
      derivedDisconnected: "Connect Office365 to import support emails.",
      // Operability rows on the status panel.
      opLastSyncAt: "Last sync:",
      opLastSuccessfulRenewal: "Last successful renewal:",
      opSubscriptionExpiry: "Subscription expires:",
      opRenewalFailedCount: "Renewal failures (consecutive):",
      opWebhookFailedCount: "Webhook deliveries given up:",
      opLastError: "Last error: {reason}",
      opLastErrorAt: "(at {at})",
      opWebhookUrl: "Webhook URL:",
      opNever: "never",
    },
  },

  settingsWebhooks: {
    eyebrow: "Settings · Integrations",
    title: "Outbound webhooks",
    subtitle: "Forward FixControl events to any HTTPS endpoint your team owns.",
    addWebhook: "Add webhook",
    noWebhooks: "No webhooks configured yet.",
    noWebhooksHint: "Click \"Add webhook\" to forward events to a teammate's endpoint.",
    table: {
      name: "Name",
      url: "URL",
      events: "Events",
      enabled: "Enabled",
      created: "Created",
      actions: "Actions",
      secret: "Secret",
    },
    actions: {
      edit: "Edit",
      delete: "Delete",
      test: "Send test",
      viewLog: "View deliveries",
      enable: "Enable",
      disable: "Disable",
    },
    create: {
      title: "Create outbound webhook",
      sub: "Pick which events to forward and where.",
      name: "Name",
      namePlaceholder: "PagerDuty bridge",
      url: "Endpoint URL",
      urlPlaceholder: "https://your-app.example.com/fixcontrol-webhook",
      urlHint: "Must accept POST. Use HTTPS in production.",
      events: "Events",
      eventsHint: "Pick at least one event type.",
      cancel: "Cancel",
      save: "Create webhook",
      saving: "Creating…",
    },
    edit: {
      title: "Edit webhook",
      save: "Save changes",
      saving: "Saving…",
    },
    confirmDelete: "Delete this webhook? Past delivery logs are kept for audit.",
    secretModal: {
      title: "Copy your webhook secret",
      sub: "FixControl shows this exactly once. Store it securely — there is no recovery.",
      copy: "Copy secret",
      copied: "Copied",
      done: "I've saved it",
      preview: "Preview",
    },
    deliveries: {
      title: "Recent deliveries",
      empty: "No deliveries yet.",
      event: "Event",
      status: "Status",
      code: "Code",
      attempts: "Attempts",
      error: "Error",
      time: "Time",
      requestId: "Request ID",
    },
    status: {
      pending: "Pending",
      retrying: "Retrying",
      success: "Success",
      failed: "Failed",
      gave_up: "Gave up",
    },
    events: {
      "issue.created": "Issue created",
      "issue.updated": "Issue updated",
      "patch.ready": "Patch ready",
      "patch.approved": "Patch approved",
      "patch.failed": "Patch failed",
      "pr.created": "Pull request opened",
      "integration.error": "Integration error",
    },
    errors: {
      missingTenant: "Pick a workspace first.",
      loadFailed: "Could not load webhooks.",
      saveFailed: "Could not save webhook.",
      testFailed: "Test delivery failed to enqueue.",
      deleteFailed: "Could not delete webhook.",
    },
    testDispatched: "Test delivery enqueued — open the deliveries log to confirm.",
  },

  settingsAudit: {
    eyebrow: "Settings",
    title: "Audit log",
    subtitle: "Security-sensitive actions — who did what, when, and with what result",
    fromMission: "Mission",
    exportJson: "Export JSON",
    exportTitle: "Download the currently visible rows as JSON",
    refresh: "Refresh",
    accessRequired: "Audit access required",
    accessRequiredDetail: "Only admins and managers can view the audit log.",
    failedToLoad: "Failed to load audit log",
    eventPlaceholder: "Exact event, e.g. patch.applied",
    searchPlaceholder: "Search…",
    sinceLast1h: "Last 1h",
    sinceLast24h: "Last 24h",
    sinceLast7d: "Last 7d",
    sinceLast30d: "Last 30d",
    sinceAllTime: "All time",
    byEvent: "By event",
    headers: {
      time: "Time",
      event: "Event",
      user: "User",
      tenant: "Tenant",
      target: "Target",
      fields: "Fields",
    },
    noRows: "No rows",
    bulk: {
      exportSelected: "Export selected",
    },
    groups: {
      all: "All",
      auth: "Auth",
      governance: "Governance",
      workflow: "Workflow",
      orchestration: "Orchestration",
      devops: "DevOps",
      patch: "Patch",
      secrets: "Secrets",
      settings: "Settings",
      user: "User",
      agent: "Agent",
      codebase: "Codebase",
      // Retained for older bookmarks / spooled rows that used these prefixes.
      connection: "Connection",
      infra: "Infra",
      runFix: "Run-fix",
      maintenance: "Maintenance",
      cli: "CLI",
    },
    drawer: {
      eyebrow: "Audit event",
      userId: "User id",
      tenant: "Tenant",
      target: "Target",
      auditId: "Audit id",
      summary: "Summary",
      fields: "Fields",
      closeAria: "Close",
    },
    spool: {
      header: "Queued for retry",
      refresh: "Refresh",
      databaseSpool: "Database spool",
      webhookPrefix: "Webhook: {sink}",
      eventCount: "{count} event",
      eventCountPlural: "{count} events",
      oldest: "oldest {ts}",
      newest: " — newest {ts}",
    },
    pills: {
      outcome: "Outcome",
      action: "Action",
      newStatus: "New status",
      workspace: "Workspace",
      hasNote: "Has note",
      mode: "Mode",
      pr: "PR",
      mr: "MR",
      branch: "Branch",
      files: "Files",
      path: "Path",
      overall: "Overall",
      patchCount: "Patch count",
      changedFields: "Changed fields",
      rotatedSecrets: "Rotated secrets",
      fingerprint: "Fingerprint",
      carriedKeys: "Carried keys",
      applies: "Applies",
      onRestart: "on restart",
      now: "now",
      runsRetention: "Runs retention",
      auditRetention: "Audit retention",
      runsDeleted: "Runs deleted",
      auditDeleted: "Audit deleted",
      pass: "pass",
      fail: "fail",
      yes: "yes",
      no: "no",
      provider: "Provider",
      kind: "Kind",
      account: "Account",
      decision: "Decision",
      channel: "Channel",
      gateType: "Gate",
      riskLevel: "Risk",
      fromState: "From",
      toState: "To",
      reason: "Reason",
    },
  },

  // The sandbox preview gateway's own refusal pages
  // (src/app/api/preview/[previewId]/[[...path]]/route.ts). Rendered only for
  // a top-level document request; an API client still gets JSON. Kept short
  // and free of machinery: the reader clicked "Open preview", they are not
  // reading a log.
  previewGateway: {
    pending: {
      title: "The environment is starting",
      body: "Pulling images, starting services, waiting for the application to answer.",
      /** Under the spinner: why the page moves on its own. */
      retrying: "This usually takes about half a minute. This page refreshes itself and shows the application as soon as it is running.",
      /** Same place, once the automatic refresh has stopped. */
      retryStopped: "This is taking longer than expected. We have stopped refreshing automatically.",
      retryNow: "Try again now",
    },
    ended: {
      title: "This preview has ended",
      body: "A preview is a temporary validation environment and it has now been stopped. Open a new one from the project's Code & environments section.",
    },
    unavailable: {
      title: "This preview is not reachable",
      body: "The temporary validation environment cannot be reached right now. Try again in a moment, or open a new preview from the project.",
    },
    stopped: {
      title: "This preview is no longer running",
      body: "The application in this temporary validation environment has stopped. Open a new preview from the project's Code & environments section.",
    },
    /** One page for every authorisation refusal — a missing session, another
     *  tenant's preview and one that never existed must read identically. */
    unauthorized: {
      title: "Open this preview from FixControl",
      body: "A preview is tied to your FixControl session, and the sign-in for this address is short-lived. Open the preview again from the sandbox run it belongs to.",
    },
  },
  // Where a project's work is allowed to happen, and the refusals that follow
  // from it. The server states these rules in its own vocabulary (enum values,
  // a policy hash, a column name) because that text goes in the audit record;
  // these are the same facts said once, plainly, to the person reading them.
  // Codes are the closed lists in src/lib/refusal-codes.ts.
  residency: {
    refusal: {
      residency_execution_downgrade:
        "This project is set to run validation in your own environment, but the validation this needs would run on FixControl's own infrastructure. Nothing was moved or run. Either set the project's validation to run in your own environment, or remove the requirement.",
      residency_runner_unavailable:
        "This project is set to run validation in your own environment, and no runner in your environment is available right now. Nothing was run. Bring the FixControl agent back online and try again.",
      strict_source_residency_unavailable:
        "Keeping source code entirely inside your own environment is not available yet. Running validation in your own environment is available today.",
    },
    section: {
      title: "Where this project runs",
      loadError: "Couldn't load where this project is allowed to run.",
      none: "No location requirement set — this project runs on FixControl-managed infrastructure.",
      execution: "Code execution",
      aiProcessing: "AI processing",
      executionCustomer: "Your environment",
      executionHosted: "FixControl-managed",
      aiHosted: "FixControl-managed",
      aiCustomerProvider: "Your own AI provider",
      aiCustomerLocal: "A model inside your environment",
      readOnly: "Agreed with your team during onboarding. It is not editable here.",
      docsCta: "How validation runs",
    },
  },
  // The three validation strategies, named for the person choosing one. The
  // stored values (docker / remote_kubernetes / docker_then_remote) are what a
  // run cites and never change; see src/lib/validation-strategy-ui.ts.
  validationStrategy: {
    label: {
      docker: "Isolated Docker",
      remote_kubernetes: "Your own Kubernetes environment",
      docker_then_remote: "Docker first, then your environment",
      /** A value this build does not know. Framed, never printed bare. */
      other: "Custom setup ({value})",
    },
    explainer: {
      docker: "Fast, isolated validation on FixControl-managed infrastructure.",
      remote_kubernetes: "Validate against the environment the change will actually ship to.",
      docker_then_remote: "The isolated run happens first; only after it passes does the run in your own environment start. The gate then requires both.",
    },
  },
  projectControl: {
    loadError: "Couldn't load this project's overview.",
    retry: "Retry",
    saveError: "Couldn't save your changes ({status}). Nothing was changed.",
    deleteError: "Couldn't delete this project ({status}).",
    nav: {
      /** Accessible name for the section rail on a project page — the app
       *  sidebar is also a <nav>, and two unnamed navigation landmarks are
       *  indistinguishable to anyone moving by landmark. */
      sectionsLabel: "Project sections",
      overview: "Setup & status",
      intake: "Incoming work",
      code: "Code & environments",
      testEnvironments: "Environments",
      knowledge: "Knowledge",
      updates: "Updates & notifications",
      team: "Members",
      automation: "AI team",
      activity: "Activity",
      advanced: "Advanced",
      connections: "Connections",
      governance: "Approvals & rules",
      communication: "Customer communication",
      websiteChat: "Chat",
      /** Only rendered for projects with HR governance switched on
       *  (hr_project_settings.enabled) — see project-control-center.tsx. */
      hr: "HR governance",
      /** The two groups in the rail. "Part of this project" are the sections
       *  this project's active use cases declare; the rest stays reachable
       *  under "Also available". */
      groups: {
        relevant: "Work",
        additional: "Also available",
        pages: "Separate pages",
      },
      /** One line under each rail entry: eleven bare nouns ask a new customer
       *  to guess where something lives. */
      hint: {
        overview: "Is this project ready, and what to do next.",
        intake: "Where new questions and requests come from.",
        issues: "This project's issues.",
        missions: "What the AI is working on for this project.",
        patches: "Proposed code changes waiting for a decision.",
        workspaces: "The sessions where the AI works on this code.",
        websiteChat: "Chat on your website, or internal for colleagues.",
        code: "The repositories this project works on.",
        testEnvironments: "Where changes are tried safely before anyone approves them.",
        knowledge: "Documentation the AI draws on.",
        updates: "Where progress is reported back to.",
        team: "Who works on this project, and in which role.",
        automation: "What the AI may do on its own here.",
        activity: "What has happened.",
        advanced: "Technical details and troubleshooting.",
        connections: "Email, chat, support software and code this project uses.",
        governance: "What needs a person's approval on this project.",
        communication: "How replies to customers sound, look and are signed.",
      },
    },
    unsaved: {
      /** The form is spread over several pages, so the bar has to say WHERE
       *  the edit is — otherwise finding it means visiting all of them. */
      messageOn: "Unsaved changes in: {sections}.",
      revert: "Revert",
      saved: "Changes saved.",
      saveHint: "Changes only take effect once you save them with “Save changes” at the bottom.",
      sectionHint: "This page holds changes that have not been saved yet.",
    },
    /** A section this project does not have (or this actor may not see) is
     *  now a URL somebody can type. */
    sectionUnavailable: {
      title: "This part doesn't apply to this project.",
      body: "This page is not part of this kind of project. Everything else stays available.",
      noPermissionTitle: "You can't open this part of the project.",
      noPermissionBody: "Your role on this project doesn't include it. An administrator of this project can give you access.",
      back: "Back to the overview",
    },
    team: {
      title: "Team access",
      subtitle: "Members see this project's issues in the app and its internal ticket details in Slack and Teams answers. Admins and managers always have organization-wide access; operators only see projects they're a member of and tickets assigned to or created by them.",
      empty: "No members yet.",
      emptyHint: "Operators won't see this project's issues (in the app or in chat answers) until you add them. Admins and managers always have access.",
      emailPlaceholder: "colleague@company.com",
      add: "Add member",
      remove: "Remove member",
      userNotFound: "No active user with email {email} in this organization. Invite colleagues via Settings → Users first; then you can add them here.",
      addFailed: "Couldn't add this member. Nothing was changed.",
      removeConfirmTitle: "Remove {name} from this project?",
      removeConfirm: "They will no longer see this project's issues and ticket details.",
      removeFailed: "Couldn't remove this member. Nothing was changed.",
      loadError: "Couldn't load the member list.",
      manageUsers: "Manage or invite users",
    },
    status: {
      connected: "Connected",
      degraded: "Needs attention",
      notConfigured: "Switched off",
      none: "Not set up",
    },
    statusReason: {
      providerDisconnected: "{provider} isn't connected at the organization level yet — connect it first, then this link will work.",
      providerUnhealthy: "The connection to {provider} isn't working right now — fix the connection to get this link working again.",
      connectProvider: "Connect",
      fixConnection: "Fix connection",
      deliveryFailing: "Recent updates to this source were not delivered",
      deliveryDetail: "Last error: {detail}",
      viewActivity: "View activity",
      disabled: "This link is switched off — no work comes in and no updates go out until you enable it.",
      enable: "Enable",
      enableFailed: "Couldn't enable the link.",
    },
    addResource: {
      title: {
        intake: "Add work source",
        code: "Link runtime or repository",
        updates: "Add update route",
        approvals: "Add approval channel",
      },
      intro: {
        intake: "Pick where the work comes from.",
        code: "Pick where the code lives, or where it runs.",
        updates: "Pick where progress should go.",
        approvals: "Pick where approval requests land.",
      },
      group: {
        humanReported: {
          title: "Someone reports it",
          hint: "A message, ticket, issue or email becomes work in this project.",
        },
        systemReported: {
          title: "A system reports itself",
          hint: "A failure or alert from your own environment opens work, without anyone having to report it.",
        },
        sourceCode: {
          title: "Where the code lives",
          hint: "The repository agents work in.",
        },
        runtime: {
          title: "Where the code runs",
          hint: "The environment a change ships to, and the deployment events that come back out of it.",
        },
        backToReporter: {
          title: "Back to whoever reported it",
          hint: "Progress and status land on the ticket or issue the work came from.",
        },
        toYourTeam: {
          title: "To your own team",
          hint: "Updates arrive in a channel or mailbox your team watches.",
        },
        toYourSystems: {
          title: "To your own systems",
          hint: "The outcome goes back to the repository, pipeline or endpoint waiting on it.",
        },
        humanApproves: {
          title: "Where people decide",
          hint: "The approval request arrives here; someone approves or rejects it.",
        },
        deployGate: {
          title: "At the rollout itself",
          hint: "The rollout pauses until someone gives the go-ahead.",
        },
        other: {
          title: "Other",
          hint: "Also linkable in this part of the project.",
        },
        inert: {
          title: "Delivers nothing here yet",
          hint: "You can link it — it still helps attribute incoming work to this project — but FixControl sends nothing here today.",
        },
      },
      statusUnverified: "Couldn't verify which systems are connected — pick a system, or connect one first via Settings → Channels.",
      notConnected: "Not connected",
      connectFirst: "Connect this system at the organization level first; then you can link it here.",
      connectLink: "Connect",
    },
    refKind: {
      channel: "channel",
      userGroup: "user group",
      operatorGroup: "operator group",
      ticketGroup: "ticket group",
      product: "product",
      tag: "tag",
      account: "entire account",
      issueProject: "project",
      component: "component",
      team: "team",
      repository: "repository",
      cluster: "cluster",
      namespace: "namespace",
      service: "service",
      labelSelector: "label selector",
      application: "application",
      rollout: "rollout",
      argoProject: "Argo project",
      mailbox: "mailbox",
      webhook: "webhook",
    },
    usedFor: "Used for",
    fromProvider: "via {provider}",
    overview: {
      title: "Setup & status",
      subtitle: "Everything this project needs, in one list.",
      health: {
        ready: "Project ready",
        needsSetup: "Setup incomplete",
        degraded: "Needs attention",
        setupRecommended: "Project ready",
        setupRecommendedHint: "Everything required works. A recommended step is still open — see the checklist.",
        stepSeparator: "·",
        readyHint: "Everything this project needs is set up and working.",
        needsSetupHint: "A required setup step is still open — the checklist says which.",
        degradedHint: "Everything required is set up, but not all of it is working or tested yet.",
      },
      attentionTitle: "Needs attention",
      attentionDisconnected: "{provider} isn't connected yet — connect it first.",
      attentionUnhealthy: "the connection to {provider} isn't working right now.",
      attentionDelivery: "updates to {provider} are not being delivered.",
      attentionView: "View",
      setup: {
        title: "Get started",
        subtitle: "A few more steps and this project is live: work comes in, agents have knowledge to work with, and approvals land in the right place.",
        steps: {
          workSource: {
            title: "Connect where work comes in",
            hint: "A Slack channel, service desk, issue tracker or mailbox — or an alert from your own systems.",
            action: "Add work source",
          },
          codebase: {
            title: "Link the code",
            hint: "The repository or codebase agents work on.",
            action: "Add codebase",
          },
          knowledge: {
            title: "Add knowledge",
            hint: "Docs agents draw on — or have a linked codebase inspected.",
            action: "Go to knowledge",
          },
          approvals: {
            title: "Choose who approves",
            hint: "Where approval requests land before risky actions go ahead.",
            action: "Add approval channel",
          },
        },
      },
      connected: "Connected",
      connectedNone: "Nothing connected yet.",
      agentsUse: "Agents use",
      counts: {
        knowledge: "{count} knowledge source",
        knowledgePlural: "{count} knowledge sources",
        codebases: "{count} codebase",
        codebasesPlural: "{count} codebases",
        intake: "{count} work source",
        intakePlural: "{count} work sources",
        approvalRules: "{count} approval rule",
        approvalRulesPlural: "{count} approval rules",
        updateRoutes: "{count} update route",
        updateRoutesPlural: "{count} update routes",
      },
    },
    intake: {
      title: "Work intake",
      subtitle: "Where work for this project comes in.",
      empty: "No work sources yet.",
      emptyHint: "Connect a Slack channel, service desk, issue tracker or mailbox so new work lands here — or let your own systems report a failure.",
      add: "Add work source",
      addMailbox: "Connect a mailbox",
      mailboxHint: "With a mailbox connected, every mail to that address becomes an issue in this project. A reply to the sender only goes out once someone approves it.",
      manage: "Manage work intake",
    },
    code: {
      title: "Code & environments",
      subtitle: "The code agents work on and the runtime it ships to.",
      codebasesTitle: "Codebases",
      runtimeTitle: "Runtime & deployment",
      runtimeEmpty: "No runtime linked.",
      runtimeEmptyHint: "Link a Kubernetes namespace or Argo app to map deployments and incidents to this project.",
      inspected: "Inspected",
      notInspected: "Not inspected",
      inspectNow: "Inspect now",
      defaultBadge: "default",
      addCodebase: "Add codebase",
      addRuntime: "Add runtime",
      unlinkCodebase: "Unlink codebase",
      manageRuntime: "Manage runtime",
    },
    testEnvironments: {
      title: "Test environments",
      subtitle:
        "Start an exact validated revision of this project on demand and look at it. "
        + "Each one is the exact commit and the exact verified environment its "
        + "validation proved — nothing is re-discovered when it starts.",
      runningOfMax: "{running} of {max} running",
      baseline: "Main branch",
      revisionOnly: "Revision",
      staleBadge: "superseded",
      open: "Open",
      start: "Start",
      stop: "Stop",
      restart: "Restart",
      rebuild: "Rebuild",
      revalidate: "Validate again",
      more: "More actions",
      stepOf: "step {done} of {total}",
      policyLabel: "When test environments are created",
      policy: {
        disabled: "Off",
        on_demand: "On demand",
        after_validation: "After a passed validation",
        auto_start: "Start automatically",
      },
      keepAlive: "Keep open for an hour",
      releaseHold: "Release the hold",
      viewValidation: "View the validation",
      viewPatch: "View the change",
      loadFailed: "The test environments could not be loaded.",
      actionFailed: "That did not work. Nothing was changed.",
      emptyDisabled: "Test environments are switched off for this project.",
      emptyNoValidation:
        "No test environment is available yet, because no revision of this project "
        + "has been validated with a startable application target.",
      addRevision: "Add a revision",
      pickerTitle: "Pick the revision you want to start",
      pickerHint:
        "A branch here is a way to find a commit, not a target that keeps moving: we "
        + "pin the commit the branch points at now. That commit stays this "
        + "environment, even after the branch moves on tomorrow.",
      pickerSearch: "Search branches",
      pickerBranches: "Branches",
      pickerCommit: "Or an exact commit",
      pickerCommitPlaceholder: "40-character commit",
      pickerDefaultBranch: "main branch",
      pickerNotFetched: "not fetched yet",
      pickerRemoteUnreachable:
        "The remote could not be reached, so these are only the branches already on "
        + "disk.",
      pickerNoBranches: "No branches were found in this project's repository.",
      pickerBranchesFailed: "The branches could not be loaded.",
      pin: "Pin",
      pinning: "Pinning…",
      cancel: "Cancel",
      status: {
        starting: "Starting",
        stopping: "Stopping",
        running: "Running",
        startFailed: "Application failed to start",
        validationFailed: "Validation failed",
        needsRevalidation: "Needs revalidation",
        revisionUnavailable: "Revision no longer available",
        previewUnavailable: "No runtime preview for this target",
        stale: "Superseded",
        expired: "Expired",
        readyToStart: "Ready to start",
        notValidated: "Not validated yet",
      },
    },
    environment: {
      title: "Verified environment",
      verified: "Verified — environment v{version}, {date}",
      startTargets: "Starts",
      recommendation: "Recommended validation: {strategy}",
      running: "Working out what this project needs to run…",
      blocked: "Needs an answer before this environment can be verified",
      observed: "What we found",
      tried: "What we tried",
      error: "The last verification attempt did not finish.",
      cancelled: "The last verification was cancelled.",
      none: "This project's environment has not been verified yet.",
      noneHint: "FixControl can work out what the repository needs to run and prove it by starting it once. Nothing is verified until a run succeeds.",
      verify: "Verify environment",
      verifying: "Verifying…",
      verifyFailed: "Couldn't start environment verification ({status}). Nothing was changed.",
      loadError: "Couldn't load this project's environment status.",
      /** A verified contract without a start target: nothing launches, so nothing previews. */
      noStartTarget: "Verified without a start command — the tests ran, nothing was launched, so there is no preview yet.",
      /**
       * The same empty start target, but the contract already knows WHAT this
       * project runs, and it is not something a preview can show. So no "find a
       * start command" — there is nothing to find. One sentence per kind in
       * `@/lib/artifact-kind`; they say "not yet", because that is the state of
       * the gateway rather than a property of the project.
       */
      notPreviewable: {
        android_app: "This project builds an Android app. FixControl can't show one yet — that needs an emulator, and the lane for it doesn't exist.",
        ios_app: "This project builds an iOS app. FixControl can't show one yet — that needs a simulator on Apple hardware, and the lane for it doesn't exist.",
        desktop_app: "This project builds a desktop application. FixControl can't show one yet — that needs a display rather than a port, and the lane for it doesn't exist.",
        http: "This project runs a web application, but this environment started none.",
      },
      findStartTarget: "Find a start command",
      /**
       * An application made of more processes than this environment starts.
       * Not a failure — the environment is exactly what was proven — but the
       * preview is part of an application, and without this line the only
       * symptom is a page that never finishes loading.
       */
      partialApplication: "This application is made of {declared} processes: {all}. This environment starts {started}: {startedNames}. Let FixControl propose the rest, or add them yourself under environment settings.",
      partialApplicationAction: "Fill in the rest from the compose file",
      /**
       * Services the repository explicitly requires that this environment
       * filled with nothing. Not a failure — the environment is what a green
       * run proved — but an application without its queue or object store
       * fails in ways that look like the code being broken.
       */
      missingServices: "The repository asks for services FixControl cannot start: {services}. There is no image for that engine, or this environment's budget is full. Add them yourself under environment settings, or leave them out if the validation does not need them.",
      declaredCoveringServices: "The validation did not prove these services, but your environment settings start them on every check: {services}.",
      disabledServices: "Switched off by you, so they do not run: {services}. The repository does still ask for them.",
      environmentSupplementsAction: "Switch off what you do not need",
      environmentSupplements: "FixControl starts these on every check and every preview, because the repository asks for them and the validation did not prove them: {services}. That costs time per run.",
      /**
       * What a preview adds on top. This is where the "exact verified
       * environment" promise is corrected: for a preview it no longer holds in
       * full, and that belongs on the screen.
       */
      /** The per-project environment settings editor (migration 281). */
      settings: {
        title: "Environment settings",
        switches: "What runs beside your environment",
        switchesHint: "FixControl starts the services your repository asks for that the validation did not prove. Switch off what this project does not need: that saves time on every check and every preview. Switching one off does not mean the repository stopped asking for it — it means we do not start it.",
        switchesNone: "Nothing extra runs beside your environment.",
        switchesHere: "Below is what runs beside your environment. Switch off what you do not need and save.",
        switchSource: {
          repository: "asked for by the repository",
          declared: "added by you",
        },
        targetPath: "Path a preview reaches this process on",
        targetPathHint: "For example /api. Without a path a preview does not start this process at all — only the first process starts on its own.",
        targetPathHintPrimary: "The first process is the one the preview shows; a path is only needed here if it is served under a prefix.",
        seeded: "Added from the repository: {added}. The command, the port and — where the compose or proxy file states one — the path are filled in. Check them, complete an empty path (without one a preview does not start that process) and save.",
        seededLeftOut: "Not added: {leftOut}.",
        seededNone: "There was nothing to add. Not added: {leftOut}.",
        /** Why a declared process was not turned into a start target. */
        seedReason: {
          no_port: "no port in the compose file, so nothing to wait for",
          no_command: "no command in the compose file",
          shell_command: "the command is a shell line, which cannot run here",
          no_room: "no room left; an environment starts at most six",
          already_started: "already starts",
        },
        hint: "What discovery cannot read from the repository: how the application starts, its port and readiness path, extra environment, system packages and install steps. Applied to every verification, judged by the same policy as the planner's own commands, and still proven by the run.",
        open: "Edit environment settings",
        close: "Hide",
        prefilled: "Filled in from the verified environment — the tagged sections below show what your project is running with right now. Edit what is wrong.",
        fromVerified: "in use now",
        envPrefilledNote: "These {count} variables come from the verified environment. Change one and your value is used from the next check onwards.",
        /**
         * The read-only half of the panel: what the platform decided FOR the
         * customer and is using. Plain language first, the resolver's own
         * provenance lines behind the "why this one?" fold.
         */
        resolved: {
          title: "What your project is running with now",
          explain: "FixControl worked this out by starting your application once and watching it come up. These are results, not settings: they are worked out again the next time the environment is verified.",
          image: "Machine your code runs on",
          imageHelp: "Your code runs on a clean, disposable machine that is thrown away after every check, so nothing it does can reach your own servers. FixControl picks the version from what your repository asks for; to get another one, change that (or pin a version in the project's runtime settings) and verify again.",
          imageFrom: {
            catalog: "A ready-made image from the FixControl catalogue: {image}",
            derived: "The catalogue has no {language} image for this version, so it was composed from the official naming convention: {image}",
            project_override: "The image this project pins itself: {image}",
            operator_default: "The default image for this workspace: {image}",
            toolchain: "Built by FixControl on top of {image}, with the extra software below added to it",
            unknown: "Image: {image}",
          },
          versionAsked: "Your repository asked for version {constraint}.",
          uncertain: "Part of this was a best guess — the reasons are below.",
          pinned: "Fixed to exactly this build: {digest}",
          why: "Why this one?",
          services: "Started alongside it",
          servicesHelp: "Your application needs these to run — a database, a cache. A brand-new empty one is started for every check and deleted afterwards, so your real data is never involved.",
          packages: "Extra software installed",
          packagesHelp: "Building your code takes more than the language itself; these are the libraries FixControl added. If a build ever fails because something is missing, name it under System packages below and it is added here too.",
          tools: "Tools: {tools}",
          extensions: "Language extensions: {extensions}",
          install: "Prepared before your application starts",
          installHelp: "These fetch and prepare everything your code depends on. They already run — whatever you put under Extra install steps runs after them, so there is no need to repeat them.",
          checks: "Run to check a change",
          checksHelp: "Your repository's own build, tests, type checks and lint. A proposed change is not offered for approval until these pass in this environment.",
          required: "Must pass.",
          advisory: "Advisory — a failure is reported, not blocking.",
          autoEnv: "Filled in automatically",
          autoEnvHelp: "FixControl sets these itself when your application starts. They get a new value on every check — the address of a freshly started service, a newly generated password — so they cannot be typed in below and no value is kept here.",
          fromService: "The address of {service} (port {port}), handed over when the run starts.",
          fromServiceNoPort: "The address of {service}, handed over when the run starts.",
          fromGenerated: "A password generated for that one run.",
          fromSecret: "Read from your own cluster's secret store — the value never reaches FixControl.",
          forTarget: "for {target}",
        },
        startTargets: "Start commands",
        addTarget: "Add start command",
        services: "Extra services",
        servicesHint: "Images that run beside the verified environment: on every check of this project and in every preview. They are not part of what the validation proved — so a test environment does not start them — and because the image is not built from this repository, a change to the code it serves does not show up in it.",
        serviceName: "Name",
        serviceNameHint: "The hostname it answers on inside the run. Use the name the application already looks for — the one in your compose file.",
        serviceImage: "Image",
        serviceImageHint: "A pinned reference, vetted against this deployment's registry allowlist.",
        servicePort: "Port",
        servicePortHint: "The port inside the container, and what readiness waits for.",
        servicePath: "Reachable at",
        servicePathHint: "A path the preview sends here instead of to the application, e.g. /api — use the path the frontend already calls. Leave empty and the service is reachable only from inside the run.",
        invalidTargetPath: "Process \"{target}\": \"{prefix}\" is not a usable path — start with / and use no wildcards or query.",
        invalidServicePath: "Service \"{name}\": \"{prefix}\" is not a usable path — start with / and use no wildcards or query.",
        serviceInitCommand: "Run once, first",
        serviceInitCommandHint: "Runs to completion in this image before the service starts, and must exit 0 — a migration, a seed, a schema load. Leave empty if there is nothing to run.",
        serviceCommand: "Command",
        serviceCommandHint: "Overrides the image's own command. Leave empty to use it.",
        serviceEnv: "Environment",
        serviceEnvHint: "KEY=value per line. The run's own database, cache and queue are already wired in — set these only to correct a name this application spells differently. Never a password.",
        removeService: "Remove service",
        addService: "Add service",
        serviceNameRequired: "Every extra service needs a name.",
        invalidServiceName: "\"{name}\" is not a usable service name — lowercase letters, digits and hyphens, not starting or ending with a hyphen.",
        serviceImageRequired: "Service \"{name}\" needs an image.",
        invalidServicePort: "Service \"{name}\" needs a port between 1 and 65535.",
        invalidServiceEnv: "Service \"{name}\": \"{line}\" is not KEY=value.",
        duplicateServiceName: "Two extra services are both called \"{name}\".",
        removeTarget: "Remove",
        targetName: "Name",
        command: "Command",
        commandHint: "One command line, no shell: quotes group, nothing expands.",
        port: "Port",
        readinessPath: "Readiness path",
        readinessTimeout: "Readiness timeout (s)",
        env: "Environment variables",
        envHint: "One NAME=value per line. Non-secret only; credentials belong in env bindings.",
        systemPackages: "System packages",
        systemPackagesHint: "Names, comma-separated, from the recipe allowlist (e.g. libldap2-dev).",
        installSteps: "Extra install steps",
        installStepsHint: "One command per line, run after the planner's own install steps.",
        /** Blocker questions answered earlier — remembered on the project. */
        answers: {
          title: "What you answered earlier",
          hint: "The planner is given these on every verification, so the same question is not asked again. Remove one when it no longer holds.",
          forget: "Forget this answer",
        },
        notes: "Notes for the planner",
        save: "Save",
        saveAndVerify: "Save and verify",
        saved: "Saved.",
        savedVerifying: "Saved — verification started.",
        saveFailed: "Couldn't save the environment settings ({status}). Nothing was changed — try again.",
        invalidCommand: "\"{target}\" has no start command. Type the command that starts it, or remove that start command.",
        invalidPort: "The port for \"{target}\" must be a whole number between 1 and 65535 — the port the application listens on.",
        invalidReadiness: "The readiness wait for \"{target}\" must be between 1 and 600 seconds — how long the application may take to answer.",
        invalidEnv: "Every environment line must read NAME=value. This one does not: {line}",
        /** Per-project sandbox resource limits. Written for someone who has
         *  never heard of a container: the words are "machine", "memory",
         *  "programs at once", never "cgroup" or "pids-limit". */
        resources: {
          title: "How much machine the checks get",
          hint: "Every check runs on a small machine of its own, thrown away afterwards. These boxes show what this project gets today. Change one only if a check runs out of memory or time; leave a box at its shown value and it keeps following the server.",
          cpus: "Processor cores",
          memory: "Memory (MB)",
          pids: "Programs at once",
          stepTimeout: "Longest one step (min)",
          runTimeout: "Longest whole check (min)",
          explainCpus: "How many processor cores the check may use at once. More cores make a build finish sooner; they do not make a failing test pass.",
          explainMemory: "Working memory for the check, in megabytes (1024 MB is 1 GB). Raise it if a build or test stops with \u201cout of memory\u201d \u2014 that is the machine being too small, not your code being wrong.",
          explainPids: "How many programs the check may have running at the same time. It is a safety net against a runaway process filling the host; only raise it if a build says it cannot start another process.",
          explainStepTimeout: "How long a single step \u2014 installing, testing, building \u2014 may take before we stop it and call it failed. Raise it if a slow test suite is cut off halfway.",
          explainRunTimeout: "How long all the steps together may take. A step can never run longer than what is left of this, so raising the step above this one changes nothing.",
          fromServer: "Now: the server default for every project.",
          fromProject: "Now: set for this project.",
          clamped: "Set for this project, but above what this server allows \u2014 the lower number is what runs.",
          invalid: {
            cpus: "Processor cores must be a number above zero, for example 2 or 1.5.",
            memory: "Memory must be a whole number of megabytes, for example 8192 for 8 GB.",
            pids: "Programs at once must be a whole number, for example 512.",
            stepTimeout: "The longest step must be a whole number of minutes, for example 25.",
            runTimeout: "The longest check must be a whole number of minutes, for example 60.",
          },
          tooHigh: {
            cpus: "This server allows at most {max} processor cores per check. Lower the number, or ask a platform administrator to raise the limit.",
            memory: "This server allows at most {max} MB of memory per check. Lower the number, or ask a platform administrator to raise the limit.",
            pids: "This server allows at most {max} programs at once per check. Lower the number, or ask a platform administrator to raise the limit.",
            stepTimeout: "This server allows a step to run for at most {max} minutes. Lower the number, or ask a platform administrator to raise the limit.",
            runTimeout: "This server allows a whole check to run for at most {max} minutes. Lower the number, or ask a platform administrator to raise the limit.",
          },
        },
        clear: "Clear all settings",
        empty: "No environment settings — discovery derives everything from the repository.",
        active: "Environment settings are in force for every verification.",
      },
      previewTarget: "Application preview",
      previewStarting: "Starting…",
      previewElapsed: "{elapsed} so far",
      /** Under the stepper while a discovery runs: how long the current phase has taken. */
      phaseAttempt: "attempt {n} — the steps start over on each attempt",
      phaseElapsed: "{elapsed} in this phase",
      attemptOf: "attempt {n} of {total}",
      reverifyNote: "the verified environment stays in force until this run replaces it",
      /** Where a booting preview is, from the preview job's running note. */
      previewProgress: {
        preparing: "Checking the verified environment and the workspace…",
        environment: "Starting the task and pulling the image…",
        steps: "Step {done} of {total} done: {name}",
        stepFailed: "Step failed: {name}",
        startingTarget: "Starting {target}, waiting for it to answer…",
        failed: "Did not start: {reason}",
      },
      /** Where the task is, read off the runner's own log. */
      previewMilestone: {
        checked_out: "Revision checked out, dependencies next…",
        deps_restored: "Dependencies restored from cache ({mb} MB), building…",
        deps_cold: "No usable cache — installing dependencies from scratch…",
        patch_applied: "Patch applied, building the environment…",
        app_starting: "Starting the application…",
      },
      previewFailedRecently: "The last preview of {target} did not start: {reason}",
      /** The refusal codes the preview job audits, as sentences. */
      previewFailure: {
        start_target_not_ready: "the application was started but never answered",
        contract_fingerprint_mismatch: "the repository's configuration changed since it was verified — the environment is being re-discovered",
        preview_run_failed: "the sandbox run failed before the application started",
        no_active_contract: "this project has no verified environment",
        contract_has_no_start_target: "the verified environment declares nothing to start",
        project_codebase_unresolved: "the project's codebase could not be found on disk",
        patch_overlay_unavailable: "the patch this preview was to show is no longer available",
        supplement_service_unavailable: "a service the repository declares, but the verification never proved, could not be started",
        preview_disabled: "previews are switched off for this workspace",
        discovery_disabled: "environment discovery is switched off for this workspace",
      },
      previewLive: "Running",
      previewEnded: "Ended",
      expiresIn: "Ends in {minutes} min",
      open: "Open preview",
      stop: "Stop preview",
      stopFailed: "Couldn't stop this preview ({status}).",
      accept: "This looks right",
      reject: "This does not look right",
      reviewFailed: "Couldn't record your verdict ({status}).",
      reviewHint: "Your verdict is recorded next to the automated validation as human evidence — it never replaces a test result and never opens a gate on its own.",
      verdict: {
        accepted: "reviewed: looks right",
        rejected: "reviewed: does not look right",
      },
      // The blocked card used to print the raw failure vocabulary
      // (ValidationFailureKind, src/server/sandbox-runtime/types.ts) at a
      // project admin. These are the same kinds, said once, plainly. An
      // unmapped kind keeps its raw form rather than guessing a neighbour.
      whereItStopped: "Where it stopped",
      technicalDetails: "Technical details",
      noAttempt: "No attempt was made",
      failureKind: {
        test_failure: "A test failed",
        startup_failure: "The application did not start",
        readiness_timeout: "The application never became ready in time",
        image_pull: "A container image could not be downloaded",
        scheduling: "There was no capacity to run the environment",
        infrastructure: "An infrastructure problem stopped the run",
        transport: "The connection to the runner broke",
        cancelled: "The attempt was cancelled",
        runtime_incompatible: "The runtime version does not fit this project",
        dependency_installation: "Installing the project's dependencies failed",
        missing_runtime_dependency: "Something the application needs is missing",
        service_boot: "A supporting service (database, cache, …) did not start",
        service_connection: "The application could not reach a supporting service",
        environment_configuration: "The environment configuration is incomplete",
        application_boot: "The application crashed while starting",
        build_failure: "The build failed",
        policy_refusal: "The proposed environment was refused by policy",
        network_policy: "Network rules blocked the run",
        resource_budget: "The run went over its resource budget",
        unsupported_environment: "No execution target can provide what this project needs",
        toolchain_build: "The toolchain image for this project could not be built",
        external_dependency_required: "This project depends on something FixControl cannot provide on its own",
        dependency_lock_mismatch: "The dependency manifest and its lock file disagree",
        dependency_resolution_failure: "The declared dependencies cannot be resolved",
        tests_not_executed: "The test run executed no tests at all — the test command selected nothing belonging to this change",
        unknown: "The reason could not be determined",
      },
      // What the verified environment actually proved. Names and versions
      // only — no images, no ports, no environment values.
      runtimesLabel: "Runtime",
      servicesLabel: "Services",
      // Opening a preview (POST .../environment/preview). Distinct from
      // `open`, which follows an already-running one.
      previewCreate: "Open preview",
      previewCreating: "Opening…",
      previewCreateFailed: "Couldn't open a preview ({status}). Nothing was started.",
      previewTemporary: "Temporary validation environment — not your production system.",
      previewRefusal: {
        preview_disabled: "Previews are not switched on for this workspace.",
        discovery_disabled: "Environment verification is not switched on for this workspace, so a preview cannot be started.",
        no_active_contract: "This project has no verified environment yet — verify the environment first.",
        contract_has_no_start_target: "This project's verified environment does not start an application, so there is nothing to preview. Let FixControl find a start command, or add one under Environment settings, then verify again.",
        preview_limit_reached: "The maximum number of previews is already running. Stop one before opening another.",
        run_not_passed: "This run did not verify the patch, so it cannot be shown as a running application.",
        run_has_no_patch: "This run verified no patch — open a preview of the project's environment instead.",
        run_has_no_project: "This run is not bound to a project, so there is no verified environment to run it in.",
        patch_overlay_unavailable: "This patch's files can no longer be read, so the preview would show the unpatched code.",
      },
      // Refusals a discovery START can return (`DiscoveryRefusal`,
      // src/server/environment-discovery/discovery-requests.ts). The server
      // writes those messages for the record — they name settings and policy
      // versions; these are the same decisions said to the person who clicked.
      discoveryRefusal: {
        discovery_disabled: "Environment verification is not switched on for this workspace.",
        residency_customer_hosted_unsupported: "This project must be validated on your own runner, and there is no runner FixControl can reach that is able to carry it. Register a container-capable runner for this project, or switch remote validation on for it.",
        discovery_in_progress: "FixControl is already working this project out. Progress shows above.",
        discovery_resume_exhausted: "This environment has been answered and retried the maximum number of times; re-verify from scratch or change the project's validation settings.",
      },
      // ── Onboarding phases ────────────────────────────────
      // The six steps every project passes through on its way to a verified
      // environment, so "working on it" stops being one opaque spinner.
      // Derived from the discovery record's phase when it has one, and from
      // its status + failure kind when it does not.
      phase: {
        title: "Getting this project ready",
        analyzing: "Analyzing codebase",
        resolving: "Resolving environment",
        preparing_runtime: "Preparing runtime",
        starting_services: "Starting services",
        verifying: "Verifying application",
        ready: "Ready",
        stoppedHere: "Stopped here",
        inProgress: "In progress",
      },
      // ── Blocked: one sentence, one question, and the ways out ─────────
      blockedDependency: "FixControl could not materialize one dependency: {name}",
      blockedQuestionTitle: "What FixControl needs to know",
      // Titled by the blocker's machine key, so the input says what to type
      // instead of repeating the runner's English sentence as a label.
      questionLabel: {
        runtime_version: "Which runtime version does this project run on?",
        system_packages: "Which system packages does this project need?",
        services: "Which services does this project need (database, cache, queue)?",
        env_vars: "Which environment variables does this project need to start?",
        start_command: "How is this application started?",
        build_command: "How is this project built?",
        unsupported_environment: "What does this project need that FixControl could not provide?",
        external_dependency: "Which external system does this project depend on, and how should the sandbox stand in for it?",
        generic: "Your answer",
      },
      unanswerableTitle: "An answer cannot move this",
      unanswerableWhatHelps: "This requirement is stated by the repository itself, and no available execution target provides it — typing an answer does not change that. What does: register a runner that provides it and allow remote validation for this project, or change the repository so the requirement goes away.",
      answerPlaceholder: "For example: PHP 8.1, the queue worker needs Redis, start with bin/console app:serve",
      answerSubmit: "Answer and retry",
      answerSubmitting: "Starting…",
      answerFailed: "Couldn't start a new attempt ({status}). Nothing was changed.",
      answerStarted: "Thanks — a new attempt has started with your answer.",
      // Honest about the seam: an answer this build cannot carry into the
      // planner must not look like it was used.
      answerNotCarried: "A new attempt has started, but this version cannot pass your answer to the planner yet.",
      answerCharsLeft: "{count} characters left",
      // ── Customer-hosted fallback ───────────────────────────
      fallbackTitle: "Your own runner could host this",
      fallbackAllowed: "{agent} advertises everything this project needs. Validating there does not change where your source is stored.",
      fallbackValidate: "Validate on {agent}",
      fallbackValidating: "Starting…",
      // Policy said no: name the exact permission, and nothing else.
      fallbackBlocked: "{agent} could host this environment, but remote validation is switched off for this project. The setting is “Remote validation for this project” under How changes are validated.",
      fallbackBlockedCta: "Open remote validation permission",
      // A capable runner that was never tried, because it has never said what
      // its machine offers. Named so the refusal above is not misread as a
      // permission problem.
      skippedRunner: "A runner named {name} is registered but has not declared its capabilities, so it could not be tried.",
      strategyLink: "Remote validation strategy",
      // ── Advanced ─────────────────────────────────────
      attemptsTitle: "Attempts",
      attemptLine: "Attempt {n}: {kind}",
      /** The verification log on the project card: what each attempt hit. */
      attemptLog: {
        title: "Verification log ({count})",
        /** Attempt 0 is the capability check before the first real attempt. */
        beforeFirstAttempt: "Before the first attempt: {kind}",
        failedStep: "Stopped at: {name} ({kind})",
        exitCode: "exit code {code}",
        servicesNotReady: "Not ready: {services}",
        logWithheld: "The output of this attempt is visible only to someone who may manage this project.",
      },
    },
    // Which validation this project's changes must pass. Read/write through
    // GET/PUT /api/projects/[id]/validation/settings.
    validation: {
      title: "How changes are validated",
      subtitle: "Where this project's changes are built and tested before a gate can pass.",
      loadError: "Couldn't load this project's validation setting.",
      saveFailed: "Couldn't save the validation setting ({status}). Nothing was changed.",
      saved: "Saved.",
      enableLabel: "Use a validation strategy for this project",
      enableHelp: "Off means this project follows the workspace default.",
      sourceProject: "Project setting",
      sourceDefault: "Workspace default",
      readOnly: "You do not have permission to change this project's settings.",
      // Per-project runtime pins (WS-A: validation profile `runtimeOverrides`).
      // A project whose production runtime is older or newer than what the
      // catalog resolves to says so here, once, instead of every discovery
      // guessing again.
      runtimeOverrides: {
        title: "Runtime versions for this project",
        help: "Pin what this project actually runs on. Give a language and a version, or a language and an image when the version alone is not enough. Leave empty to let FixControl resolve it from the repository.",
        language: "Language",
        version: "Version",
        image: "Image (optional)",
        languagePlaceholder: "php",
        versionPlaceholder: "8.1",
        imagePlaceholder: "registry.example.com/php:8.1-fpm",
        add: "Add runtime",
        remove: "Remove",
        empty: "No runtime pinned — FixControl resolves this from the repository.",
        max: "At most {count} runtimes can be pinned.",
        unsupported: "This version of FixControl cannot save runtime pins yet.",
      },
      // ── The start proof, and the one way to ship without it ───────────
      // A project whose verified environment declares a start command must
      // prove the application still comes up before a change is applied.
      // A customer's own runner cannot make that proof, so the operator
      // either moves the project to a lane that can, or accepts — by name,
      // on a date, here — that it ships without one. The copy never says
      // "lane", "runtime verification" or "waiver": it says what is true.
      startVerification: {
        title: "Proof that the application still starts",
        help: "Before a change is applied, FixControl starts this project's application and waits for it to answer. Tests passing is not the same as the application still coming up, and this is the check that tells them apart.",
        required: "Required — a change is not applied until the application has started and answered.",
        waived: "Not required — changes are applied on the test results alone.",
        waivedBy: "Accepted by {who} on {date}.",
        waiveLabel: "Allow changes without this proof",
        waiveHelp: "Only for a project validated on your own runner, which cannot start the application and check it. You are accepting that a change can be applied while nobody has confirmed the application still comes up. Your name and today's date are recorded next to every approval that relies on it.",
        waiveConfirm: "Yes, apply changes without this proof",
        readOnly: "Only admins and managers can change this.",
        saveFailed: "Couldn't save this setting ({status}). Nothing was changed.",
      },
      remotePermissionLabel: "Remote validation for this project",
      remotePermissionHelp:
        "Whether this project may run its validation in your Kubernetes cluster. " +
        "Inherit follows the workspace setting; the other two decide for this project alone.",
      remotePermissionInherit: "Inherit workspace setting",
      remotePermissionOn: "Allowed for this project",
      remotePermissionOff: "Not allowed for this project",
    },
    updates: {
      title: "Updates & notifications",
      subtitle: "Where FixControl sends progress on this project's work.",
      originTitle: "Updates back to source",
      originSubtitle: "Progress and status flow back to where the work came from.",
      originEmpty: "No update routes configured.",
      originEmptyHint: "Updates fall back to in-app notifications until you connect a channel or ticket system.",
      approvalsTitle: "Approval rules",
      approvalsSubtitle: "Who must sign off before risky actions go ahead.",
      approvalsEmpty: "No approval rules configured.",
      approvalsEmptyHint: "Risky actions pause for in-app approval until you set up approval rules.",
      requiresApproval: "Requires approval",
      noApproval: "No approval required",
      manage: "Configure approval rules",
      manageUpdates: "Manage update routes",
      addRoute: "Add update route",
      addApprovalChannel: "Add approval channel",
      riskAny: "any risk",
      risk: { low: "low risk", medium: "medium risk", high: "high risk", critical: "critical risk" },
      channel: { inapp: "in-app", email: "email", slack: "Slack", teams: "Teams", webhook: "webhook" },
    },
    automation: {
      title: "AI team",
      subtitle: "How the AI works on this project: which agents help, what they may do on their own, and the rules they always follow.",
      readOnlyNotice: "You can look, not change. Only admins and managers edit these settings.",
      allowed: "Allowed",
      requiresApproval: "Requires approval",
      disabled: "Disabled",
      /** The choice comes first and its consequence second. The other way
       *  round the page read as a table of outcomes with the cause hidden
       *  underneath it. */
      accessTitle: "What may agents do with your code?",
      accessHint: "This is the main choice on this page: it decides the list below.",
      accessReadOnly: "FixControl reads your code but never changes anything in it.",
      accessSandbox: "FixControl tries changes in a separate, safe copy and proposes them. Your own code stays untouched.",
      accessWritable: "Once a person approves a change, FixControl applies it to the linked codebase.",
      effectTitle: "What that means in practice",
      effectHint: "This follows automatically from your choice above; you don't set it separately.",
      /** Short enough for a pill in the projects list and a filter tab; what
       *  each mode means is in accessReadOnly/Sandbox/Writable, which sit
       *  under the name on the settings page. */
      mode: { read_only: "Only look", sandbox: "Try changes in a safe copy", writable: "Apply approved changes" },
      /** Short enough for a pill in the projects list and a filter tab; the
       *  full name above shows as its tooltip. */
      modeShort: { read_only: "Only look", sandbox: "Safe copy", writable: "Apply changes" },
      behaviourTitle: "How this project handles incoming work",
      behaviourHint: "Two choices that apply to this project only.",
      guardrailsTitle: "Standing rules for agents",
      guardrailsHint: "Rules in your own words that agents on this project always follow — which folders to leave alone, for instance. They apply on top of the settings above.",
      guardrailsEmpty: "No rules yet. Add one that agents on this project always follow.",
      guardrailsExamplesLabel: "Click an example to use it:",
      guardrailsExamples: "Never touch /payments without review|Always update the tests|No database migration changes without approval",
      guardrailsAdd: "Add rule",
      guardrailsPlaceholder: "e.g. Never touch production config",
      guardrailsRemove: "Remove rule “{rule}”",
      guardrailsReadOnly: "Only admins and managers can change these rules.",
      supportDeskTitle: "Support only, no code work",
      supportDeskHint: "Incoming questions become tickets that are tracked properly, but no agent ever starts changing code on its own. When code work IS needed, use “Escalate to engineering”. While this is on, Patches and Work sessions are hidden and the project does not change code.",
      llmFirstTitle: "Let AI interpret chat messages",
      llmFirstHint: "Messages in this project's Slack and Teams channels are read by AI first instead of by fixed word patterns. Free-form phrasing is then understood far more often, and one message may ask for several things at once. Costs a little more time and AI budget per message. Only the understanding changes: every action still needs the same confirmation and approvals, and if the AI is down the fixed patterns take over again.",
      cap: {
        readKnowledge: "Read project knowledge",
        inspectCodebases: "Inspect linked codebases",
        postProgress: "Post progress updates",
        createPatch: "Create patch proposals",
        openPullRequests: "Open pull requests",
        productionChanges: "Production changes",
        securityChanges: "Security-sensitive changes",
        customerReplies: "Customer-facing replies",
        directProductionWrites: "Direct writes to production",
        automaticEmailReplies: "Automatic email replies",
      },
      modeTitle: "Default agent mode",
    },
    activity: {
      title: "Activity & audit",
      subtitle: "Recent events across intake, agents and updates for this project.",
      empty: "No activity recorded yet.",
      emptyHint: "Once work flows through this project, resolution, sync and learning events show up here.",
      viewAudit: "View full audit log",
      showMore: "Show {count} more",
      showLess: "Show less",
      events: {
        project_context_resolved: "Event resolved to this project",
        project_context_ambiguous: "Event could match multiple projects",
        project_context_missing: "Event couldn't be matched to a project",
        external_sync_delivered: "Update delivered to source",
        external_sync_skipped: "Update skipped",
        external_sync_failed: "Update delivery failed",
        learning_captured: "Learning captured",
        approval_requested: "Approval requested",
        notification_route_resolved: "Notification routed",
        codebase_inspected: "Codebase inspected",
        knowledge_source_added: "Knowledge source added",
        unknown: "Event",
      },
    },
    advanced: {
      title: "Advanced settings",
      subtitle: "The raw connection editor and tags. If you don't need them, there is nothing to do here.",
      warning: "This is the technical view of EVERY connection this project has. To ADD a system, use the ordinary sections in the menu; here you edit, test or manually enter existing connections in detail.",
      show: "Show advanced settings",
      hide: "Hide advanced settings",
      connectedSystemsTitle: "Connected systems (raw bindings)",
      governanceTitle: "Governance",
      tagsTitle: "Tags",
      tagsHint: "Free-form labels to find projects back in lists. They change nothing about what agents may do.",
      removeTag: "Remove tag {tag}",
      readOnly: "You don't have permission to change advanced settings.",
    },
  },

  projectIntegrations: {
    title: "Connected systems",
    help: "Choose which external resources belong to this project. Events, approvals and notifications resolve to this project through these bindings.",
    vocabConnection: "Connection = tenant-level access to an external system",
    vocabBinding: "Project binding = external resource linked to this project",
    vocabRouting: "Routing = rules that decide where approvals and notifications are sent",
    loadFailed: "Could not load project bindings.",
    saveFailed: "Could not save the binding.",
    removeFailed: "Could not remove the binding.",
    toggleFailed: "Could not update the binding.",
    noPermission: "You need project integration management permission to change this.",
    conflict: "This resource is already bound to another project.",
    conflictHint: "Remove that binding before linking it here.",
    addBinding: "Add",
    saveBinding: "Save",
    refIdPlaceholder: "External id (channel id, group id, owner/repo, namespace, app name…)",
    refNamePlaceholder: "Display name (optional, e.g. #payments-platform)",
    enabled: "Enabled",
    disabled: "Disabled",
    testRoute: "Test route",
    participation: {
      inbound: "Inbound → project",
      inboundPlanned: "Inbound: planned",
      outboundLive: "Sync back: live",
      outboundPlanned: "Sync back: planned",
      learning: "Learning capture",
    },
    cap: {
      progress: "Progress",
      status: "Status",
      patchLinks: "Patch links",
      comments: "Comments",
      transitions: "Transitions",
      incidents: "Incidents",
    },
    testOk: "Resolution OK — events from this resource land on this project.",
    testMismatch: "Resolution does NOT land on this project:",
    testOtherProject: "Resolves to another project: {project}",
    testAmbiguous: "Ambiguous — multiple projects match this resource.",
    testNoMatch: "No project matched — events from this resource would need manual selection.",
    testFailed: "Test failed — could not run the resolution.",
    routingPreviewTitle: "Routing for this project:",
    readOnly: "You need project integration management permission to change this.",
    configure: "Configure",
    addAnother: "Add another link",
    configureTitle: "Configure {provider}",
    usedFor: "Used for:",
    remove: "Remove",
    useFor: "Use for",
    pickOne: "Select…",
    connected: "Connected",
    notConnected: "Not connected",
    configuredUnverified: "Connected \u00b7 not yet verified",
    connectedTo: "Connected to {name}",
    slackConnectedTo: "Connected to workspace {team}",
    notConnectedHint: "{provider} is not connected at tenant level. Ask an admin to connect it in Settings → Channels.",
    webhookAlwaysAvailable: "Available — signed inbound webhooks",
    pickerLoadFailed: "Connected, but resources could not be loaded. Retry, or use Advanced mode below.",
    resourceRequired: "Pick a resource first.",
    catalogueEmpty: "Connected, but nothing to pick yet — this account has no items of this type. Create one in the source system, or use Advanced mode below.",
    moreOptions: "More options",
    resourceRequiredBy: {
      argo: "Enter the Argo application or rollout name first (exactly as it appears in Argo).",
      kubernetes: "Enter the Kubernetes resource first (namespace, cluster or label selector).",
      teams: "Enter the Teams channel id first.",
      topdesk: "Pick a TOPdesk operator group first.",
      webhook: "Enter the webhook endpoint name first.",
    },
    purposeRequired: "Select at least one purpose.",
    // Marks a (provider, purpose) pair that `purposeDelivery`
    // (src/server/command-center/binding-delivery.ts) proves nothing reads.
    // The pair is still storable — it is not an error — but a picker that
    // offers it at the same weight as a working one is how a Teams
    // notifications binding turns into silence.
    purposeInert: "nothing delivers this yet",
    purposeInertHint: "You can save it, but no part of FixControl sends anything for this combination today.",
    purposeInertIntake: "no new issues",
    purposeInertIntakeHint: "A new ticket does not become an issue. Tickets linked to a FixControl issue belong to this project and their status follows along.",
    partialSave: "Some bindings could not be saved —",
    advancedToggle: "Advanced: enter external reference manually",
    advancedWarning: "Use this only when the resource is not discoverable through the connected integration.",
    advancedRefType: "Reference type",
    advancedRefId: "External id",
    advancedConfig: "Config (JSON, optional)",
    advancedRefIdRequired: "Advanced mode needs an external id.",
    configJsonInvalid: "Config must be a valid JSON object.",
    searchChannels: "Search channels…",
    slackMissingScope: "The Slack app is missing the channel-list permission (channels:read). Reconnect Slack in Settings → Channels to grant it, or use Advanced mode below.",
    slackChannel: "Channel",
    slackWorkspace: "Workspace: {team}",
    slackUseThread: "Use the original issue's thread for progress updates",
    slackAskProject: "Ask for a project when a message arrives in an unmapped channel",
    slackPreview: "Messages from {channel} will create issues in {project}.",
    freshdeskMatchBy: "Match tickets by",
    freshdeskTag: "Tag",
    freshdeskMirrorNote: "Freshdesk mirrors progress and status updates unless approval routing is explicitly enabled.",
    freshdeskNoGroups: "No agent groups exist in this Freshdesk account. Create one under Freshdesk → Admin → Groups, or match tickets by Product or Tag instead.",
    freshdeskNoProducts: "No products exist in this Freshdesk account (products need a Freshdesk Estate/Enterprise plan). Match tickets by Group or Tag instead.",
    freshdeskCatchAllNote: "Every ticket from this Freshdesk account routes to this project, unless a more specific Group, Product or Tag binding sends it elsewhere.",
    githubExplain: "Repository access, PR delivery and webhook events. Repositories are usually linked as codebases — a binding here additionally routes webhook events to this project.",
    githubConnection: "GitHub connection",
    githubRepo: "Repository",
    githubCodebaseHint: "To use a repository as code context for the agent, add it as a codebase via the Codebases section above.",
    githubAutoDeliver: "Auto-deliver code — open a PR on this repo after the patch is approved",
    githubAutoDeliverHint: "On approval FixControl applies the patch and opens a pull request (no push to main). Requires the repo wired as a GitHub codebase.",
    k8sBindingType: "Binding type",
    k8sCluster: "Cluster",
    anyCluster: "Any cluster",
    k8sLegacyClusterWarning:
      "No cluster selected. With more than one cluster, a namespace bound to “any cluster” is ambiguous — pick a cluster so events route to the right project.",
    environment: "Environment",
    environments: {
      development: "Development",
      staging: "Staging",
      production: "Production",
    },
    k8sPreview: "Events from {resource} resolve to {project}.",
    k8sDiscoveryNote: "Namespace discovery is not available — enter the namespace exactly as it appears in the cluster.",
    argoInstance: "Argo connection",
    argoManualNote: "Argo applications can't be discovered automatically — enter the name exactly as it appears in Argo.",
    argoPromotionApprovals: "Promotion approvals — pause rollouts until approved in FixControl",
    argoAutoDeployGate: "Auto-open deployment gate — after the patch is approved, open an Argo-bound deploy approval",
    argoAutoDeployHint: "On approval FixControl resumes the paused rollout below. Requires namespace + rollout.",
    argoNamespace: "Rollout namespace",
    argoRollout: "Rollout name",
    emailAlias: "Mailbox / alias",
    webhookEndpoint: "Endpoint / source name",
    webhookEventType: "Event type (optional)",
    jiraProject: "Jira project",
    linearTeam: "Linear team",
    jiraMirrorNote: "Issue trackers mirror progress and status updates; intake creates issues in this project.",
    topdeskOperatorGroup: "Operator group",
    topdeskMirrorNote: "Incidents assigned to this operator group land in this project; progress and status are mirrored back to the incident.",
    teamsChannelId: "Channel id",
    teamsManualNote: "Teams channels can't be discovered with the bot credential. In Teams: channel → ⋯ → Get link to channel — the id is the 19:…@thread part.",
    teamsChannel: "Teams channel",
    teamsManualToggle: "Channel not listed? Enter the id manually",
    teamsNoChannelsDiscoverable: "Add the FixControl bot to a Teams channel (@mention it once) and it'll appear here. Until then, enter the channel id manually.",
    notConnectedList: "Not connected at tenant level:",
    notConnectedListHint: "connect them in Settings → Channels and they appear here.",
    checkingConnections: "Checking which systems are connected…",
    statusUnverified: "Couldn't verify tenant connections — showing only systems that already have bindings.",
    unverifiedList: "Not verified:",
    expandCard: "Expand",
    collapseCard: "Collapse",
    boundCountOne: "{count} resource bound",
    boundCountOther: "{count} resources bound",
    providers: {
      slack: {
        title: "Slack",
        help: "Slack is connected at workspace level. Choose which channels belong to this project — intake channels route new issues here; an approvals channel receives decision cards.",
        example: "Example — bind #payments-support so messages there open issues in this project.",
      },
      teams: {
        title: "Microsoft Teams",
        help: "Map Teams channels to this project so messages and approvals route correctly. Channels of any team the bot has been @-mentioned in show up in the list; for anything else, paste the channel id from Teams.",
        example: "Example — bind the ‘Payments – General’ channel so its messages route here.",
      },
      freshdesk: {
        title: "Freshdesk",
        help: "Map Freshdesk groups, products or tags to this project. Freshdesk mirrors progress and status updates unless approval routing is explicitly enabled.",
        example: "Example — route tickets tagged fc-payments, or all tickets, to this project.",
      },
      odoo: {
        title: "Odoo",
        help: "Map Odoo helpdesk teams to this project. Odoo mirrors progress and status updates; tickets from a mapped team land here.",
        example: "Example — bind the Customer Care team so its tickets route to this project.",
      },
      topdesk: {
        title: "TOPdesk",
        help: "Map TOPdesk operator groups to this project. TOPdesk mirrors progress and status updates; incidents assigned to a mapped group land here.",
        example: "Example — bind the Servicedesk Payments operator group so its incidents route to this project.",
      },
      jira: {
        title: "Jira",
        help: "Map Jira projects to this project. Jira mirrors progress and status updates; issues from a mapped project land here.",
        example: "Example — bind the PAY Jira project so its issues mirror into this project.",
      },
      linear: {
        title: "Linear",
        help: "Map Linear teams to this project. Linear mirrors progress and status updates; issues from a mapped team land here.",
        example: "Example — bind the ENG Linear team so its issues mirror into this project.",
      },
      github: {
        title: "GitHub",
        help: "Repositories are usually linked as codebases (above). A repo binding here additionally routes webhook events to this project.",
        example: "Example — bind acme/ledger so its webhook events route to this project.",
      },
      gitlab: {
        title: "GitLab",
        help: "Projects are usually linked as codebases (above). A project binding here additionally routes webhook events to this project.",
        example: "Example — bind acme/ledger so its webhook events route to this project.",
      },
      bitbucket: {
        title: "Bitbucket",
        help: "Repositories are usually linked as codebases (above). A repo binding here additionally routes webhook events to this project.",
        example: "Example — bind acme/ledger so its webhook events route to this project.",
      },
      kubernetes: {
        title: "Kubernetes",
        help: "Map clusters, namespaces or services to this project so deployment events and guardrails resolve to the right context.",
        example: "Example — bind namespace payments so deployments there resolve to this project.",
        // Kubernetes straddles three sections. Under "Add work source" the
        // purpose being bound is `incidents`, not `deployment` — say so, or
        // the card reads as a mistake in the list.
        exampleIn: {
          intake: "Example — a stuck rollout or crash-looping pod in namespace payments arrives here as work.",
          updates: "Example — bind cluster prod so deployment status from it updates this project.",
        },
      },
      argo: {
        title: "Argo",
        help: "Map Argo applications or rollouts to this project so deployment approvals and promotion events are routed correctly.",
        example: "Example — bind the Argo application ledger-api so its promotion approvals land in this project.",
        exampleIn: {
          code: "Example — bind the Argo application ledger-api as this project’s runtime.",
          updates: "Example — bind the Argo application ledger-api so deployment status from it updates this project.",
        },
      },
      email: {
        title: "Email",
        help: "Map inbound email aliases to this project so email intake lands on the right project.",
        example: "Example — bind support@yourco.com so emails there open issues in this project.",
      },
      webhook: {
        title: "Webhooks",
        help: "Route inbound webhook events from a named source to this project — deployment events, CI signals, incident alerts.",
        example: "Example — bind ci-pipeline so its deployment events route to this project.",
        exampleIn: {
          intake: "Example — bind monitoring-alerts so an alert there opens work in this project.",
        },
      },
    },
    purposes: {
      intake: "New issues",
      approvals: "Approval requests",
      notifications: "Progress updates",
      status: "Status updates",
      delivery: "PR / patch delivery",
      deployment: "Deployment events",
      incidents: "Incident alerts",
      code: "Code context",
    },
    purposeLabels: {
      freshdesk: {
        intake: "New tickets",
        notifications: "Progress notes",
      },
      topdesk: {
        intake: "New incidents",
        notifications: "Progress notes",
      },
      github: {
        notifications: "Webhook events",
      },
      kubernetes: {
        status: "Runtime context",
      },
      argo: {
        deployment: "Rollout / promotion events",
        approvals: "Promotion approvals",
        status: "Deployment status",
      },
    },
    purposeDescriptions: {
      intake: "Inbound items from this resource create issues in this project.",
      approvals: "Approval requests and decision cards are sent here.",
      notifications: "Progress updates are posted to this resource.",
      status: "Status changes are mirrored to this resource.",
      delivery: "Patches and pull requests are delivered through this resource.",
      deployment: "Deployment events from this resource attach to this project.",
      incidents: "Incident alerts from this resource land on this project.",
      code: "This resource provides code context for the agent.",
    },
    refTypes: {
      slack_channel: "Channel",
      slack_user_group: "User group",
      teams_channel: "Channel",
      freshdesk_group: "Group",
      freshdesk_product: "Product",
      freshdesk_tag: "Tag",
      freshdesk_account: "All tickets",
      odoo_team: "Helpdesk team",
      topdesk_operator_group: "Operator group",
      jira_project: "Jira project",
      jira_component: "Component",
      linear_team: "Team",
      linear_project: "Linear project",
      github_repo: "Repository",
      gitlab_repo: "Repository",
      bitbucket_repo: "Repository",
      kubernetes_cluster: "Cluster",
      kubernetes_namespace: "Namespace",
      kubernetes_service: "Service",
      kubernetes_label_selector: "Label selector",
      argo_application: "Application",
      argo_rollout: "Rollout",
      argo_project: "Argo project",
      argo_namespace: "Namespace",
      email_alias: "Alias",
      webhook_endpoint: "Endpoint",
    },
  },

  projectCodebaseWizard: {
    title: "Add codebase",
    openButton: "Add codebase…",
    linkExisting: "Link an existing codebase…",
    stepLabel: "Step {step} of {total}",
    steps: {
      source: "Choose source",
      connection: "Connection",
      select: "Select repo or folder",
      configure: "Configure access",
      attach: "Attach to project",
      done: "Codebase added",
    },
    explainer: "A connection provides access (the credential, e.g. a GitHub App install). A codebase is the concrete repo or folder agents work on. Channels and integrations handle intake and delivery — Slack, PR delivery, webhooks.",
    sources: { github: "GitHub", gitlab: "GitLab", bitbucket: "Bitbucket", local: "Local folder", ssh: "SSH server" },
    sourceHints: {
      github: "Repository on github.com",
      gitlab: "Repository on GitLab",
      bitbucket: "Repository on Bitbucket",
      local: "Folder on the FixControl host",
      ssh: "Repository or folder on a remote server",
    },
    connectionHelp: "A connection is the credential FixControl uses to reach your code. Pick an existing one or connect a new account.",
    connected: "Connected",
    noConnections: "No {source} connection yet.",
    connectCta: "Connect {source}",
    repoSearch: "Search repositories…",
    noRepoMatches: "No repositories match your search.",
    repoFreeFormLabel: "Repository (owner/name)",
    branch: "Branch",
    localPathLabel: "Folder path",
    localBaseHint: "Connection base path: {base}",
    remotePathLabel: "Remote path",
    nameLabel: "Codebase name",
    sourceLabel: "Source",
    accessMode: "Access mode",
    modeHelp: {
      read_only: "Agents can inspect and analyse the code but never change it.",
      sandbox: "Agents propose patches that are verified in an isolated sandbox; changes ship through review. Recommended.",
      writable: "Agents may write directly to the working tree. Use with care.",
    },
    attachProject: "Project",
    useAsDefault: "Use as default codebase for this project",
    addCodebase: "Add codebase",
    adding: "Adding…",
    retryAttach: "Retry attach",
    partialFailureTitle: "Codebase created, but not attached",
    partialFailureBody: "\u201c{name}\u201d exists, but attaching it to {project} failed: {error}. Retry the attach — no duplicate codebase will be created.",
    noPermission: "Adding codebases requires the admin role — ask an admin to add one.",
    doneSub: "\u201c{name}\u201d is attached to {project}.",
    nextInspect: "Run codebase inspection",
    nextPosture: "Check codebase posture",
    nextGuardrails: "Configure guardrails",
    close: "Done",
    back: "Back",
    next: "Continue",
  },
  settingsCodebases: {
    eyebrow: "Settings",
    title: "Codebases",
    subtitle: "Code repositories and folders that agents can operate on for {company}.",
    subtitleDefault: "your tenant",
    addCodebase: "Add codebase",
    deleteConfirm: "Remove codebase \"{name}\"? The connection to the repository is removed. Historical commit references, patch lineage and verification evidence are kept, and an admin can restore it.",
    deletedToast: "{name} deleted",
    testFailedToast: "Connection test for {name} failed — network error.",
    createdToast: "{name} created",
    updatedToast: "{name} updated",
    empty: "No codebases configured",
    emptyHint: "Add a codebase to tell agents where your code lives.",
    needInfra: "Set up an infrastructure connection first (Settings → Infrastructure).",
    needInfraStepLabel: "First, this",
    needInfraStep: "Tell FixControl where your code lives. After that you can add codebases here.",
    needInfraCta: "Connect infrastructure",
    sandboxDb: {
      label: "Database for verification runs",
      hint: "Whether a verification run applies this codebase's database migrations before it tests the change. With no database, migrations are skipped and a change that depends on one cannot be proven.",
      inherit: "Default (use system setting)",
      none: "None — skip database migrations",
      url: "URL — use a database URL for this codebase",
      docker: "A fresh throwaway database for every run",
      engineLabel: "Sidecar database engine",
      engineInherit: "Default (use system setting)",
      enginePostgres: "PostgreSQL",
      engineMysql: "MySQL",
      engineHint: "Pick MySQL for Laravel/Magento codebases whose migrations use MySQL-specific SQL. Leave it on Default and this codebase follows the workspace-wide choice, which is PostgreSQL unless an operator changed it.",
      urlLabel: "Database URL for this codebase",
      urlPlaceholderUnset: "postgres://user:pass@host:5432/db",
      urlPlaceholderSet: "•••••••• stored — leave blank to keep",
      urlHint: "Stored encrypted and used only for this codebase. Leave it blank and this codebase uses the workspace-wide database instead. Point it at an isolated throwaway database — verification runs migrations against whatever this names.",
      urlClear: "Clear the stored URL",
    },
    fields: {
      projectFile: "Project file",
      projectRoot: "Project root",
      scm: "SCM",
      analyses: "Analyses",
      techDocs: "Tech docs",
      access: "Access",
      repository: "Repository",
      path: "Path",
      remotePath: "Remote path",
      branch: "Branch",
      mode: "Mode",
      via: "Via",
      unknown: "Unknown",
      analysisCount: "{count} file",
      analysisCountPlural: "{count} files",
      docCount: "{count} ref",
      docCountPlural: "{count} refs",
    },
    multiEntry: {
      add: "Add",
    },
    actions: {
      viewPosture: "View posture",
      testConnection: "Test connection",
      edit: "Edit",
      delete: "Delete",
    },
    modal: {
      titleEdit: "Edit {name}",
      titleAddWindev: "Add WINDEV codebase",
      titleAdd: "Add codebase",
      subEdit: "Update codebase configuration",
      subAdd: "Connect a codebase to an existing infrastructure connection",
      submitSave: "Save",
      submitAddWindev: "Add WINDEV project",
      submitAdd: "Add codebase",
      noConnections: "No infrastructure connections configured",
      noConnectionsHint: "Set up a connection first in Settings → Infrastructure, then come back here.",
      infrastructureConnection: "Infrastructure connection",
      codebaseName: "Codebase name",
      codebaseNamePlaceholderWindev: "e.g. TaurusKassa",
      codebaseNamePlaceholderGeneric: "e.g. POS Backend",
      description: "Description",
      descriptionOptional: "(optional)",
      descriptionPlaceholder: "What does this codebase contain?",
      platformType: "Platform type",
      repository: "Repository",
      repoSelectPlaceholder: "— select a repository —",
      repoPrivate: " (private)",
      repoSelectFooter: "{count} repo accessible to this installation.",
      repoSelectFooterPlural: "{count} repos accessible to this installation.",
      repoSelectManageLink: "Add or remove repos on GitHub",
      repoLoading: "Loading repositories…",
      repoLoadFailed: "Could not load repos from GitHub: {error}",
      repoPlaceholder: "org/repo-name",
      defaultBranch: "Default branch",
      branchPlaceholder: "main",
      windevHeader: "WINDEV project configuration",
      windevProjectRootPlaceholderSsh: "/opt/windev/TaurusKassa",
      windevProjectRootPlaceholderLocal: "C:/WINDEV/Projects/TaurusKassa",
      windevProjectFile: "Project file (.wdp)",
      windevProjectFilePlaceholder: "TaurusKassa.wdp",
      windevSourceControl: "Source control",
      windevAnalysisLabel: "Analysis files (.wda)",
      windevAnalysisHint: "Add paths relative to project root",
      windevAnalysisPlaceholder: "e.g. TaurusKassa.wda",
      windevDocsLabel: "Technical documentation",
      windevDocsHint: "References to exported docs or imported doc sources for knowledge retrieval",
      windevDocsPlaceholder: "e.g. docs/architecture-overview.pdf",
      defaultMode: "Default mode",
    },
  },

  settingsInfra: {
    eyebrow: "Settings",
    title: "Infrastructure",
    subtitle: "Connect a credential and pick which repositories your agents work on. {company} agents can only access codebases activated below.",
    subtitleDefault: "FixControl",
    activeConnections: "Active connections",
    addConnection: "Add connection",
    whereDoesCodeLive: "Where does your code live?",
    desktopRequired: "Desktop app required",
    downloadDesktop: "Download desktop app",
    addAnother: "Add another",
    setUp: "Set up",
    connected: "Connected",
    githubAppInstalledToast: "GitHub App installed",
    githubInstallFailedToast: "GitHub install failed: {reason}",
    gmailConnectedToast: "Gmail connected",
    gmailConnectFailedToast: "Gmail connect failed: {reason}",
    jiraConnectedToast: "Jira connected",
    jiraConnectFailedToast: "Jira connect failed: {reason}",
    linearConnectedToast: "Linear connected",
    linearConnectFailedToast: "Linear connect failed: {reason}",
    deletedToast: "{name} deleted",
    createdToast: "{name} created",
    updatedToast: "{name} updated",
    deleteConfirm: "Remove connection \"{name}\"? Codebases that use it stop working until it is reconnected. Its audit history and the verification evidence of past runs are kept.",
    localTestNeedsDesktop: "Local connections can only be tested from the desktop app.",
    localMissingBasePath: "Missing base path — edit the connection and set one.",
    localNeedsDesktopToCreate: "Local connections require the desktop app.",
    repoActiveCount: "· {active}/{total} repo active",
    repoActiveCountPlural: "· {active}/{total} repos active",
    codebaseCount: "· {count} codebase",
    codebaseCountPlural: "· {count} codebases",
    statusLabels: {
      connected: "Connected",
      pending: "Pending",
      error: "Error",
      not_configured: "Not configured",
    },
    githubHealth: {
      reconnectRequiredTitle: "Reconnect required",
      reconnectRequiredHint: "Recent GitHub calls hit a permanent auth error. Reinstall the GitHub App to restore PR delivery.",
      reconnectRequiredWithReason: "Recent GitHub calls failed: {reason}. Reinstall the App or clear the warning if you've already restored access.",
      lastErrorLabel: "Last GitHub error:",
      failedCount: "{count} consecutive failures",
      lastPrPrefix: "Last PR delivery:",
      reinstall: "Reinstall GitHub App",
      clearPin: "I've fixed it, clear the warning",
      cleared: "Reconnect warning cleared",
    },
    providerHealth: {
      reconnectRequiredTitle: "Reconnect required",
      reconnectRequiredHint: "Recent calls to this integration hit a permanent auth error. Re-run the connect flow to restore access.",
      reconnectRequiredWithReason: "Recent calls failed: {reason}. Re-run the connect flow to restore access.",
      lastErrorLabel: "Last error:",
      failedCount: "{count} consecutive failures",
      lastDeliveryPrefix: "Last successful delivery:",
      reconnect: "Reconnect",
    },
    githubRepos: {
      loading: "Loading repositories…",
      loadFailed: "Could not load repositories: {error}",
      noneYetPrefix: "This installation has access to no repositories yet.",
      addOnGithub: "Add some on GitHub",
      manageOnGithub: "Add or remove repos on GitHub",
      removeCodebaseTitle: "Remove codebase",
      activate: "Activate",
      activateFailed: "Activation failed ({status})",
      deleteFailed: "Delete failed ({status})",
      activatedToast: "{name} activated",
      removedToast: "{name} removed",
      removeConfirm: "Remove \"{name}\" from active codebases? The connection is removed. Historical commit references, patch lineage and verification evidence are kept, and an admin can restore it.",
      activeBadge: "Active",
    },
    otherCodebases: {
      empty: "No codebases yet for this connection.",
      removeCodebaseTitle: "Remove codebase",
      addCodebase: "Add codebase",
    },
    addModal: {
      title: "Add {label} connection",
      connectionName: "Connection name",
    },
    editModal: {
      title: "Edit {label} connection",
    },
    field: {
      browse: "Browse",
    },
    github: {
      connectTitle: "Connect GitHub",
      connectSub: "Install the FixControl GitHub App on selected repositories. Pull requests appear under the bot identity, and tokens rotate automatically.",
      installTitle: "Install on GitHub",
      installBody: "Recommended. You'll be redirected to GitHub to choose which repositories the app can access. PRs are attributed to the FixControl bot.",
      continueOnGithub: "Continue on GitHub",
      deviceTitle: "Use a device code",
      deviceSub: "For headless setups or when you can't sign in to FixControl in this browser. PRs are attributed to your GitHub user.",
      patTitle: "Advanced: paste a Personal Access Token",
      patSub: "Use your own token. Scopes needed: repo, workflow, read:user.",
      getDeviceCode: "Get a device code",
      deviceFailedStart: "Failed to start device flow",
      userCode: "User code",
      verifyStep1Prefix: "1. Open ",
      verifyStep2: "2. Paste the code and approve. This page will pick it up automatically.",
      waitingForApproval: "Waiting for approval…",
      deviceFailed: "Device flow failed",
      patName: "GitHub (PAT)",
      patConnectionName: "Connection name",
      patTokenLabel: "Personal Access Token",
      patTokenPlaceholder: "ghp_...",
      patHelpPrefix: "Create at ",
      patHelpSuffix: " with scopes: repo, workflow.",
      patUserLabel: "GitHub username (optional)",
      patUserPlaceholder: "your-username",
      saveToken: "Save token",
      genericFailed: "Failed",
      networkError: "Network error",
    },
    gitlab: {
      connectTitle: "Connect GitLab",
      connectSub: "Authorize FixControl to push patches and open merge requests against your GitLab projects. Refresh tokens are stored encrypted; access tokens rotate automatically.",
      oauthTitle: "Sign in with GitLab",
      oauthBody: "Recommended. You'll be redirected to GitLab to grant the api and read_user scopes. Merge requests are attributed to your GitLab user, and tokens refresh automatically.",
      continueWithGitlab: "Continue with GitLab",
      tokenTitle: "Advanced: paste a Personal Access Token",
      tokenSub: "Use your own token. Required when running against a self-hosted GitLab instance that does not expose the FixControl OAuth client.",
      tokenName: "GitLab (PAT)",
      tokenConnectionName: "Connection name",
      tokenBaseUrlLabel: "GitLab URL (leave blank for gitlab.com)",
      tokenBaseUrlHint: "Used for self-hosted instances. Cloud users leave this blank.",
      tokenLabel: "Personal Access Token",
      tokenHelpPrefix: "Create at ",
      tokenHelpSuffix: " with scope: api.",
      saveToken: "Save token",
    },
    bitbucket: {
      connectTitle: "Connect Bitbucket",
      connectSub: "Authorize FixControl to push patches and open pull requests against your Bitbucket repositories. Refresh tokens are stored encrypted; access tokens rotate automatically.",
      oauthTitle: "Sign in with Bitbucket",
      oauthBody: "Recommended. You'll be redirected to Bitbucket to grant the scopes configured on your OAuth Consumer (typically repository, repository:write, pullrequest, pullrequest:write).",
      continueWithBitbucket: "Continue with Bitbucket",
      tokenTitle: "Advanced: paste an access token or App Password",
      tokenSub: "Use a workspace/repository access token (recommended) or an App Password tied to a username. Required when the FixControl OAuth Consumer cannot be registered.",
      tokenName: "Bitbucket (token)",
      tokenConnectionName: "Connection name",
      tokenBaseUrlLabel: "Bitbucket API URL (leave blank for Cloud)",
      tokenBaseUrlHint: "Used for forward-compat with Atlassian-hosted proxies. Cloud users leave this blank.",
      tokenUsernameLabel: "Bitbucket username (App Password only)",
      tokenUsernameHint: "Leave blank when using a workspace/repository access token. Required for App Passwords.",
      tokenLabel: "Access Token, OAuth Bearer, or App Password",
      saveToken: "Save token",
    },
    jira: {
      connectTitle: "Connect Jira",
      connectSub: "Authorize FixControl to mirror BUG and FEATURE issues to a Jira Cloud project and link delivered pull requests back to the ticket. Refresh tokens are stored encrypted; access tokens rotate automatically.",
      oauthTitle: "Sign in with Atlassian",
      oauthBody: "Recommended. You'll be redirected to Atlassian to grant read:jira-work, write:jira-work, and offline_access scopes. Issues are attributed to your Atlassian user.",
      continueWithJira: "Continue with Atlassian",
      tokenTitle: "Advanced: paste an email + API token",
      tokenSub: "Use your Atlassian account email and a Jira API token. Required when the FixControl OAuth app is not registered for this deployment, or for self-hosted Jira (DC/Server).",
      tokenName: "Jira (API token)",
      tokenConnectionName: "Connection name",
      tokenBaseUrlLabel: "Jira site URL",
      tokenEmailLabel: "Atlassian account email",
      tokenLabel: "API Token",
      tokenHelpPrefix: "Create at ",
      tokenHelpSuffix: ".",
      defaultProjectLabel: "Default project key (e.g. POS)",
      saveToken: "Save token",
    },
    linear: {
      connectTitle: "Connect Linear",
      connectSub: "Authorize FixControl to mirror BUG and FEATURE issues to a Linear team and attach delivered pull requests to the issue.",
      oauthTitle: "Sign in with Linear",
      oauthBody: "Recommended. You'll be redirected to Linear to grant read and write scopes. Issues are attributed to your Linear user.",
      continueWithLinear: "Continue with Linear",
      tokenTitle: "Advanced: paste an API key",
      tokenSub: "Use a Linear personal API key. Required when the FixControl OAuth app is not registered for this deployment.",
      tokenName: "Linear (API key)",
      tokenConnectionName: "Connection name",
      tokenLabel: "API Key",
      tokenHelpPrefix: "Create at ",
      tokenHelpSuffix: ".",
      defaultTeamLabel: "Default team key (e.g. ENG)",
      saveToken: "Save token",
    },
    gmail: {
      connectTitle: "Connect Gmail",
      connectSub: "Authorize FixControl to read inbound mail from a Google account. Refresh tokens are stored encrypted; access tokens rotate automatically.",
      signInTitle: "Sign in with Google",
      signInBody: "You'll be redirected to Google to grant the {readScope} and {modifyScope} scopes (the latter enables push notifications via Pub/Sub if configured).",
      continueWithGoogle: "Continue with Google",
      footnote: "After connecting, edit the connection to add recipient routing rules — for example, mapping {example} to a specific tenant. Without rules, every inbound message routes to the tenant that owns this connection.",
      editTitle: "Edit Gmail connection",
      connectedAs: "Connected as",
      reconnect: "Reconnect",
      recipientRouting: "Recipient routing",
      recipientRoutingHelp: "Map a To: address to a tenant. Without rules, every message routes to {tenant}.",
      addRule: "Add rule",
      noRules: "No rules configured. All inbound mail routes to {tenant}.",
      rulePlaceholder: "support+flirt@fixcontrol.nl",
      tenantUnknown: "{id} (unknown)",
      lastSync: "Last sync {time}",
      syncNow: "Sync now",
      syncQueued: "Queued",
      syncFailed: "Failed",
      syncDisabledReason: "Reconnect to enable sync",
      derived: {
        // "Configured" on purpose: credentials + no stored error is not a
        // verified connection — live green comes from the health panel.
        connected: "Configured",
        sync_error: "Sync error",
        reconnect_required: "Reconnect required",
        disconnected: "Disconnected",
      },
    },
  },

  settingsSecurity: {
    eyebrow: "Settings",
    title: "Security",
    subtitle: "Your own sign-in and two-factor settings; admins also manage organization security here.",
    personalSubtitle: "Your own password and two-factor authentication.",
    organizationSubtitle: "Two-factor policy and session revocation for your whole organization.",
    platformSubtitle: "JWT signing keys and rate limits — FixControl platform internals.",
    sections: {
      personal: "My security",
      organization: "Organization security",
      platform: "FixControl platform operations",
    },
    accessRequired: "Admin access required",
    accessRequiredDetail: "Only admins can run security operations.",
    adminOnlyHint: "Session revocation, JWT rotation and rate-limit inspection are admin-only and don't apply to your account.",
    platformOnlyHint: "Signing-key rotation and the rate-limit inspector are FixControl platform operations, available to platform staff only.",
    password: {
      header: "Change password",
      description: "Use a unique password — your sign-in identity is your email and we never share it across sites.",
      currentLabel: "Current password",
      newLabel: "New password",
      confirmLabel: "Confirm new password",
      requirement: "At least {min} characters.",
      save: "Update password",
      saving: "Updating…",
      saved: "Password updated",
      errors: {
        badCurrent: "Your current password is incorrect.",
        samePassword: "Pick a password different from the current one.",
        weak: "Password must be at least {min} characters.",
        mismatch: "Passwords do not match.",
        network: "Network error — please retry.",
      },
    },
    revoke: {
      header: "Revoke sessions",
      description: "Signs users out by refusing every session token issued before this moment. Use this to immediately end the sessions of a user or workspace — affected users simply sign in again.",
      scope: "Scope",
      scopeGlobal: "Everyone (entire platform)",
      scopeTenant: "All members of one workspace",
      scopeUser: "One user",
      tenantId: "Tenant id",
      tenantPlaceholder: "e.g. FixControl",
      userId: "User id",
      userPlaceholder: "e.g. u-abc123",
      findUsersPrefix: "Find user ids in",
      findUsersLink: "Users",
      revokeButton: "Revoke…",
      confirmGlobal: "Sign every user out",
      confirmTenant: "Sign all members of workspace \"{id}\" out",
      confirmUser: "Sign user \"{id}\" out",
      confirmPlaceholder: "…",
      confirmTrailing: ". This cannot be undone — affected users will have to sign in again.",
      confirmRevoke: "Confirm revoke",
      revoking: "Revoking…",
      revokedMessage: "Revoked (cutoff {cutoff}). Matching sessions will be refused on their next request.",
      networkError: "Network error",
    },
    rateLimit: {
      header: "Rate-limit buckets",
      refresh: "Refresh",
      failedToLoad: "Failed to load",
      unsupported: "The current rate-limit store doesn't support enumeration (likely a Redis adapter without a snapshot helper). Check Redis directly to inspect buckets.",
      empty: "No tracked buckets — either no traffic yet, or every bucket is at full capacity.",
      headers: {
        bucket: "Bucket",
        subject: "Subject",
        tokens: "Tokens",
        fill: "Fill",
        updated: "Updated",
      },
      ageSeconds: "{n}s ago",
      ageMinutes: "{n}m ago",
      ageHours: "{n}h ago",
    },
    rotation: {
      header: "Rotate JWT key",
      description: "Generates a strong new signing key and applies it according to the mode you pick. The actual secret value is written to {file} (or the DB, in db-mode) — never returned in the response.",
      mode: "Mode",
      modeDb: "DB-backed (no restart)",
      modeMulti: "Multi-secret (survive sessions)",
      modeSingle: "Single (force re-login)",
      blurbDb: "Writes the new key to the DB. Node routes pick it up within 60s — no restart needed. Edge middleware still reads env at cold start and won't verify tokens signed by the new key until its worker recycles.",
      blurbMulti: "Stages a new env manifest that carries the old key forward. Both Node and Edge need a restart, but existing sessions survive (verified against the old key) until they expire.",
      blurbSingle: "Stages a single new secret. All existing sessions are invalidated on restart; every user has to sign in again.",
      rotateButton: "Rotate…",
      confirmPrefix: "Rotate with mode",
      confirmSingle: "All active sessions will be invalidated. Every user has to sign in again.",
      confirmMulti: "Restart required; existing sessions survive via the carried-over key.",
      confirmDb: "No restart for Node; Edge will lag until its worker recycles.",
      confirmTypePrefix: "Type",
      confirmTypeSuffix: "to enable the confirm button:",
      confirmRotate: "Confirm rotate",
      rotating: "Rotating…",
      stagedMessage: "Rotation staged. Follow the instructions below to activate.",
      activeMessage: "Rotation active for Node routes. Edge still needs a restart.",
      fingerprint: "New key fingerprint:",
      networkError: "Network error",
    },
    history: {
      header: "Recent revocations",
      refresh: "Refresh",
      failedToLoad: "Failed to load",
      empty: "No revocations recorded in the audit log.",
      headers: {
        when: "When",
        scope: "Scope",
        target: "Target",
        by: "By",
        cutoff: "Cutoff",
      },
    },
  },

  settingsAgents: {
    eyebrow: "Settings",
    title: "Agent behavior",
    subtitle: "Policy for the CTO-led mission orchestration, per tenant. Defaults reproduce today's behavior — flipping a switch is opt-in.",
    loading: "Loading…",
    policy: "Mission policy",
    overridden: "Overridden",
    resetToDefault: "Reset to default",
    saved: "Saved",
  },

  settingsGovernance: {
    loadFailed: "Could not load governance policies",
    signInAgain: "Sign in again",
    setUpTwoFactor: "Set up two-factor authentication",
    loading: "Loading governance policies…",
    eyebrow: "Governance",
    title: "Governance & Policy",
    subtitle: "How this organization governs AI operations — approvals, autonomy, risk, verification, communication, escalation, and retention.",
    domains: {
      approvalTitle: "Approval Policies",
      approvalDesc: "When human sign-off is required, who approves, and what happens if no one does in time.",
      notificationTitle: "Notification Policies",
      notificationDesc: "Who is told what, on which channel, when something needs attention.",
      autonomyTitle: "AI Autonomy Policies",
      autonomyDesc: "How much the AI may do on its own — from cautious to fast-moving.",
      riskTitle: "Risk Policies",
      riskDesc: "Which kinds of change are sensitive, and the controls they trigger.",
      verificationTitle: "Verification Policies",
      verificationDesc: "What must be proven — sandbox, tests, QA sign-off — before a change can ship.",
      communicationTitle: "Customer Communication Policies",
      communicationDesc: "Whether and how the AI may talk to customers, and what it may never touch.",
      escalationTitle: "Escalation Policies",
      escalationDesc: "What happens when work stalls, times out, or breaches an SLA.",
      auditTitle: "Audit & Retention Policies",
      auditDesc: "How long traces, approvals, audit, runtime logs, and customer messages are kept.",
    },
    autonomyModes: {
      conservativeLabel: "Conservative",
      conservativeDesc: "Maximum oversight. Plans always need sign-off, the AI pauses on every gate, and revise/retry chains are short.",
      balancedLabel: "Balanced",
      balancedDesc: "The recommended default. Sign-off on risky or critical work; the AI keeps moving on routine work.",
      aggressiveLabel: "Aggressive",
      aggressiveDesc: "Maximum throughput. No plan gate, the AI keeps building through gates, and approvals accept work as-is.",
      customLabel: "Custom",
      customDesc: "Tune the underlying controls by hand. Use Advanced below when a preset doesn't fit.",
    },
    approvalRequirements: {
      never: "Never",
      risky_only: "Risky changes only",
      production_impact: "Production-impact",
      security_sensitive: "Security-sensitive",
      external_integration: "External integrations",
      database_change: "Database changes",
      always: "Always",
    },
    planApprovalPolicies: {
      alwaysLabel: "Always require plan approval",
      alwaysDesc: "Every mission pauses after analysis until its execution plan is approved.",
      risky_or_criticalLabel: "Only for risky or critical work",
      risky_or_criticalDesc: "Risk-flagged or critical missions pause for plan approval; routine work proceeds.",
      neverLabel: "Never require plan approval",
      neverDesc: "The AI may execute without a plan sign-off.",
    },
    timeoutActions: {
      pause: "Pause the mission",
      escalate: "Escalate",
      auto_reject: "Auto-reject",
      notify_fallback: "Notify fallback approver",
    },
    verificationStrictness: {
      lenientLabel: "Lenient",
      lenientDesc: "Verify the working tree; auto-revise on a failed sandbox run.",
      standardLabel: "Standard",
      standardDesc: "The shipping default sandbox behaviour.",
      strictLabel: "Strict",
      strictDesc: "Verify a clean clone pinned to the run's commit; no auto-revise.",
    },
    commsModes: {
      never_autosendLabel: "Never auto-send",
      never_autosendDesc: "The AI drafts; a human always sends.",
      draft_onlyLabel: "Draft only",
      draft_onlyDesc: "The AI prepares replies for review.",
      autosend_high_confidenceLabel: "Auto-send high-confidence",
      autosend_high_confidenceDesc: "Send when confidence clears the threshold.",
      autosend_low_riskLabel: "Auto-send low-risk",
      autosend_low_riskDesc: "Send routine, low-risk replies automatically.",
    },
    riskLevels: { low: "Low", medium: "Medium", high: "High", critical: "Critical" },
    riskCategories: {
      auth: "Authentication changes",
      billing: "Billing changes",
      database_schema: "Database schema changes",
      security_files: "Security-sensitive files",
      external_api: "External API integrations",
      deployment: "Deployment / release actions",
      infrastructure: "Infrastructure changes",
      terraform: "Terraform apply",
      kubernetes: "Kubernetes cluster changes",
      secret_rotation: "Secret rotation",
      dns: "DNS changes",
      feature_flag: "Feature flag rollout",
    },
    notificationTriggers: {
      approval_required: "Approval required",
      patch_failed: "Patch failed",
      sandbox_failed: "Sandbox failed",
      mission_completed: "Mission completed",
      mission_failed: "Mission failed",
      retry_exhausted: "Retry exhausted",
      risky_patch_detected: "Risky patch detected",
      customer_reply_pending: "Customer reply pending",
    },
    escalationTriggers: {
      retry_exhausted: "Retries exhausted",
      approval_timeout: "Approval timed out",
      inactivity: "Inactivity",
      sla_breach: "SLA breach",
      stuck_mission: "Stuck mission",
    },
    escalationActions: {
      notify_fallback: "Notify in FixControl",
      reassign: "Reassign the issue and notify",
      auto_reject: "Reject automatically",
      page: "Notify in FixControl and by e-mail",
    },
    escalationTriggerHints: {
      approval_timeout: "An approval waited longer than this for a decision. Reminders go to the rule's target person when one is set, otherwise to the managers, or the administrators when there are no managers (the administrators when the rule targets Admin); automatic rejection is recorded as decided by the timeout.",
      stuck_mission: "A running mission made no progress for this long. Repeats at most once per period while it stays stuck. When on, it replaces the MISSION_STALL_MINUTES notice.",
      inactivity: "An open issue nobody changed for this long. Fires once per quiet period; any change to the issue starts a new one.",
      sla_breach: "An open issue still not done this long after it came in. Fires once per issue.",
      retry_exhausted: "A mission was stopped because its retries ran out. Fires at that moment.",
    },
    escalationAppliedWhen: "Escalation rules act on their own only when “Apply approval timeouts and escalations automatically” is on (Approval tab), or when the governance sweep runs.",
    channels: { inapp: "FixControl", email: "Email", slack: "Slack", teams: "Teams", webhook: "Webhook" },
    commsChannels: { portal: "Portal", email: "Email", slack: "Slack", api: "API" },
    roles: {
      cto: "CTO", qa: "QA", release_manager: "Release Manager", team_lead: "Team Lead",
      manager: "Manager", admin: "Admin", support: "Support",
      platform_engineer: "Platform Engineer", security_officer: "Security Officer",
      compliance_officer: "Compliance Officer", incident_commander: "Incident Commander",
      deployment_approver: "Deployment Approver",
    },
    fields: {
      simpleApprovers: "Who may approve",
      simpleApproversHint: "The role(s) approval requests are routed to by default, in order. More specific rules below keep winning for the cases they cover. With staged approvals off, any one administrator or manager decides; with staged approvals on, each role is a stage decided in order by a different person.",
      simpleApproversAdd: "Add role…",
      simpleApproversDefaultNote: "No default rule is configured yet. Without your own choice, requests are routed to CTO and then Admin as a suggestion and any administrator or manager decides; this built-in chain is never staged.",
      simpleApproversSpecificNote: "More specific rules also exist; they win for the risk levels or change types they name.",
      planApproval: "Plan approval before execution",
      planApprovalHint: "Whether the AI must get its execution plan approved before it starts implementing. This enforces the ORCH_PLAN_APPROVAL_POLICY gate.",
      planApprovalOutcomeLabel: "Policy outcome:",
      planApprovalOutcome: {
        always: "All work will pause after analysis until a workspace approver approves the execution plan. Implementation is blocked until then.",
        risky_or_critical: "Risky and critical work will pause after analysis until a workspace approver approves the execution plan; routine work executes without a plan gate.",
        never: "The AI may execute immediately after analysis — no plan sign-off is required.",
      },
      approvalWhen: "When is human sign-off required?",
      approvalWhenHint: "The baseline gate before AI work ships. Routing rules below can refine it.",
      timeout: "If no one responds in time",
      timeoutHint: "What happens to a request nobody decides in time. 0 minutes = wait indefinitely. A reminder goes to the managers, or the administrators when there are no managers; pausing keeps the request waiting and adds nobody. An enabled “Approval timed out” escalation rule with its own delay replaces this.",
      timeoutAutomatic: "Checked automatically every five minutes.",
      timeoutManual: "Not checked automatically: turn on “Apply approval timeouts and escalations automatically” (organization level, below), or run the governance sweep. Until then a late request just keeps waiting.",
      timeoutAppliedUnknown: "Applied only while “Apply approval timeouts and escalations automatically” is on, or when someone runs the governance sweep.",
      routing: "Approval routing & stages",
      routingHint: "Match approvals by risk to an approver chain (e.g. CTO → QA → Release Manager) and choose the channels requests go to. The chain is enforced only with staged approvals on: each stage is then decided in order by a different person, so a route with N stages needs N people who can decide. Otherwise it is routing only and any one administrator or manager decides.",
      routingStagedOn: "Staged approvals are on: a new request whose route has stages is decided stage by stage, one person per stage.",
      routingStagedOff: "Staged approvals are off: these chains choose channels and suggest who decides, but any one administrator or manager settles a request. Turn on “Enforce staged approvals” below to enforce the stages.",
      routeWebhookNotDelivered: "Webhook: approval requests have no webhook sender, so nothing is sent on this channel.",
      routeEmailOrgWide: "E-mail: an enabled rule with e-mail turns on approval e-mail for the whole organization, also for requests no rule with its own channels matches. It goes to every active administrator and manager, not to the roles in this rule.",
      approvalEmailOrgWide: "Approval e-mail goes to every active administrator and manager, whatever role this rule names, and this rule turns it on for the whole organization.",
      stageInheritsTimeout: "no deadline of its own: inherits the company deadline and action",
      routingEmpty: "No routing rules — the baseline above applies to everything.",
      ruleName: "Rule name",
      risk: "Risk:",
      channelsLabel: "Channels:",
      approvalMode: "Mode:",
      minApprovers: "min approvers",
      resolvedChain: "Resolved chain",
      stage: "Stage {n}",
      notificationsHint: "On each trigger, route a notification to a role via a channel.",
      // FixControl has a status-notice sender for in-app and Slack only
      // (src/server/orchestration/notify.ts): an e-mail/Teams/webhook route on
      // a mission trigger is stored, matched and audited as `skipped`. The
      // dropdown offered it with nothing saying so, which is exactly the
      // silent failure the product promise forbids.
      channelNotDelivered: "Not delivered for this trigger — FixControl has no sender for this channel on mission status notices yet. The route is kept and recorded as skipped; in-app and Slack still deliver.",
      // In-app is the only channel whose destination is knowable from this
      // screen alone; Slack resolves its target at send time.
      destinationInapp: "Goes to: the {role}s in this workspace, in their notification list.",
      via: "via",
      autonomyAdvanced: "Advanced controls",
      autonomyPlanNote: "The plan-approval gate is governed by Approval Policies above.",
      autonomyOrgDefaultHeader: "These set your organization-wide default.",
      autonomyOrgDefaultBody: "Retry caps, halt/release/resume semantics and the gate-approve semantics here apply across the whole tenant. Need different behavior for a specific workspace, project or environment? Override it per scope in the Hierarchy editor — that takes precedence over this default.",
      autonomyOrgDefaultCta: "Per-scope overrides in the Hierarchy editor",
      verifyStrictness: "Verification strictness",
      requireSandbox: "Require a sandbox pass before approval",
      requireSandboxHint: "A change must verify in the sandbox before it can be approved.",
      requireTests: "Require the test suite to complete",
      requireTestsHint: "Block approval until tests run.",
      requireQa: "Require QA sign-off",
      requireQaHint: "Shown on the execution plan as a required check. No gate waits for a QA decision yet.",
      blockProd: "Block production-impact without QA",
      blockProdHint: "Recorded with the policy. No gate enforces it yet: production changes are not held for QA.",
      commsEnforce: "Enforce these communication policies",
      commsEnforceHint: "Off by default. When on, the rules below cap AI auto-send org-wide (it can only restrict sending, never loosen a mailbox's own policy).",
      commsBehaviour: "AI response behaviour",
      commsThresholds: "Confidence thresholds",
      commsThresholdsHint: "At/above auto-send → AI may send. Between the two → human approval. Below approval → escalate.",
      commsAutosend: "Auto-send ≥",
      commsApproval: "· Human approval ≥",
      commsChannelsLabel: "Allowed channels",
      commsChannelsHint: "Where the AI may communicate externally.",
      restrictedTopics: "Restricted topics",
      restrictedTopicsHint: "The AI may never auto-handle these. Click a preset to add, × to remove.",
      auditNote: "Retention in days. 0 = keep indefinitely.",
      platformManaged: "Platform-managed",
      auditEventsPlatformHint: "Platform-managed: audit retention is enforced platform-wide with a 90-day floor. The audit hash chain is one global sequence, so this window is not a per-tenant setting.",
      runtimeLogsHint: "Runtime logs are deleted after {n} days.",
      customerCommsHint: "Customer communication bodies are redacted after {n} days; metadata and hashes stay.",
      riskApproval: "approval",
      riskVerify: "verify",
      riskEscalate: "escalate",
      escalationAfter: "after",
      stageFallback: "fallback",
      stageFallbackNone: "any admin or manager",
      escalationTo: "to",
    },
    buttons: {
      addRoute: "Add routing rule",
      addStage: "Add stage",
      addNotification: "Add notification rule",
      remove: "Remove",
    },
    knobs: {
      autoReviseLabel: "Auto-revise limit",
      autoReviseHint: "Max CTO-feedback auto-revisions per patch. 0 disables.",
      retryLabel: "Retry limit",
      retryHint: "Max human-approved retries of a failed task before abort.",
      haltLabel: "Pause work on a gate",
      haltHint: "Halt in-flight dev work while an approval is pending.",
      rejectLabel: "Reject aborts the mission",
      rejectHint: "Off → a retry gate opens instead of aborting.",
      releaseLabel: "Add a release step",
      releaseHint: "Plan a release/deployment approval at the end.",
      resumeLabel: "Resume revise on completion",
      resumeHint: "Don't strand a 'changes requested' patch.",
      semanticsLabel: "Approving a flagged patch",
      semanticsHint: "revise: agent fixes it. accept: ships as-is.",
      followupsLabel: "When to create follow-up tasks",
      followupsHint: "approve: on any positive decision (incl. approve-with-revisions). applied: only once the patch is applied/deployed.",
    },
    audit: {
      trace: "Agent traces",
      approvals: "Approvals",
      auditEvents: "Audit events",
      runtimeLogs: "Runtime logs",
      customerComms: "Customer communication",
      days: "days",
      minutes: "min",
      percent: "%",
    },
    tabs: {
      overview: "Overview",
      simulator: "Simulator",
      approval: "Approvals",
      notification: "Notifications",
      autonomy: "AI Autonomy",
      risk: "Risk",
      verification: "Verification",
      communication: "Customer Communication",
      escalation: "Escalations",
      audit: "Audit & Retention",
      safety: "Safety Controls",
    },
    console: {
      scopeLabel: "Policy scope",
      scope_organization: "Organization-wide",
      scope_workspace: "Workspace scope",
      scope_mailbox: "Environment scope",
      scopeHint: "These policies apply at the selected scope and propagate to its descendants.",
      scopeFuture: "Pick a scope on the left to edit it. Locks freeze a field at the scope where they're set — no descendant can override them.",
      posture: "Governance posture",
      saving: "Saving…",
      saved: "Saved",
      saveError: "Couldn't save — retry",
      savedIdle: "All changes saved",
    },
    postures: {
      conservativeLabel: "Conservative",
      conservativeDesc: "Maximum oversight. Production-impacting work needs sign-off, verification is strict, and the AI never sends to customers on its own.",
      balancedLabel: "Balanced",
      balancedDesc: "The recommended default. Risky work is gated and verified; routine work keeps moving, and customer replies are drafted for review.",
      aggressiveLabel: "Aggressive",
      aggressiveDesc: "Maximum throughput. Minimal gating, lenient verification, and low-risk customer replies can auto-send. The sandbox still runs before shipping.",
      customLabel: "Custom",
      customDesc: "Your policies are a custom mix that doesn't match a preset. Pick a posture to reset the baseline, or fine-tune each domain below.",
      applyTitle: "Apply the {name} posture?",
      applyBody: "This updates the organization-wide defaults across autonomy, approvals, verification, communication and retention. Your routing, risk and escalation rules are left untouched. It applies immediately.",
      applyConfirm: "Apply posture",
    },
    overview: {
      intro: "How AI currently behaves in this organization. Click any card to adjust that policy.",
      postureTitle: "Effective posture",
      postureHint: "how AI behaves across each policy domain right now — click a card to adjust it",
      approvalTimeout: "Times out after {n} min",
      approvalTimeoutManual: "{n} min timeout set, not applied automatically",
      approvalTimeoutUnknown: "{n} min timeout, applied when automatic timeouts are on",
      approvalNoTimeout: "Waits indefinitely",
      vSandbox: "Sandbox",
      vTests: "Tests",
      vQa: "QA sign-off",
      verificationNone: "Working-tree check only",
      commsEnforced: "Enforced organization-wide",
      commsAdvisory: "Advisory — not enforced",
      riskValue: "{n} of {total} categories gated",
      riskHint: "Sensitive change classes carrying a control.",
      escalationValue: "{trigger} after {n} min",
      escalationNone: "No escalations active",
      escalationNoneHint: "Nothing is auto-escalated yet.",
      notificationsValue: "{n} active rules",
      notificationsHint: "Trigger → channel → recipient routes.",
      retentionForever: "Kept indefinitely",
      retentionPlatformManaged: "Audit retention: platform-managed",
      retentionHint: "Traces {trace}d · customer messages {comms}d",
      effectiveTitle: "Effective policy",
      effectiveHint: "what happens if the AI attempts each action, under the current policy",
    },
    actionDomains: {
      code: "Code change",
      deployment: "Deployment",
      communication: "Customer communication",
      data: "Data / schema",
      infrastructure: "Infrastructure",
      security: "Security-sensitive",
      integration: "External integration",
      cicd: "CI/CD pipeline",
      kubernetes: "Kubernetes",
      terraform: "Terraform",
      secret: "Secret management",
      db_migration: "Database migration",
      freeze_window: "Freeze window",
    },
    environments: {
      development: "Development",
      staging: "Staging",
      production: "Production",
    },
    scenarios: {
      routineCodefixLabel: "Routine code fix",
      routineCodefixDesc: "A small, tested change in development.",
      largeRefactorLabel: "Large refactor",
      largeRefactorDesc: "A wide change with no tests.",
      schemaChangeLabel: "Database schema change",
      schemaChangeDesc: "An irreversible schema change targeting production.",
      productionDeployLabel: "Production deployment",
      productionDeployDesc: "Shipping a build to production.",
      authChangeLabel: "Authentication change",
      authChangeDesc: "Touches auth and security-sensitive files in production.",
      refundEmailLabel: "Sensitive customer reply",
      refundEmailDesc: "An outbound reply that touches billing, with low AI confidence.",
      routineReplyLabel: "Routine customer reply",
      routineReplyDesc: "A high-confidence outbound reply.",
      infraScaleLabel: "Infrastructure scaling",
      infraScaleDesc: "A production infrastructure change.",
      externalIntegrationLabel: "External integration call",
      externalIntegrationDesc: "An action through an external API.",
    },
    simulator: {
      intro: "Dry-run any action against the live policy. Nothing is saved — pick a scenario or build one, and see exactly what governance would do.",
      scenariosLabel: "Scenarios",
      actionLabel: "Action",
      reset: "Reset",
      domain: "Action domain",
      environment: "Target environment",
      signalsLabel: "Signals",
      blastRadius: "Files / resources",
      confidence: "AI confidence",
      restrictedTopic: "Restricted topic",
      noRestrictedTopic: "None",
      preview: "preview",
      signals: {
        touchesAuth: "Touches auth",
        touchesBilling: "Touches billing",
        touchesDatabaseSchema: "DB schema change",
        touchesSecurityFiles: "Security files",
        touchesExternalApi: "External API",
        touchesInfrastructure: "Infrastructure",
        isDeployment: "Is a deployment",
        isRollback: "Is a rollback",
        isCustomerComms: "Customer communication",
        touchesTerraform: "Terraform apply",
        touchesKubernetes: "Kubernetes change",
        touchesSecrets: "Secret rotation",
        touchesDns: "DNS change",
        touchesFeatureFlag: "Feature flag",
        hasTests: "Has tests",
        sandboxPassed: "Sandbox passed",
        externalRecipient: "External recipient",
        irreversible: "Irreversible",
      },
    },
    decision: {
      outcome: {
        auto_execute: "Auto-executes",
        draft_only: "Draft for human send",
        approval_required: "Approval required",
        blocked: "Blocked by policy",
      },
      riskScore: "Risk score",
      categories: "In play:",
      whyTitle: "Why — decision trace",
      noTriggers: "No policy raised the floor — this action runs freely.",
      conflictTitle: "Conflicts resolved",
      conflictOver: "overrode",
      approvalTitle: "Approval chain",
      noApproval: "No approval required",
      verificationTitle: "Verification",
      escalationTitle: "Escalation",
      noEscalation: "No escalation armed",
      auditTitle: "Audit events generated",
      autoExecYes: "Auto-execution allowed",
      autoExecNo: "Auto-execution held",
      fallback: "fallback",
      mode_sequential: "Sequential",
      mode_parallel: "Parallel",
      minApprovers: "{n} approvers (enforced with staged approvals)",
      sla: "{n} min SLA",
      via: "via",
    },
    scopes: {
      selectLabel: "Policy scope",
      selectHint: "Pick a node in the scope tree. The four-kind hierarchy (organization → workspace → project → environment) means deeper scopes inherit from their parent and can override per field.",
      axis_organization: "Organization",
      axis_team: "Team",
      axis_workspace: "Workspace",
      axis_repository: "Repository",
      axis_environment: "Environment",
      axis_integration: "Integration",
      axis_customer: "Customer",
      lockedHint: "First-lock-wins: a field locked at one scope is frozen for every descendant. Edit at the scope that set the lock to release it.",
      inheritsFrom: "Inherits from parent",
      overriddenHere: "Overridden here:",
      resetAll: "Reset all to parent",
      noOverrides: "No overrides — inherits everything from the parent scope.",
      rulesOrgOnly: "Routing, risk and escalation rules are organization-level — edits here apply org-wide.",
      pickerLabel: "Application level:",
      overriddenBadge: "{n} adjusted locally",
      lockedBadge: "{n} pinned centrally",
      advancedConsoleHint: "Need to manage the structure, agent-behavior bindings, pinned fields or the runtime simulator?",
      advancedConsoleOpen: "Open advanced console",
      editingBanner: "You are editing the {name} level. Changes here apply to this level only; anything unchanged inherits from the level above.",
      resetFieldTitle: "Reset {field} to the inherited value",
      lockFieldTitle: "Pin {field} at this level so levels below can't change it",
      lockedByAncestor: "Pinned centrally at a higher level",
      lockPrompt: "Pin \"{field}\" at this level. Reason:",
    },
    templates: {
      title: "Policy templates",
      hint: "Apply a coherent preset across every policy domain. Reversible — apply another any time.",
      autonomy: "Autonomy",
      friction: "Friction",
      control: "Control",
      apply: "Apply",
      active: "Active",
      applyTitle: "Apply the {name} template?",
      applyBody: "This sets organization-wide defaults across autonomy, approvals, verification, communication and retention to the chosen preset. Your routing, risk and escalation rules are left untouched. It applies immediately.",
      applyConfirm: "Apply template",
      startupLabel: "Startup",
      startupDesc: "Move fast with minimal gating — best for small teams shipping quickly.",
      startupTradeoffs: "Fast shipping · Low oversight · Short retention",
      balancedLabel: "Balanced",
      balancedDesc: "Risky work is gated and verified; routine work keeps moving. The recommended default.",
      balancedTradeoffs: "Solid default · Tests required · Moderate friction",
      enterpriseLabel: "Enterprise",
      enterpriseDesc: "Strong approvals, strict verification and longer retention for larger orgs.",
      enterpriseTradeoffs: "Production gated · QA required · Long retention",
      soc2Label: "SOC 2",
      soc2Desc: "Approvals, verification and audit retention aligned to SOC 2 controls.",
      soc2Tradeoffs: "Audit-ready · QA + tests · 1-year retention",
      iso27001Label: "ISO 27001",
      iso27001Desc: "Security-first approvals and strict verification for ISO 27001.",
      iso27001Tradeoffs: "Security-first · Strict verify · 1-year retention",
      healthcareLabel: "Healthcare (HIPAA)",
      healthcareDesc: "Maximum restraint on customer communication and 6-year retention for HIPAA.",
      healthcareTradeoffs: "No auto-send · 6-year retention · Always approve",
      financialLabel: "Financial (SOX/PCI)",
      financialDesc: "Tight approvals and 7-year retention aligned to financial controls.",
      financialTradeoffs: "7-year retention · Production gated · No auto-send",
      highSecurityLabel: "High-security",
      highSecurityDesc: "Maximum oversight — approve everything, strict verification, no auto-send.",
      highSecurityTradeoffs: "Approve everything · Strict verify · Minimal autonomy",
    },
    deploymentCapabilities: {
      title: "Deployment capabilities",
      description: "What a deployment gate may do on your systems. Each is off until you arm it, and every change is audited with your reason.",
      writeBadge: "Writes out",
      recordOnlyBadge: "Records only",
      phase: {
        disarmedLabel: "Nothing armed.",
        disarmedBody: "Deploy events are ingested and scored, but no gate opens for a deployment FixControl did not ship, and no verdict can reach your systems.",
        observeOnlyLabel: "Observe-only.",
        observeOnlyBody: "Gates open and every decision is recorded, but no verdict leaves FixControl — each carry-back is refused as “disabled” and says so on the gate. This is the phase to run before you let FixControl write.",
        armedLabel: "Armed for writes ({n}).",
        armedBody: "An approved gate can now act on your systems through the capabilities below. What it cannot do is start a deployment or roll one back.",
      },
      keys: {
        standaloneGates: {
          label: "Open gates for your own deployments",
          hint: "Open an approval gate for a deploy that has no FixControl-authored change behind it — the normal case when your pipelines build and deploy your own code. Off, every such deploy is skipped as “no mission link” and no gate ever opens. Opening a gate records a decision; it writes nothing.",
        },
        cicdCarryBack: {
          label: "Carry the verdict back to the CI/CD host",
          hint: "Let an approve or a reject reach the pipeline that is holding the pause — GitHub Actions, GitLab CI or Jenkins. Approving releases the waiting deployment; rejecting fails the run visibly there instead of leaving it to time out. Needs an enabled connection and a credential the host accepts as a reviewer.",
        },
        argoResume: {
          label: "Promote a paused Argo Rollout",
          hint: "Let an approve on a gate opened from a paused Argo Rollout promote the next step. Argo has no rejection primitive, so a reject writes nothing and says so — the pause itself stays the enforcement. Needs an Argo Rollouts capability on the Kubernetes connection.",
        },
        agentChannel: {
          label: "Hand the verdict to an fc-agent in your cluster",
          hint: "Instead of FixControl calling into your network, an approved gate publishes an expiring, single-use authorization that your own agent fetches outbound and may still refuse locally. FixControl never dials your cluster under this profile. Needs a registered, enabled agent on the Kubernetes connection.",
        },
      },
      confirm: {
        title: "Arm “{name}”?",
        bodyWrite: "From now on an approved gate may act on your own systems through this capability. It takes effect immediately, it is recorded in the audit trail, and you can disarm it here in one click.",
        bodyRecordOnly: "Gates will start opening for deploys FixControl did not ship. That records a human decision — it writes nothing to your systems on its own.",
        reasonPlaceholder: "Reason (recorded in the audit trail)",
        cta: "Arm capability",
      },
    },
    safety: {
      intro: "Emergency org-wide switches, each audited. Today they act in the Overview previews, the Simulator and policy evaluation. Deployments, customer messages, integrations and agent runs do not check them yet; only with “Risk rules may require a plan approval” on do Stop all agents and Read-only mode hold a new plan for approval (or block it).",
      statusNormal: "All systems normal",
      statusEngaged: "{n} safety control(s) engaged",
      activeBanner: "{n} emergency safety control(s) are engaged.",
      review: "Review",
      off: "Off",
      active: "Active",
      expired: "Expired",
      autoLifts: "Auto-lifts",
      engage: "Engage",
      engaged: "Engaged",
      reasonPlaceholder: "Reason (recorded in the audit trail)",
      autoLift: "Auto-lift",
      untilCleared: "Until cleared",
      setBy: "Set by",
      controls: {
        stop_all_agents: { label: "Stop all agents", desc: "Marks every AI action as blocked in policy previews. Running agents are not stopped." },
        read_only: { label: "Read-only mode", desc: "Marks every change as blocked in policy previews; analysis stays allowed. Agents do not check it yet." },
        disable_deployments: { label: "Disable deployments", desc: "Marks deployments as blocked in policy previews. The deployment gate does not check it yet." },
        disable_comms: { label: "Disable customer communication", desc: "Marks outbound customer messages as blocked in policy previews. Sending does not check it yet." },
        disable_integrations: { label: "Disable external integrations", desc: "Marks actions through external integrations as blocked in policy previews. Integrations do not check it yet." },
        maintenance_mode: { label: "Maintenance mode", desc: "Marks deployments, data and infrastructure changes as blocked in policy previews. Not checked at runtime yet." },
      },
    },
    auditExplorer: {
      title: "Audit trail",
      chain: {
        verified: "Chain verified",
        verifiedHint: "Hash-chained, append-only audit log. Last verified {when}, {walked} entries walked.",
        broken: "Chain break",
        brokenHint: "Verification stopped at sequence {seq}. The entries before it still verify; contact support with that sequence number.",
        unverified: "Not verified yet",
        unverifiedHint: "Hash-chained, append-only audit log. Verification runs hourly; no result has been recorded yet for this chain.",
      },
      refresh: "Refresh",
      export: "Export",
      searchPlaceholder: "Search event, user, target…",
      denied: "You don't have permission to view the audit trail.",
      loading: "Loading audit events…",
      empty: "No audit events match these filters.",
      pageOf: "Page {page} of {total} · {count} events",
      prev: "Previous",
      next: "Next",
      categories: {
        all: "All",
        policy: "Policy changes",
        safety: "Safety controls",
        approvals: "Approvals",
        comms: "Customer comms",
        deployments: "Deployments",
        auth: "Auth",
      },
    },
  },

  commandPalette: {
    title: "Quick find",
    resultsLabel: "Search results",
    placeholder: "Search or jump to a page, queue or issue…",
    nav: {
      inbox: "Go to Inbox", issues: "Go to Issues", patches: "Go to Patches",
      workspaces: "Go to Work sessions", missions: "Go to Missions", "follow-ups": "Go to Follow-ups",
      knowledge: "Go to Knowledge", posture: "Go to Codebase health", agents: "Go to Agents",
      runs: "Go to Agent runs", operations: "Go to Operations", governance: "Go to Governance",
      projects: "Go to Projects", memory: "Go to AI memory", notifications: "Go to Notifications",
      devops: "Go to DevOps connections", audit: "Go to Audit trail",
      dashboard: "Go to Dashboard", settings: "Go to Settings",
    },
    navDest: {
      inbox: "Inbox", issues: "Issues", patches: "Patches", workspaces: "Work sessions",
      missions: "Missions", "follow-ups": "Follow-ups", knowledge: "Knowledge", posture: "Codebase health",
      agents: "Agents", runs: "Agent runs", operations: "Operations", governance: "Governance",
      projects: "Projects", memory: "AI memory", notifications: "Notifications",
      devops: "DevOps", audit: "Audit", dashboard: "Dashboard", settings: "Settings",
    },
  },
  /**
   * The "every attempt on this issue" panel on the issue page. Its own
   * namespace because the panel is shared chrome, not part of one page.
   */
  missionListPanel: {
    title: "Everything FixControl has tried on this issue",
    subtitle: "Each attempt, what it was for, and where it got to. Nothing left the sandbox without an approval.",
    count: "{n} attempt(s)",
    empty: "FixControl has not worked on this issue yet. Start it with the action above.",
    staleAttempt: "Based on an earlier version of this issue",
  },

  missions: {
    status: {
      queued: "queued",
      planning: "planning",
      awaiting_plan_approval: "awaiting plan approval",
      executing: "executing",
      awaiting_gate: "awaiting gate",
      verifying: "verifying",
      succeeded: "succeeded",
      failed: "failed",
      rolled_back: "rolled back",
      cancelled: "cancelled",
      abandoned: "abandoned",
    },
    intent: {
      investigate: "investigate",
      patch_proposal: "patch proposal",
      apply_patch: "apply patch",
      deploy: "deploy",
      rollback: "rollback",
      respond: "respond",
      verify: "verify",
      freeform: "freeform",
    },
  },
  missionsPage: {
    eyebrow: "AI Operations",
    title: "AI Missions",
    intro: "Every governed AI operational attempt — investigation, patch proposal, deployment, rollback. Open one to see its plan, gates, agents and telemetry.",
    statOpenGate: "Open gate",
    statExecuting: "Executing",
    statSucceeded: "Succeeded",
    emptyTitle: "No missions yet",
    emptyBodyPrefix: "Missions are governed AI operational attempts started from an issue or from the operator chat. Open any issue and use",
    emptyBodyAction: "Run fix",
    emptyBodySuffix: "to launch one.",
    emptyAsk: "What can FixControl do about an issue?",
    deleteTitle: "Archive mission",
    deleteConfirm: "Archive this mission? It leaves the missions list. The mission, its gates, approvals and history are kept — you'll find it under \"Archived\", and you can restore it.",
    restoreTitle: "Restore mission",
    archivedBadge: "Archived",
    archivedBanner: "This mission is archived — it does not appear in the missions list. Nothing was deleted.",
    emptyArchivedTitle: "No archived missions",
    emptyArchivedBody: "Missions you remove from the list land here. Nothing is deleted — restore one to put it back.",
    searchPlaceholder: "Search title, instruction, issue, id…",
    viewAll: "All",
    viewActive: "Active",
    viewDone: "Done",
    viewArchived: "Archived",
    exportCsv: "Export CSV",
    headers: {
      mission: "Mission",
      issue: "Issue",
      status: "Status",
      priority: "Priority",
      completed: "Completed",
    },
    bulk: {
      delete: "Archive",
      deleted: "{count} archived",
      deleteFailed: "{ok} archived, {failed} failed",
      deleteConfirmTitle: "Archive missions",
      deleteConfirmLabel: "Archive",
      deleteConfirmBody: "Archive {count} mission(s)? They leave the missions list. Missions, their gates, approvals and history are kept — you'll find them under \"Archived\", and you can restore them.",
      restore: "Restore",
      restored: "{count} restored to the list",
      restoreFailed: "{ok} restored, {failed} failed",
    },
    pane: {
      title: "Mission detail",
      enableSplit: "Enable split view",
      disableSplit: "Disable split view",
      resizeHandle: "Resize detail pane",
      open: "Open",
      openFullPage: "Open mission cockpit",
      close: "Close",
      emptyTitle: "Select a mission",
      emptyBody: "Click a row to inspect it here without leaving the list.",
      issue: "Issue",
      priority: "Priority",
      plan: "Plan",
      planVersions: "approved v{approved} · latest v{latest}",
      planNone: "No plan yet",
      started: "Started",
      completed: "Completed",
      instruction: "Instruction",
    },
    commandBarPolicy: "policy v{version}",
    commandBarPolicyNotFrozen: "policy not yet frozen",
    operatorChannel: "Operator channel",
    chatEmptyTitle: "No operator channel yet",
    chatEmptyBody: "The chat surface is created on the first agent dialogue or when an operator starts a workspace from the issue.",
    chatBackToCockpit: "Back to mission cockpit",
    chatOpenIssue: "Open issue {key}",
  },
  settingsAi: {
    supportOnly: "FixControl support only",
    supportOnlyBody: "AI provider configuration is managed by the FixControl team. Contact support if you need a model or task routed differently for your workspace.",
    loginTriggered: "Login triggered",
    loading: "Loading…",
    eyebrow: "Settings",
    title: "AI providers",
    subtitle: "Choose which model runs each agent. Mix cloud + local, bring your own keys, or use free models.",
    save: "Save", saving: "Saving…", saved: "Saved",
    routingTitle: "Agent routing",
    routingSub: "Pick a provider + model for each task. Changes save when you hit Save.",
    overridesTitle: "Per-agent overrides",
    overridesSub: "Optional: give a specific agent its own model, overriding the task routing above. Leave on “Inherit” to follow the task. cto / security / release / ux are rule-based gates and make no LLM call.",
    workspaceTitle: "Workspace agent",
    workspaceSub: "Controls which Claude Code CLI tools the in-app workspace chat is allowed to invoke. File-mutation tools (Edit/Write/MultiEdit/NotebookEdit) are always blocked so changes flow through the patch-approve panel.",
    cacheTitle: "Context efficiency + caching",
    cacheSub: "Controls how agents reuse stable context across runs. The cacheable prompt prefix never contains per-ticket data or secrets — only static prompts and operator-curated context packs.",
    provider: "Provider", model: "Model",
    freeTierSuffix: " · free tier", freeSuffix: " · free", noToolUseParen: " (no tool-use)",
    pillCliNotSignedIn: "CLI not signed in", pillNeedsKey: "needs key", pillCliAuth: "CLI auth",
    pillNoToolUse: "no tool-use", pillOverride: "override", pillInherits: "inherits · {task}",
    pillAnthropicOnly: "Anthropic only", pillConfigured: "Configured", pillNotConfigured: "Not configured",
    pillDesktop: "Desktop", pillCloud: "Cloud",
    inheritOption: "Inherit ({provider} · {model})",
    trusted: "Trusted",
    trustedDesc: "CLI may run shell commands and WebFetch (it already runs with the user's own rights). Permission prompts are auto-bypassed so the chat doesn't hang. Recommended for desktop / single-user installs.",
    restricted: "Restricted",
    restrictedDesc: "Also blocks Bash / PowerShell / KillShell / BashOutput / WebFetch and removes the permission bypass. Recommended for hosted / multi-tenant deployments where the agent runs against tenant data on shared infrastructure.",
    getCredentials: "Get credentials",
    browserLogin: "Browser login (CLI)",
    useCliInstead: "Use CLI instead of API key",
    openingBrowser: "Opening browser…",
    signInWith: "Sign in with {name}",
    checkStatus: "Check status",
    cliNotInPath: "{cli} CLI not in PATH. Install:",
    apiKey: "API key", baseUrl: "Base URL", optional: "(optional)",
    modelsKnown: "{n} models known", modelKnown: "{n} model known",
    test: "Test",
    replyIn: "Reply in {ms}ms:",
    providers: "Providers",
    cacheRowEnabled: "Context caching",
    cacheRowEnabledDesc: "Load tenant context packs into the cacheable prefix and enforce per-phase token budgets. Context manifests are recorded either way.",
    cacheRowPrompt: "Prompt caching for Claude",
    cacheRowPromptDesc: "Mark the stable prefix with Anthropic cache-control breakpoints. Non-Claude providers receive the identical blocks and ignore the flag.",
    cacheRowCheap: "Prefer cheap model for summaries",
    cacheRowCheapDesc: "Use the cheap classify-task model for context / thread summaries, with a deterministic truncation fallback.",
    workspaceEnvHint: "Override at the OS level with WORKSPACE_AGENT_RESTRICTED=1 (force restricted) or =0 (force trusted) — the env var wins over this setting.",
    cacheBudgetHint: "Per-phase token budgets default from token-budget.ts and can be overridden with CONTEXT_BUDGET_<PHASE> env vars. Token usage per run is visible on the context-cache dashboard.",
    cacheBudgetLink: "context-cache dashboard",
    capacityHint: "Provider refusals (billing, authentication, context, rate limits), context pressure and output budgets across all customers are on the",
    capacityLink: "AI capacity cockpit",
    infoNoKey: "No API key? Anthropic and Codex support CLI auth — install the official CLI, click “Sign in with Claude/Codex” to open a browser login, then toggle “Use CLI instead of API key”.",
    infoImplAgent: "Implementation Agent requires tool-use (file reading, patch proposals). Use any provider with tool-use for Ticket Analyst, Support Agent, and Financial Control.",
    infoKeys: "Keys are stored in the app_settings table and redacted when displayed. Env vars are used as fallback when no key is configured here.",
    cliChecking: "checking…", cliNotInstalled: "not installed",
    cliInstalledNotSignedIn: "installed · not signed in", cliSignedIn: "signed in",
    tasks: {
      classify: { label: "Ticket Analyst", desc: "Classify inbound emails into structured issues." },
      reply: { label: "Support Agent", desc: "Draft customer-facing replies." },
      coordinate: { label: "Operations Coordinator", desc: "Extract trackable follow-up actions per issue." },
      finance: { label: "Financial Control", desc: "Cross-check actions vs invoices. Conservative." },
      impl: { label: "Implementation Agent", desc: "Read the repo, propose patches, open PRs. Requires tool-use." },
    },
    roles: {
      qa: { label: "QA / Review", desc: "Verifies patches; drives the CTO review verdict." },
      workflow: { label: "Workflow", desc: "Decides issue state transitions + follow-ups." },
      intake: { label: "Intake / Triage", desc: "Classifies inbound messages into issues." },
      support: { label: "Support reply", desc: "Drafts customer-facing replies." },
      dev: { label: "Implementation", desc: "Generates code patches (Anthropic tool-use only)." },
      chat: { label: "Workspace chat", desc: "Interactive codebase chat (Anthropic only)." },
      command_center: { label: "Command Center", desc: "Turns what you ask for into the governed steps FixControl can run (Anthropic only)." },
    },
  },
  adminPosture: {
    loading: "Loading…",
    adminOnly: "Platform-admin only.",
    loadFailed: "Failed to load posture overview.",
    networkError: "Network error.",
    bulkFailed: "Bulk re-assessment failed",
    eyebrow: "Platform admin",
    title: "Operational posture (cross-tenant)",
    subtitle: "Latest Operational Posture Assessment per (tenant, codebase). High-risk + insufficient-evidence rows are the operator-attention list.",
    loadingRollup: "Loading posture rollup…",
    statCodebases: "Codebases assessed",
    statTenants: "Tenants",
    statRefusing: "Refusing auto-exec",
    statHighRisk: "High risk",
    statInsufficient: "Insufficient evidence",
    filter: "Filter",
    allPostures: "All postures",
    rowCount: "{n} row(s)",
    selectedCount: "{n} selected (max 10)",
    selectHighRisk: "Select high-risk",
    clear: "Clear",
    reassessing: "Re-assessing…",
    reassessSelected: "Re-assess selected",
    colTenant: "Tenant",
    colCodebase: "Codebase",
    colPosture: "Posture",
    colConfidence: "Confidence",
    colRiskPos: "Risk / Pos",
    colVersion: "v",
    colLatest: "Latest",
    colAutoExec: "Auto-exec",
    selectAria: "Select {id} for bulk re-assess",
    approvalRequired: "Approval required",
    allowed: "Allowed",
    noRows: "No rows for this filter.",
    bulkResult: "Bulk re-assess result",
    bulkSucceeded: "{succeeded}/{requested} succeeded",
    bulkFailedSuffix: " · {failed} failed",
    posturePrefix: "posture",
    footer: "This view bypasses tenant RLS via the platform-admin session. Posture refusing autonomous execution is the enterprise default — it's not a problem to fix, but a stuck row that should drop after the next clean scan is worth checking.",
    posture: {
      low_risk: "low risk", moderate_risk: "moderate risk", elevated_risk: "elevated risk",
      high_risk: "high risk", insufficient_evidence: "insufficient evidence",
    },
  },
  agentBehaviorPage: {
    pickTenant: "Pick a tenant first.",
    title: "Agent behavior profiles",
    introLead: "How agents operate when they're allowed to act. Bind a profile to a scope on",
    consoleLink: "the governance console",
    builtinBadge: "no persisted profiles yet — showing built-ins",
    newProfile: "New custom profile",
    edit: "Edit",
    builtinReadonly: "Built-in (read-only)",
    cancel: "Cancel",
    saving: "Saving…",
    saveProfile: "Save profile",
    saveFailed: "Save failed: HTTP {status}",
    editTitle: "Edit profile",
    newTitle: "New custom profile",
    modalSubtitle: "Custom profiles can be referenced by any scope binding.",
    rows: {
      planningDepth: "Planning depth",
      verification: "Verification",
      retryCap: "Retry cap",
      autonomyPreset: "Autonomy preset",
      contextExpansion: "Context expansion",
      confidenceThreshold: "Confidence threshold",
      sandboxAutoReviseMax: "Sandbox auto-revise max",
      sandboxAutoRevision: "Sandbox auto-revision",
      includeReleaseStep: "Include release step",
      haltDevOnGate: "Halt dev on gate",
      resumeAfterRevise: "Resume after revise",
      rejectAborts: "Reject aborts mission",
      gateApprove: "Gate approve",
    },
    val: { on: "on", off: "off", yes: "yes", no: "no" },
    /**
     * The stored settings, said out loud. Every one of these was printed as
     * the raw value (`shallow`, `wide`, `accept`, `builtin`), which made a
     * governance page that decides how much rope an agent gets readable only
     * by whoever wrote the enum.
     */
    enum: {
      kind: {
        builtin: "Built-in",
        custom: "Your own",
        conservative: "Careful",
        balanced: "Balanced",
        autonomous: "Independent",
        experimental: "Experimental",
      },
      planningDepth: { shallow: "Plan briefly", normal: "Plan normally", deep: "Plan thoroughly" },
      verificationStrictness: { light: "Check lightly", normal: "Check normally", strict: "Check strictly" },
      autonomyPreset: {
        conservative: "Ask early and often",
        balanced: "Ask when it matters",
        aggressive: "Ask only when it must",
      },
      contextExpansion: {
        narrow: "Read only what was pointed at",
        moderate: "Read the surrounding code",
        wide: "Read widely across the codebase",
      },
      gateApproveSemantics: {
        revise: "Approving asks for a revision first",
        accept: "Approving accepts the work as it stands",
      },
    },
    fields: {
      displayName: "Display name",
      planningDepth: "Planning depth",
      verificationStrictness: "Verification strictness",
      retryCap: "Retry cap",
      autonomyPreset: "Autonomy preset",
      contextExpansion: "Context expansion",
      confidenceThreshold: "Confidence threshold",
      sandboxAutoReviseMaxDepth: "Sandbox auto-revise max depth",
      sandboxAutoRevisionOnFailure: "Sandbox auto-revision on failure",
      includeReleaseStepInPlan: "Include release step in plan",
      haltDevWhileGateOpen: "Halt dev agent while gate open",
      resumeAfterChangesDone: "Continue once the requested changes are made",
      rejectAbortsMission: "Reject aborts the mission",
      gateApproveSemantics: "Gate approve semantics",
    },
  },
  /**
   * Policy & scope (/governance/hierarchy).
   *
   * Everything the customer-facing half of the page says. The rule that keeps
   * it honest lives in src/lib/governance/policy-language.ts: a sentence here
   * may only restate a field `resolveEffectivePolicy` actually consults, in
   * the same branch it uses. "Risky actions need approval" is the reading of
   * `approvalRequirement = risky_only`; it is not a softer promise.
   */
  approvalsPage: {
    eyebrow: "Governance",
    title: "Approvals",
    subtitle: "Everything in this workspace that waits on a person's decision.",
    gatesTitle: "Waiting gates ({count})",
    repliesTitle: "Customer replies waiting for approval ({count})",
    openGate: "Open to decide",
    overdue: "past the time limit",
    cannotDecide: "Only administrators and managers can approve customer replies.",
    emptyTitle: "Nothing waits on a decision",
    emptyBody: "Approval requests appear here as soon as work needs one.",
    loadError: "Couldn't load the approvals. Try again in a moment.",
    gateType: {
      plan: "AI plan",
      risky_patch: "Code change",
      security_override: "Security override",
      deployment: "Deployment",
      retry: "Retry",
      ui_change_approval: "UI change",
      ux_regression_override: "UX regression",
      commit: "Commit",
      pr_merge: "Pull request merge",
      development_review: "Development review",
      infra_action: "Infrastructure action",
    },
  },
  governanceScope: {
    page: {
      pickTenant: "Pick a tenant first.",
      title: "Policy & scope",
      intro: "See which policy applies to the organisation, a project, or a codebase.",
      authorHint: "Organisation-wide policy is authored in",
      consoleLink: "the governance console",
      codebaseInherits: "Follows the policy of {project} — a codebase has no policy of its own.",
    },
    tabs: {
      effective: "Effective policy",
      overrides: "Overrides",
      behavior: "Agent behaviour",
      simulator: "Simulator",
    },
    kinds: {
      organization: "Organisation",
      project: "Project",
      codebase: "Codebase",
      environment: "Environment",
      workspace: "Work scope",
    },
    picker: {
      searchPlaceholder: "Search projects and codebases",
      searchLabel: "Search scopes",
      navLabel: "Scopes",
      empty: "No scopes yet. The organisation scope is created the first time a policy decision is made.",
      noMatches: "Nothing matches “{query}”.",
      hasOverride: "This scope changes one or more organisation rules.",
      overrideBadge: "Override",
      lockCount: "{n} frozen field(s)",
      addChild: "Add a scope below this one",
      archive: "Archive this scope",
      archiveConfirm: "Archive “{name}” ({kind})? Anything below it stays, and inherits from the level above instead.",
      advancedTitle: "Advanced scopes",
      advancedHint: "Per-issue work scopes and any scope outside the project structure. They still apply — they are just not part of the everyday hierarchy.",
      advancedWithPolicy: "{n} of these change policy",
    },
    capabilities: {
      title: "What may FixControl do in {scope}?",
      subtitle: "Read from the policy that applies here — not a separate setting.",
      advisory: "· recorded, not enforced",
      planWork: {
        label: "Plan work",
        always: "Every plan needs approval before any work starts.",
        riskyOrCritical: "Risky or critical plans need approval; the rest start on their own.",
        never: "Plans start without approval.",
      },
      changeCode: {
        label: "Approval rule",
        never: "Never asks for an approval. Code changes and pull requests are not gated by this rule: a risk-flagged issue gets a CTO sign-off, and commit or merge gates are separate settings.",
        always: "Every deployment of a change FixControl made needs a person to approve it. Code changes and pull requests are not gated by this rule.",
        risky_only: "A deployment of a change FixControl made needs approval when it is high or critical risk. Code changes and pull requests are not gated by this rule.",
        production_impact: "A deployment of a change FixControl made needs approval when it touches production. Code changes and pull requests are not gated by this rule.",
        security_sensitive: "A deployment of a change FixControl made needs approval when it touches authentication or security files. Code changes and pull requests are not gated by this rule.",
        external_integration: "A deployment of a change FixControl made needs approval when it touches an external integration. Code changes and pull requests are not gated by this rule.",
        database_change: "A deployment of a change FixControl made needs approval when it touches the database schema. Code changes and pull requests are not gated by this rule.",
      },
      runSandbox: {
        label: "Verify in a sandbox",
        required: "Listed as required on the execution plan. No gate checks this setting yet.",
        optional: "Allowed, and not required by this policy.",
      },
      runTests: {
        label: "Run tests",
        required: "Listed as required on the execution plan. No gate checks this setting yet.",
        optional: "Allowed, and not required by this policy.",
      },
      releaseToProduction: {
        label: "Release to production",
        blockedWithoutQa: "Meant to block until QA has signed off, but no gate enforces this yet.",
        qaSignoff: "Asks for a QA sign-off, but no gate waits for it yet.",
        approval: "A deployment of a change FixControl made needs a person to approve it.",
        conditional: "A deployment of a change FixControl made needs approval when it meets this scope's approval rule.",
        open: "Released without a separate approval step.",
      },
      contactCustomers: {
        label: "Message customers",
        never: "Never sent automatically — a person sends every message.",
        draftOnly: "Drafts only — a person sends the message.",
        highConfidence: "Sent automatically from {autosend}% confidence; between {approval}% and {autosend}% a person approves first.",
        lowRisk: "Only low-risk replies go out automatically; the rest are reviewed.",
      },
    },
    groups: {
      autonomy: "Autonomy",
      approval: "Approval",
      verification: "Verification",
      production: "Production",
      communication: "Customer communication",
      audit: "Retention",
    },
    statements: {
      autonomyMode: {
        conservative: "Maximum oversight: short retry chains and a plan gate on every mission.",
        balanced: "The standard balance between oversight and throughput.",
        aggressive: "Maximum throughput: longer retry chains and no plan gate.",
        custom: "A custom mix of autonomy settings, authored per field.",
      },
      approvalRequirement: {
        never: "Actions never need a separate approval.",
        risky_only: "High and critical risk actions need approval.",
        production_impact: "Actions that touch production need approval.",
        security_sensitive: "Actions that touch authentication or security files need approval.",
        external_integration: "Actions that touch an external integration need approval.",
        database_change: "Actions that change the database schema need approval.",
        always: "Every action needs approval.",
      },
      planApprovalPolicy: {
        always: "Every plan is approved before execution.",
        risky_or_critical: "Risky and critical plans are approved before execution.",
        never: "Plans are executed without a separate plan approval.",
      },
      approvalTimeoutMinutes: {
        value: "An approval request waits {minutes} minutes before the timeout rule applies.",
        none: "An approval request has no deadline — it waits until someone decides.",
      },
      requireSandbox: {
        on: "Every change is verified in a sandbox first.",
        off: "A sandbox run is available but not required.",
      },
      requireTests: {
        on: "A change must carry tests to pass verification.",
        off: "Tests are not required to pass verification.",
      },
      verificationStrictness: {
        lenient: "Verification is lenient: fewer checks have to pass.",
        standard: "Verification uses the standard set of checks.",
        strict: "Verification is strict: every check has to pass.",
      },
      requireQaSignoff: {
        on: "A QA sign-off is required before work is complete.",
        off: "No separate QA sign-off is required.",
      },
      blockProdWithoutQa: {
        on: "Production work cannot complete without a QA sign-off.",
        off: "Production work does not wait on a QA sign-off.",
      },
      commsMode: {
        never_autosend: "Customer messages are never sent automatically.",
        draft_only: "Customer messages are prepared as drafts for a person to send.",
        autosend_high_confidence: "Customer messages are sent automatically above the confidence threshold.",
        autosend_low_risk: "Only low-risk customer messages are sent automatically.",
      },
      commsEnforced: {
        on: "The communication policy is enforced at runtime.",
        off: "The communication policy is recorded but not enforced at runtime.",
      },
      commsAutosendThreshold: {
        value: "Automatic sending starts at {percent}% confidence.",
      },
      commsApprovalThreshold: {
        value: "Below {percent}% confidence a message stays a draft.",
      },
      retentionAuditDays: {
        value: "Audit records are kept for {days} days.",
      },
      retentionTraceDays: {
        value: "Execution traces are kept for {days} days.",
      },
    },
    fields: {
      autonomyMode: "Autonomy mode",
      approvalRequirement: "Approval requirement",
      planApprovalPolicy: "Plan approval",
      approvalTimeoutMinutes: "Approval timeout",
      requireSandbox: "Sandbox required",
      requireTests: "Tests required",
      requireQaSignoff: "QA sign-off required",
      blockProdWithoutQa: "Block production without QA",
      verificationStrictness: "Verification strictness",
      commsMode: "Communication mode",
      commsEnforced: "Communication policy enforced",
      commsAutosendThreshold: "Auto-send threshold",
      commsApprovalThreshold: "Approval threshold",
      retentionAuditDays: "Audit retention",
      retentionTraceDays: "Trace retention",
    },
    source: {
      inheritedFrom: "Inherited from {scope}",
      overriddenAt: "Set for {scope}",
      overriddenHere: "Set here",
      lockedAt: "Frozen by {scope}",
      lockedReason: "Reason: {reason}",
      advisory: "recorded, not enforced",
    },
    overrides: {
      emptyTitle: "No overrides here.",
      emptyBody: "{scope} follows the organisation policy in full.",
      intro: "{n} rule(s) differ from the organisation policy for {scope}.",
      organisationRow: "{scope}",
      effectiveRow: "Applies here",
      noValue: "no value of its own",
      noOverrideHere: "{scope} does not change this rule — it comes from above.",
    },
    advanced: {
      title: "Technical detail",
      chain: "Resolved along: {chain}",
      colKey: "Policy key",
      colRaw: "Stored value",
      colResolved: "Reads as",
      colSource: "Set by",
      fromScope: "{kind}: {name}",
      orgDefault: "organisation default",
      conflicts: "{n} override attempt(s) discarded because the field is frozen higher up.",
      conflictDetail: "{field} attempted at {at}, frozen at {by}; {attempted} rejected, {enforced} kept.",
    },
    values: {
      boolean: { yes: "yes", no: "no" },
      conservative: "conservative",
      balanced: "balanced",
      aggressive: "aggressive",
      custom: "custom",
      never: "never",
      always: "always",
      risky_only: "risky actions only",
      production_impact: "production impact",
      security_sensitive: "security sensitive",
      external_integration: "external integration",
      database_change: "database change",
      risky_or_critical: "risky or critical",
      lenient: "lenient",
      standard: "standard",
      strict: "strict",
      never_autosend: "never auto-send",
      draft_only: "drafts only",
      autosend_high_confidence: "auto-send above the confidence threshold",
      autosend_low_risk: "auto-send low risk only",
    },
    gone: {
      title: "This scope no longer exists.",
      body: "The project or work scope it belonged to has been deleted or archived. Its policy history is kept in the audit trail.",
      action: "Back to the organisation",
    },
    errors: {
      treeLoad: "Could not load the scope tree (HTTP {status}).",
      bindingSave: "Could not save the agent-behaviour binding (HTTP {status}).",
      bindingClear: "Could not clear the agent-behaviour binding (HTTP {status}).",
      archive: "Could not archive the scope (HTTP {status}).",
    },
  },
  governanceHierarchy: {
    page: {
      pickTenant: "Pick a tenant first.",
      title: "Hierarchy & behavior",
      introLead: "Structure-level view of the scope tree, resolved decisions, and agent-behavior bindings. Policy authoring (Approval / Risk / Verification / …) lives on",
      consoleLink: "the main governance console",
    },
    tabs: { effective: "Effective", behavior: "Agent behavior", simulator: "Simulator" },
    fields: {
      autonomyMode: "Autonomy mode",
      approvalRequirement: "Approval requirement",
      planApprovalPolicy: "Plan approval policy",
      approvalTimeoutMinutes: "Approval timeout (minutes)",
      requireSandbox: "Require sandbox",
      requireTests: "Require tests",
      requireQaSignoff: "Require QA sign-off",
      blockProdWithoutQa: "Block prod without QA",
      verificationStrictness: "Verification strictness",
      commsMode: "Communication mode",
      commsEnforced: "Comms ceiling enforced",
      commsAutosendThreshold: "Comms auto-send threshold (%)",
      commsApprovalThreshold: "Comms approval threshold (%)",
      retentionAuditDays: "Audit retention (days)",
      retentionTraceDays: "Trace retention (days)",
    },
    bindingFields: {
      planningDepth: "Planning depth",
      verificationStrictness: "Verification strictness",
      retryCap: "Retry cap",
      autonomyPreset: "Autonomy preset",
      contextExpansion: "Context expansion",
      confidenceThreshold: "Confidence threshold",
      sandboxAutoReviseMaxDepth: "Sandbox auto-revise max depth",
      sandboxAutoRevision: "Sandbox auto-revision on failure",
      includeReleaseStep: "Include release step in plan",
      haltDevOnGate: "Halt dev agent while gate open",
      resumeReviseOnComplete: "Continue once the requested changes are made",
      rejectGateAborts: "Reject aborts the mission",
      gateApproveSemantics: "Gate approve semantics",
    },
    signals: {
      isDeployment: "Deployment",
      environment: "Environment",
      touchesDatabaseSchema: "Touches DB schema",
      touchesSecurityFiles: "Touches security files",
      touchesInfrastructure: "Touches infrastructure",
      irreversible: "Irreversible",
      isCustomerComms: "Customer comms",
      externalRecipient: "External recipient",
      hasTests: "Has tests",
      sandboxPassed: "Sandbox passed",
    },
    environments: {
      none: "none",
      production: "production",
      staging: "staging",
      development: "development",
    },
    common: {
      inherit: "inherit",
      cancel: "Cancel",
      close: "Close",
      true: "true",
      false: "false",
    },
    scopeTree: {
      navLabel: "Scope tree",
      empty: "No scope tree yet. It's auto-created on first decision — open the Effective view to bootstrap it.",
      addChild: "Add child scope",
      archive: "Archive scope",
      lockCountOne: "{n} locked field",
      lockCountMany: "{n} locked fields",
      archiveConfirm: "Archive “{name}” ({kind})? Its descendants stay in the tree but inherit from its grandparent.",
      archiveFailed: "Archive failed: HTTP {status}",
    },
    policyEditor: {
      rootScope: "root scope",
      fieldsLockedOne: "{n} field locked",
      fieldsLockedMany: "{n} fields locked",
      save: "Save changes",
      saving: "Saving…",
      saved: "Saved",
      lockReasonPrompt: "Reason for locking \"{label}\":",
      lockedByAncestor: "locked by ancestor",
      lockedHere: "locked here",
      lockedAtTooltip: "Locked at {scope}: {reason}",
      inherited: "inherited",
      overriddenHere: "overridden here",
      clearOverride: "Clear override (inherit from parent)",
      lockField: "Lock this field",
      unlock: "Unlock",
      lockForAllDescendants: "Lock for all descendants",
      conflictsOne: "{n} lock conflict in this chain — overrides on locked fields are discarded.",
      conflictsMany: "{n} lock conflicts in this chain — overrides on locked fields are discarded.",
    },
    lockManager: {
      title: "Field locks · {scope}",
      intro: "Locked fields can't be overridden by any descendant scope. Ancestor locks are read-only here; remove them at the scope that set them.",
      addLock: "Add lock",
      fieldLabel: "Field",
      pickField: "— pick a field —",
      fieldOptionWithDomain: "{label} ({domain})",
      reasonLabel: "Reason",
      reasonPlaceholder: "e.g. SOC2 — retention is a compliance policy, not configurable per project",
      errPickFieldReason: "Pick a field and provide a reason.",
      lockingButton: "Locking…",
      lockButton: "Lock field",
      removeConfirm: "Remove the lock on \"{field}\"? Descendants will be able to override it again.",
      noLocks: "No locks at this scope or above.",
      valueLabel: "value:",
      removeLock: "Remove lock",
      ancestorLocksOne: "{n} ancestor lock (read-only here)",
      ancestorLocksMany: "{n} ancestor locks (read-only here)",
      atScope: "at",
    },
    scopeCreate: {
      title: "New scope",
      intro: "Add a workspace, project, or environment under an existing scope. Org is auto-created on first contact and can't be added here.",
      parentLabel: "Parent",
      kindLabel: "Kind",
      slugLabel: "Slug",
      displayNameLabel: "Display name",
      repositoryLabel: "Repository",
      criticalityLabel: "Criticality",
      pickParent: "— pick a parent —",
      noAllowedChildren: "— this parent has no allowed children —",
      slugPlaceholder: "e.g. payments",
      displayNamePlaceholder: "e.g. Payments",
      repositoryPlaceholder: "optional · matches approval-routes by repo id",
      errMissingFields: "Parent, slug and display name are required",
      errInvalidSlug: "Slug must be lowercase alphanumeric + dashes (start with a letter or digit)",
      creatingButton: "Creating…",
      createButton: "Create scope",
      criticality: {
        none: "—",
        low: "low",
        medium: "medium",
        high: "high",
        critical: "critical",
      },
      kinds: {
        workspace: "workspace",
        project: "project",
        environment: "environment",
      },
    },
    bindingEditor: {
      title: "Agent behavior · {scope}",
      introBase: "How an agent operates at this scope. Independent of \"what is allowed\" (governance).",
      introResolved: "Resolved from {chain}.",
      removeBindingTooltip: "Remove binding at this scope",
      clearButton: "Clear",
      save: "Save binding",
      saving: "Saving…",
      saved: "Saved",
      agentRoleLabel: "Agent role",
      baseProfileLabel: "Base profile",
      pickProfile: "— pick a profile —",
      profileOption: "{name} ({kind})",
      pickProfileHint: "Pick a base profile to start overriding fields.",
      overriddenHere: "overridden here",
      fromProfile: "from profile",
      lockedByAncestor: "locked by ancestor",
      lockedHere: "locked here",
      lockedAtTooltip: "Locked at {scope}: {reason}",
      clearOverride: "Clear override",
      unlock: "Unlock",
      lockForDescendants: "Lock for descendants",
      lockReasonPrompt: "Reason for locking \"{label}\":",
      conflictsOne: "{n} lock conflict in the behavior chain — overrides on locked fields are discarded.",
      conflictsMany: "{n} lock conflicts in the behavior chain — overrides on locked fields are discarded.",
      removeConfirm: "Remove the binding at this scope (descendants will resolve from the parent)?",
    },
    bindingReview: {
      title: "Which behaviour bindings on this scope apply",
      hint: "A binding below the organisation applies only after someone reviews and activates it, and only in the form it had then: editing it afterwards puts it back to waiting for review.",
      hintOff: "Project and per-agent behaviour is switched off for this organisation (ORCH_SCOPED_BEHAVIOR_RESOLUTION), so even an activated binding here changes nothing until it is switched on.",
      roleAll: "Every agent",
      role: "Role {role}",
      details: "Profile {profile} · {patch} overridden · {locks} locked",
      status: {
        applies: "Applies",
        pendingReview: "Waiting for review",
        scopedResolutionOff: "Activated, not applied: project behaviour is switched off",
      },
      activate: "Review and activate",
      deactivate: "Stop applying",
      activateConfirmTitle: "Activate this behaviour binding?",
      activateConfirmBody: "Agents working in this scope ({role}) will use this profile, with these overrides and locks, exactly as stored now. They read it from their next step on, including on missions already running. It reaches agents only while project and per-agent behaviour is switched on for the organisation. A later edit waits for review again.",
      activateConfirm: "Activate",
      deactivateConfirmTitle: "Stop applying this binding?",
      deactivateConfirmBody: "The binding stays stored, but agents in this scope ({role}) go back to what they inherit from the organisation, from their next step on.",
      deactivateConfirm: "Stop applying",
      cancel: "Cancel",
      activated: "Activated.",
      deactivated: "Stopped. The binding is kept but no longer applies.",
      failed: "That did not work. Nothing was changed.",
    },
    simulator: {
      title: "Runtime simulator",
      intro: "Preview the decision for a scope + action. Runs the full v2 hierarchy resolver against the live policy — no writes.",
      scopeLabel: "Scope",
      scopeOption: "{kind}: {name}",
      agentRoleLabel: "Agent role",
      domainLabel: "Domain",
      kindLabel: "Kind",
      signalsLegend: "Signals",
      simulate: "Simulate",
      simulating: "Simulating…",
      errorPrefix: "Error:",
      decisionTitle: "Decision",
      riskLabel: "risk:",
      profileLabel: "profile:",
      chainTitle: "Chain",
      conflictsOne: "{n} lock conflict — overrides discarded.",
      conflictsMany: "{n} lock conflicts — overrides discarded.",
    },
    effective: {
      title: "Effective policy",
      resolvedChain: "Resolved along the chain: {chain}",
      fromScope: "from {kind}: {name}",
      orgDefault: "org default",
      lockedTooltip: "Locked: {reason}",
      discardedOne: "{n} discarded override attempt (lock conflicts)",
      discardedMany: "{n} discarded override attempts (lock conflicts)",
      conflictDetail: "{field} tried at {at}, locked at {by}; value {attempted} rejected, kept {enforced}.",
    },
  },

  settingsSystem: {
    eyebrow: "Settings",
    title: "System",
    subtitle: "Housekeeping and retention for background state.",
    loading: "Loading…",
    externalLocale: {
      title: "Language for external messages",
      sub: "The default language FixControl uses when it composes messages outside the app.",
      label: "Default language",
      optionEn: "English",
      optionNl: "Nederlands",
      saved: "Saved",
      hint: "Applies to Slack approval cards and decision updates, email approvals and mission status posts. A ticket that arrives in another language keeps that language for its own mission; the in-app interface follows each user's own language picker.",
    },
    retention: {
      title: "Retention",
      sub: "One pass over the state that ages out: finished agent runs with their event history, and audit events older than the audit retention window. Running jobs are never touched, the pass covers every tenant, and the pass itself is audited.",
      cleanupEnabled: "Automatic cleanup enabled",
      windowLabel: "Agent-run retention window (days)",
      windowHint: "Runs finished more than {days}d ago will be purged.",
      sweepNote: "The sweep runs every 6 hours and shortly after server startup. Use the button below for an immediate pass.",
      auditNote: "Audit events follow the audit retention window, which never goes below 90 days; set it to 0 to keep them forever. Pruning walks the chain in order and records an anchor, so the remaining entries stay verifiable. It is configured under Governance → retention, not here.",
      save: "Save",
      saving: "Saving…",
      saved: "Saved",
      runCleanupNow: "Run retention now",
      cleaning: "Running…",
      runCleanupTitle: "Run the retention pass now",
      confirmTitle: "Run retention now?",
      confirmBody: "This prunes finished agent runs with their event history, and audit events older than the audit retention window (at least 90 days), across every tenant. Approval decisions, orchestration events and the audit chain anchor stay in place, so the chain stays verifiable — and the pass is written to the audit trail.",
      confirmLabel: "Run retention",
      chainLabel: "Audit chain",
      chainVerified: "Verified {when} · {walked} entries",
      chainBroken: "Break at sequence {seq}",
      chainUnverified: "Not verified yet",
      removedSummary: "Removed {runs} run and {events} event row.",
      removedSummaryRunsPlural: "Removed {runs} runs and {events} event row.",
      removedSummaryEventsPlural: "Removed {runs} run and {events} event rows.",
      removedSummaryBothPlural: "Removed {runs} runs and {events} event rows.",
    },
    issueKeys: {
      title: "Issue key sequences",
      sub: "Define the prefixes new issue keys are drawn from, and route specific issue kinds to their own sequence.",
      defaultNote: "New issues use the {prefix} sequence unless a kind is routed elsewhere below.",
      sequencesHeading: "Sequences",
      nextNumber: "next: {n}",
      noSequences: "No sequences yet — the default is created on first use.",
      prefixLabel: "Prefix",
      labelLabel: "Label",
      labelPlaceholder: "e.g. Acme customer",
      addSequence: "Add",
      routingHeading: "Routing by issue kind",
      tenantDefault: "Default (all kinds)",
      kind: {
        BUG: "Bug",
        FEATURE: "Feature",
        SUPPORT: "Support",
      },
      useBuiltinDefault: "Built-in default ({prefix})",
      useTenantDefault: "Use tenant default",
      example: "→ {key}",
      noTenant: "Select a tenant to configure per-kind routing. Sequences above are shared across the platform.",
      saved: "Saved",
      errorGeneric: "Could not save. Check the prefix and try again.",
    },
  },

  settingsUsers: {
    eyebrow: "Settings",
    title: "Users",
    subtitle: "Manage team members and access roles",
    accessRequired: "Admin access required",
    accessRequiredDetail: "Only admins can manage users.",
    createUser: "Create user",
    headers: {
      user: "User",
      role: "Role",
      lastSeen: "Last seen",
      status: "Status",
      twoFactor: "2FA",
      actions: "Actions",
    },
    you: "(you)",
    statusActive: "Active",
    statusInactive: "Inactive",
    twoFactorOn: "On",
    twoFactorOff: "Off",
    lastLogin: {
      never: "Never",
      justNow: "just now",
      minutesAgo: "{n}m ago",
      hoursAgo: "{n}h ago",
      daysAgo: "{n}d ago",
      noSuccessfulLogin: "No successful login recorded",
    },
    actions: {
      edit: "Edit",
      resetPassword: "Reset password",
      activate: "Activate",
      deactivate: "Deactivate",
      toggleActive: "Toggle active",
      delete: "Delete",
    },
    bulk: {
      activate: "Activate selected",
      deactivate: "Deactivate selected",
      exportSelected: "Export selected",
      confirmDeactivateTitle: "Deactivate selected users?",
      confirmDeactivateBody: "Deactivate {n} user(s)? They will lose access immediately. Already-inactive accounts and your own account are skipped.",
      toastActivated: "Activated",
      toastDeactivated: "Deactivated",
      toastFailed: "Bulk update failed",
    },
    filter: {
      allRoles: "All roles",
      allStatus: "All status",
    },
    searchPlaceholder: "Search by name or email…",
    empty: "No users found",
    toasts: {
      deleted: "{name} deleted",
      created: "{name} created",
      updated: "{name} updated",
      activated: "{name} activated",
      deactivated: "{name} deactivated",
      passwordReset: "Password reset for {name}",
      failed: "Failed",
    },
    deleteModal: {
      title: "Delete user",
      bodyPrefix: "Permanently delete",
      bodySuffix: "?",
    },
    create: {
      title: "Create user",
      sub: "Add a new team member",
      submit: "Create user",
      nameLabel: "Name",
      namePlaceholder: "Full name",
      emailLabel: "Email",
      emailPlaceholder: "user@company.com",
      passwordLabel: "Password",
      passwordPlaceholder: "Set initial password",
      roleLabel: "Role",
      roleOperator: "Operator — view and work",
      roleManager: "Manager — view, assign, approve",
      roleAdmin: "Admin — full access",
      requiredMark: "*",
    },
    edit: {
      title: "Edit {name}",
      submit: "Save",
      nameLabel: "Name",
      emailLabel: "Email",
      roleLabel: "Role",
      roleOperator: "Operator",
      roleManager: "Manager",
      roleAdmin: "Admin",
    },
    reset: {
      title: "Reset password",
      sub: "Set new password for {name}",
      submit: "Reset password",
      newPasswordLabel: "New password",
      newPasswordPlaceholder: "Minimum 8 characters",
      tooShort: "Minimum 8 characters",
    },
  },

  postureOperationalRails: {
    allClear: {
      eyebrow: "Operational rails",
      headline: "All clear — no execution blockers, autonomy degraders or governance escalations",
      body: "The interpretation engine found no surfaces with strong-enough evidence to withdraw permissions. Routine governance + blast-radius rules apply.",
    },
    executionBlockers: {
      eyebrow: "Execution blockers",
      headline: "What stops autonomous execution today",
      body: "Surfaces where the evidence on file is strong enough that autonomous execution is refused until mitigated.",
    },
    autonomyDegraders: {
      eyebrow: "Autonomy degraders",
      headline: "Permissions the posture has withdrawn",
      body: "Specific autonomy permissions reduced by the interpretation engine. Each entry traces back to the signal kinds that caused it.",
    },
    governanceEscalations: {
      eyebrow: "Governance escalations",
      headline: "What deployment now requires",
      body: "Approver chain expansions and deployment requirements driven by the operational posture.",
    },
    releaseConfidence: {
      eyebrow: "Release confidence",
      headline: "Change landing safety",
      scoreLabel: "Score: {score}/100",
    },
    communicationSafety: {
      eyebrow: "Communication safety",
      headlineAvailable: "Outbound autonomy available",
      headlineDisabled: "Outbound autonomy disabled",
    },
    scannerProvenance: {
      eyebrow: "Scanner provenance",
      headline: "Adapter runs that fed this assessment",
      body: "Each adapter is capability-detected; a \"Not installed\" status means the scanner could not run on this host — its absence is itself evidence.",
      succeededFooter: "{count} finding(s) · {durationMs}ms",
      failedFooter: "Error: {error}",
      unknownError: "unknown",
    },
    level: {
      high: "High",
      moderate: "Moderate",
      low: "Low",
      blocked: "Blocked",
      insufficient_evidence: "Insufficient evidence",
    },
    exploit: {
      demonstrated: "Demonstrated",
      conditional: "Conditional",
      theoretical: "Theoretical",
      insufficient_evidence: "Insufficient evidence",
    },
    scannerStatus: {
      succeeded: "Succeeded",
      failed: "Failed",
      skipped: "Skipped",
      unavailable: "Not installed",
    },
    confidenceLabel: "Confidence: {confidence}",
    surfaceLabel: "Surface: {surface}",
    appliesAtLabel: "Applies at: {appliesAt}",
    approversAddedLabel: "Approvers added:",
    anchoredOn: "Anchored on {count} signal kind(s).",
    anchoredOnSample: "Anchored on {kinds}.",
    anchoredOnSamplePlus: "Anchored on {kinds} (+{extra} more).",
  },

  codebasePosture: {
    // Drilldown page header
    eyebrow: "Operational posture",
    title: "Codebase health",
    subtitle: "Risk, automation readiness, governance coverage and execution confidence — evidence-first.",
    reassess: "Re-assess",
    runAssessment: "Run assessment",
    assessing: "Assessing…",
    loading: "Loading latest assessment…",
    loadError: "Could not load posture assessment.",
    networkError: "Network error loading posture.",
    assessmentFailed: "Posture assessment failed",
    noAssessmentTitle: "No assessment on record",
    noAssessmentBody: "Run a posture assessment to surface risk signals, automation readiness, and governance coverage for this codebase. Until then, governance treats it as insufficient evidence and refuses autonomous execution on every effecting domain.",

    // Landing page
    landing: {
      subtitle: "Risk, automation readiness, governance coverage, and execution confidence — per codebase, evidence-first.",
      loadingCodebases: "Loading codebases…",
      noCodebases: "No codebases configured for this tenant.",
      noCodebasesHint: "Add a codebase to enable posture assessment.",
      openCodebaseSettings: "Open codebase settings",
      panelTitle: "Codebases",
      panelSub: "Click a codebase to open its posture drilldown — overview cards, signals, evidence, and version history.",
      notAssessed: "Not assessed",
      rowMeta: "v{version} · {risk} risk · {positive} positive · {when}",
    },

    // Compare versions
    compareTitle: "Compare versions",
    compareHint: "Pick two scans of this codebase and see what changed: posture, signals, governance coverage and recommended controls.",
    compareAction: "Compare",
    diffing: "Diffing…",
    pickTwoVersions: "Pick two different versions to compare.",
    diffFailed: "Diff failed",
    versionFrom: "From",
    versionTo: "To",

    // Posture levels — used for the headline pill and the landing list
    level: {
      low_risk: "Low risk",
      moderate_risk: "Moderate risk",
      elevated_risk: "Elevated risk",
      high_risk: "High risk",
      insufficient_evidence: "Insufficient evidence",
    },
    levelCopy: {
      low_risk: "Automation is safe for investigation, plan generation, and patch proposal.",
      moderate_risk: "Investigation and plan generation are safe; execution requires approval.",
      elevated_risk: "Sensitive surfaces present — approval required for sensitive changes.",
      high_risk: "Autonomous execution refused on every effecting domain.",
      insufficient_evidence: "Not enough signal to establish risk — autonomous execution refused until a re-scan.",
    },

    confidence: {
      low: "Low",
      moderate: "Moderate",
      high: "High",
      insufficient_evidence: "Insufficient evidence",
    },

    stage: {
      investigation: "Investigation",
      plan_generation: "Plan generation",
      patch_proposal: "Patch proposal",
      validation: "Validation",
      customer_communication: "Customer communication",
      deployment: "Deployment",
      infrastructure_mutation: "Infrastructure mutation",
    },

    decision: {
      allowed: "Allowed",
      approval_required: "Approval required",
      blocked: "Blocked",
      insufficient_evidence: "Insufficient evidence",
    },

    blast: {
      isolated: "Isolated",
      module_scoped: "Module-scoped",
      service_scoped: "Service-scoped",
      tenant_scoped: "Tenant-scoped",
      platform_wide: "Platform-wide",
      insufficient_evidence: "Insufficient evidence",
    },

    section: {
      operationalPosture: "Operational posture",
      automationReadiness: "Automation readiness",
      governanceCoverage: "Governance coverage",
      executionConfidence: "Execution confidence",
      blastRadius: "Blast radius",
      perStageReadiness: "Per-stage execution readiness",
      operationalImplications: "Operational implications",
      governanceImplications: "Governance implications",
      riskSignals: "Risk signals",
      positiveSignals: "Positive signals",
      sensitiveAreas: "Sensitive areas",
      observabilityGaps: "Observability gaps",
      recommendedControls: "Recommended controls",
      recurringFailures: "Recurring failure areas (memory)",
      approvalImpact: "Approval impact preview",
      historicalReliability: "Historical reliability (last {n} days)",
      aiNarrative: "AI narrative (advisory) · {model}",
      suggestedControls: "Suggested controls",
    },

    readiness: {
      allowed: "Allowed",
      approvalRequired: "Approval required",
      unsafeToggle: "Unsafe for autonomous execution ({n})",
      investigationLabel: "Investigation",
      planGenerationLabel: "Plan generation",
      patchProposalLabel: "Patch proposal",
      autonomousExecutionLabel: "Autonomous execution",
    },

    coverage: {
      detected: "Detected",
      notDetected: "Not detected",
      approvalHooks: "Approval hooks",
      auditLogging: "Audit logging",
      policyEnforcement: "Policy enforcement",
      permissionChecks: "Permission checks",
      tenantIsolation: "Tenant isolation",
    },

    signal: {
      evidenceLabel: "{n} evidence",
      noExplanation: "No additional explanation.",
      noEvidenceStored: "No evidence stored for this signal.",
      rowMeta: "{bucket} · {kind} · confidence {n}",
      severity: { info: "Info", notice: "Notice", warning: "Warning", critical: "Critical" },
      bucket: {
        code: "Code", governance: "Governance", operational: "Operational",
        memory: "Memory", sensitive: "Sensitive",
      },
    },

    empty: {
      risk: "No risk signals detected.",
      positive: "No positive signals detected — coverage may be insufficient.",
      sensitiveAreas: "None detected by path heuristics.",
      observability: "No observability gaps detected.",
      controls: "No additional controls recommended.",
      operationalImplications: "No operational implications inferred from current evidence.",
      governanceImplications: "No governance implications inferred from current evidence.",
      blastRadius: "Insufficient evidence to bound blast radius.",
      listFallback: "Not available",
      historicalNa: "Not available.",
    },

    blastRadiusSection: {
      contributors: "Contributors ({n})",
      subtitle: "Detected sensitive surfaces in this codebase, plus how far a failure here would reach. Not a vulnerability scan — this shows what is present and the corresponding blast radius.",
    },

    perStage: {
      conf: "conf: {level}",
      confNone: "—",
      missingEvidence: "Missing evidence: {list}",
    },

    implications: {
      anchoredTo: "Anchored to: {list}",
      domainAnchored: "Domain: {domain} · anchored to: {list}",
      domainOnly: "Domain: {domain}",
    },

    approvalImpact: {
      sensitiveSystems: "Sensitive systems: {list}",
    },

    historical: {
      insufficientMemory: "Insufficient memory",
      succeeded: "Succeeded",
      failed: "Failed",
      rolledBack: "Rolled back",
      hotspots: "Module hotspots",
      rollback: "Rollback",
      validationFailures: "Validation failures",
      recurringRejections: "Recurring rejections",
      countSuffix: "({n}×)",
    },

    evidenceFootnote: "Every conclusion is anchored to evidence — click a signal to expand its supporting files, migrations, audit events, or memory entries.",

    sparkline: {
      historyTitle: "History — {n} scan",
      historyTitlePlural: "History — {n} scans",
      versionRange: "v{from} → v{to}",
      hint: "Click a cell to load that version. Hover for date + signal count.",
      hintChips: "Click a version to load that assessment. Hover for date + signal count.",
      cellAria: "Assessment v{version} — {posture}",
    },
    trend: {
      insufficient: "Fewer than 2 assessments on record.",
      improving: "Posture improved across last {n} scans.",
      deteriorating: "Posture deteriorated across last {n} scans.",
      stable: "Posture stable across last {n} scans.",
    },

    // Diff view
    diff: {
      sectionLabel: "Posture diff",
      coverageFlips: "Coverage & readiness flips",
      detectedShort: "✓ detected",
      notDetectedShort: "✗ not detected",
      riskAdded: "Risk added ({n})",
      riskResolved: "Risk resolved ({n})",
      positiveAdded: "Positive added ({n})",
      positiveLost: "Positive lost ({n})",
      none: "None",
      added: "Added ({n})",
      resolved: "Resolved ({n})",
    },
  },

  applicableGovernance: {
    title: "Applicable governance",
    shortTitle: "Governance",
    refresh: "Refresh",
    noTenant: "No active organization — open the org switcher first.",
    loadError: "Failed to load governance ({code}).",
    sourceResolved: "Resolved chain: {chain}",
    sourceFallback: "No specific scope bound — falling back to the organization default.",
    sourceScopeGone: "That scope no longer exists — showing the organization default instead.",
    overrideHere: "Override at this scope",
    addChildScope: "Add child scope",
    openHierarchy: "Open hierarchy",
    compactOverrides: "Overrides",
    compactLocks: "Locks",
    collapsedSummary: "{overrides} overrides · {locks} locks",
    editInline: "Edit policy here",
    inlineEditorTitle: "Override at {scope}",
    lockedByAncestor: "Locked by an ancestor scope — override blocked.",
    lockedByAncestorShort: "Locked",
    lockHere: "Lock here",
    saving: "Saving…",
  },

  demoBanner: {
    active: "Demo data active — Northwind Bank",
    removeCta: "Remove all demo data",
    removing: "Removing…",
    confirmRemove: "Are you sure you want to remove all demo data? This action cannot be undone.",
    removeError: "Removal failed. Please try again.",
    resetCta: "Reset demo",
    resetTitle: "Remove and recreate the demo data",
    resetting: "Resetting…",
    confirmReset: "This will wipe the current demo data and recreate it from scratch. Any notes or edits you made on demo issues will be lost.",
    resetError: "Reset failed. Please try again.",
    closeTitle: "Hide banner — controls remain available under Settings → Demo data",
  },

  // Reply style — professionalization of customer-facing replies (mig 198).
  settingsReplyStyle: {
    title: "Reply style",
    description: "Give every customer-facing reply a consistent, professional shape: a signature, a salutation and closing, and a compact sender line on chat channels. Off by default — nothing changes until you switch it on.",
    loadError: "Could not load reply-style settings.",
    saveError: "Could not save reply-style settings.",
    savedNotice: "Saved.",
    saving: "Saving…",
    logoTooLarge: "Logo must be 256 KB or smaller.",
    logoUploadError: "Logo upload failed — png, jpeg, webp or gif up to 256 KB.",
    enabledLabel: "Professionalize customer replies",
    enabledHint: "Master switch for this organization. When off, replies go out exactly as written.",
    removeOverride: "Remove override",
    signatureLabel: "Signature",
    signatureHint: "Appended to e-mail, Freshdesk and Jira Service Management replies. Use variables for the agent and company name; add a logo via an https image URL.",
    salutationLabel: "Salutation",
    salutationHint: "First line of the reply. Skipped automatically when the draft already opens with a greeting.",
    salutationPlaceholder: "Hi {{customer_name}},",
    closingLabel: "Closing",
    closingHint: "Sign-off line directly above the signature. Skipped when the draft already ends with one.",
    closingPlaceholder: "Kind regards,",
    senderLineLabel: "Sender line (chat)",
    senderLineHint: "Compact attribution appended to Slack, Teams and website-chat replies instead of a full signature.",
    senderLinePlaceholder: "— {{agent_name}}, {{company_name}} Support",
    toneLabel: "Default tone",
    toneHint: "Pre-selects the tone for AI reply drafts. An explicit choice in the composer always wins.",
    toneNone: "No preference",
    channelsLabel: "Active channels",
    channelsHint: "Composition only applies on the channels selected here.",
    channels: {
      email: "E-mail",
      freshdesk: "Freshdesk",
      odoo: "Odoo",
      topdesk: "TOPdesk",
      jsm: "Jira Service Management",
      slack: "Slack",
      teams: "Teams",
      website_chat: "Website chat",
    },
    scopes: {
      organization: "Organization",
      project: "Project",
      mailbox: "Mailbox",
      user: "User",
    },
    scopeHints: {
      project: "Project override — fields set here replace the organization defaults for this project's replies. Empty fields fall back to the organization values.",
      mailbox: "Mailbox override — applies to replies sent from this mailbox (beats the project and organization values field-by-field). Tip: a plain signature configured on the mailbox itself still wins in the inbox composer until you clear it there.",
      user: "User override — applies to replies this operator sends (strongest scope). Empty fields fall back to mailbox, project, then organization values.",
    },
    preview: {
      heading: "Preview",
      sampleBody: "Good news — the problem you reported has been fixed and rolled out. Could you check on your side and confirm everything works as expected?",
      sampleCompany: "Acme",
      note: "Rendered by the exact same composition code the server uses on send — what you see here is what the customer receives.",
    },
  },

  // Reply templates — reusable reply skeletons (mig 199).
  settingsReplyTemplates: {
    title: "Reply templates",
    description: "Reusable, categorised reply skeletons for the issue composer. Bodies are maintained in English and Dutch side by side; variables fill in customer, ticket and agent details on insert.",
    loadError: "Could not load reply templates.",
    saveError: "Could not save the template.",
    newTemplate: "New template",
    newHeading: "New template",
    editHeading: "Edit template",
    nameLabel: "Name",
    categoryLabel: "Category",
    projectLabel: "Project",
    bodyEnLabel: "Body — English",
    bodyNlLabel: "Body — Dutch",
    subjectEnPlaceholder: "Subject (English, e-mail only — optional)",
    subjectNlPlaceholder: "Subject (Dutch, e-mail only — optional)",
    enabledLabel: "Available in the composer",
    enabledHint: "Disabled templates stay here but are hidden from the picker.",
    disabledBadge: "Off",
    filterAll: "All templates",
    filterTenantWide: "Tenant-wide",
    empty: "No templates yet. Create one to give operators a consistent starting point.",
    categories: {
      confirmation: "Confirmation",
      resolution: "Resolution",
      escalation: "Escalation",
      closing: "Closing",
      general: "General",
    },
  },

  // Restricted rich-text editor (signatures, reply templates).
  replyRichEditor: {
    bold: "Bold",
    italic: "Italic",
    link: "Link",
    linkPrompt: "Link URL (https:// or mailto:)",
    bulletList: "Bullet list",
    image: "Logo / image (https URL)",
    imagePrompt: "Image URL (https only)",
    imageVanishes: "This image is light on a transparent background and almost disappears in a white e-mail. Use a dark or coloured version of your logo.",
  },

  // Website chat — public intake channel settings + inbox (migration 180).
  websiteChat: {
    title: "Chat",
    subtitle: "Put a chat on your website for visitors, or an internal chat for your own colleagues. Everything that comes in shows up in your inbox, just like questions from Slack, Teams and email.",
    status: { connected: "Connected", healthy: "Healthy", error: "Needs attention", disabled: "Switched off" },
    /* The three states a chat can be in. "Not reachable yet" applies to a
     * public chat only: it answers nobody while its list of website
     * addresses is empty. */
    state: {
      live: "Live",
      needs_origins: "Not reachable yet",
      disabled: "Switched off",
    },
    usedFor: "Used for: answering questions, collecting contact details, taking a request, taking demo requests",
    knowledge: "Knowledge",
    knowledgeNone: "No knowledge set",
    knowledgeHint: {
      public: "A public chat may only read from a public set.",
      internal: "An internal chat may also read from an internal set. Never from a restricted one.",
    },
    /* The two kinds of chat. This is the first question when creating one,
     * because it decides who may talk to it and which knowledge it may read. */
    types: {
      public: {
        label: "Public chat",
        description: "For visitors to your website. Anonymous, and reads public knowledge only.",
        defaultName: "Website chat",
      },
      internal: {
        label: "Internal chat",
        description: "For colleagues signed in to FixControl. May also read internal knowledge.",
        defaultName: "Internal chat",
      },
    },
    /* What the chat is for. Each choice carries a set of settings we fill in
     * for you; you can still change them individually afterwards. */
    purposes: {
      answer: {
        label: "Answer questions",
        description: {
          public: "The chat answers questions from your public documentation and creates nothing.",
          internal: "The chat answers colleagues' questions from your internal documentation.",
        },
      },
      support: {
        label: "Questions and requests",
        description: {
          public: "The chat answers questions and files a request when someone needs help.",
          internal: "The chat answers colleagues' questions and files a request when one is needed.",
        },
      },
      sales: {
        label: "Leads and demo requests",
        description: {
          public: "The chat asks for a name, company and email so you can follow up.",
          internal: "Not available for an internal chat.",
        },
      },
    },
    setup: {
      audienceLabel: "Who talks to this chat?",
      purposeLabel: "What is the chat for?",
      nameLabel: "Name",
      namePlaceholder: "For example: Support on the website",
      create: "Create chat",
      newChat: "New chat",
      failed: "Could not create the chat.",
      publicNote: "A public chat stays off until you fill in your website addresses below, so a half-configured chat can never quietly go live.",
      internalNote: "An internal chat is on right away. Only colleagues signed in to FixControl can reach it.",
    },
    behavior: {
      greeting: "Opening line",
      greetingHelp: "The first thing the chat says. Leave empty for the default line in the visitor's language.",
      persona: "Tone and behaviour",
      personaHelp: "Free text passed into the prompt — for example: short and businesslike, always answer in the visitor's language.",
      allowedTopics: "What it may help with",
      blockedTopics: "What it must decline",
      maxQuestions: "Maximum questions",
      maxQuestionsHelp: "How many clarifying questions the chat asks before summarising. Leave empty for the default.",
      kbScope: "Knowledge reach",
      kbScopeHelp: "Within the set you chose above: whether this project's sources come first. This can only narrow the reach, never widen it.",
      kbScopes: {
        project_first: "This project first",
        project_only: "This project only",
        tenant: "Whole workspace",
      },
    },
    card: {
      revokeLink: "Withdraw link",
      shareLink: "Shareable link",
      shareLinkHelp: "A link you can send, or put in your help docs \u2014 no website needed. It opens this chat on a branded page. This is what used to be called the customer intake link; it is now one of the two ways to reach this chat.",
      createLink: "Create a shareable link",
      openLink: "Open",
      linkNotRenderable: "This chat has a link, but its key was shown once at the time and cannot be displayed again. Create a new link if you have lost it.",
      changeType: "Change kind",
      changeTypeHint: "Changing this resets the settings that belong to it. An attached knowledge set is detached when the new kind is not allowed to read it.",
      advanced: "Advanced settings",
      openChat: "Open chat",
      hideChat: "Close chat",
      reach: "Where colleagues find this chat",
      directLink: "Direct link:",
      embedHelpInternal: "Paste this snippet on an intranet page. The visitor must be signed in to FixControl; the page asks for permission once in a small window. Also fill in that intranet page's address below.",
    },
    panel: {
      greeting: "Hi! What can I help you with?",
      placeholder: "Type a message…",
      send: "Send",
      thinking: "Working…",
      failed: "Something went wrong. Please try again.",
      unavailable: "This chat is unavailable right now.",
      file: "File a request",
      filed: "Request filed:",
    },
    authorize: {
      connecting: "Connecting to the chat…",
      refused: "This chat cannot be opened here.",
      refusedHint: "Sign in to FixControl, or ask an admin to add this page's address to the chat.",
    },
    conversations: {
      title: "Conversations",
      subtitle: "What came in through this project's chats.",
      empty: "No conversations yet.",
      emptyHint: "As soon as someone uses the chat, the conversation appears here — along with the request it produced.",
      anonymous: "Anonymous visitor",
    },
    projectIntro: "This project's chats: on your website, or internal for colleagues. You create and configure them here.",
    projectEmpty: "This project has no chat yet.",
    projectEmptyNoRights: "Ask an admin of this workspace to create one.",
    loadFailed: "Couldn't load this project's chats.",
    enabled: "Enabled",
    name: "Name",
    project: "Project",
    allowedOrigins: "Your website addresses",
    allowedOriginsHelp: "One address per line (https://example.com). Required: without it the chat answers nobody.",
    intranetOrigins: "Your intranet addresses (optional)",
    intranetOriginsHelp: "Only needed if you put the chat on an intranet page of your own. Inside FixControl it works either way.",
    localeDefault: "Default language",
    intakeMode: "Intake mode",
    intakeModes: { answer_only: "Answer only", lead_capture: "Collect contact details", issue_intake: "Take a request", demo_intake: "Take a demo request" },
    defaultIntent: "Default intent",
    knowledgeScope: "Knowledge set",
    knowledgeMode: "Knowledge mode",
    knowledgeModes: { public_only: "Public only", project_public: "This project's public knowledge", internal_scope: "Internal set", disabled: "Disabled" },
    notifyEmail: "Notification email",
    createIssue: "Create a FixControl request",
    createFreshdesk: "Create a Freshdesk ticket on a qualified lead (via the delivery queue)",
    createJira: "Create a Jira issue on a qualified lead (via the delivery queue)",
    requireEmail: "Require an email for follow-up",
    rateLimitProfile: "Message rate limit",
    embedCode: "Embed code",
    embedHelp: "Paste this snippet on your public site. The chat id is public; workspace, project and settings are resolved on our side.",
    copy: "Copy",
    copied: "Copied",
    testWidget: "Test chat",
    save: "Save",
    saved: "Saved",
    widgets: "Chats",
    widgetsSubtitle: "Every chat in this workspace. A chat bound to a project can also be configured on that project's page.",
    newWidget: "New chat",
    noWidgets: "No chats yet.",
    noWidgetsHint: "Create one with the button above, or from a project's chat page.",
    inbox: "All conversations",
    inboxSubtitle: "Every chat conversation in this workspace, public and internal.",
    inboxEmpty: "No conversations yet.",
    columns: { visitor: "Who", kind: "Kind", intent: "Intent", company: "Company", status: "Status", created: "Created" },
    scopes: "Knowledge sets",
    scopesSubtitle: "A set is a group of documentation with a visibility. A public chat reads public sets only.",
    newScope: "Name of the new set",
    newPublicScope: "Public set",
    newInternalScope: "Internal set",
    noScopes: "No knowledge sets yet.",
    visibility: "Visibility",
    visibilities: { public: "Public", internal: "Internal", restricted: "Restricted" },
    publicOnlyWarning: "Only PUBLIC sets can be attached to a public chat. Never expose tickets, Slack threads, private docs or codebase content.",
    privacyNote: "Visitors are warned not to share passwords, tokens or sensitive data. No raw IP is stored.",

    /* Why an action failed. One sentence per situation, because the action
     * the reader has to take differs: sign in again, ask an admin, reload the
     * page, or start the server. */
    errors: {
      offline: "FixControl is unreachable. Is the server still running?",
      unauthenticated: "Your session has expired. Sign in again.",
      two_factor: "Sign in again with your two-factor code.",
      forbidden: "You do not have permission for this. Ask an admin of your organisation.",
      csrf: "The request was blocked for security reasons. Reload the page and try again.",
      invalid: "The request was refused. Check the details you entered.",
      rate_limited: "Too many requests in a short time. Wait a moment.",
      server: "Something went wrong on our side. Try again.",
      unknown: "Something went wrong. Try again.",
    },
    /* Knowledge-set contents: what is in it, and who may read it. */
    scopeEditor: {
      manage: "Manage contents",
      hide: "Hide contents",
      contents: "What this set contains",
      count: "{n} sources",
      countOne: "1 source",
      empty: "This set is empty. A chat using it finds no documents at all.",
      add: "Add sources",
      searchPlaceholder: "Search your knowledge…",
      searchEmpty: "No sources found.",
      searchFailed: "Your knowledge could not be loaded.",
      addAll: "Add all {n} results",
      limitReached: "A set holds at most 500 sources.",
      added: "Added",
      addOne: "Add",
      remove: "Remove",
      missing: "This source no longer exists",
      missingCount: "{n} sources in this set no longer exist.",
      removeMissing: "Clean up",
      unsafe: "{n} of these sources describe internal systems (codebase analyses, case learnings, customer documentation). In a PUBLIC set an anonymous visitor can be answered from them.",
      notAutomatic: "New knowledge is not added here automatically. Add a source after you put it on the Knowledge page.",
      nameLabel: "Set name",
      delete: "Delete set",
      deleteConfirm: "Delete this set? Chats using it fall back to no knowledge.",
      newHeading: "New set",
      namePlaceholder: "For example: Public documentation",
      create: "Create set",
      loadFailed: "The knowledge sets could not be loaded.",
    },
    /* On the chat card: what the selected set actually holds. */
    knowledgeEmptyWarning: "This set is empty — the chat answers nothing from documentation yet.",
    knowledgeNoneWarning: "With no set attached this chat searches no documentation at all.",
    knowledgeManage: "Manage sets",
  },

  // Microsoft Teams → Identity bindings panel.
  teamsIdentities: {
    title: "Teams identity bindings",
    subtitle: "Self-linked Teams users for this tenant. Only elevated bindings can approve governance gates from Teams.",
    loading: "Loading…",
    emptyTyping: "No bindings yet. Users self-link by typing",
    emptyToBotAdmin: "to the bot in Teams; admins elevate the binding here so it can approve.",
    emptyToBotMember: "to the bot in Teams; ask an admin to elevate a binding before they can approve.",
    unknownUser: "(unknown user)",
    linkedAt: "linked",
    elevatedAt: "elevated",
    revokedAt: "revoked",
    elevate: "Elevate",
    revoke: "Revoke",
    badges: { elevated: "Elevated", verified: "Verified", revoked: "Revoked" },
    errors: {
      load: "Could not load bindings.",
      reach: "Could not reach FixControl.",
      elevate: "Could not elevate.",
      revoke: "Could not revoke.",
    },
  },

  /* Project-page issues & missions sections (src/app/projects/[id]/_components/
   * sections/project-issues.tsx + project-missions.tsx). Compact read-only
   * lists scoped to this project; row labels reuse the existing
   * issuesDetail.* and missions.status.* dictionaries. */
  projectSections: {
    issues: {
      navLabel: "Issues",
      title: "Issues",
      subtitle: "Every issue in this project — search, filter and triage them here.",
      newIssue: "New issue",
      viewAll: "Open in the full issue list",
      empty: "No issues in this project yet.",
      emptyHint: "Issues appear here as soon as work arrives through this project's intake sources — channels, mailboxes, or a connected tracker.",
    },
    missions: {
      navLabel: "Missions",
      title: "Missions",
      subtitle: "Every AI mission run for this project — each external step stays behind approval.",
      viewAll: "Open in the full mission list",
      empty: "No missions for this project yet.",
      emptyHint: "A mission is started from an issue and runs plan → sandbox verification → approval. Start one from an issue in this project.",
    },
    patches: {
      navLabel: "Patches",
      title: "Patches",
      subtitle: "Code changes proposed for this project. Approve or reject them here; the full diff is one click away.",
      searchPlaceholder: "Search patches…",
      viewAll: "Open in the full patch queue",
      empty: "No patches for this project yet.",
      emptyHint: "A patch appears when an agent proposes a code change in a work session. Nothing leaves the system without an approval.",
      bulkApprove: "Approve",
      bulkReject: "Reject",
      bulkRejectConfirmTitle: "Reject selected patches?",
      bulkRejectConfirmBody: "{count} patch(es) will be rejected. The work session stays, so an agent can propose a new version.",
    },
    workspaces: {
      navLabel: "Work sessions",
      title: "Work sessions",
      subtitle: "The sessions where agents work on this project's code — with the issue they came from and the patches they produced.",
      viewAll: "Open in the full session list",
      empty: "No work sessions for this project yet.",
      emptyHint: "A work session starts when an agent picks up an issue in this project. Start a mission from an issue to get one.",
      renameFailed: "Rename failed.",
      deleteFailed: "Delete failed.",
    },
    list: {
      exportCsv: "Export CSV",
    },
  },

  /* Context summary on the project page (src/app/projects/[id]/_components/
   * sections/context-summary.tsx). Honest phrasing only: FixControl uses the
   * sources linked to THIS project — never "understands your organization";
   * configured-but-unverified capabilities are shown as exactly that. */
  projectContextSummary: {
    loadError: "Could not load the project context summary.",
    purpose: {
      title: "What is this project used for?",
      primaryBadge: "Primary goal",
      addLabel: "Add another use case",
      addPlaceholder: "Choose a use case…",
      addButton: "Add",
      setPrimary: "Make primary",
      remove: "Remove",
      removeAria: "Remove {label}",
      primaryRemoveError: "The primary goal cannot be removed. Make another use case primary first.",
      updateError: "Could not update the use cases.",
    },
    context: {
      title: "Which context does FixControl use?",
      intro: "FixControl uses the linked sources below for this project.",
      counts: {
        intakeSources: "Intake sources",
        codebases: "Codebases",
        knowledgeSources: "Knowledge sources",
        notificationRoutes: "Notification routes",
        deploymentBindings: "Deployment bindings",
        codeBindings: "Code & delivery bindings",
        approvalChannels: "Approval channels",
      },
      /* Every tile opens the list behind its own number — a count you cannot
       * ask "which ones?" about is not an answer. */
      detailHint: "Click a tile to see exactly which sources are behind it.",
      detailEmpty: "Nothing linked in this category yet.",
      manage: "Manage",
      more: "…and {count} more",
      sourceStatus: {
        processing: "Indexing",
        failed: "Indexing failed",
      },
      resourcesHeading: "Other linked sources",
      empty: "No sources are linked to this project yet.",
    },
    capabilities: {
      title: "What can FixControl do right now?",
      groupAvailable: "Available now",
      groupUnverified: "Configured, not verified",
      groupNotConfigured: "Not configured",
      approvalChip: "Approval required",
      status: {
        unknown: "Not verified",
        degraded: "Degraded",
        unavailable: "Unavailable",
      },
    },
    gaps: {
      title: "What's still missing?",
      none: "Nothing is missing — the checks for the active use cases are ready.",
      fix: "Set up",
      showOptional: "Show optional checks ({count})",
      hideOptional: "Hide optional checks",
      status: {
        missing: "Missing",
        degraded: "Degraded",
        optional: "Optional",
        not_applicable: "Not applicable",
      },
    },
    firstSteps: {
      title: "First steps",
      intro: "Suggested starting points for this project's active use cases. Each link opens an existing page — nothing runs automatically.",
    },
    approvalNote: "External actions — replies, pull requests, deployment promotion — always require human approval.",
  },

  /* The project action / resolution layer (src/lib/use-cases/project-actions.ts).
   * Product language only: the resolver speaks in ids like `setup.workSource`
   * and `operate.engineering.work`; nothing internal reaches the screen. */
  projectActions: {
    more: "More project actions",
    setup: {
      title: "Setup & status",
      progress: "Setup: {completed} of {total} steps complete",
      nextLabel: "Next step",
      doneLabel: "Done",
      laterLabel: "Later",
      allDone: "Everything this project needs is set up.",
      noPermission: "Someone with configuration rights needs to do this step.",
    },
    steps: {
      workSource: {
        label: "Where work comes in",
        hint: "A Slack channel, service desk, issue tracker or mailbox — or an alert from your own systems. That is how work reaches the project.",
      },
      codebase: {
        label: "The code",
        hint: "The repository agents read, analyse and propose changes against.",
      },
      knowledge: {
        label: "Knowledge",
        hint: "Documentation agents draw on — or let a linked codebase be inspected.",
      },
      deployment: {
        label: "Deployment source",
        hint: "The pipeline or rollout controller whose deployments this project governs.",
      },
      delivery: {
        label: "Pull request delivery",
        hint: "Where approved changes are delivered. Without it the project stays analysis-only.",
      },
      approvals: {
        label: "Who approves",
        hint: "Where approval requests land before a risky action goes ahead.",
      },
      notifications: {
        label: "Where updates go",
        hint: "The channel that gets progress and outcome updates.",
      },
    },
    setup_workSource: { label: "Add work source" },
    setup_codebase: { label: "Connect code" },
    setup_knowledge: { label: "Add knowledge" },
    setup_deployment: { label: "Connect deployment source" },
    setup_delivery: { label: "Connect delivery" },
    setup_approvals: { label: "Add approval channel" },
    setup_notifications: { label: "Add update channel" },
    operate_support_work: { label: "Open issues" },
    operate_support_inbox: { label: "Open inbox" },
    operate_engineering_work: { label: "Open project work" },
    operate_engineering_missions: { label: "Open missions" },
    operate_deployment_activity: { label: "View deployment activity" },
    operate_security_posture: { label: "View security posture" },
    operate_knowledge_sources: { label: "Open knowledge" },
    operate_custom_work: { label: "Open project work" },
    inspect_patchHistory: { label: "Patch history" },
    inspect_developerWork: { label: "Developer work" },
    inspect_activity: { label: "Project activity" },
    configure_advanced: { label: "Advanced settings" },
    remove_project: { label: "Delete project" },
  },

  /* Task-oriented project creation wizard (src/app/projects/_components/
   * create-project-wizard.tsx). The preview.* keys are referenced by the
   * pure plan builder in src/lib/use-cases/wizard.ts. */
  projectWizard: {
    stepIndicator: "Step {current} of {total}",
    buttons: {
      back: "Back",
      next: "Continue",
      cancel: "Cancel",
    },
    quickCreate: {
      button: "Create now",
      hint: "Skip the remaining steps and create the project with just this name and goal — sources can be linked later on the project page.",
    },
    steps: {
      goal: {
        title: "What do you want to achieve?",
        sub: "Give the project a name and pick its primary goal.",
      },
      sources: {
        title: "Which sources belong to this project?",
        sub: "Pick from resources that are already connected. You can add more later.",
      },
      confirm: {
        title: "Confirm project context",
        sub: "This is the context the project starts with.",
      },
      additional: {
        title: "Additional use cases",
        sub: "Optionally combine other goals in the same project.",
      },
      permissions: {
        title: "What may FixControl do?",
        sub: "Choose how far analysis may go for this project.",
      },
      review: {
        title: "Review",
        sub: "Nothing is created until you confirm.",
      },
      firstSteps: {
        title: "First step",
        sub: "The project is ready — this is where to start.",
      },
    },
    fields: {
      nameLabel: "Project name",
      namePlaceholder: "e.g. Webshop customer support",
      descriptionLabel: "Description (optional)",
      descriptionPlaceholder: "What is this project about?",
      goalLabel: "Primary goal",
    },
    sources: {
      groupCommunication: "Communication",
      groupCode: "Code",
      groupKnowledge: "Knowledge",
      slackLabel: "Slack channels",
      teamsLabel: "Teams channels",
      mailboxLabel: "Mailboxes",
      codebasesLabel: "Codebases",
      notConnected: "Not connected",
      connectHint: "Connect this integration first in Settings.",
      connectLink: "Go to integrations",
      noChannels: "No channels found.",
      teamsNotListable: "Teams channels can’t be listed here. After creating the project, link a Teams channel from the project’s settings.",
      noMailboxes: "No mailboxes set up yet.",
      noCodebases: "No codebases connected yet.",
      knowledgeCount: "{count} knowledge sources available to this organization.",
      knowledgeNone: "No knowledge sources yet.",
      knowledgeHint: "Knowledge sources are managed centrally — nothing to select here.",
      knowledgeLink: "Manage knowledge sources",
      optionalHint: "Selecting sources is optional — you can add or change them later on the project page.",
    },
    confirm: {
      none: "No sources selected. The project starts without linked sources; you can add them later.",
      slackHeading: "Slack channels",
      teamsHeading: "Teams channels",
      mailboxHeading: "Mailboxes",
      codebaseHeading: "Codebases",
    },
    additional: {
      intro: "Select any extra use cases for this project. They add dashboards and readiness checks — never permissions.",
    },
    permissions: {
      readOnly: {
        label: "Only look",
        description: "FixControl reads the linked sources and reports findings. No working copies, no draft changes.",
      },
      sandbox: {
        label: "Try changes in a safe copy",
        description: "FixControl may also try proposed changes in an isolated, safe copy. Nothing leaves that copy without approval.",
      },
      approvalNote: "Actions outside FixControl — customer replies, pull requests, deploying to production — always need a person’s approval, whatever you choose here.",
    },
    review: {
      intro: "This is exactly what will be created. Cancel applies nothing.",
      newBadge: "new",
      approvalsHeading: "Always requires your approval",
      noApprovals: "The selected use cases include no external actions.",
      createButton: "Create project",
      creating: "Creating…",
      failed: "Could not create the project.",
    },
    preview: {
      name: "Project name",
      primaryGoal: "Primary goal",
      additionalUseCase: "Additional use case",
      executionMode: "Execution mode",
      remediationStart: "Remediation start",
      codebase: "Codebase",
      binding: "Linked source",
    },
    modeValue: {
      read_only: "Only look",
      sandbox: "Try changes in a safe copy",
      writable: "Apply approved changes",
    },
    remediationValue: {
      manual: "Manual — a person decides when work starts",
      automatic_on_intake: "Automatic on intake",
    },
    firstSteps: {
      createdIntro: "Project “{name}” has been created.",
      conflictsHeading: "Some sources are already linked to another project",
      conflictsHint: "These sources were not linked here. Adjust them on the project pages if needed.",
      links: {
        project: "Open the project page",
        inbox: "Watch incoming questions in the inbox",
        issues: "View the issue list",
        devops: "Review deployment settings",
        posture: "View knowledge & security posture",
        knowledge: "Manage knowledge sources",
      },
      toProject: "Go to project",
    },
  },

  /* Use-case registry (src/lib/use-cases/) — context-first product model.
   * Copy claims only shipped functionality; the deployment texts keep the
   * observe/action distinction and security is explicitly beta. */
  useCases: {
    availability: { available: "Available", beta: "Beta", preview: "Preview" },
    support: {
      label: "Handle customer questions",
      short: "Understand customer questions, find relevant knowledge and tickets, and propose governed answers.",
      full: "FixControl receives questions from your connected channels, grounds answers in the knowledge sources linked to this project, files tickets, and keeps every customer-facing reply behind your reply policy. Escalation to engineering is always an explicit human step.",
      outcome: "Customer questions answered from your own knowledge, with every reply governed.",
      prereq: {
        intake: "At least one intake source (channel, mailbox, or chat widget) linked to this project.",
        knowledge: "At least one knowledge source linked to this project, so answers are grounded.",
      },
    },
    engineering: {
      label: "Fix software problems",
      short: "Analyze issues with code context, propose patches, verify in a sandbox, and prepare pull requests.",
      full: "FixControl reads the repositories linked to this project, analyzes issues, proposes patches and verifies them in a sandbox. Delivery is pull-request only, behind plan and patch approval gates — nothing merges or ships without a recorded human decision.",
      outcome: "Verified patches prepared as pull requests, behind your approval gates.",
      prereq: {
        codebase: "A linked codebase this project may read.",
        knowledge: "Relevant documentation or knowledge improves analysis quality (recommended).",
      },
    },
    deployment: {
      label: "Control deployments",
      short: "Observe deployment activity, assess risk, and organize approvals. Actions only where supported.",
      full: "FixControl observes deployment activity from your linked providers, assesses risk, and organizes deployment approvals with evidence. Observing a provider never implies FixControl can act on it: the only controlled action today is promoting a paused Argo Rollout after an approved deployment gate. Capability health shows per project what is observation and what is an available action.",
      outcome: "Deployment decisions with evidence and approvals — action only where demonstrably supported.",
      prereq: {
        binding: "A deployment binding (environment, application, or namespace) linked to this project.",
        approvalRoute: "An approval route for deployment gates.",
        actionSupport: "Controlled promotion requires a configured Argo Rollouts connection; other providers are observe-only.",
      },
    },
    security: {
      label: "Investigate security risks",
      short: "Run the shipped code and dependency scanners, review posture, and route risks into governed remediation.",
      full: "FixControl runs its shipped scanners (secret detection, dependency audit, SAST, vulnerability scanning) against the codebase linked to this project and turns results into a posture assessment. Remediation follows the normal governed mission flow. Scheduled scans and a dedicated findings workflow are not available yet — this use case is in beta and shows exactly what runs.",
      outcome: "A scanner-backed posture view with risks routed into governed remediation.",
      prereq: {
        codebase: "A linked codebase to scan.",
        scanner: "At least one available scanner in this environment.",
      },
    },
    knowledge: {
      label: "Unlock organization knowledge",
      short: "Connect scattered knowledge sources and get source-grounded answers per project.",
      full: "FixControl connects the wikis, documentation, tickets, procedures and earlier decisions you link to this project and answers questions grounded in those sources — with links to where each answer came from. What is not in a linked source, FixControl does not claim to know. A knowledge project needs no codebase and no ticket integration.",
      outcome: "Reliable, source-grounded answers over the knowledge you connected.",
      prereq: {
        source: "At least one linked knowledge source.",
      },
    },
    custom: {
      label: "Compose your own",
      short: "Combine capabilities yourself, starting from what is actually connected and healthy.",
      full: "A custom project starts from your real configuration: which sources are linked, which capabilities are healthy, which prerequisites are missing, and which actions require approval. You add use cases and bindings step by step — nothing is claimed that is not configured.",
      outcome: "A project composed from your own combination of connected sources and capabilities.",
      prereq: {
        context: "At least one linked source or capability to work with.",
      },
    },
    capabilities: {
      intake_receive: {
        label: "Receive work",
        description: "Receive questions and issues from linked channels, mailboxes, and chat widgets.",
      },
      knowledge_answer: {
        label: "Answer from knowledge",
        description: "Ground answers in the knowledge sources linked to this project.",
      },
      tickets_read: {
        label: "Read tickets",
        description: "Answer questions about tickets in FixControl and bound trackers, read-only.",
      },
      reply_govern: {
        label: "Govern replies",
        description: "Draft customer replies behind the reply policy and safety scan.",
      },
      reply_send: {
        label: "Send approved replies",
        description: "Deliver an approved reply through the connected mail or ticket provider. External send — always policy-governed.",
      },
      code_read: {
        label: "Read code",
        description: "Read the repositories linked to this project.",
      },
      sandbox_analyze: {
        label: "Analyze in sandbox",
        description: "Run analysis and tests in an isolated sandbox.",
      },
      delivery_pr: {
        label: "Prepare pull requests",
        description: "Deliver approved patches as pull requests — never a direct push. External write — approval required.",
      },
      deployment_observe: {
        label: "Observe deployments",
        description: "Ingest deployment activity and rollout status from linked providers. Observation only.",
      },
      deployment_promote: {
        label: "Promote rollout (governed)",
        description: "Promote a paused Argo Rollout after an approved deployment gate. External action — approval required.",
      },
      security_scan: {
        label: "Scan for risks",
        description: "Run the shipped secret, dependency, SAST and vulnerability scanners.",
      },
      approvals_route: {
        label: "Route approvals",
        description: "Route decisions to the right approvers and record them in the decision ledger.",
      },
      notifications_post: {
        label: "Post updates",
        description: "Post status updates to bound channels. External write — goes through the delivery queue.",
      },
    },
    /**
     * Evidence lines for capabilities, readiness checks and setup steps.
     *
     * The resolvers (src/lib/use-cases/{readiness,readiness-checks,
     * project-actions}.ts) emit one of these keys plus its params; the
     * project page and the Command Center project block both format from
     * here, so the same fact never gets two different sentences. Countable
     * facts come in a One/Other pair — this is what replaced "1 codebase(s)".
     */
    evidence: {
      intakeSourcesOne: "{count} intake source",
      intakeSourcesOther: "{count} intake sources",
      intakeSourcesNotTestedOne: "{count} source set up, not tested yet",
      intakeSourcesNotTestedOther: "{count} sources set up, none tested yet",
      intakeWaitingFirstMail: "Set up, waiting for the first email",
      knowledgeSourcesOne: "{count} knowledge source",
      knowledgeSourcesOther: "{count} knowledge sources",
      codebasesOne: "{count} codebase",
      codebasesOther: "{count} codebases",
      deploymentBindingsOne: "{count} deployment binding",
      deploymentBindingsOther: "{count} deployment bindings",
      codeBindingsOne: "{count} code/delivery binding",
      codeBindingsOther: "{count} code/delivery bindings",
      approvalChannelsOne: "{count} approval channel",
      approvalChannelsOther: "{count} approval channels",
      notificationRoutesOne: "{count} notification route",
      notificationRoutesOther: "{count} notification routes",
      approvalRoutesLedgerOne: "{count} approval route; decision ledger always on",
      approvalRoutesLedgerOther: "{count} approval routes; decision ledger always on",
      approvalRoutesReplyPolicyOne: "{count} approval route; reply policy always on",
      approvalRoutesReplyPolicyOther: "{count} approval routes; reply policy always on",
      approvalRoutesPlanPatchOne: "{count} approval route; plan/patch gates always on",
      approvalRoutesPlanPatchOther: "{count} approval routes; plan/patch gates always on",
      approvalRoutesDeploymentGateOne: "{count} approval route; a deployment FixControl starts from a mission waits for approval when your policy requires it",
      approvalRoutesDeploymentGateOther: "{count} approval routes; a deployment FixControl starts from a mission waits for approval when your policy requires it",
      approverStepsHeldOne: "Someone can approve the {count} approval step",
      approverStepsHeldOther: "Someone can approve each of the {count} approval steps",
      approverStepsUnheldOne: "Nobody can approve yet: {count} approval step has no one who can approve it",
      approverStepsUnheldOther: "Nobody can approve yet: {count} approval steps have no one who can approve them",
      approversUnknown: "Could not check who approves. Open Approvals & rules to see.",
      ticketsBuiltIn: "FixControl tickets; bound trackers when healthy",
      replyGovernAlwaysOn: "Reply policy + safety scan (always on)",
      replySendConfigured: "Send-capable provider configured",
      replySendMissing: "No send-capable provider",
      sandboxWithCodebasesOne: "Sandbox runtime; {count} codebase",
      sandboxWithCodebasesOther: "Sandbox runtime; {count} codebases",
      sandboxUnavailable: "Sandbox runtime unavailable",
      argoPromoteConfigured: "Argo Rollouts promote configured",
      argoPromoteMissing: "No promote-capable connection (observe-only)",
      scannersVerifiedOne: "{count} scanner verified on last run",
      scannersVerifiedOther: "{count} scanners verified on last run",
      scannersNoneVerified: "No scanner verified yet",
      scannersNoneVerifiedHere: "No scanner verified in this environment yet",
      environmentVerified: "Verified environment contract in force",
      environmentBlockedQuestion: "Blocked: {question}",
      environmentBlockedFailure: "Blocked ({failure})",
      environmentBlocked: "Blocked",
      environmentRunning: "Working out what this project needs to run",
      environmentNotVerified: "Environment not verified yet",
      knowledgeIndexing: "{indexed} indexed, {processing} processing, {failed} failed",
      knowledgePublicScopesOne: "{count} public scope; everything else stays internal",
      knowledgePublicScopesOther: "{count} public scopes; everything else stays internal",
      lastAssessment: "Last assessment {at}",
      noAssessment: "No posture assessment recorded yet",
      unimplementedCheck: "Check {id} has no implementation",
    },
    readiness: {
      unimplemented: {
        title: "Check not implemented",
        description: "This readiness check has no implementation yet — treat it as not ready.",
      },
      support_intake_source: {
        title: "Intake source",
        description: "A channel, mailbox or chat widget where customer questions arrive for this project.",
      },
      support_knowledge_source: {
        title: "Knowledge source",
        description: "Linked knowledge so answers are grounded instead of guessed.",
      },
      support_reply_destination: {
        title: "Reply destination",
        description: "A send-capable provider for delivering approved replies. Optional for suggest-only projects.",
      },
      support_approval_policy: {
        title: "Reply approval policy",
        description: "The reply policy and safety scan that govern every customer-facing answer.",
      },
      support_send_health: {
        title: "Send capability health",
        description: "Verified delivery health for outgoing replies. Configured is not the same as verified.",
      },
      engineering_codebase: {
        title: "Codebase",
        description: "A linked repository this project can analyze.",
      },
      engineering_code_access: {
        title: "Code access",
        description: "Verified read access to the linked repositories.",
      },
      engineering_sandbox: {
        title: "Sandbox analysis",
        description: "The isolated runtime that verifies patches before anyone reviews them.",
      },
      engineering_environment: {
        title: "Runnable environment",
        description: "FixControl has worked out what this repository needs to run and proved it by starting it once.",
      },
      engineering_approval_route: {
        title: "Approval route",
        description: "Plan and patch gates with the approvers who decide.",
      },
      engineering_pr_provider: {
        title: "Pull-request delivery",
        description: "A git provider binding for delivering approved patches as pull requests. Optional for analysis-only projects.",
      },
      deployment_binding: {
        title: "Deployment binding",
        description: "The environment, application or namespace this project governs.",
      },
      deployment_observe_health: {
        title: "Observation health",
        description: "Verified ingest of deployment activity from the linked provider.",
      },
      deployment_approval_route: {
        title: "Deployment approvals",
        description: "Deployments FixControl starts from a mission wait for approval when your policy requires it. Your own pipelines' deployments get a gate only with “Open gates for your own deployments” on; otherwise they are observed, not gated.",
      },
      deployment_action_capability: {
        title: "Controlled action",
        description: "Whether a governed promote action is available. Observing a provider never implies acting on it.",
      },
      security_codebase: {
        title: "Codebase",
        description: "A linked repository to scan.",
      },
      security_scanner_capability: {
        title: "Scanner availability",
        description: "At least one scanner verified working in this environment on the last run.",
      },
      security_scan_status: {
        title: "Scan status",
        description: "A recorded posture assessment for this project's code.",
      },
      security_remediation_route: {
        title: "Remediation route",
        description: "Optional: pull-request delivery for governed fixes.",
      },
      knowledge_source: {
        title: "Knowledge source",
        description: "At least one linked source to answer from.",
      },
      knowledge_indexing: {
        title: "Indexing status",
        description: "Whether linked sources are indexed and searchable.",
      },
      knowledge_scope: {
        title: "Visibility scope",
        description: "What is published publicly versus kept internal. Public chat can only ever read public scopes.",
      },
      knowledge_intake_channel: {
        title: "Question channel",
        description: "Optional: a channel or widget where people can ask questions.",
      },
      custom_any_context: {
        title: "Project context",
        description: "At least one linked source, codebase, channel or binding to work with.",
      },
    },
    onboardingSteps: {
      support_process_test_question: {
        label: "Process a test question",
        description: "Ask something in the intake surface and see how it is answered from your knowledge.",
      },
      support_look_up_ticket: {
        label: "Look up a ticket",
        description: "Open this project's ticket list and check what FixControl can see.",
      },
      support_draft_answer: {
        label: "Review a draft answer",
        description: "Open the inbox and see how governed reply drafts look.",
      },
      engineering_select_issue: {
        label: "Pick an issue",
        description: "Choose an issue in this project to analyze first.",
      },
      engineering_read_only_analysis: {
        label: "Start a read-only analysis",
        description: "Let FixControl read the linked code without changing anything.",
      },
      engineering_codebase_readiness: {
        label: "Check codebase readiness",
        description: "Verify code access and sandbox status on the project overview.",
      },
      deployment_review_recent: {
        label: "Review recent deployments",
        description: "Open the DevOps overview and see what is being observed.",
      },
      deployment_assess_risk: {
        label: "Assess a deployment",
        description: "Look at the approvals surface for this project's deployment gates.",
      },
      deployment_test_approval_route: {
        label: "Check the approval route",
        description: "Confirm who decides on deployment gates — no real promotion involved.",
      },
      security_view_scanner_status: {
        label: "View scanner status",
        description: "See which scanners are available and what the last run produced.",
      },
      security_run_read_only_scan: {
        label: "Run a read-only scan",
        description: "Start a posture assessment where supported — analysis only.",
      },
      knowledge_ask_test_question: {
        label: "Ask a test question",
        description: "Ask something and check that the answer is grounded in your sources.",
      },
      knowledge_view_sources_used: {
        label: "See the sources used",
        description: "Open the knowledge base and review what this project can answer from.",
      },
      knowledge_add_missing_source: {
        label: "Add a missing source",
        description: "Link the documentation the last answer was missing.",
      },
      custom_advanced_setup: {
        label: "Open advanced setup",
        description: "Compose bindings and capabilities step by step.",
      },
    },
  },

  // Developer-governance UI (project → Development). Event/status/mode keys
  // mirror the closed unions in src/lib/dev-governance/types.ts one-for-one;
  // src/lib/dev-governance/view-model.ts derives every key path from those
  // unions, and view-model.test.ts fails the build when one is missing here.
  devGovernance: {
    eyebrow: "Development governance",
    title: "Development work",
    subtitle:
      "Every recorded unit of developer work in this project: who did it, on which branch, and which review it is still waiting for.",
    navLink: "Development",
    loadError: "The development work could not be loaded.",
    retry: "Try again",
    noAccess: {
      title: "You have no access to the development work in this project.",
      body: "Development permissions come from project membership — an organisation role does not grant them. Ask a project administrator to add you as a member.",
    },
    notFound: {
      title: "This project no longer exists.",
      body: "It may have been deleted. Go back to the project list to pick another one.",
    },
    unset: "—",
    list: {
      empty: "No development work has been recorded for this project yet.",
      emptyHint:
        "Work appears here as soon as a developer starts it from the command line.",
      cliCommand: "fixcontrol dev start",
      filterStatus: "Status",
      filterMode: "Type of work",
      allStatuses: "All statuses",
      allModes: "All types of work",
      columns: {
        status: "Status",
        mode: "Type of work",
        issueKey: "Issue",
        branch: "Branch",
        initiator: "Started by",
        pullRequest: "Pull request",
        created: "Started",
      },
    },
    detail: {
      emptyTitle: "Select a piece of work",
      emptyHint:
        "Pick a row to see its full timeline, its checkpoints and its review status.",
      close: "Close",
      section: {
        timeline: "Timeline",
        checkpoints: "Checkpoints",
      },
      fields: {
        mode: "Type of work",
        issue: "Issue",
        branch: "Branch",
        commits: "Commits",
        workspace: "Workspace",
        pullRequest: "Pull request",
        initiator: "Started by",
        startedAt: "Started",
        submittedAt: "Submitted",
        completedAt: "Finished",
        executionId: "Reference",
      },
      openIssue: "Open issue",
      openPullRequest: "Open pull request",
      readOnly:
        "This page reads the record. Developers write to it from the command line.",
    },
    review: {
      title: "Review",
      requiredYes: "This work has to be reviewed.",
      requiredNo: "Review is not required in this project.",
      requiredUnknown:
        "Whether a review is required does not show in the history loaded here.",
      waitingSince: "Waiting for a decision since",
      stallWarning:
        "An open review does not expire on its own. This work keeps waiting until somebody decides.",
      openDecision: "Open the decision",
      noDecision: "No decision point has been opened for this work yet.",
      phase: {
        submitted: "Submitted, waiting for the review to open",
        in_review: "Waiting for a reviewer's decision",
        changes_requested: "Changes requested — back with the developer",
      },
    },
    overlap: {
      badge: "Possible overlap",
      title: "Possibly overlapping work",
      subtitle:
        "Other work in this codebase looks like it touches the same files. This is a signal, not a conclusion: FixControl compares the paths that were recorded and declared, and it cannot see everything a developer will open.",
      peer: "Other work",
      openPeer: "Open this work",
      matchedPaths: "Shared paths",
      morePaths: "and {count} more",
      noPaths: "No shared paths were recorded.",
      detectedAt: "Noticed",
    },
    staleBase: {
      title: "Starting point may be out of date",
      body:
        "Work was merged into this codebase after this work started, so the starting commit may be behind. Consider rebasing on the latest state before you submit. We cannot tell from here whether it actually affects this work.",
      baseCommit: "Starting commit",
      detectedAt: "Noticed",
    },
    timeline: {
      title: "Timeline",
      subtitle: "Every recorded step, in the order it happened.",
      empty: "No steps have been recorded yet.",
      showOlder: "Show {count} older",
      showLess: "Show less",
      loadMore: "Load more",
      counter: "{shown} of {total} steps",
      follows: "follows on {label}",
    },
    agentSteps: {
      title: "AI steps",
      subtitle:
        "What an AI model did on this work, recorded by FixControl itself while the model ran.",
      empty: "No AI steps have been recorded for this work.",
      detailHidden:
        "Which files and commands were involved is only visible with the right to read patches.",
      modelUnknown: "Model not recorded",
      filesRead: "{count} files read",
      filesChanged: "{count} files changed",
      commands: "{count} commands",
      toolCalls: "{count} tools",
      tests: "{count} test runs",
      patch: "Patch",
      instruction: "Instruction fingerprint",
    },
    worktrees: {
      title: "Isolated work directories",
      subtitle:
        "An autonomous agent never works in a developer's workspace. It gets its own throwaway copy of the repository, on its own branch, and FixControl records what became of it. The full server path is deliberately not shown here.",
      branch: "Branch",
      detached: "No branch (detached)",
      created: "Created",
      removed: "Cleaned up",
      stillOpen: "Still on disk",
      status: {
        provisioning: "Being created",
        active: "In use",
        cleaning: "Being cleaned up",
        removed: "Cleaned up",
        stale: "Left behind",
      },
    },
    deployments: {
      title: "Where this code ran",
      subtitle:
        "Deployments FixControl observed for this work. Each entry is a recorded fact from the deployment log, not a live status.",
      artifactDigest: "Artifact",
      digestUnknown: "Not reported",
      run: "Deployment run",
      heuristic:
        "This deployment was matched to this work by issue key, not by the exact commit. Treat it as an indication, not proof that these bytes came from this work.",
      environment: {
        production: "Production",
        staging: "Staging",
        development: "Development",
      },
      phase: {
        queued: "Queued",
        running: "Running",
        succeeded: "Succeeded",
        failed: "Failed",
        cancelled: "Cancelled",
        awaiting_approval: "Awaiting approval",
      },
    },
    checkpoints: {
      title: "Checkpoints",
      subtitle: "Snapshots the developer recorded while working.",
      empty: "No checkpoints have been recorded.",
      changedFiles: "{count} changed files",
      droppedFiles: "{count} left out",
      truncatedFiles: "{count} not recorded",
      noDescription: "No description",
      diffHash: "Diff",
      testExit: "Exit code {code}",
    },
    status: {
      active: "In progress",
      submitted: "Submitted",
      in_review: "In review",
      changes_requested: "Changes requested",
      completed: "Finished",
      abandoned: "Stopped",
      failed: "Failed",
    },
    mode: {
      human_local: "Manual, local",
      ai_assisted_local: "AI-assisted, local",
      autonomous_agent: "Autonomous AI agent",
      support_fix: "Support fix",
      review: "Review",
      deployment: "Deployment",
    },
    checkpointKind: {
      checkpoint: "Checkpoint",
      test_run: "Test run",
      submit_snapshot: "Submission snapshot",
    },
    agentStepRole: {
      planner: "Planner",
      executor: "Builder",
      reviewer: "Reviewer",
    },
    agentStepStatus: {
      running: "Still running",
      completed: "Finished",
      failed: "Failed",
      cancelled: "Stopped",
    },
    actor: {
      user: "Person",
      agent: "AI agent",
      system: "System",
      webhook: "Webhook",
    },
    event: {
      execution: {
        started: "Work started",
        submitted: "Submitted for review",
        withdrawn: "Submission withdrawn",
        resumed: "Work resumed",
        review_opened: "Review opened",
        changes_requested: "Changes requested",
        approved: "Approved",
        completed: "Finished",
        abandoned: "Stopped",
        failed: "Failed",
      },
      checkpoint: { recorded: "Checkpoint recorded" },
      test_run: { recorded: "Test run recorded" },
      commit: { linked: "Commit linked" },
      pull_request: {
        linked: "Pull request linked",
        merged: "Pull request merged",
      },
      ci: { linked: "CI result linked" },
      deployment: { linked: "Deployment linked" },
      agent_step: {
        started: "AI step started",
        completed: "AI step finished",
        failed: "AI step failed",
      },
      patch: {
        proposed: "Patch proposed",
        applied: "Patch applied",
        rejected: "Patch rejected",
      },
      overlap: { detected: "Overlapping work detected" },
      stale_base: { detected: "Outdated starting point detected" },
      unknown: "Unknown step",
    },
    payload: {
      executionMode: "Type of work",
      issueKey: "Issue",
      branchName: "Branch",
      codebaseId: "Codebase",
      kind: "Kind",
      role: "Role",
      modelId: "Model",
      reviewRequired: "Review required",
      decision: "Decision",
      reason: "Reason",
      note: "Note",
      prNumber: "PR",
      mergedBy: "Merged by",
      mergeCommitSha: "Merge commit",
      sha: "Commit",
      state: "Result",
      context: "Check",
      changedFileCount: "Changed files",
      droppedFileCount: "Files left out",
      truncatedCount: "Files not recorded",
      testCommand: "Test command",
      testExitCode: "Exit code",
      mergedSha: "Merged commit",
      matchedPathCount: "Shared paths",
      heuristic: "Signal, not proof",
    },
  },
};

/**
 * Elke HR-waarde die het HR-bord als label kan tonen. Als union getypt zodat
 * de lockstep tussen `en` en `nl` hier net zo hard is als elders: een code
 * die in één locale ontbreekt of te veel staat, breekt de typecheck.
 */
export type HRLabelCode =
  | "NEW" | "TRIAGED" | "WAITING_FOR_INFORMATION" | "WAITING_FOR_HUMAN_REVIEW" | "IN_REVIEW"
  | "ANSWER_PROPOSED" | "APPROVAL_REQUIRED" | "RESOLVED" | "ESCALATED" | "BLOCKED"
  | "CANCELLED" | "GENERATED" | "DRAFT" | "REVIEW_REQUIRED" | "APPROVED" | "DELIVERED"
  | "UNDER_REVIEW" | "CORRECTED" | "SUPERSEDED" | "RETRACTED" | "PENDING" | "REJECTED"
  | "EXPIRED" | "INVALIDATED" | "CONSUMED" | "OPEN" | "ANSWERED" | "NORMAL" | "SENSITIVE"
  | "HIGHLY_RESTRICTED" | "INTERNAL" | "PERSONAL" | "CONFIDENTIAL_HR"
  | "PAYROLL_CONFIDENTIAL" | "MEDICAL_RESTRICTED" | "LEGAL_RESTRICTED" | "GENERAL_POLICY"
  | "PERSONAL_INFORMATION" | "PERSONAL_CALCULATION" | "PROCEDURAL_REQUEST"
  | "SENSITIVE_EMPLOYEE_RELATIONS" | "MEDICAL_OR_ABSENCE" | "PAYROLL"
  | "LEGAL_OR_COMPLIANCE" | "UNKNOWN" | "AUTO_ANSWER_ALLOWED" | "DRAFT_ONLY"
  | "HUMAN_REVIEW_REQUIRED" | "ESCALATION_REQUIRED" | "DENIED" | "CLARIFICATION_REQUIRED"
  | "SENSITIVE_CASE" | "ANSWER_APPROVAL" | "INCORRECT_ANSWER" | "CONNECTOR_FAILURE"
  | "AUTHORITATIVE" | "SUPPORTING" | "DEPRECATED" | "SOURCE_CONFLICT" | "SOURCE_NOT_FOUND"
  | "SOURCE_EXPIRED" | "LOW_CONFIDENCE" | "PERSONAL_IMPACT" | "CURRENT_AUTHORITATIVE_SOURCE"
  | "SAFE_DEFAULT" | "DATA_SCOPE_VIOLATION" | "CONNECTOR_NOT_ALLOWLISTED"
  | "EXTERNAL_CONNECTOR_UNAVAILABLE" | "SOURCE_METADATA_INCOMPLETE"
  | "EXPIRED_OR_INACTIVE_SOURCE_PRESENT" | "AUTHORITATIVE_RULE_CONFLICT"
  | "DRAFT_CONTRADICTS_AUTHORITATIVE";

export type Dictionary = DictionaryShape;

/**
 * Hand-written shape so leaves stay `string` (not literal types).
 *
 * The named sections (`common`, `nav`, `topbar`, `pages`, `intake`, `landing`,
 * `time`) are typed strictly: every other locale must supply the same keys or
 * the build breaks. Beyond those, the index signature lets feature pages add
 * their own namespace (e.g. `dashboard`, `inbox`) without an interface edit
 * per page — the translator falls back to English when a locale forgets a key
 * (logged once in dev).
 */
export interface DictionaryShape {
  [namespace: string]: any;
  common: {
    loading: string; save: string; cancel: string; delete: string;
    edit: string; create: string; close: string; open: string;
    yes: string; no: string; search: string; back: string; next: string;
    submit: string; error: string; failedToLoad: string; tryAgain: string; empty: string;
    filters: { project: string; allProjects: string; clearProject: string; projectUnnamed: string };
    providers: { email: string; webhook: string; local: string; ssh: string };
    roles: { admin: string; manager: string; operator: string };
  };
  language: { label: string; switchTo: string };
  nav: {
    /** Accessible name for the main left rail — see the value's comment. */
    sidebarLabel: string;
    overview: string; work: string; aiResults: string; context: string; agents: string;
    system: string; fixcontrol: string; aiOperations: string; aiOperationsMetrics: string; platform: string;
    dashboard: string; inbox: string; notifications: string; approvals: string;
    issues: string; customerDirectory: string; missions: string; projects: string; workspaces: string; patches: string;
    sandboxRuns: string;
    followUps: string; knowledge: string; codebasePosture: string; memory: string; tenants: string;
    aiTeam: string; skills: string; governance: string; governanceHierarchy: string; orchestrationConsole: string; runs: string; commandCenter: string;
    settings: string; customers: string; signups: string; adminSettings: string;
    patchDelivery: string; gitProvidersHealth: string; office365Health: string; knowledgeEmbeddings: string;
    brandExplorer: string; internalDocs: string; aiCapacity: string;
    newIssue: string; signOut: string;
    activeAgents: string; agentsRunning: string; agentsRunningPlural: string;
    taskLabels: {
      support: { dashboard: string; inbox: string; issues: string; knowledge: string };
      engineering: { issues: string; missions: string; patches: string; sandboxRuns: string; workspaces: string };
    };
    profileHint: { text: string; action: string; dismiss: string };
  };
  topbar: {
    dashboard: string; live: string; searchPlaceholder: string;
    /** Label on the Command Center button beside the search field, and its
     *  accessible name. The palette's own entries are not localised, so this
     *  is the one place the console is named in the user's language. */
    commandCenter: string; openCommandCenter: string;
    openNavigation: string; openHelp: string;
    supportMode: string; supportModeDetail: string;
    backToCustomers: string; stopSupport: string; stopSupportTitle: string;
  };
  pages: {
    hrDemo: {
      eyebrow: string; title: string; subtitle: string;
      tenantLabel: string; projectLabel: string;
      scenarioGeneral: string; scenarioPersonal: string;
      scenarioConfidential: string; scenarioConflict: string;
      questionLabel: string; submit: string; submitting: string; submitFailed: string;
      boardLoadFailed: string; boardLoading: string;
      policyDecision: string; sources: string; sourceVersion: string;
      noAuthoritativeSource: string; sourceConflict: string; caseLabel: string;
      personalData: string; salaryRule: string;
      reviewWork: string; noReviewTasks: string;
      auditTimeline: string; noAuditEvents: string;
      casesTitle: string; noCases: string; answersTitle: string; noAnswers: string;
      correctionsTitle: string; assignedTo: string; unassigned: string;
      assignLoad: string; assignAction: string; escalateAction: string;
      resolveAction: string; resolutionPlaceholder: string;
      requestApproval: string; approve: string; reject: string; deliver: string;
      fourEyesNote: string; reportIncorrect: string; reportReasonPlaceholder: string;
      correctAction: string; retractAction: string;
      correctedTextPlaceholder: string; correctionReasonPlaceholder: string;
      actionDone: string; actionFailed: string;
      settingsTitle: string; settingsEnabled: string; settingsThreshold: string;
      save: string; settingsSaved: string;
      versionLabel: string;
      /** Presentatielabels bij opgeslagen HR-waarden — nooit de waarden zelf. */
      labels: Record<HRLabelCode, string>;
    };
    customers: {
      eyebrow: string; indexTitle: string; indexSubtitle: string;
      searchPlaceholder: string; statusActive: string; statusArchived: string; statusAll: string;
      newCustomer: string; createTitle: string; nameLabel: string; contactLegend: string;
      firstName: string; lastName: string; email: string; contactRole: string;
      create: string; cancel: string; save: string;
      colName: string; colStatus: string; colContacts: string; colUpdated: string;
      empty: string; emptyHint: string; loadError: string; notFound: string; backToList: string;
      detailEyebrow: string; rename: string; archive: string; reactivate: string;
      syncAction: string; syncOk: string; syncNotConnected: string;
      contactsTitle: string; contactsEmpty: string; addContact: string; editContact: string; contactArchived: string;
      communicationsTitle: string; communicationsEmpty: string; communicationsHint: string; commSubjectFallback: string;
      commStatus: {
        draft: string; pending_approval: string; approved: string; sent: string;
        failed: string; rejected: string; discarded: string;
      };
      toastCreated: string; toastUpdated: string; toastContactAdded: string; toastContactUpdated: string;
    };
    issues: {
      eyebrow: string; title: string; subtitle: string; newIssue: string;
      open: string; resolved: string; all: string; shown: string;
      searchPlaceholder: string; empty: string; emptyHint: string;
      exportCsv: string;
      filters: {
        project: string; allProjects: string;
        status: string; allStatuses: string; allPriorities: string;
      };
      headers: {
        key: string; issue: string; project: string; status: string;
        priority: string; actions: string;
      };
    };
    patches: {
      eyebrow: string; title: string; subtitle: string; subtitleNoTenant: string;
      pending: string; approved: string; applied: string; rejected: string;
      total: string; empty: string;
      emptyAll: string; emptyHint: string; emptyAction: string; filteredEmptyHint: string;
      fileCount: string; fileCountPlural: string; workspaceLink: string; untitledWorkspace: string;
      cols: { patch: string; version: string; status: string; verification: string };
      verification: { notRun: string };
      rowAction: { approve: string; reject: string; archive: string };
      searchPlaceholder: string;
      columnsMenu: string;
      splitView: {
        enable: string; disable: string; detail: string; resize: string;
        emptyTitle: string; emptyHint: string;
      };
      bulk: {
        approve: string; reject: string; archive: string;
        rejectTitle: string; rejectBodyOne: string; rejectBodyMany: string;
        archiveTitle: string; archiveBodyOne: string; archiveBodyMany: string;
      };
      actionError: {
        approve: string; reject: string; archive: string;
        gateOpen: string; notArchivable: string; unknown: string;
      };
      preview: {
        start: string; starting: string; open: string; dismiss: string;
        startFailed: string; openFailed: string; gone: string;
      };
      filter: {
        pending: string; approved: string; applied: string;
        rejected: string; archived: string; all: string;
      };
      status: {
        draft: string; approved: string; rejected: string;
        applied: string; changes_requested: string;
        archived: string; superseded: string;
      };
      supersededHint: string;
    };
    knowledge: {
      eyebrow: string; title: string; subtitle: string;
      addSource: string; empty: string; emptyHint: string;
      inspectButton: string; inspectButtonNoCodebase: string; inspectTooltip: string;
      scrapeButton: string;
      stats: {
        sources: string; sourcesSub: string;
        chunks: string; chunksSub: string;
        codebaseDocs: string; codebaseDocsSub: string;
        learnings: string; learningsSub: string;
        windev: string; windevSub: string;
        coverage: string; coverageSub: string;
      };
      filters: {
        searchPlaceholder: string;
        allTenants: string; allTypes: string; allCodebases: string;
        general: string; windev: string; codebase: string; learning: string;
        allScopes: string; scopeTenant: string; scopeProject: string; scopeCodebase: string;
        allProjects: string;
      };
      scope: {
        column: string;
        tenant: string; project: string; codebase: string;
        tenantHint: string; projectHint: string; codebaseHint: string;
        tenantWideWarning: string;
      };
      sourcesPanelTitle: string; sourcesPanelSub: string;
      table: {
        title: string; tenant: string; kind: string; chunks: string; status: string;
        moreTags: string; attemptHint: string;
      };
      noSources: string; noSourcesHint: string; noSourcesAsk: string;
      filteredEmpty: string; filteredEmptyHint: string;
      deleteOne: {
        confirmTitle: string;
        confirmLabel: string;
        confirmBody: string;
      };
      bulkDelete: {
        confirmTitle: string;
        confirmLabel: string;
        confirmBody: string;
        resultDeleted: string;
        resultSkipped: string;
      };
    };
    memory: {
      eyebrow: string; title: string; subtitle: string;
      tabGlobal: string; tabProject: string; saved: string; saving: string;
    };
    workspace: {
      eyebrow: string; title: string; subtitle: string;
      emptyHint: string; emptyAction: string;
      patchPanel: string; activity: string; chat: string;
      noPatch: string; runDev: string; reRunDev: string;
      newSession: string; searchPlaceholder: string; empty: string;
      shownOf: string;
      headers: { title: string; issue: string; status: string; updated: string };
      rename: string; renameTitle: string;
      deleteTitle: string; deleteConfirm: string;
      createTitle: string; createSubtitle: string;
      createFields: {
        project: string;
        sessionTitle: string; sessionTitlePlaceholder: string;
        linkedIssue: string; linkedIssuePlaceholder: string;
        instruction: string; instructionPlaceholder: string;
      };
      noProjects: string; createOneFirst: string;
      starting: string; startSession: string;
      contextHint: {
        codebase: string; source: string; repo: string; path: string; mode: string;
      };
      status: {
        drafting: string; ready_for_review: string; approved: string;
        rejected: string; applied: string;
      };
      bulk: {
        delete: string; deleteConfirmTitle: string; deleteConfirmLabel: string;
        deleteConfirmBody: string; result: string; resultPartial: string;
      };
    };
    sandboxRuns: {
      eyebrow: string; title: string; subtitle: string; empty: string;
      filter: { all: string; passed: string; failed: string; running: string; errored: string; cancelled: string; archived: string };
      cols: { status: string; run: string; language: string; lane: string; steps: string; duration: string; created: string };
      lane: { local: string; remote: string };
      noPatch: string; patchRef: string; patchRefId: string; stepsSummary: string;
      emptyHint: string; emptyAction: string;
      filteredEmpty: string; filteredEmptyHint: string;
      archivedEmpty: string; archivedEmptyHint: string;
      row: { archive: string; restore: string };
      detail: {
        cancel: string; rerun: string; loading: string; loadFailed: string;
        cancelled: string; cancelFailed: string;
        archive: string; restore: string; archived: string; archiveFailed: string;
        restored: string; restoreFailed: string; archivedBanner: string;
        rerunRequested: string; rerunReused: string; rerunFailed: string;
        attemptOf: string;
        patchSuperseded: string; patchSupersededLink: string;
        newerRunPassed: string; newerRunFailed: string; newerRunRunning: string;
        newerRunLink: string;
        technicalDetails: string; artifacts: string; evidenceTitle: string;
        steps: string; stepLog: string; stepLogTail: string; noSteps: string; notExecuted: string; runError: string;
        profileNotes: string; profileNotesUntranslated: string;
        notExecutedTitle: string; notExecutedTechnical: string;
        notExecutedReason: { dotnetframework: string; windev: string };
        notExecutedNext: { dotnetframework: string; windev: string };
        strategyFromProject: string; none: string;
        runId: string; patchValue: string;
        tiles: {
          language: string; workspace: string; patch: string;
          duration: string; lane: string; strategy: string;
        };
        fields: {
          executor: string; fingerprint: string; cluster: string;
          namespace: string; agent: string; operation: string;
          /** Migration 277: did the APPLICATION start, not only the tests pass? */
          appStart: string;
        };
        /** The reproduction session (mig 280), as an operator reads it. */
        reproduction: {
          title: string; lead: string; note: string; finding: string; cause: string;
          direction: string; confidence: string; transcript: string;
          noFinding: string; commands: string; exit: string; outputTruncated: string;
          confidenceValue: { high: string; medium: string; low: string };
          outcome: {
            ran: string; timeout: string; refused: string;
            cancelled: string; infrastructure: string;
          };
          /** The opt-in, offered where a reader meets the gap it fills: a run
           *  that failed and was explained only by the tail of a log. The
           *  feature is OFF per tenant by default. */
          optIn: {
            title: string; body: string; cost: string; enable: string;
            enabling: string; enabled: string; failed: string; noPermission: string;
          };
        };
        /** The application-start proof, as an operator reads it. */
        appStart: {
          title: string; verified: string; failed: string; notAttempted: string;
          unsupported: string; target: string; answered: string; budget: string;
          took: string; note: string;
          consequence: {
            verified: string; failed: string; notAttempted: string; unsupported: string;
          };
        };
        /** The "see it live" action: open a preview running THIS run's patch
         *  (POST /api/sandbox-runs/[id]/preview), and the card that follows the
         *  lease from "still booting" to "here is the link". The starting copy
         *  is not decoration: a container takes tens of seconds to come up, and
         *  what the page says during those seconds is the whole difference
         *  between waiting and thinking nothing happened. */
        preview: string; previewFailed: string; previewTitle: string;
        previewOpen: string; previewNote: string;
        previewStarting: string; previewStartingHint: string; previewElapsed: string;
        previewExpiresIn: string; previewStartFailed: string; previewStartTimeout: string;
        previewRetry: string;
        /** Said when the run's failureKind is environment-class: the sandbox
         *  never judged the patch, so the regression verdict on the same page
         *  must not be read as a statement about the change. */
        environmentBlock: {
          title: string; body: string; verdictCaveat: string; openProject: string;
          noProjectTitle: string; noProjectBody: string;
          /** The UNKNOWN cause. Not the same sentence as `body`: "unknown" is
           *  not evidence that the environment failed, only that nothing
           *  classified this, and blaming the environment on that basis sends
           *  a reader away from their own code. */
          bodyUnknown: string; bodyUnknownStep: string;
        };
        /** The regression-aware acceptance gate, explained where its verdict
         *  sits: which failures are the patch's fault, which were already red,
         *  and — the two cases that look like a verdict but are not — a suite
         *  that never ran and a suite that ran and selected nothing. */
        regressionVerdict: {
          headPassed: string; headFailed: string;
          headNeverRan: string; headRanNothing: string;
          headUiUnsupported: string; headUiNotRun: string;
          whyUiUnsupported: string; whyUiNotRun: string;
          note: string;
          whyPassed: string; whyPassedFixed: string;
          whyNeverRan: string; whyRanNothing: string;
          whyRegressions: string; whyUnparsed: string;
          unparsedStep: string; unparsedExit: string;
          whyAcceptanceAbsent: string; whyAcceptanceFailed: string;
          whyFixedNothing: string; whyGeneric: string;
          preexisting: string; regressions: string; newlyFixed: string;
          acceptance: string; acceptanceValue: string;
          acceptanceFailed: string; acceptanceAbsent: string; acceptanceSkipped: string;
          listRegressions: string; listUnattributed: string;
          listPreexisting: string; listNewlyFixed: string;
          acceptanceTargets: string;
          status: { passed: string; failed: string; skipped: string; absent: string };
        };
      };
      bulk: {
        cancel: string; cancelConfirmTitle: string; cancelConfirmLabel: string;
        cancelConfirmBody: string; result: string; resultPartial: string;
        archive: string; archiveConfirmTitle: string; archiveConfirmLabel: string; archiveConfirmBody: string;
        archiveResult: string; archiveResultSkipped: string; archiveResultPartial: string;
        restore: string; restoreResult: string; restoreResultPartial: string;
      };
    };
    projects: {
      eyebrow: string; defaultSubtitle: string;
      indexEyebrow: string; indexTitle: string; indexSubtitle: string;
      newProject: string; searchPlaceholder: string;
      modeFilter: { all: string };
      notFound: string; pickWorkspace: string; backToProjects: string;
      back: string; patchHistory: string;
      saveChanges: string; savingChanges: string; changesSaved: string;
      cardLabels: {
        codebases: string; primary: string; platforms: string;
        mode: string; updated: string;
      };
      guardrails: string; guardrailsPlural: string;
      empty: { title: string; hint: string; ask: string };
      countLabel: string; countLabelPlural: string;
      delete: {
        title: string; confirm: string;
        cancel: string; confirmButton: string;
      };
      bulk: {
        delete: string; deleteConfirmTitle: string; deleteConfirmLabel: string;
        deleteConfirmBody: string; result: string; resultPartial: string;
      };
      create: {
        title: string; subtitle: string;
        nameLabel: string; namePlaceholder: string;
        descriptionLabel: string; descriptionPlaceholder: string;
        primaryCodebaseLabel: string; primaryCodebaseNone: string;
        supportDeskLabel: string; supportDeskHint: string;
        helperText: string;
        cancel: string; createButton: string; creating: string;
        failed: string;
      };
      sections: {
        general: string; executionMode: string; tags: string;
        codebases: string; codebasesHelp: string;
        knowledgeSources: string; knowledgeHelp: string;
        guardrails: string; guardrailsHelp: string;
      };
      fields: {
        name: string; description: string; descriptionPlaceholder: string;
        addTag: string; addGuardrail: string; add: string;
        defaultBadge: string; setAsDefault: string;
        noCodebases: string;
        analyses: string; technicalDocs: string; procedures: string;
        general: string; chunksSuffix: string;
      };
      projectKnowledge: {
        title: string; help: string; empty: string;
        addSource: string; scrapeUrl: string; inspect: string; viewAll: string;
        includeGlobal: string; readOnly: string;
        stats: {
          projectSources: string; codebaseDocs: string; scrapedUrls: string;
          learnings: string; coverage: string;
        };
        filter: {
          showing: string; clear: string; none: string;
          allIndexed: string; notIndexed: string; more: string;
        };
        groups: { project: string; codebase: string; tenant: string };
      };
    };
  };

  time: {
    justNow: string;
    minutesAgoShort: string; hoursAgoShort: string;
    daysAgoShort: string; secondsAgoShort: string;
  };
  intake: {
    headerTitle: string; inputPlaceholder: string; greeting: string;
    pageSubtitle: string; pageBody: string; pageProductLabel: string;
    bubbleAria: string; closeAria: string; sendAria: string; thinking: string;
    contactPlaceholder: string; askAnother: string; reportAnother: string;
    yesHelped: string; noStuck: string; yesCreateTicket: string; noThanks: string;
    addDetail: string; createIssue: string; tellMeChange: string;
    issueCreated: string; typeStatus: string; sortedOut: string;
    sortedOutDetail: string; issueCreatedThanks: string; didThisSolve: string;
    inputPlaceholderShort: string;
    openInFixControl: string;
    preview: {
      ticketPreview: string; steps: string; expected: string;
      actual: string; environment: string; impact: string;
    };
  };
  /**
   * Developer governance. Typed strictly rather than left to the index
   * signature: the event/status/mode leaves are a projection of closed unions,
   * so a Dutch translation going missing must break the build, not silently
   * fall back to English inside a Dutch-first product surface.
   */
  devGovernance: {
    eyebrow: string; title: string; subtitle: string; navLink: string;
    loadError: string; retry: string; unset: string;
    noAccess: { title: string; body: string };
    notFound: { title: string; body: string };
    list: {
      empty: string; emptyHint: string; cliCommand: string;
      filterStatus: string; filterMode: string;
      allStatuses: string; allModes: string;
      columns: {
        status: string; mode: string; issueKey: string; branch: string;
        initiator: string; pullRequest: string; created: string;
      };
    };
    detail: {
      emptyTitle: string; emptyHint: string; close: string;
      section: { timeline: string; checkpoints: string };
      fields: {
        mode: string; issue: string; branch: string; commits: string;
        workspace: string; pullRequest: string; initiator: string;
        startedAt: string; submittedAt: string; completedAt: string;
        executionId: string;
      };
      openIssue: string; openPullRequest: string; readOnly: string;
    };
    review: {
      title: string;
      requiredYes: string; requiredNo: string; requiredUnknown: string;
      waitingSince: string; stallWarning: string;
      openDecision: string; noDecision: string;
      phase: { submitted: string; in_review: string; changes_requested: string };
    };
    overlap: {
      badge: string; title: string; subtitle: string; peer: string;
      openPeer: string; matchedPaths: string; morePaths: string;
      noPaths: string; detectedAt: string;
    };
    staleBase: {
      title: string; body: string; baseCommit: string; detectedAt: string;
    };
    timeline: {
      title: string; subtitle: string; empty: string;
      showOlder: string; showLess: string; loadMore: string;
      counter: string; follows: string;
    };
    checkpoints: {
      title: string; subtitle: string; empty: string;
      changedFiles: string; droppedFiles: string; truncatedFiles: string;
      noDescription: string; diffHash: string; testExit: string;
    };
    status: {
      active: string; submitted: string; in_review: string;
      changes_requested: string; completed: string; abandoned: string;
      failed: string;
    };
    mode: {
      human_local: string; ai_assisted_local: string; autonomous_agent: string;
      support_fix: string; review: string; deployment: string;
    };
    agentSteps: {
      title: string; subtitle: string; empty: string; detailHidden: string;
      modelUnknown: string; filesRead: string; filesChanged: string;
      commands: string; toolCalls: string; tests: string;
      patch: string; instruction: string;
    };
    worktrees: {
      title: string; subtitle: string; branch: string; detached: string;
      created: string; removed: string; stillOpen: string;
      status: {
        provisioning: string; active: string; cleaning: string;
        removed: string; stale: string;
      };
    };
    deployments: {
      title: string; subtitle: string; artifactDigest: string;
      digestUnknown: string; run: string; heuristic: string;
      environment: { production: string; staging: string; development: string };
      phase: {
        queued: string; running: string; succeeded: string;
        failed: string; cancelled: string; awaiting_approval: string;
      };
    };
    checkpointKind: { checkpoint: string; test_run: string; submit_snapshot: string };
    agentStepRole: { planner: string; executor: string; reviewer: string };
    agentStepStatus: {
      running: string; completed: string; failed: string; cancelled: string;
    };
    actor: { user: string; agent: string; system: string; webhook: string };
    event: {
      execution: {
        started: string; submitted: string; withdrawn: string; resumed: string;
        review_opened: string; changes_requested: string; approved: string;
        completed: string; abandoned: string; failed: string;
      };
      checkpoint: { recorded: string };
      test_run: { recorded: string };
      commit: { linked: string };
      pull_request: { linked: string; merged: string };
      ci: { linked: string };
      deployment: { linked: string };
      agent_step: { started: string; completed: string; failed: string };
      patch: { proposed: string; applied: string; rejected: string };
      overlap: { detected: string };
      stale_base: { detected: string };
      unknown: string;
    };
    payload: {
      executionMode: string; issueKey: string; branchName: string;
      codebaseId: string; kind: string; role: string; modelId: string;
      reviewRequired: string;
      decision: string; reason: string; note: string; prNumber: string;
      mergedBy: string; mergeCommitSha: string; sha: string; state: string;
      context: string; changedFileCount: string; droppedFileCount: string;
      truncatedCount: string; testCommand: string; testExitCode: string;
      mergedSha: string; matchedPathCount: string; heuristic: string;
    };
  };
}
